import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import ApiClient from '../utils/ApiClient';
import { humanizeShippingMode } from '../utils/shippingMode';
// Task 103: every analyze on this page now runs as a durable server-side job, so navigating away
// or reloading no longer abandons it -- see utils/plannerJobs for the kickoff/poll/re-attach model.
import {
    SCOPE_DRAFT, SCOPE_SPLIT_GROUP, SCOPE_PLANNER, SCOPE_KEY_PO_MODE,
    runAnalyzeJob, findActiveJob, resolveFinishedJob, waitForJob, jobProgressLabel,
    fetchSplitSetStatus, jobIsActive, splitOutcomesFromJob, runningDraftId, runAllProgress,
    startJob, rememberSelectedPlacement, initialPlacementFor,
} from '../utils/plannerJobs';
import {
    ExclamationTriangleIcon, TrashIcon, MagnifyingGlassIcon, TruckIcon, CubeIcon, MapPinIcon, ClockIcon,
    ArrowPathIcon, Squares2X2Icon, InformationCircleIcon, XMarkIcon, ArrowUpTrayIcon,
    DocumentTextIcon, ClipboardDocumentListIcon, PencilIcon, PlusIcon, EyeIcon,
    ChevronDownIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import FcCoverageTable from '../components/WarRoom/FcCoverageTable';
import CancelConfirmedShipmentModal from '../components/Common/CancelConfirmedShipmentModal';
import PrintLabelsAction from '../components/Common/PrintLabelsAction';
import BoxConfigModal from '../components/BoxConfigModal';
import { useBoxConfig } from '../hooks/useBoxConfig';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Fallback only — the backend now supplies the real reason via score.confirmDisabledReason
// (Miami-gate or freight/margin-erosion gate, whichever failed). Used only if that field is
// ever missing, so the disabled-confirm affordance never renders blank.
const MIAMI_GATE_REASON = 'Destinations cover <25% of demand-weighted need — park & retry, restructure, or change origin';

// ---- Task 19 (ASIN-mode run loading): pure parse of a loaded run's PlanItem[] (the AnalysisResponse
// `items` field -- identical content to the DB's items_json column) into AsinModePanel's row-state
// shape ({ asin, sku, name, quantity, sellingPrice }) plus a packagingByAsin map for any item that
// carries a complete 5-field packaging breakdown. Exported for unit testing -- the rest of the
// ASIN-mode run-loading path (wiring this into AsinModePanel's state) is build-gated only.
export function parseRunItemsForAsinPanel(items) {
    const list = Array.isArray(items) ? items : [];
    const parsedItems = list.map(it => ({
        asin: it.asin,
        sku: it.sku ?? null,
        name: it.name ?? it.asin,
        quantity: it.quantity || 0,
        sellingPrice: it.sellingPrice ?? null,
    }));
    const packagingByAsin = {};
    list.forEach(it => {
        if (it.unitsPerBox > 0 && it.boxLengthIn > 0 && it.boxWidthIn > 0 && it.boxHeightIn > 0 && it.boxWeightLbs > 0) {
            packagingByAsin[it.asin] = {
                unitsPerBox: it.unitsPerBox,
                boxLengthIn: it.boxLengthIn,
                boxWidthIn: it.boxWidthIn,
                boxHeightIn: it.boxHeightIn,
                boxWeightLbs: it.boxWeightLbs,
            };
        }
    });
    return { items: parsedItems, packagingByAsin };
}

// ---- Task 24 (packaging modal redesign): exact-match check between an ASIN's current packaging
// config and the stored default row -- drives the PO-confirm "saved default" source label.
// Pure/module-level since it doesn't touch component state.
function packagingValuesMatch(a, b) {
    if (!a || !b) return false;
    return Number(a.unitsPerBox) === Number(b.unitsPerBox)
        && Number(a.boxLengthIn) === Number(b.boxLengthIn)
        && Number(a.boxWidthIn) === Number(b.boxWidthIn)
        && Number(a.boxHeightIn) === Number(b.boxHeightIn)
        && Number(a.boxWeightLbs) === Number(b.boxWeightLbs);
}

// ---- Task 41 (two-tier box model -- owner, verbatim: "the user should specify the weight of the
// unit, and also specify the weight and qty of boxes to make the shipment. and the total is added
// together"). Pure helpers mirroring the server's formula (ShipmentPlannerAnalysisService), used
// for the modal's live math line, the chips, and local-only "Use for this shipment" applies.
// Exported for unit testing. ----

export function fmtLbsShort(v) {
    if (v == null || !isFinite(v)) return '';
    const r = Math.round(v * 100) / 100;
    return String(r);
}

// Loaded weight of one full box: unitsPerBox x unitWeightLbs + the shared carton's tare.
// Null unless every input is a positive number.
export function computeLoadedBoxWeightLbs(unitsPerBox, unitWeightLbs, tareWeightLbs) {
    const upb = Number(unitsPerBox), uw = Number(unitWeightLbs), tare = Number(tareWeightLbs);
    if (!(upb > 0) || !(uw > 0) || !(tare > 0)) return null;
    return Math.round((upb * uw + tare) * 100) / 100;
}

// The five legacy analyze/watch fields, derived from the two-tier config (dims copied off the
// shared carton, weight computed) -- exactly what the server derives at save time, so a
// local-only "Use for this shipment" apply and a saved default behave identically downstream.
export function deriveLegacyPackagingFields(unitsPerBox, unitWeightLbs, box) {
    if (!box) return null;
    const loaded = computeLoadedBoxWeightLbs(unitsPerBox, unitWeightLbs, box.tareWeightLbs);
    if (loaded == null) return null;
    return {
        unitsPerBox: Number(unitsPerBox),
        boxLengthIn: box.boxLengthIn,
        boxWidthIn: box.boxWidthIn,
        boxHeightIn: box.boxHeightIn,
        boxWeightLbs: loaded,
    };
}

// Shipment totals for a given qty: boxCount = ceil(qty / unitsPerBox); total weight = full boxes
// at the loaded weight + a partial last box (r x unitWeight + tare) when qty isn't a multiple.
export function computeShipmentTotals(qty, unitsPerBox, unitWeightLbs, tareWeightLbs) {
    const q = Number(qty), upb = Number(unitsPerBox), uw = Number(unitWeightLbs), tare = Number(tareWeightLbs);
    if (!(q > 0) || !(upb > 0)) return null;
    const boxCount = Math.ceil(q / upb);
    const loaded = computeLoadedBoxWeightLbs(upb, uw, tare);
    if (loaded == null) return { boxCount, totalLbs: null };
    const fullBoxes = Math.floor(q / upb);
    const remainder = q % upb;
    const total = fullBoxes * loaded + (remainder > 0 ? remainder * uw + tare : 0);
    return { boxCount, totalLbs: Math.round(total * 100) / 100 };
}

// Chip / row summary: "5/box · 18×18×32.5 · 29 lb (computed)" -- the "(computed)" suffix only
// when the weight was derived from unit weight + shared carton (pkg.computed), not typed as a
// legacy loaded weight. Task 67: single-unit entries read "Unit ships as its own box — 1/box ·
// 39.6×3.9×3.9 · 7 lb (from Amazon)" -- the suffix only while the values still match the
// Amazon-catalog suggestion they came from.
export function packagingSummaryLine(pkg) {
    if (!pkg) return '';
    if (pkg.style === 'UNIT') {
        return `Unit ships as its own box — 1/box · ${pkg.boxLengthIn}×${pkg.boxWidthIn}×${pkg.boxHeightIn}`
            + ` · ${fmtLbsShort(pkg.boxWeightLbs)} lb${pkg.unitBoxSource === 'AMAZON' ? ' (from Amazon)' : ''}`;
    }
    return `${pkg.unitsPerBox}/box · ${pkg.boxLengthIn}×${pkg.boxWidthIn}×${pkg.boxHeightIn} · ${pkg.boxWeightLbs} lb${pkg.computed ? ' (computed)' : ''}`;
}

// Owner, 2026-09-01 ("the edit box function is not working to edit a preexisting template"):
// there was no edit affordance at all. The catalog card's pencil prefills the box form from the
// card via this helper (numbers become input strings), and Save goes through the same
// upsert-by-name the backend already supports.
export function boxFormFromCatalogBox(box) {
    const s = v => (v == null ? '' : String(v));
    return {
        name: box?.name || '',
        boxLengthIn: s(box?.boxLengthIn),
        boxWidthIn: s(box?.boxWidthIn),
        boxHeightIn: s(box?.boxHeightIn),
        tareWeightLbs: s(box?.tareWeightLbs),
    };
}

// Owner, 2026-09-01 ("the delete function ... is not working"): it worked, but the in-use
// refusal went to the page-top toast, which the full-screen packaging modal covers -- the
// operator saw nothing happen. Failures render INSIDE the modal, formatted here.
export function boxCatalogErrorText(err, action) {
    const detail = err?.response?.data?.error || err?.message || 'unknown error';
    return `Could not ${action} box: ${detail}`;
}

// ---- Task 67 (owner insight, verbatim: "most of these are oversized products and not cartons -
// each unit is the 'box'" + "maybe a toggle that would allow for carton vs single unit 'boxes'?"):
// the "unit = box" packaging style. Pure helpers, exported for ShipmentPlanner.test.js. ----

// A packagingByAsin working entry built from a GET /packaging row's derivedUnitBox suggestion --
// units/box locked to 1, box dims/weight = the unit's own Amazon-known package. Null when the
// suggestion is absent or unusable (the honest guard: no partial prefill, the manual path stands).
export function unitBoxEntryFromDerived(derived) {
    if (!derived || !(derived.boxLengthIn > 0) || !(derived.boxWidthIn > 0)
        || !(derived.boxHeightIn > 0) || !(derived.boxWeightLbs > 0)) {
        return null;
    }
    return {
        unitsPerBox: 1,
        boxLengthIn: derived.boxLengthIn,
        boxWidthIn: derived.boxWidthIn,
        boxHeightIn: derived.boxHeightIn,
        boxWeightLbs: derived.boxWeightLbs,
        unitWeightLbs: derived.boxWeightLbs, // 1/box, no carton: the unit weight IS the box weight
        boxCatalogId: null,
        boxName: null,
        tareWeightLbs: null,
        computed: false,
        style: 'UNIT',
        unitBoxSource: 'AMAZON',
        derivedUnitBox: derived,
    };
}

// True when a working entry's four physical values still equal the Amazon suggestion they were
// prefilled from (drives the "(from Amazon)" suffix and the bulk action's don't-clobber guard).
export function unitBoxMatchesDerived(entry, derived) {
    if (!entry || !derived) return false;
    const eq = (a, b) => Number.isFinite(Number(a)) && Math.abs(Number(a) - Number(b)) <= 0.01;
    return eq(entry.boxLengthIn, derived.boxLengthIn) && eq(entry.boxWidthIn, derived.boxWidthIn)
        && eq(entry.boxHeightIn, derived.boxHeightIn) && eq(entry.boxWeightLbs, derived.boxWeightLbs);
}

// The packaging modal's toggle default: the working entry's style wins, then the saved row's
// packagingStyle (which the server already display-time-infers for pre-column rows), then --
// when NOTHING is configured but Amazon knows the unit's package dims -- UNIT (the owner's
// zero-data-entry path for oversize products). Everything else defaults CARTON (the pre-task-67
// behavior).
export function defaultPackagingStyle(workingEntry, metaRow) {
    if (workingEntry?.style === 'UNIT') return 'UNIT';
    if (workingEntry?.unitsPerBox > 0) return 'CARTON';
    if (metaRow?.packagingStyle === 'UNIT') return 'UNIT';
    if (metaRow?.unitsPerBox > 0) return 'CARTON';
    if (metaRow?.derivedUnitBox) return 'UNIT';
    return 'CARTON';
}

// One PO-confirm row's eligibility for the bulk "Apply 'unit = box' to all unconfigured (N)"
// action: no saved default, an Amazon suggestion to fill from, not already confirmed, and no
// manual edit in the way (only untouched Amazon prefills / incomplete values are overwritten --
// per the warehouse-confirmation doctrine each row stays individually re-editable afterwards).
export function isUnitBoxBulkTarget(defaultRow, workingEntry, confirmed, isComplete) {
    if (confirmed) return false;
    if (defaultRow?.unitsPerBox > 0) return false;         // has a saved config -- not "unconfigured"
    const derived = defaultRow?.derivedUnitBox;
    if (!unitBoxEntryFromDerived(derived)) return false;   // no usable suggestion -> manual path
    if (isComplete && !(workingEntry?.style === 'UNIT' && unitBoxMatchesDerived(workingEntry, derived))) {
        return false;                                      // operator typed something -- keep it
    }
    return true;
}

// The spelled-out math for a computed config: "5 × 5 lb + 4 lb box = 29 lb per box". Null for
// legacy (non-computed) configs.
function packagingMathLine(pkg) {
    if (!pkg || !pkg.computed || !(pkg.unitWeightLbs > 0) || !(pkg.tareWeightLbs > 0)) return null;
    return `${pkg.unitsPerBox} × ${fmtLbsShort(pkg.unitWeightLbs)} lb + ${fmtLbsShort(pkg.tareWeightLbs)} lb box = ${fmtLbsShort(pkg.boxWeightLbs)} lb per box`;
}

// A packagingByAsin working entry built from a GET /packaging response row -- the five legacy
// analyze/watch fields plus (when the stored default carries the task-41 two-tier config) the
// unit-weight/carton fields and the `computed` flag driving "(computed)" chips + math lines.
// tareWeightLbs is recovered from the server-computed loadedBoxWeightLbs rather than shipped as
// its own field (loaded = units x unitWeight + tare, all three others known).
// Task 67: a row with NO saved config but a derivedUnitBox suggestion (the stub rows GET
// /packaging now returns for unconfigured ASINs with Amazon catalog dims) becomes a prefilled
// single-unit entry -- the owner's oversize products never require data entry. Saved rows carry
// their packagingStyle through (server-side display inference included).
export function packagingEntryFromRow(row) {
    if (!row) return null;
    if (!(row.unitsPerBox > 0)) {
        return unitBoxEntryFromDerived(row.derivedUnitBox); // null when no usable suggestion either
    }
    const computed = !!(row.unitWeightLbs > 0 && row.boxCatalogId != null);
    const style = row.packagingStyle ?? null;
    return {
        unitsPerBox: row.unitsPerBox,
        boxLengthIn: row.boxLengthIn,
        boxWidthIn: row.boxWidthIn,
        boxHeightIn: row.boxHeightIn,
        boxWeightLbs: row.boxWeightLbs,
        unitWeightLbs: row.unitWeightLbs ?? null,
        boxCatalogId: row.boxCatalogId ?? null,
        boxName: row.boxName ?? null,
        tareWeightLbs: (computed && row.loadedBoxWeightLbs != null && row.unitsPerBox > 0)
            ? Math.round((row.loadedBoxWeightLbs - row.unitsPerBox * row.unitWeightLbs) * 100) / 100
            : null,
        computed,
        style,
        unitBoxSource: style === 'UNIT' && unitBoxMatchesDerived(row, row.derivedUnitBox) ? 'AMAZON' : null,
        derivedUnitBox: row.derivedUnitBox ?? null,
        // Milestone C phase 0 (owner's Send to Amazon pattern, "Pallets: 1 = Boxes: 38 = Units: 38"):
        // the packing template's boxes-per-pallet, carried through so the LTL entry UI can offer
        // pallet-denominated quantity entry. SQL_PACKAGING_GET_TEMPLATE already returns it; this was
        // the one thing dropping it on the floor.
        boxesPerPallet: row.boxesPerPallet ?? null,
    };
}

// Task 24/41: a working/saved packaging entry counts as "complete" (i.e. every legacy analyze/
// watch field is a real, positive value) -- drives the PO-confirm gate, chips, and the bulk
// unit-box target check. Pure (takes only `pkg`), so relocated here verbatim from inside the
// ShipmentPlanner component (Task 3, PO Creation mount) so a second host can import it; behaviour
// and name unchanged.
export function isPackagingComplete(pkg) {
    return !!(pkg && pkg.unitsPerBox > 0 && pkg.boxLengthIn > 0
        && pkg.boxWidthIn > 0 && pkg.boxHeightIn > 0 && pkg.boxWeightLbs > 0);
}

// Milestone C phase 0: pallets -> units, mirroring Amazon's own Send to Amazon pattern (1 pallet =
// boxesPerPallet boxes = boxesPerPallet x unitsPerBox units). Units remain the wire truth
// (ItemInput.quantity) -- this is a display/entry convenience only. Any non-positive or missing
// input (pallets, boxesPerPallet, or unitsPerBox) yields 0, which callers treat as "not derivable,"
// never as a real quantity.
export function palletsToUnits(pallets, pkg) {
    const p = Number(pallets);
    const boxesPerPallet = Number(pkg?.boxesPerPallet);
    const unitsPerBox = Number(pkg?.unitsPerBox);
    if (!(p > 0) || !(boxesPerPallet > 0) || !(unitsPerBox > 0)) return 0;
    return p * boxesPerPallet * unitsPerBox;
}

// The spelled-out math behind a pallet-denominated entry -- "1 pallet = 38 boxes = 38 units",
// Amazon's own Send to Amazon phrasing. Null when it isn't derivable (mirrors palletsToUnits'
// guard), which is what keeps the affordance itself from rendering.
export function palletDerivationLabel(pallets, pkg) {
    const p = Number(pallets);
    const boxesPerPallet = Number(pkg?.boxesPerPallet);
    const unitsPerBox = Number(pkg?.unitsPerBox);
    if (!(p > 0) || !(boxesPerPallet > 0) || !(unitsPerBox > 0)) return null;
    const boxes = p * boxesPerPallet;
    const units = boxes * unitsPerBox;
    return `${p} pallet${p === 1 ? '' : 's'} = ${boxes} box${boxes === 1 ? '' : 'es'} = ${units} unit${units === 1 ? '' : 's'}`;
}

// ---- Task 61 (LTL toggle) pure helpers -- exported for ShipmentPlanner.test.js ----

// ---- Task 88 (owner, verbatim: "we need to be able to designate a product as 'UPS', 'LTL', or
// 'UPS/LTL' - these shipping designations will be associated with the ASIN. when the user selects
// the UPS tab to create UPS shipments, the UPS and UPS/LTL product will remain, when LTL tab is
// selected the LTL and UPS/LTL products will remain") ----
//
// The canonical vocabulary, mirroring org.calibrate.amazonspi.util.PackagingMode on the server.
// calibrate_asin_packaging_defaults.allowed_modes was ENUM('UPS_ONLY','LTL_ONLY','BOTH') and held
// 'BOTH' on all 21 of its rows on 2026-08-20; the legacy *_ONLY spellings stay readable forever.
// UNKNOWN IS NEVER RESTRICTIVE: null/blank/garbage all normalize to BOTH, so no PO line can ever
// silently disappear from the modal because of a value nobody recognizes.
export function normalizeAllowedModes(raw) {
    if (raw == null) return 'BOTH';
    const token = String(raw).trim().toUpperCase().replace(/[^A-Z]/g, '');
    if (token === 'UPS' || token === 'UPSONLY') return 'UPS';
    if (token === 'LTL' || token === 'LTLONLY') return 'LTL';
    return 'BOTH';
}

// Has this ASIN actually been designated, or is it just defaulting to BOTH? Drives the subtle
// "not set" hint -- the operator can then designate it instead of assuming somebody already did.
export function isAllowedModesSet(raw) {
    return raw != null && String(raw).trim() !== '';
}

/** The stored (possibly null) designation for one ASIN, from the shared asin -> value map. */
export function allowedModesFor(asin, modesByAsin) {
    return asin ? (modesByAsin?.[asin] ?? null) : null;
}

/** Owner's rule: UPS tab keeps UPS + UPS/LTL; LTL tab keeps LTL + UPS/LTL. Unset counts as BOTH. */
export function lineAllowedInMode(rawAllowedModes, mode) {
    const designation = normalizeAllowedModes(rawAllowedModes);
    if (designation === 'BOTH') return true;
    return designation === normalizeAllowedModes(mode);
}

/**
 * Split PO lines into the ones the current tab shows and the ones it hides. A line with no ASIN
 * can't carry a designation (nothing to store it against) and is never hidden. `showAll` is the
 * one-click escape hatch -- nothing is hidden while it is on.
 */
export function partitionPoLinesByMode(lines, mode, modesByAsin, showAll = false) {
    const visible = [];
    const hidden = [];
    (lines || []).forEach(line => {
        if (showAll || !line?.asin || lineAllowedInMode(allowedModesFor(line.asin, modesByAsin), mode)) {
            visible.push(line);
        } else {
            hidden.push(line);
        }
    });
    return { visible, hidden };
}

/** "12 LTL-only lines hidden in UPS mode" -- empty string when nothing is hidden. */
export function hiddenLinesMessage(hiddenCount, mode) {
    if (!hiddenCount) return '';
    const m = normalizeAllowedModes(mode);
    const other = m === 'LTL' ? 'UPS' : 'LTL';
    return `${hiddenCount} ${other}-only line${hiddenCount !== 1 ? 's' : ''} hidden in ${m} mode`;
}

/** Chip/button wording. The owner says "UPS/LTL" for both-modes; the segmented control says "Both". */
export function allowedModesLabel(raw) {
    const designation = normalizeAllowedModes(raw);
    if (designation === 'UPS') return 'UPS only';
    if (designation === 'LTL') return 'LTL only';
    return 'UPS/LTL';
}

/**
 * Fold a GET /packaging response into the shared asin -> designation map. Every REQUESTED ASIN is
 * seeded null first, so an ASIN the response omits entirely (no calibrate_asin_packaging_defaults
 * row) is recorded as "not set" rather than left looking un-fetched.
 */
export function mergeAllowedModes(prev, requestedAsins, rows) {
    const next = { ...(prev || {}) };
    (requestedAsins || []).forEach(asin => { if (asin) next[asin] = null; });
    (rows || []).forEach(row => {
        if (!row || !row.asin) return;
        next[row.asin] = isAllowedModesSet(row.allowedModes)
            ? normalizeAllowedModes(row.allowedModes)
            : null;
    });
    return next;
}

// Default for the Import-from-PO modal's UPS|LTL toggle: LTL only when EVERY selected ASIN is
// designated LTL; anything else (BOTH/UPS/unset/mixed/empty selection) defaults UPS.
export function defaultShipmentModeForAsins(asins, metaByAsin) {
    if (!asins || asins.length === 0) return 'UPS';
    return asins.every(a => normalizeAllowedModes(metaByAsin?.[a]?.allowedModes) === 'LTL') ? 'LTL' : 'UPS';
}

// Task 65 (compute-after-split): the LTL block of the analyze-asin-list payload -- ONLY what the
// warehouse can know before Amazon splits the plan (footprint + tare are warehouse facts;
// freight class optional, "auto (density)" when blank; declared value optional plan-total edit).
// Pallet count/height/weight are NEVER sent upfront: the backend computes them per shipment from
// stacking math once the split exists. Blank/invalid fields become null = backend auto.
export function buildLtlRequestPayload(form) {
    const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : null; };
    const int = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : null; };
    return {
        palletLengthIn: num(form.palletLengthIn),
        palletWidthIn: num(form.palletWidthIn),
        palletTareLbs: num(form.palletTareLbs),
        stackable: form.stackable !== false,
        freightClass: form.freightClass ? form.freightClass : null,
        declaredValueTotal: num(form.declaredValue),
        // Milestone C phase 0: how many pallets the operator declares up front, BEFORE Amazon has
        // split anything -- reconciled against the built total in phase 2 (task C4). Blank must
        // never become 0 -- "never declared" and "declared none" are different facts -- so a blank
        // or invalid field sends null, exactly like every other field here.
        declaredPalletCount: int(form.declaredPalletCount),
    };
}

// ---- Task 70b: ready-for-pickup date (owner, verbatim: "amazon may also require a pickup date
// to be specified for the ltl - we need a way to indicate that") ----
//
// The backend has always sent a readyToShipWindow on every LTL transportation configuration --
// ShipmentPlannerService.resolveReadyToShipStart uses the request's readyToShipDate when present
// and otherwise falls back to +2 business days. Nothing in the UI ever set it, so the fallback
// silently decided the carrier's pickup day. Amazon quotes real partnered-LTL carrier appointments
// against that date (a single analyze came back with CENTRAL TRANSPORT slots on Aug 20, 24, 26, 27
// and 31), so the operator -- who knows when the pallets will actually be built and staged -- must
// be the one to choose it.
//
// Default lead time. One business day: the warehouse stages a PO the day after it is picked, and
// Amazon offered next-day appointments in practice. Weekends are skipped; public holidays are NOT
// modelled (there is no holiday calendar in this codebase, and a wrong holiday table is worse than
// none -- the operator can always move the date forward). Note this deliberately differs from the
// backend's +2-business-day FALLBACK, which stays as-is because it serves the non-UI callers
// (placement probe / watch / sweep) whose behavior must not change; once the UI sends an explicit
// date the fallback no longer applies.
export const DEFAULT_PICKUP_LEAD_BUSINESS_DAYS = 1;
// How far ahead a pickup may be scheduled. Amazon's partnered quotes expire quickly and its
// appointment slots ran ~2 weeks out, so a month is a generous ceiling that still blocks typos
// like a year-ahead date.
export const MAX_PICKUP_HORIZON_DAYS = 30;

const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

/** ISO (YYYY-MM-DD) for a Date, in LOCAL time -- toISOString() would shift the day for anyone
 * west of UTC, which is every warehouse this app serves. */
export function toIsoDate(d) {
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `n` business days after `from`, skipping Saturdays and Sundays. n=0 returns `from` unchanged
 * (even if it is a weekend -- callers that need a business day pass n>=1). */
export function addBusinessDays(from, n) {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let added = 0;
    while (added < n) {
        d.setDate(d.getDate() + 1);
        if (!isWeekend(d)) added++;
    }
    return d;
}

/** Default "ready for pickup" date: the next business day. Friday -> Monday, Saturday -> Monday,
 * Sunday -> Monday. */
export function defaultPickupDate(today = new Date()) {
    return toIsoDate(addBusinessDays(today, DEFAULT_PICKUP_LEAD_BUSINESS_DAYS));
}

/**
 * Earliest selectable pickup date: today (the warehouse may already be staged).
 *
 * <p>Today stays selectable deliberately. Picking it used to produce run #255 -- the server sent
 * the day as UTC midnight, which for an ET operator is already stale before their working day
 * begins, and Amazon answered "DateTime value '2026-08-21T00:00:00Z' cannot be in the past" with
 * no transportation options for any placement. The fix belongs on the server, and is there: per the
 * owner (2026-08-21) today's date now resolves to the current date-time, so "today" means "ready
 * now" rather than an instant that has expired. See ShipmentPlannerService.resolveReadyToShipStart.
 *
 * <p>What this floor still does is keep genuinely past days off the picker, which is why it is
 * also what draftPayloadToPanelState uses to drop a stale stored pickup date.
 */
export function minPickupDate(today = new Date()) {
    return toIsoDate(today);
}

/** Latest selectable pickup date. */
export function maxPickupDate(today = new Date()) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    d.setDate(d.getDate() + MAX_PICKUP_HORIZON_DAYS);
    return toIsoDate(d);
}

/** "Fri Aug 21" for option cards / confirmed view. Empty string for a missing/unparseable date. */
export function formatPickupLabel(iso) {
    if (!iso || typeof iso !== 'string') return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** LTL cannot be analyzed without a pickup date -- the carrier needs a day to show up. UPS treats
 * it as optional (Amazon schedules parcel pickup itself), so only LTL gates on it. */
export function pickupDateBlocksAnalyze(shipmentMode, pickupDate) {
    return shipmentMode === 'LTL' && !(pickupDate && String(pickupDate).trim());
}

// Task 65: NMFC density table (mirrors ShipmentPlannerService.NMFC_DENSITY_THRESHOLDS/CLASSES) --
// used by the adjust-pallets modal to recompute density + class live before re-quoting.
// NMFC density scale, CURRENT as of 2025-07-19 -- NMFTA replaced the long-standing 18-class table
// with this 13-tier one, retiring classes 77.5, 110, 150, 200 and 500. We carried the pre-2025
// table until 2026-08-21, which over-classed by a tier everywhere below 15 lb/ft3 (run 266 at
// 2.6 lb/ft3 read FC_300 where the current scale says FC_250).
// MUST stay in lockstep with NMFC_DENSITY_THRESHOLDS/CLASSES in ShipmentPlannerService.java --
// the operator sees this copy while Amazon is sent that one, so a divergence is invisible.
const NMFC_DENSITY_TABLE = [
    [50, 'FC_50'], [35, 'FC_55'], [30, 'FC_60'], [22.5, 'FC_65'], [15, 'FC_70'],
    [12, 'FC_85'], [10, 'FC_92_5'], [8, 'FC_100'], [6, 'FC_125'], [4, 'FC_175'],
    [2, 'FC_250'], [1, 'FC_300'],
];

export function freightClassFromDensity(density) {
    if (!Number.isFinite(density) || density <= 0) return null;
    for (const [threshold, fc] of NMFC_DENSITY_TABLE) {
        if (density >= threshold) return fc;
    }
    return 'FC_400';   // below 1 lb/ft3 -- lowest tier on the current NMFC scale
}

// Shipment density lb/ft³ over the pallet cube (count x L x W x H).
export function palletDensity(totalWeightLb, count, lengthIn, widthIn, heightIn) {
    const cube = (count * lengthIn * widthIn * heightIn) / 1728;
    if (!Number.isFinite(cube) || cube <= 0 || !Number.isFinite(totalWeightLb) || totalWeightLb <= 0) return null;
    return totalWeightLb / cube;
}

export function freightClassLabel(fc) {
    return fc ? fc.replace('FC_', '').replace('_5', '.5') : '—';
}

// The EXACT string PlacementScoringService.UNPRICED_CONFIRM_REASON emits (see
// PlacementScoringService.java:59). Matched verbatim rather than pattern-sniffed: the merge below
// may only lift the confirm gate it can prove is the unpriced one, never some other gate that
// happens to be worded about freight. Keep in sync with the Java constant.
export const UNPRICED_CONFIRM_REASON =
    'Amazon has not quoted freight for this placement yet, so it has no price and no verdict.';

// Task 65: merge a POST /requote-ltl response into the in-memory analysis result -- replaces the
// adjusted option's per-shipment transport fields + pallet summaries (keeping destination/items
// from the original analyze), recomputes the option totals, and unions the warnings.
//
// Final-review Critical 1: it must also lift the confirm gate. Both gates (this page's
// score.confirmAllowed and the server's stored-transportCostKnown refusal) were computed at
// ANALYZE time, when Amazon had not quoted. A re-quote that returns real prices and leaves those
// gates alone is a dead end: the card shows fresh Amazon money next to "Amazon has not quoted
// freight for this placement yet", and Confirm stays disabled. Pricing is all that is unlocked --
// the verdict stays withdrawn (scoreKnown/decision untouched), because a price is not a score.
export function mergeLtlRequoteIntoAnalysis(analysisResult, placementOptionId, resp) {
    if (!analysisResult || !resp) return analysisResult;
    // "Any shipment came back priced" is exactly what the backend's anyPriced flag reports (a
    // genuinely free quote is $0.00 and must still count, so the amount cannot be the test).
    const anyPriced = resp.transportCostKnown === true;
    const placementOptions = (analysisResult.placementOptions || []).map(po => {
        if (po.placementOptionId !== placementOptionId) return po;
        const shipments = (po.shipments || []).map(sh => {
            const upd = (resp.shipments || []).find(u => u.shipmentId === sh.shipmentId);
            if (!upd) return sh;
            return {
                ...sh,
                transportCost: upd.transportCost,
                carrierName: upd.carrierName ?? sh.carrierName,
                shippingMode: upd.shippingMode ?? sh.shippingMode,
                transportationOptionId: upd.transportationOptionId ?? sh.transportationOptionId,
                availableTransportOptions: upd.availableTransportOptions ?? sh.availableTransportOptions,
                pallets: upd.pallets ?? sh.pallets,
            };
        });
        const totalTransportCost = shipments.reduce((s, sh) => s + (sh.transportCost || 0), 0);
        return {
            ...po,
            shipments,
            totalTransportCost,
            totalCost: (po.placementFee || 0) + totalTransportCost,
            // An unpriced re-quote changes nothing here: it has learned nothing better than what
            // the analyze already stored, and must not downgrade an option that WAS priced.
            transportCostKnown: anyPriced ? true : po.transportCostKnown,
        };
    });
    // Lift ONLY the unpriced gate, and only on the matching option. A different
    // confirmDisabledReason (negative margin, the Miami rule, ...) is a different question that a
    // freight quote does not answer, so it stands. decision / decisionReason / scoreKnown / score
    // are deliberately untouched: freight arriving unlocks confirming, it does not invent a verdict
    // for a score that was never computed with freight in it.
    const scores = anyPriced && Array.isArray(analysisResult.scores)
        ? analysisResult.scores.map(sc => (
            sc && sc.placementOptionId === placementOptionId
                && sc.confirmDisabledReason === UNPRICED_CONFIRM_REASON
                ? { ...sc, confirmAllowed: true, confirmDisabledReason: null }
                : sc))
        : analysisResult.scores;
    const warnings = [...new Set([...(analysisResult.warnings || []), ...(resp.warnings || [])])];
    // 2026-08-21: a requote's warnings arrive as raw strings only (the requote path does not build
    // boxes, so it has no box->product map). Appending them to the GROUPED list as unattributed
    // entries keeps both kinds visible in one place rather than letting a requote warning vanish
    // behind the grouped view.
    const existingGroups = analysisResult.warningGroups || [];
    const known = new Set(existingGroups.flatMap(g => g.rawMessages || []));
    const extraGroups = (resp.warnings || [])
        .filter(w => w && !known.has(w))
        .map(w => ({ message: w, attributed: false, affectedBoxes: 0, rawMessages: [w] }));
    const warningGroups = existingGroups.length > 0 || extraGroups.length > 0
        ? [...existingGroups, ...extraGroups]
        : analysisResult.warningGroups;
    return {
        ...analysisResult,
        placementOptions,
        scores,
        warnings: warnings.length > 0 ? warnings : analysisResult.warnings,
        warningGroups,
    };
}

// ---- Task 71 (partnered-LTL carrier gap) pure helpers -- exported for ShipmentPlanner.test.js ----
//
// Amazon Partnered LTL needs ONE carrier across EVERY shipment of a placement. When no such
// carrier exists the backend used to throw, an outer catch swallowed it, and the operator got $0
// stub quotes with no explanation (owner's PO 5951: the 69in/323lb shower-tray shipment had no
// partnered LTL offering at all). The backend now returns a structured `ltlCarrierGap`; these
// helpers turn it into panel copy and into the exclusion remedy's row filter.

/** The gap to render, or null. Present only when NO placement option could be quoted -- if even
 * one option came back priced, the answer is "pick that option", not "the plan is unquotable". */
export function ltlCarrierGapFrom(analysisResult) {
    const gap = analysisResult?.ltlCarrierGap;
    if (!gap) return null;
    if (!Array.isArray(gap.shipments) || gap.shipments.length === 0) return null;
    return gap;
}

/** Remedy (a)'s target set: the ASINs sitting in shipments Amazon offered no partnered LTL for.
 * Empty when the gap is a disjoint-carrier one (every shipment has an offer, none in common) --
 * there is no single item to drop, so the panel hides the exclude action. */
export function ltlGapExcludableAsins(gap) {
    return Array.isArray(gap?.blockingAsins) ? gap.blockingAsins.filter(Boolean) : [];
}

/** Drop the offending rows from the plan composition. The task-69 draft autosave keeps the
 * original safe, so the excluded ASIN can be shipped separately afterwards. */
export function excludeAsinsFromRows(rows, asins) {
    const drop = new Set((asins || []).filter(Boolean));
    if (drop.size === 0) return rows || [];
    return (rows || []).filter(r => !drop.has(r.asin));
}

/** "SCK8 · Stockton, CA" -- falls back to the raw shipment id when the destination never resolved. */
export function ltlGapShipmentLabel(q) {
    if (!q) return '';
    const fc = q.destinationFc || q.shipmentId || '';
    const city = q.destinationCity ? `${q.destinationCity}${q.destinationState ? ', ' + q.destinationState : ''}` : '';
    return city ? `${fc} · ${city}` : fc;
}

/** Requirement 3 (per-shipment truth): that shipment's OWN best partnered-LTL quote, or the
 * explicit absence. Never "$0.00" -- a missing offer is not free freight. */
export function ltlGapQuoteLabel(q) {
    if (!q?.partneredLtlOffered) return 'none offered';
    const cost = typeof q.bestCost === 'number' ? `$${q.bestCost.toFixed(2)}` : '—';
    return q.bestCarrierName ? `${q.bestCarrierName} ${cost}` : cost;
}

// ---- Task 73 (own-carrier / non-partnered confirmation) pure helpers ----
//
// When Amazon quotes no partnered LTL, the workflow is stuck in Seller Central at "you must
// complete the step using your software" -- the confirm path only ever selects partnered options
// WITH a quote, so there is nothing to select. Amazon does offer USE_YOUR_OWN_CARRIER options for
// exactly those shipments. These helpers turn the backend's `ownCarrierOptions` into the panel's
// per-shipment carrier picker, and compute whether the whole placement is selectable.

/** "FREIGHT_LTL" -> "LTL", "GROUND_SMALL_PARCEL" -> "Parcel". Anything else passes through. */
export function shippingModeLabel(mode) {
    if (mode === 'FREIGHT_LTL') return 'LTL';
    if (mode === 'GROUND_SMALL_PARCEL') return 'Parcel';
    return mode || '—';
}

/** One own-carrier option as a <option> label. Never shows a price: Amazon does not quote these. */
export function ownCarrierOptionLabel(opt) {
    if (!opt) return '';
    return `${opt.carrierName} · ${shippingModeLabel(opt.shippingMode)}`;
}

/** The shipments in the gap that Amazon offered at least one own-carrier option for. */
export function ownCarrierShipments(gap) {
    return (gap?.shipments || []).filter(s => (s.ownCarrierOptions || []).length > 0);
}

/**
 * True when every shipment in the placement has a selection. Amazon confirms transportation for
 * the WHOLE placement in one call, so a partial selection is not a partial confirm -- it is a
 * failed one, and the button must stay disabled until the operator has picked for all of them.
 */
export function ownCarrierSelectionComplete(gap, selections) {
    const shipments = gap?.shipments || [];
    if (shipments.length === 0) return false;
    return shipments.every(s => !!(selections || {})[s.shipmentId]);
}

/** Shipments with no own-carrier option at all -- the panel must say so rather than imply a pick. */
export function ownCarrierUnservedShipments(gap) {
    return (gap?.shipments || []).filter(s => (s.ownCarrierOptions || []).length === 0);
}

/**
 * Which placement option this gap describes. The response-level gap is the REPRESENTATIVE one
 * (fewest blocking ASINs), which is not necessarily the option the operator has highlighted, so
 * neither the live option fetch nor the confirm may assume `selectedOptionId`. Matched by shipment
 * id, which is the only thing the gap and the placement actually share.
 */
export function gapPlacementOptionId(analysisResult, gap) {
    const options = analysisResult?.placementOptions || [];
    const ids = new Set((gap?.shipments || []).map(s => s.shipmentId).filter(Boolean));
    if (ids.size > 0) {
        const match = options.find(po => (po.shipments || []).some(s => ids.has(s.shipmentId)));
        if (match?.placementOptionId) return match.placementOptionId;
    }
    // Fallback only when it is unambiguous: with a single placement option there is nothing else
    // the gap could describe. With several and no shipment-id match we return null rather than
    // guess -- confirming the wrong placement commits the wrong freight.
    if (options.length === 1) return options[0].placementOptionId || null;
    return null;
}

/**
 * Overlay freshly-listed own-carrier options onto the gap.
 *
 * <p>Needed because a run analyzed before task 73 (the owner's blocked wf76d77a61) has gap rows
 * with no own-carrier options at all, and because Amazon's option ids rotate — the ids the confirm
 * validates against are the ids from a fresh list, not whatever the run was saved with.
 */
export function mergeOwnCarrierOptions(gap, optionsByShipment) {
    if (!gap || !optionsByShipment) return gap;
    return {
        ...gap,
        shipments: (gap.shipments || []).map(s => {
            const live = optionsByShipment[s.shipmentId];
            return live ? { ...s, ownCarrierOptions: live } : s;
        }),
    };
}

/** Default pick per shipment: the first option Amazon offered (backend sorts LTL before parcel). */
export function defaultOwnCarrierSelections(gap) {
    const out = {};
    (gap?.shipments || []).forEach(s => {
        const first = (s.ownCarrierOptions || [])[0];
        if (first) out[s.shipmentId] = first.transportationOptionId;
    });
    return out;
}

// Owner addition (2026-08-18): a PO line whose ordered units have ALL already been sent to FBA
// (sentToFba >= ordered -- sentToFba counts confirmed shipment-planner runs since the PO date, the
// truest "sent in" signal; openQty measures supplier receiving, not FBA sends). Fully-sent lines
// grey out, sort to the bottom, and default UNCHECKED -- but stay manually selectable (a resend is
// legitimate), unlike no-ASIN lines which are fully disabled.
export function isPoLineFullySent(line) {
    return !!line?.asin && (line.ordered ?? 0) > 0 && (line.sentToFba ?? 0) >= (line.ordered ?? 0);
}

// ---- Task 85 (owner: "in that modal, i want to see how many units have already been assigned to
// other shipments - even if that shipment is not confirmed - the goal is to know what is
// leftover"): the Assigned / Remaining columns. The server does the counting (see
// ShipmentPlannerAnalysisService.computeAssignments) and sends the breakdown per line;
// these helpers only read it, and every one of them degrades to the pre-task-85 behavior when the
// fields are absent (an older server, or a fixture that predates them). ----

// The server's leftover for a line, or null when this payload doesn't carry one. Deliberately NOT
// recomputed client-side from ordered - sent - ...: a missing `remaining` means "unknown", and
// guessing 0 would grey out and un-check lines the operator can legitimately ship.
export function poLineRemaining(line) {
    if (!line || !line.asin) return null;
    return typeof line.remaining === 'number' ? Math.max(0, line.remaining) : null;
}

// Units already spoken for: the server's total when present, else the three buckets summed, else
// the pre-task-85 signal (sentToFba) so an old payload still shows something truthful.
export function poLineAssigned(line) {
    if (!line || !line.asin) return 0;
    if (typeof line.assignedTotal === 'number') return line.assignedTotal;
    const sent = line.sentUnits ?? line.sentToFba ?? 0;
    return sent + (line.activePlanUnits ?? 0) + (line.draftUnits ?? 0) + (line.localUnits ?? 0);
}

// Tooltip text for the Assigned cell -- the whole point of the column is that the number is
// explainable, so it names the buckets and, cheaply, the run ids / draft names behind them.
export function poLineAssignedTooltip(line) {
    if (!line || !line.asin) return '';
    const sent = line.sentUnits ?? line.sentToFba ?? 0;
    const active = line.activePlanUnits ?? 0;
    const draft = line.draftUnits ?? 0;
    const total = poLineAssigned(line);
    if (total === 0) return 'No units assigned to any shipment yet';
    const runIds = (line.activeRunIds || []).map(id => `#${id}`).join(', ');
    const draftNames = (line.draftNames || []).join(', ');
    const parts = [];
    if (sent > 0) parts.push(`${sent} sent to FBA (confirmed runs + Amazon inbound data)`);
    if (active > 0) parts.push(`${active} in open plans${runIds ? ` (run ${runIds})` : ''}`);
    if (draft > 0) parts.push(`${draft} in drafts${draftNames ? ` (${draftNames})` : ''}`);
    // Owner 2026-09-01: the fourth bucket — units split off to Orlando SFP stock.
    const local = line.localUnits ?? 0;
    if (local > 0) parts.push(`${local} assigned to local (Orlando)`);
    return `${total} units assigned — ${parts.join('; ')}`;
}

// A line whose every ordered unit is already committed somewhere (sent, an open plan, or a draft).
// Treated like the fully-sent case: greyed, unchecked by default, skipped by select-all -- but
// still manually selectable, because "spoken for" is a plan, not a law.
export function isPoLineSpokenFor(line) {
    if (!line?.asin || (line.ordered ?? 0) <= 0) return false;
    return poLineRemaining(line) === 0;
}

// Ship-qty default (documented change, task 85): the REMAINING units, not openQty -- openQty is a
// supplier-receiving measure and says nothing about what is already committed to other shipments.
// Falls back to openQty when the server reports no remaining, or when nothing is left over (so the
// operator still sees a sane starting number on a line they deliberately re-select).
export function poLineDefaultShipQty(line) {
    const remaining = poLineRemaining(line);
    if (remaining != null && remaining > 0) return remaining;
    return line?.openQty ?? 0;
}

// ---- Task 92 (owner, verbatim: "when the import po modal is opened, we should be able to open
// the latest shipment for that asin from a link"): the per-line "latest run" link, stacked under
// the Remaining cell. The server resolves latestRunId/latestRunStatus/latestRunAt/latestRunUnits
// (see ShipmentPlannerAnalysisService#decorateWithLatestRun); these helpers only read them, and
// degrade to "no link" (never a broken one) when the ASIN has never appeared in any run. ----

// "2d ago" / "3h ago" / "just now" from an ISO-ish timestamp. Null-safe -- an unparsable or
// missing timestamp renders no age rather than "NaN ago".
export function poLineLatestRunAgo(iso, now = Date.now()) {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return null;
    const diffMs = Math.max(0, now - then);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

// Status -> Tailwind text color for the link. Confirmed reads as "done and shipped" (green);
// active as "still live, worth a look" (indigo); cancelled/superseded as "history only" (grey).
// An unrecognized/missing status also greys out rather than defaulting to an alarming color.
export const LATEST_RUN_STATUS_COLOR = {
    CONFIRMED: 'text-green-700 hover:text-green-900',
    ACTIVE: 'text-indigo-600 hover:text-indigo-800',
    CANCELLED: 'text-gray-400 hover:text-gray-600',
    SUPERSEDED: 'text-gray-400 hover:text-gray-600',
};

export function poLineLatestRunColorClass(status) {
    return LATEST_RUN_STATUS_COLOR[String(status || '').toUpperCase()] || 'text-gray-400 hover:text-gray-600';
}

// "latest: run 173 · confirmed · 2d ago" -- null when the ASIN has never been in a run, so the
// caller renders "—" instead of a link.
export function poLineLatestRunLabel(line, now = Date.now()) {
    if (!line || line.latestRunId == null) return null;
    const status = line.latestRunStatus ? String(line.latestRunStatus).toLowerCase() : null;
    const ago = poLineLatestRunAgo(line.latestRunAt, now);
    return `latest: run ${line.latestRunId}${status ? ` · ${status}` : ''}${ago ? ` · ${ago}` : ''}`;
}

// The new-tab deep link target. Same route the Recommended board and drafts bar already use.
export function poLineLatestRunHref(line) {
    return line?.latestRunId != null ? `/shipment-planner?run=${line.latestRunId}` : null;
}

// Stable ordering for the PO-lines table: normal lines first (original order), fully-sent lines
// at the bottom (original order among themselves). Task 85 deliberately does NOT sort spoken-for
// lines to the bottom -- they grey, but their position keeps the table matching the PO on screen.
export function orderPoLines(lines) {
    const list = lines || [];
    return [...list.filter(l => !isPoLineFullySent(l)), ...list.filter(l => isPoLineFullySent(l))];
}

// ---- Task 107 (owner: "we need to do a better job demonstrating on the import po modal what
// products are remaining to ship out") ----
//
// The numbers were all there -- ordered, received, sent, assigned, remaining -- and the operator
// still had to read eighteen lines and add up. Worse, two OPPOSITE situations rendered identically:
// a line waiting on the supplier and a line already fully committed both showed "0 to ship".
//
// Since 2026-08-24 the PO list itself is gated on units still to SHIP rather than units still to
// RECEIVE, so a PO can now be fully received with everything left to ship (PO 5952: 421 of 421) or
// part-received with everything already planned. Telling those apart is the whole job.

/** What state a PO line is in, for the operator deciding what to add to a shipment. */
export function poLineShipState(line) {
    if (!line?.asin) return 'NO_ASIN';
    if (line.blocked) return 'BLOCKED';
    const remaining = poLineRemaining(line);
    if (remaining == null) return 'UNKNOWN';       // older server: don't guess
    // remaining = max(0, ordered - sent - activePlan - draft), so remaining === 0 ALWAYS means
    // every unit is committed. "Waiting on the supplier" therefore cannot be a zero-remaining
    // state -- an earlier draft of this got that wrong and a test caught it. The genuine
    // can't-ship-yet case is the opposite: units are still to ship on paper, but none have landed.
    if (remaining === 0) return 'FULLY_ASSIGNED';
    const received = line.received ?? Math.max(0, (line.ordered ?? 0) - (line.openQty ?? 0));
    return received <= 0 ? 'AWAITING_SUPPLIER' : 'TO_SHIP';
}

export const PO_LINE_STATE_LABEL = {
    TO_SHIP: 'To ship',
    AWAITING_SUPPLIER: 'Awaiting supplier',
    FULLY_ASSIGNED: 'All assigned',
    BLOCKED: 'Blocked',
    UNKNOWN: '—',
    NO_ASIN: 'No ASIN',
};

/**
 * PO-level roll-up over the lines the current tab shows. Every figure is summed from the same
 * per-line fields the table renders, so the header and the rows can never disagree.
 */
export function summarisePoLines(lines) {
    const list = (lines || []).filter(l => l?.asin);
    const out = {
        lines: list.length, ordered: 0, toShip: 0, assigned: 0, sent: 0, inPlans: 0,
        awaitingSupplier: 0,
        linesToShip: 0, linesAwaiting: 0, linesDone: 0, linesBlocked: 0, unknownLines: 0,
    };
    for (const l of list) {
        out.ordered += l.ordered ?? 0;
        const remaining = poLineRemaining(l);
        if (remaining == null) out.unknownLines += 1; else out.toShip += remaining;
        out.assigned += poLineAssigned(l) ?? 0;
        // Sent in (confirmed shipments) broken out from plan/draft commitments -- the owner
        // reads these as different facts: "gone to Amazon" vs "spoken for on paper".
        out.sent += l.sentUnits ?? l.sentToFba ?? 0;
        out.inPlans += (l.activePlanUnits ?? 0) + (l.draftUnits ?? 0);
        switch (poLineShipState(l)) {
            case 'TO_SHIP': out.linesToShip += 1; break;
            case 'AWAITING_SUPPLIER':
                out.linesAwaiting += 1;
                out.awaitingSupplier += l.openQty ?? 0;
                break;
            case 'FULLY_ASSIGNED': out.linesDone += 1; break;
            case 'BLOCKED': out.linesBlocked += 1; break;
            default: break;
        }
    }
    return out;
}

// ---- Task 69 (owner: "we need a way to save progress - the asins selected and the qty entered
// into the import from po modal, so if there is an error we can resume - right now i need to
// rebuild the shipment each time"): server-side autosave draft of the ASIN-mode working build.
// The panel PUTs a whole-snapshot payload to /api/shipment-planner/draft (debounced) on every
// change to rows / packaging / mode / LTL config, restores it automatically on mount when the
// panel is empty, and clears it when a plan built from it is CONFIRMED (or on explicit discard).
// Analyze results are deliberately NOT saved -- they re-generate from the rows. ----

export const DRAFT_AUTOSAVE_DELAY_MS = 2000;

// Task 87: the exact packaging fields the draft contract carries. Whitelisted rather than spread
// wholesale -- packagingByAsin is populated from several places (the packaging popover, box-catalog
// picks, PO-import prefill) and a whitelist makes it structurally impossible for a future addition
// there (a DOM ref, a stray event, a whole API response) to ever reach the autosave PUT body. Only
// keys actually present on the source object are copied, so a round-trip with a partial packaging
// entry doesn't grow spurious `null` fields.
const PACKAGING_DRAFT_FIELDS = [
    'unitsPerBox', 'boxLengthIn', 'boxWidthIn', 'boxHeightIn', 'boxWeightLbs',
    'unitWeightLbs', 'boxCatalogId', 'boxName', 'tareWeightLbs', 'computed',
    'style', 'unitBoxSource', 'boxesPerPallet',
];

function whitelistPackagingForDraft(pkg) {
    if (!pkg || typeof pkg !== 'object') return null;
    const out = {};
    PACKAGING_DRAFT_FIELDS.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(pkg, key)) out[key] = pkg[key];
    });
    return out;
}

// Snapshot of the working build -> the durable draft payload. Null when there is nothing worth
// saving (no rows) -- the caller treats null as "delete any stored draft".
// Task 83: `allowEmpty` keeps a NAMED draft alive after its last row is removed -- with multiple
// drafts the row is a container the operator created and only the Delete button retires it
// (pre-task-83 an emptied build simply deleted the single draft).
export function buildPlannerDraftPayload({ items, packagingByAsin, packagingConfirmedAsin,
    shipmentMode, ltlForm, source, pickupDate, splitGroup, allowEmpty = false, palletMeasurements }) {
    const rows = (items || []).filter(it => it && it.asin);
    if (rows.length === 0 && !allowEmpty) return null;
    // Task 96: the split-set stamp rides every save of a member draft. It MUST be re-emitted here
    // -- the autosave rewrites the whole payload, so a stamp that wasn't carried through would be
    // erased the first time the operator typed in a shipment the split created.
    const stamp = splitGroup
        ? splitGroupStamp(splitGroup.id, splitGroup.index, splitGroup.total, splitGroup.asin) : null;
    return {
        ...(stamp ? { splitGroup: stamp } : {}),
        rows: rows.map(it => ({
            asin: it.asin,
            sku: it.sku ?? null,
            name: it.name ?? null,
            quantity: it.quantity ?? 0,
            sellingPrice: it.sellingPrice ?? null,
            packaging: whitelistPackagingForDraft(packagingByAsin?.[it.asin]),
            packagingConfirmed: !!packagingConfirmedAsin?.[it.asin],
        })),
        shipmentMode: shipmentMode === 'LTL' ? 'LTL' : 'UPS',
        ltlForm: ltlForm ?? null,
        // Task 70b: the operator's chosen pickup day must survive a resume -- rebuilding the plan
        // tomorrow against a date they picked yesterday is exactly the surprise this feature exists
        // to remove.
        pickupDate: pickupDate || null,
        source: source ?? null,
        // Milestone C task C5: the operator's pallet measurements, keyed by destination FC so they
        // survive a re-analyze's shipmentId rotation -- see restoredPalletRows.
        palletMeasurements: whitelistPalletMeasurementsMap(palletMeasurements),
        savedAt: new Date().toISOString(),
    };
}

// Draft payload -> the panel-state slices to restore. Null when the payload is absent/unusable
// (no valid rows) so callers can treat "no draft" and "junk draft" identically.
// Owner, 2026-09-01 ("the product names and item sku need to appear on the Shipment Planner
// section"): what a staged row should CALL a product. The arrival-path name wins when it is a
// real name; a draft-restored item whose name degraded to the ASIN falls back to the packaging
// batch's server-resolved product title. Never blank — the ASIN itself is the floor.
export function stagedDisplayName(item, pkg) {
    if (item?.name && item.name !== item.asin) return item.name;
    return pkg?.productName || item?.name || item?.asin || '';
}

export function draftPayloadToPanelState(payload) {
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    const items = [];
    const packagingByAsin = {};
    const packagingConfirmedAsin = {};
    rows.forEach(r => {
        if (!r || !r.asin) return;
        items.push({
            asin: r.asin,
            sku: r.sku ?? null,
            name: r.name || r.asin,
            quantity: r.quantity || 0,
            sellingPrice: r.sellingPrice ?? null,
        });
        if (r.packaging && typeof r.packaging === 'object') packagingByAsin[r.asin] = r.packaging;
        if (r.packagingConfirmed) packagingConfirmedAsin[r.asin] = true;
    });
    if (items.length === 0) return null;
    return {
        items,
        packagingByAsin,
        packagingConfirmedAsin,
        shipmentMode: payload.shipmentMode === 'LTL' ? 'LTL' : 'UPS',
        ltlForm: payload.ltlForm && typeof payload.ltlForm === 'object' ? payload.ltlForm : null,
        // Task 70b: a stored pickup date that has since gone stale (drafts outlive the day they
        // were saved) is dropped rather than restored into the past -- the caller then falls back
        // to today's default instead of quoting a pickup Amazon cannot honour.
        pickupDate: (payload.pickupDate && payload.pickupDate >= minPickupDate())
            ? payload.pickupDate : null,
        source: payload.source || null,
        savedAt: payload.savedAt || null,
        // Task 96: which split minted this draft, if any -- the panel needs it to show the set and
        // to keep re-stamping it on every autosave.
        splitGroup: splitGroupFromPayload(payload),
        // Milestone C task C5: forward-compat whitelist, same as every other field here -- an
        // unrecognized FC entry (empty rows, junk shape) restores identically to "never saved".
        palletMeasurements: whitelistPalletMeasurementsMap(payload.palletMeasurements),
    };
}

// The restore-only-into-an-empty-panel rule as a pure decision: 'restore' (apply automatically,
// show the restored banner), 'offer' (rows already present -- never clobber them; show the
// resume affordance instead), or 'none' (no usable draft).
export function draftRestoreAction(payload, currentItemCount) {
    const state = draftPayloadToPanelState(payload);
    if (!state) return { action: 'none', state: null };
    return currentItemCount > 0 ? { action: 'offer', state } : { action: 'restore', state };
}

// Banner label, e.g. "PO 5951 · 8 ASINs · LTL".
export function draftSummaryLabel(payload) {
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    const parts = [];
    if (payload?.source) parts.push(payload.source);
    parts.push(`${rows.length} ASIN${rows.length !== 1 ? 's' : ''}`);
    parts.push(payload?.shipmentMode === 'LTL' ? 'LTL' : 'UPS');
    return parts.join(' · ');
}

// "from 11:20" timestamp for the banner; null when the draft carries no usable savedAt.
export function draftSavedAtLabel(savedAt) {
    if (!savedAt) return null;
    const d = new Date(savedAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Trailing-edge debouncer for the autosave: schedule(fn) runs fn once, delayMs after the LAST
// schedule call; cancel() drops anything pending (unmount / explicit clear).
export function createDraftDebouncer(delayMs = DRAFT_AUTOSAVE_DELAY_MS) {
    let timer = null;
    return {
        schedule(fn) {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => { timer = null; fn(); }, delayMs);
        },
        cancel() {
            if (timer) { clearTimeout(timer); timer = null; }
        },
        pending() { return timer != null; },
    };
}

// Task 87 (owner-reported "circular JSON" error, no matching server-side error -- purely a
// client-side JSON.stringify failure on a request body that carries something axios can't
// serialize, e.g. a React synthetic event, a DOM node/ref, or a whole axios response object
// stashed wholesale into state). Every JSON body this file builds now goes through an EXPLICIT
// whitelist serializer (buildPlannerDraftPayload, buildConfirmRequestBody,
// buildOwnCarrierConfirmRequestBody) rather than spreading raw state -- this is the belt-and-braces
// backstop: a last check, immediately before the request fires, that the body really is
// stringify-able. If it ever isn't (a future change reintroduces the class of bug), this turns a
// silent dead click / unhandled promise rejection into a loud console.error plus a visible toast.
export function safeSerializationCheck(payload, context) {
    try {
        JSON.stringify(payload);
        return null;
    } catch (err) {
        console.error(`[ShipmentPlanner] ${context} payload is not JSON-serializable`, err, payload);
        return err;
    }
}

// Task 87 root cause: confirmPlan/confirmSelected/confirmOwnCarrier all take
// `acknowledgeUsedCondition` as a leading boolean parameter, and TWO of the three confirm buttons
// were wired as a bare `onClick={confirmPlan}` / `onClick={confirmSelected}` -- React then calls the
// handler with its click SyntheticEvent as that first argument. The event is truthy (silently
// skipping the window.confirm dialog) AND, unwhitelisted, would serialize straight into the request
// body, whose DOM-fiber back-references broke JSON.stringify. The onClick wiring is fixed to always
// pass explicit arguments; this is the defense-in-depth backstop so a future bare-reference regression
// degrades to "treated as not acknowledged" instead of a circular-JSON crash.
export function coerceAcknowledgeFlag(value) {
    if (value && typeof value === 'object'
        && (typeof value.preventDefault === 'function' || 'nativeEvent' in value
            || (typeof Event !== 'undefined' && value instanceof Event))) {
        return false;
    }
    return !!value;
}

// Fire-and-forget wrapper for the draft PUT/DELETE calls: logs failures, and tolerates a
// non-promise return (CRA's jest resetMocks leaves ApiClient verbs returning undefined in tests
// that don't explicitly mock them -- a background autosave must never crash the panel over it).
export function fireDraftRequest(request, onSuccess, label) {
    if (!request || typeof request.then !== 'function') return;
    request.then(res => { if (onSuccess) onSuccess(res); })
        .catch(err => console.error(label, err));
}

// ---- Task 83 (owner: "we need to be able to create another shipment plan and then resume the
// saved one. basically we should be able to work on multiple shipments simultaneously"): the
// single task-69 draft becomes N named drafts, listed in a bar above the ASIN panel. The draft id
// is the server's `slot`, so the pre-task-83 saved build is simply the draft with id 'default'.
// Autosave targets the ACTIVE draft only; switching flushes the pending save first, so no keystroke
// is ever stranded on the draft being left behind. ----

// Which draft the panel opens with, persisted so a refresh returns to the one being worked on
// rather than jumping to whichever draft happened to autosave last.
export const ACTIVE_DRAFT_STORAGE_KEY = 'plannerActiveDraftId';

// Mirror of ShipmentPlannerAnalysisService.MAX_OPEN_DRAFTS -- used only to hide the "+ New
// shipment" affordance before the server has to refuse it (the 409 is still the authority).
// 10 → 25 (owner, 2026-08-31): break-apart flows open 5+ drafts per split, and confirmed
// drafts now auto-retire server-side, so the cap is a runaway backstop, not tidiness police.
export const MAX_OPEN_DRAFTS = 25;

// Shared "Xm / Xh / Xd" magnitude cascade behind draftAgeLabel (PAST -- drafts) and
// relativeTimeUntilLabel (FUTURE -- Milestone C's LTL quote windows, added task C4). One bucketing
// rule, two directions and two verbs wrapping it, so the two never drift into different
// granularities for what is otherwise the same "how far away" question.
function relativeMagnitudeLabel(mins) {
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}

// "2h ago" for the drafts dropdown. Prefers the payload's own ISO savedAt (written by the browser)
// over the server's naive updated_at; a clock skew that would read as the future shows "just now"
// rather than a nonsense negative age.
export function draftAgeLabel(entry, now = Date.now()) {
    const raw = entry?.savedAt || entry?.updatedAt;
    if (!raw) return null;
    const t = new Date(typeof raw === 'string' && /^\d{4}-\d{2}-\d{2} /.test(raw)
        ? raw.replace(' ', 'T') : raw).getTime();
    if (Number.isNaN(t)) return null;
    const mins = Math.floor((now - t) / 60000);
    if (mins < 1) return 'just now';
    return `${relativeMagnitudeLabel(mins)} ago`;
}

// "45m" / "2h" -- bare relative magnitude UNTIL a future instant, task C4's reuse of
// draftAgeLabel's bucket cascade in the opposite direction. Deliberately returns the bare
// magnitude rather than pre-wrapping it in "in" or "ago" -- the two LTL call sites need different
// prepositions around the same number ("Quote window closes in 2h" vs "Free to void until 45m"),
// and a shared prefix would read wrong in one of them. Feeds an option's placementExpiration
// (Amazon's own deadline on the placement) and, after a requote, each shipment's
// quoteVoidableUntil (the one-hour free-void window, spec 3.7). Both arrive as the threeten
// OffsetDateTime string the backend stores -- JS Date parses that shape fine, so no second parser
// is needed. An instant already in the past (or an unparseable string) reads as 'any moment'
// rather than a nonsense negative "-12m".
export function relativeTimeUntilLabel(iso, now = Date.now()) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const mins = Math.ceil((t - now) / 60000);
    if (mins <= 0) return 'any moment';
    return relativeMagnitudeLabel(mins);
}

// Dropdown row label: "PO 5951 · 6 ASINs · 114 units · LTL · 2h ago" (each part dropped when the
// draft doesn't have it, so an empty new draft reads "0 ASINs · UPS").
/** 2026-08-23 (owner): "once a shipment is submitted for analysis, it should no longer be part
 * of the saved planner section." The draft is stamped at submission and the saved list filters
 * stamped entries out. The draft ITSELF survives (it carries the pallet measurements the
 * two-phase flow restores) and is deleted at confirm exactly as before; a failed analyze
 * un-stamps so the work reappears where the operator expects it. */
export function draftIsSubmitted(entry) {
    return !!entry?.payload?.submittedForAnalysis;
}

export function draftEntryLabel(entry, now = Date.now()) {
    const parts = [];
    if (entry?.source) parts.push(entry.source);
    const count = entry?.itemCount || 0;
    parts.push(`${count} ASIN${count !== 1 ? 's' : ''}`);
    if (entry?.units) parts.push(`${entry.units} units`);
    parts.push(entry?.mode === 'LTL' ? 'LTL' : 'UPS');
    const age = draftAgeLabel(entry, now);
    if (age) parts.push(age);
    return parts.join(' · ');
}

// Shipment-level LTL inputs as they start out. Module-level so "+ New shipment" and a draft
// SWITCH can reset to exactly the same baseline the panel mounts with (task 83) -- otherwise the
// previous draft's pallet/freight-class config would silently ride along into the next one.
export const DEFAULT_LTL_FORM = {
    palletLengthIn: '48', palletWidthIn: '40', palletTareLbs: '50',
    stackable: true, freightClass: '', declaredValue: '', savePalletDefault: false,
    // Milestone C phase 0: blank by default -- a declaration never made must not become a zero.
    declaredPalletCount: '',
};

// Optimistic drafts-bar refresh after an autosave: recompute the saved draft's summary in place
// (or append it if the row was just created) instead of re-listing on every keystroke.
export function applyDraftSummaryUpdate(drafts, id, payload, name) {
    const rows = (payload?.rows || []).filter(r => r && r.asin);
    const summary = {
        id,
        slot: id,
        name: name || 'Shipment',
        itemCount: rows.length,
        units: rows.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0),
        mode: payload?.shipmentMode === 'LTL' ? 'LTL' : 'UPS',
        source: payload?.source ?? null,
        savedAt: payload?.savedAt ?? null,
        updatedAt: payload?.savedAt ?? null,
    };
    const list = Array.isArray(drafts) ? drafts : [];
    return list.some(d => d && d.id === id)
        ? list.map(d => (d && d.id === id ? { ...d, ...summary, name: d.name || summary.name } : d))
        : [...list, summary];
}

// Which draft to open on mount: the last one this browser was working on if it still exists,
// otherwise the most recently updated (the list arrives newest-first), otherwise the legacy
// 'default' id so a first-ever save still lands somewhere.
export function pickActiveDraftId(drafts, storedId) {
    const list = Array.isArray(drafts) ? drafts : [];
    if (storedId && list.some(d => d && d.id === storedId)) return storedId;
    const first = list.find(d => d && d.id);
    return first ? first.id : 'default';
}

// Confirm-clears rule (documented choice): confirming a plan retires the draft when the
// confirmed plan's ASIN set is a SUBSET of the draft's rows -- covers both "confirmed exactly
// what I built" and "confirmed a slice of it" (a partial confirm still means the build reached
// its goal), while a confirm of some unrelated loaded run leaves the draft alone.
export function confirmedPlanClearsDraft(planAsins, draftPayload) {
    const draftAsins = new Set((draftPayload?.rows || []).map(r => r && r.asin).filter(Boolean));
    const plan = (planAsins || []).filter(Boolean);
    if (plan.length === 0 || draftAsins.size === 0) return false;
    return plan.every(a => draftAsins.has(a));
}

// ---- Task 90 (owner, verbatim: "on the planner page - before analyze shipping options, add
// another button for break single asin into multiple - this will open a modal and indicate how many
// shipments to make of this configuration - for example 2 shipments of 4 units total 4 units per 1
// box"): the MANUAL companion to task 53's automated partition sweep. The operator takes ONE staged
// ASIN and says "N shipments of U units"; the panel materialises that as N separate planner DRAFTS
// (task 83's model), each holding that ASIN at U units with the same packaging / shipment mode /
// LTL config, so Amazon is asked for several smaller plans instead of one big one.
//
// WHY DRAFTS rather than an in-session queue: a draft is a server row, so the set survives a
// refresh, a crash and a day off; each shipment can be analyzed, re-analyzed and CONFIRMED
// independently (the analyze + confirm path is already draft-scoped, task 83/87); and the drafts
// bar already shows/switches/deletes them. An in-session queue would need all of that rebuilt and
// would evaporate on reload -- exactly the failure task 69 exists to prevent. ----

// How the leftover units of a non-even split are handled -- the operator picks, we never guess.
export const REMAINDER_TO_LAST = 'LAST';       // last shipment carries U + remainder
export const REMAINDER_UNASSIGNED = 'LEAVE';   // remainder stays behind on the source draft

// Boxes one shipment of `units` needs at this ASIN's packaging (null when packaging isn't set yet
// -- the split is still allowed, we just can't show the box math the owner asked for).
export function splitBoxesForUnits(units, unitsPerBox) {
    const u = Number(units);
    const per = Number(unitsPerBox);
    if (!Number.isFinite(u) || u <= 0 || !Number.isFinite(per) || per <= 0) return null;
    return Math.ceil(u / per);
}

// "4 units = 1 box per shipment (4 units/box)" -- the owner's own phrasing for the box math.
export function splitBoxMathLabel(units, unitsPerBox) {
    const boxes = splitBoxesForUnits(units, unitsPerBox);
    if (boxes == null) return null;
    const per = Number(unitsPerBox);
    return `${units} unit${units !== 1 ? 's' : ''} = ${boxes} box${boxes !== 1 ? 'es' : ''} per shipment `
        + `(${per} unit${per !== 1 ? 's' : ''} per box)`;
}

// The split math as a pure decision. Never allocates more than the row actually has -- an
// over-allocation returns { valid:false, error } and the modal's action stays disabled.
export function computeAsinSplit({ totalQty, shipments, unitsPerShipment, remainderMode = REMAINDER_TO_LAST }) {
    const total = Math.floor(Number(totalQty) || 0);
    const n = Math.floor(Number(shipments));
    const u = Math.floor(Number(unitsPerShipment));
    const base = {
        total, shipmentCount: n, unitsPerShipment: u, shipments: [],
        allocated: 0, remainder: 0, leftover: 0, valid: false, error: null,
    };
    if (total < 1) return { ...base, error: 'This ASIN has no units staged — nothing to break up.' };
    if (!Number.isFinite(n) || n < 2) {
        return { ...base, error: 'Enter at least 2 shipments — that is what breaking it up means.' };
    }
    if (n > MAX_OPEN_DRAFTS) {
        return { ...base, error: `At most ${MAX_OPEN_DRAFTS} shipments at once — that is the open-draft limit.` };
    }
    if (!Number.isFinite(u) || u < 1) return { ...base, error: 'Enter at least 1 unit per shipment.' };
    const allocated = n * u;
    if (allocated > total) {
        return {
            ...base, allocated,
            error: `${n} shipments × ${u} units = ${allocated} units — only ${total} `
                + `${total === 1 ? 'unit is' : 'units are'} staged on this row.`,
        };
    }
    const remainder = total - allocated;
    const toLast = remainderMode === REMAINDER_TO_LAST;
    const list = [];
    for (let i = 1; i <= n; i += 1) {
        list.push({ index: i, units: u + (toLast && i === n ? remainder : 0) });
    }
    return {
        ...base, shipments: list, allocated, remainder,
        leftover: toLast ? 0 : remainder, valid: true, error: null,
    };
}

// "2 shipments × 4 units = 8 of 8 units" -- the live line the owner asked to see while typing.
export function splitMathLabel(plan) {
    if (!plan) return '';
    return `${plan.shipmentCount || 0} shipments × ${plan.unitsPerShipment || 0} units = `
        + `${plan.allocated || 0} of ${plan.total || 0} units`;
}

// Opening default for "units per shipment": an even share of the row, at least 1.
export function defaultSplitUnitsPerShipment(totalQty, shipmentCount) {
    const total = Math.floor(Number(totalQty) || 0);
    const n = Math.floor(Number(shipmentCount) || 0);
    if (total < 1 || n < 1) return 1;
    return Math.max(1, Math.floor(total / n));
}

// Task 94 (owner, live prod evidence): a shipment draft minted by a split is named
// "<base> · <ASIN> 2/3", and its name is what the NEXT split used as its base -- so re-splitting
// chained the suffix instead of replacing it:
//     "Shipment 1 · B010GSNT1M 2/3 · B010GSNT1M 1/3 · B010GSNT1M 1/3"
// Names are therefore always derived from the BASE label: every trailing " · <ASIN> i/N" a
// previous split appended is stripped first (repeatedly -- an already-chained name has several),
// so a re-split yields "<base> · <ASIN> 1/N" no matter how many times it has been split before.
const SPLIT_NAME_SUFFIX_RE = /\s*·\s*[A-Z0-9]{10}\s+\d+\s*\/\s*\d+\s*$/i;
export function splitBaseLabel(label) {
    let base = String(label ?? '').trim();
    for (let guard = 0; guard < 20; guard += 1) {
        const stripped = base.replace(SPLIT_NAME_SUFFIX_RE, '').trim();
        if (stripped === base) break;
        base = stripped;
    }
    return base || 'Shipment';
}

// Draft names: "PO 5951 · B0DJG4S9M5 1/2", clamped to the server's VARCHAR(80) name column.
export const SPLIT_DRAFT_NAME_MAX = 80;
export function splitDraftName(baseLabel, asin, index, count) {
    const suffix = `${asin ? ` · ${asin}` : ''} ${index}/${count}`;
    const base = splitBaseLabel(baseLabel);
    const room = SPLIT_DRAFT_NAME_MAX - suffix.length;
    return `${room > 0 && base.length > room ? base.slice(0, room).trim() : base}${suffix}`;
}

// Task 94: the units a SAVED draft payload actually holds for one ASIN -- the number the split is
// validated against, because the rows on screen are not guaranteed to be this draft's contents
// (a loaded run, a re-import or the "a saved draft exists" resume-offer all leave the panel
// holding rows that came from somewhere else while the draft bookkeeping points at another draft).
export function draftStagedQuantity(payload, asin) {
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    const row = rows.find(r => r && r.asin === asin);
    if (!row) return 0;
    const q = Math.floor(Number(row.quantity));
    return Number.isFinite(q) && q > 0 ? q : 0;
}

// ---- Task 95 (owner data loss, confirmed on prod): PROVENANCE of the rows on the panel. ----
//
// The panel's `items` and the drafts bookkeeping (activeDraftId / activeDraftName) are two separate
// things, and task 94 already documented that they can disagree. Until now the debounced autosave
// ignored that and wrote whatever was on screen into whatever draft happened to be active -- so
// opening a run (?run=<id>, the Runs list, Previous Runs) silently overwrote an unrelated draft's
// contents about two seconds later, with nothing on screen to say it had happened. That is how a
// 6-ASIN / 114-unit run's build landed on top of a 2-unit split draft.
//
// The fix is to record WHERE the rows came from and let only the legitimate cases write:
//   * ROWS_FROM_DRAFT   -- the active draft's own contents (restore, resume, switch, new/empty
//                          draft, post-split rewrite) AND every edit made on top of them
//                          (import from PO, paste list, search-and-add, qty edits). Autosaves.
//   * ROWS_FROM_RUN     -- a run loaded into the panel. NEVER autosaves; the operator is offered an
//                          explicit "copy into a draft" instead.
//   * ROWS_FROM_FOREIGN -- rows that were already on the panel when a stored draft was found, which
//                          the task-69 resume path deliberately declined to clobber. Writing them
//                          would destroy the very draft the banner is offering to resume, so this
//                          does not autosave either.
export const ROWS_FROM_DRAFT = 'draft';
export const ROWS_FROM_RUN = 'run';
export const ROWS_FROM_FOREIGN = 'foreign';

// The single gate every write path consults. Unknown/absent origin is treated as the draft's own
// contents so that any future entry path fails SAFE for the operator's typing (it saves) rather
// than silently stranding their work -- the destructive direction is the one that is opted into.
export function autosaveAllowedFor(origin) {
    return origin !== ROWS_FROM_RUN && origin !== ROWS_FROM_FOREIGN;
}

// "5 ASINs / 27 units" -- both sides of every overwrite confirmation are named with this, so the
// operator compares like with like instead of guessing what "replace" means.
export function rowsCountLabel(rows) {
    const list = (Array.isArray(rows) ? rows : []).filter(r => r && r.asin);
    const units = list.reduce((sum, r) => {
        const q = Math.floor(Number(r.quantity));
        return sum + (Number.isFinite(q) && q > 0 ? q : 0);
    }, 0);
    return `${list.length} ASIN${list.length !== 1 ? 's' : ''} / ${units} unit${units !== 1 ? 's' : ''}`;
}

// asin -> units, for comparing a saved draft's contents with what is about to replace them.
function rowQtyMap(rows) {
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach(r => {
        if (!r || !r.asin) return;
        const q = Math.floor(Number(r.quantity));
        map.set(r.asin, (map.get(r.asin) || 0) + (Number.isFinite(q) && q > 0 ? q : 0));
    });
    return map;
}

// Does writing `incomingRows` actually CHANGE what the draft holds? An empty draft has nothing to
// lose, and re-writing identical contents is a no-op -- neither is worth a confirmation dialog.
// Anything else destroys work and must be confirmed.
export function draftContentsDiffer(savedRows, incomingRows) {
    const saved = rowQtyMap(savedRows);
    if (saved.size === 0) return false;
    const incoming = rowQtyMap(incomingRows);
    if (saved.size !== incoming.size) return true;
    for (const [asin, qty] of saved) {
        if (incoming.get(asin) !== qty) return true;
    }
    return false;
}

// The confirmation text. Names BOTH sides with their ASIN/unit counts -- "replace draft “PO 5951”
// (5 ASINs / 27 units) with this run's build (6 ASINs / 114 units)?" -- because the whole failure
// mode being fixed here is an overwrite the operator could not see coming.
export function overwriteDraftConfirmMessage({ draftName, savedRows, incomingRows,
                                               incomingLabel = 'the rows on screen' }) {
    return `Replace draft “${draftName || 'this shipment'}” (${rowsCountLabel(savedRows)}) `
        + `with ${incomingLabel} (${rowsCountLabel(incomingRows)})?\n\n`
        + 'The draft\'s current contents cannot be recovered.';
}

// What the drafts bar says when the panel is NOT holding the active draft's build. Null in the
// ordinary case, so the bar stays quiet unless there is something real to warn about.
export function panelProvenanceNotice({ origin, runId, draftName }) {
    const name = draftName || 'this shipment';
    if (origin === ROWS_FROM_RUN) {
        return {
            text: `viewing run #${runId != null ? runId : '?'} — not saving to draft “${name}”`,
            incomingLabel: `run #${runId != null ? runId : '?'}’s build`,
            newDraftLabel: 'Copy this run into a new draft',
            intoDraftLabel: `Copy into “${name}”`,
            title: `These rows came from run #${runId != null ? runId : '?'}, not from draft `
                + `“${name}”, so nothing is being autosaved. Copy them into a draft to keep working `
                + 'on them.',
        };
    }
    if (origin === ROWS_FROM_FOREIGN) {
        return {
            text: `these rows didn’t come from draft “${name}” — not autosaving`,
            incomingLabel: 'the rows on screen',
            newDraftLabel: 'Copy these rows into a new draft',
            intoDraftLabel: `Copy into “${name}”`,
            title: `Draft “${name}” holds a different build (see the banner below), so nothing is `
                + 'being autosaved. Copy these rows into a draft to keep working on them.',
        };
    }
    return null;
}

// What the split will actually DO to the drafts, decided before anything is written.
//
// Documented rule for the source draft (shown in the modal before the operator commits):
//   * when the split ASIN is the ONLY thing the draft holds and nothing is left over, the source
//     draft BECOMES shipment 1/N (renamed) and only N-1 new drafts are minted -- no empty husk is
//     left behind and a draft slot is saved;
//   * otherwise the source draft STAYS as it is minus the split units (its other ASINs, plus any
//     "leave unassigned" remainder) and all N shipments are new drafts.
export function planSplitDrafts({ plan, rows, asin, openDraftCount = 0, baseLabel, maxDrafts = MAX_OPEN_DRAFTS }) {
    const list = (rows || []).filter(r => r && r.asin);
    const splitRow = list.find(r => r.asin === asin) || null;
    const otherRows = list.filter(r => r.asin !== asin);
    const leftover = plan?.leftover || 0;
    const sourceKeepsSomething = otherRows.length > 0 || leftover > 0;
    const reuseSource = !sourceKeepsSomething;
    const count = plan?.shipments?.length || 0;
    const names = (plan?.shipments || []).map(sh => splitDraftName(baseLabel, asin, sh.index, count));

    const newShipments = (plan?.shipments || [])
        .filter(sh => !(reuseSource && sh.index === 1))
        .map(sh => ({ ...sh, name: names[sh.index - 1] }));

    // plan.shipments is empty while the inputs are still invalid -- this runs on every render, so
    // it must survive that (the modal only shows/uses the decision once plan.valid).
    const firstUnits = plan?.shipments?.[0]?.units;
    const sourceRows = reuseSource
        ? (splitRow && firstUnits != null ? [{ ...splitRow, quantity: firstUnits }] : [])
        : [...otherRows, ...(leftover > 0 && splitRow ? [{ ...splitRow, quantity: leftover }] : [])];

    const newDraftCount = newShipments.length;
    const capOk = openDraftCount + newDraftCount <= maxDrafts;
    const over = openDraftCount + newDraftCount - maxDrafts;
    return {
        reuseSource,
        sourceName: reuseSource ? names[0] : null,
        sourceRows,
        newShipments,
        newDraftCount,
        leftover,
        capOk,
        capMessage: capOk ? null
            : `${count} shipments needs ${newDraftCount} more draft${newDraftCount !== 1 ? 's' : ''}, but `
                + `${openDraftCount} of ${maxDrafts} are already open — delete ${over} `
                + `draft${over !== 1 ? 's' : ''} (or split into fewer shipments) first.`,
    };
}

// ---- Task 96 (owner, verbatim: "there is no way for us tell if it worked - we need an indicator
// on the page showing progress and that the shipments are being created. where do the results
// appear once it is complete for the broken up shipments? does each shipment need to be confirmed
// or accessed independently?") ----
//
// Task 90 made the split real but invisible: N drafts were POSTed behind a single button and the
// only feedback was a toast. Three things fix that, and all three hang off ONE idea -- the drafts a
// split mints are a SET, and the set is written into the drafts themselves:
//
//   1. progress while the drafts are created ("Creating shipment 2 of 3…", a tick per draft, and on
//      a failure an explicit "these exist, these do not" -- never a silent half-split);
//   2. a "Split set" panel that answers "where do the results appear": one row per sibling with its
//      status (Draft / Analyzed / Confirmed) and its actions;
//   3. the plain sentence that each shipment is analyzed and confirmed separately, plus an
//      "Analyze all in this set" convenience that runs them one at a time and NEVER confirms.
//
// The set survives a reload because every member draft's payload carries the stamp below -- a
// client-side field inside the existing draft JSON (the backend stores the payload opaquely, so
// there is no schema change and no server work).

// Group id for one split. Opaque, sortable-ish, and short enough to sit in a draft payload.
export function newSplitGroupId(now = Date.now(), rand = Math.random) {
    const r = Math.floor(rand() * 1679616).toString(36).padStart(4, '0');
    return `sg-${Number(now).toString(36)}-${r}`;
}

// The stamp itself: which split, which shipment of how many, and the ASIN that was broken up.
// Returns null for anything that isn't a usable stamp, so a hand-edited/corrupt payload simply
// reads as "not part of a set" instead of rendering a broken panel.
export function splitGroupStamp(id, index, total, asin) {
    const gid = String(id || '').trim();
    const i = Math.floor(Number(index));
    const n = Math.floor(Number(total));
    if (!gid || !Number.isFinite(i) || !Number.isFinite(n) || i < 1 || n < 2 || i > n) return null;
    return { id: gid, index: i, total: n, asin: asin || null };
}

export function splitGroupFromPayload(payload) {
    const g = payload?.splitGroup;
    if (!g || typeof g !== 'object') return null;
    return splitGroupStamp(g.id, g.index, g.total, g.asin);
}

// ---- progress ----

// The modal's live line while the drafts are being written. `created` counts drafts that the
// server has already acknowledged with an id.
export function splitProgressLabel(progress) {
    if (!progress) return '';
    const total = progress.total || 0;
    const steps = progress.steps || [];
    const doneCount = steps.filter(s => s.status === 'done').length;
    if (progress.phase === 'failed') {
        return `Stopped after ${doneCount} of ${total} shipment${total !== 1 ? 's' : ''}`;
    }
    if (progress.phase === 'done') {
        return `All ${total} shipment${total !== 1 ? 's' : ''} created`;
    }
    if (progress.phase === 'source') return 'Updating the shipment you started from…';
    const next = steps.find(s => s.status === 'pending');
    return `Creating shipment ${next ? next.index : total} of ${total}…`;
}

// The partial-failure sentence. Says exactly which shipments exist and which do not, and that the
// build the operator was standing on was left alone (the creations run BEFORE the source draft is
// rewritten -- see runAsinSplit).
export function splitPartialFailureNote(progress) {
    if (!progress || progress.phase !== 'failed') return null;
    const steps = progress.steps || [];
    const made = steps.filter(s => s.status === 'done').map(s => s.label);
    // 'source' steps were never going to be created (that shipment IS the draft the operator was
    // standing on), so they belong in neither list.
    const missed = steps.filter(s => s.status !== 'done' && s.status !== 'source').map(s => s.label);
    const madePart = made.length
        ? `${made.join(', ')} ${made.length === 1 ? 'was' : 'were'} created and ${made.length === 1 ? 'is' : 'are'} in the drafts bar`
        : 'No shipments were created';
    const missedPart = missed.length
        ? `${missed.join(', ')} ${missed.length === 1 ? 'was' : 'were'} not`
        : null;
    return `${madePart}${missedPart ? `; ${missedPart}` : ''}. The shipment you started from was left `
        + 'exactly as it was — nothing was split off it.';
}

// ---- the set, and each member's status ----

// Best decision of an analyze response: the top-scored placement option's verdict. Null when the
// response carried no scores (a legacy/loaded run, or an analyze that returned nothing scorable).
export function bestAnalysisDecision(data) {
    const scores = Array.isArray(data?.scores) ? data.scores : [];
    if (scores.length === 0) return null;
    const best = [...scores].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
    return best?.decision || null;
}

// One member row's status, from the outcome ledger + whether its draft row still exists.
// Deliberately three states and no more -- they are the three the owner asked about.
export function splitMemberStatus(member) {
    const o = member?.outcome || {};
    if (o.error) return { kind: 'failed', label: `Analyze failed — ${o.error}` };
    if (member?.analyzing) return { kind: 'analyzing', label: 'Analyzing…' };
    if (o.confirmed) {
        const ids = (o.confirmed.ids || []).filter(Boolean);
        const idPart = ids.length ? ` · ${ids.join(', ')}`
            : (o.confirmed.shipmentCount ? ` · ${o.confirmed.shipmentCount} shipment${o.confirmed.shipmentCount !== 1 ? 's' : ''}` : '');
        return { kind: 'confirmed', label: `Confirmed${idPart}` };
    }
    if (o.runId != null) {
        return {
            kind: 'analyzed',
            label: `Analyzed · run ${o.runId}${o.decision ? ` · ${o.decision}` : ''}`,
        };
    }
    return { kind: 'draft', label: 'Draft — not analyzed yet' };
}

// ASIN + units + boxes for a member row, straight from its stored payload.
export function splitMemberContentsLabel(member) {
    const rows = Array.isArray(member?.rows) ? member.rows : [];
    if (rows.length === 0) return 'no rows';
    const units = rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
    const boxes = rows.reduce((s, r) => {
        const b = splitBoxesForUnits(r.quantity, r.packaging?.unitsPerBox);
        return b == null ? s : s + b;
    }, 0);
    const asinPart = rows.length === 1 ? rows[0].asin : `${rows.length} ASINs`;
    const boxPart = boxes > 0 ? ` · ${boxes} box${boxes !== 1 ? 'es' : ''}` : '';
    return `${asinPart} · ${units} unit${units !== 1 ? 's' : ''}${boxPart}`;
}

// Assemble the panel's rows: every draft whose payload carries this group's stamp, in shipment
// order. Members whose draft has since been retired (a confirm deletes the draft it was built
// from) survive as ledger-only rows so a confirmed shipment never silently vanishes from the set.
export function buildSplitSetMembers({ groupId, drafts, payloadsById, outcomes }) {
    if (!groupId) return [];
    const byIndex = new Map();
    (drafts || []).forEach((d) => {
        if (!d || !d.id) return;
        const payload = payloadsById?.[d.id];
        const stamp = splitGroupFromPayload(payload);
        if (!stamp || stamp.id !== groupId) return;
        byIndex.set(stamp.index, {
            draftId: d.id,
            name: d.name || `Shipment ${stamp.index}`,
            index: stamp.index,
            total: stamp.total,
            asin: stamp.asin,
            rows: Array.isArray(payload?.rows) ? payload.rows : [],
            exists: true,
            outcome: outcomes?.[d.id] || null,
        });
    });
    Object.entries(outcomes || {}).forEach(([draftId, o]) => {
        if (!o || o.index == null || byIndex.has(o.index)) return;
        byIndex.set(o.index, {
            draftId,
            name: o.name || `Shipment ${o.index}`,
            index: o.index,
            total: o.total,
            asin: o.asin || null,
            rows: [],
            exists: false,
            outcome: o,
        });
    });
    return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

// A stored draft payload -> the analyze-asin-list request body. Used ONLY by "Analyze all in this
// set", which analyzes siblings without opening them; the panel's own analyze path is unchanged.
export function splitSetAnalyzePayload(payload) {
    const rows = (payload?.rows || []).filter(r => r && r.asin);
    if (rows.length === 0) return null;
    const body = {
        items: rows.map((r) => {
            const line = { asin: r.asin, qty: r.quantity };
            if (r.sku) line.sku = r.sku;
            const pkg = r.packaging;
            if (pkg) {
                line.unitsPerBox = pkg.unitsPerBox;
                line.boxLengthIn = pkg.boxLengthIn;
                line.boxWidthIn = pkg.boxWidthIn;
                line.boxHeightIn = pkg.boxHeightIn;
                line.boxWeightLbs = pkg.boxWeightLbs;
            }
            return line;
        }),
    };
    if (payload?.shipmentMode === 'LTL') {
        body.preferredShippingMode = 'FREIGHT_LTL';
        body.ltl = buildLtlRequestPayload(payload.ltlForm || {});
    }
    // NO readyToShipDate. Owner, 2026-08-21: "remove the ready date from the split shipment
    // process". The date was sent to Amazon as UTC midnight of the chosen day, so from 8pm ET
    // onwards it was already in the past and generateTransportationOptions failed for every member
    // of the split (runs 228-231) -- destinations, freight and scores all silently empty. Analyses
    // that send no date quote normally; the backend still supplies Amazon a window two business
    // days out. The backend drops it for split-stamped requests too
    // (ShipmentPlannerAnalysisService.readyToShipDateFor), so this is defence in depth, not the
    // only guard.
    // Task 97: carry the set stamp to the backend so the RUN row records which split it belongs
    // to. Without this the set only ever existed in this browser -- and a confirm deletes the
    // draft that held it, so a set lost members exactly as the operator worked through it.
    const stamp = splitGroupFromPayload(payload);
    if (stamp) body.splitGroup = stamp;
    return body;
}

// ---- the outcome ledger (runs / confirms), localStorage ----
//
// The DRAFTS carry the set; this carries what has HAPPENED to each member (which run analyzed it,
// what the best decision was, whether it was confirmed). It cannot live in the draft payload alone
// because a confirm DELETES the draft it was built from -- the confirmed member would disappear
// from its own set. Browser-local and purely informational: losing it costs a label, never a
// shipment.
export const SPLIT_OUTCOMES_STORAGE_KEY = 'plannerSplitOutcomes';
const SPLIT_OUTCOMES_MAX_GROUPS = 8;

export function readSplitOutcomeStore(storage) {
    try {
        const raw = (storage || window.localStorage).getItem(SPLIT_OUTCOMES_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
}

export function readSplitOutcomes(groupId, storage) {
    const all = readSplitOutcomeStore(storage);
    const g = groupId ? all[groupId] : null;
    return g && typeof g === 'object' ? g : {};
}

// Merge one member's outcome and write the store back, keeping only the most recent groups.
export function writeSplitOutcome(groupId, draftId, patch, storage) {
    if (!groupId || !draftId) return {};
    const store = readSplitOutcomeStore(storage);
    const group = { ...(store[groupId] && typeof store[groupId] === 'object' ? store[groupId] : {}) };
    group[draftId] = { ...(group[draftId] || {}), ...patch, at: new Date().toISOString() };
    const next = { ...store, [groupId]: group };
    const keys = Object.keys(next);
    if (keys.length > SPLIT_OUTCOMES_MAX_GROUPS) {
        keys.slice(0, keys.length - SPLIT_OUTCOMES_MAX_GROUPS).forEach(k => { delete next[k]; });
    }
    try { (storage || window.localStorage).setItem(SPLIT_OUTCOMES_STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
    return group;
}

// Amazon FreightClass enum values for the LTL freight-class dropdown.
export const FREIGHT_CLASS_OPTIONS = [
    'FC_50', 'FC_55', 'FC_60', 'FC_65', 'FC_70', 'FC_77_5', 'FC_85', 'FC_92_5', 'FC_100',
    'FC_110', 'FC_125', 'FC_150', 'FC_175', 'FC_200', 'FC_250', 'FC_300', 'FC_400', 'FC_500',
];

// ---- Task 11: freight economics badge (mirrors PlacementScoringService.OptionScore.profitability) ----
//
// Final-review Minor 6: `profitability = 'UNKNOWN'` used to mean one thing -- we hold no margin
// data for these ASINs. Since the two-phase work it also means Amazon was never asked for freight
// (PlacementScoringService sets UNKNOWN and nulls marginErosionPct whenever freightUnknown). The
// fallback label blamed our own catalog for both, which is the same misattribution the spec's
// thesis rules out: "this placement is bad" and "we never found out" must not look the same.
function ProfitabilityBadge({ profitability, freightUnknown }) {
    if (profitability === 'OK') {
        return (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                OK
            </span>
        );
    }
    if (profitability === 'HIGH_EROSION') {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                high freight
            </span>
        );
    }
    if (profitability === 'UNPROFITABLE') {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-300">
                unprofitable
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-300">
            {freightUnknown ? 'not priced yet' : 'margin data missing'}
        </span>
    );
}

// ---- Task 23 (decision messaging, owner feedback 2026-08-16): "honestly, im still not clear from
// the messaging what our pre and post 1 day and 2 day coverage would be for this shipment. please
// continue working on the messaging to help the user understand if the shipment should be ignored
// or taken as well." One before->after coverage row (tabular numbers + a colored delta chip).
// `kind` picks the plain-language delivery word used in the annotation sentence below the
// numbers -- '1d' -> "next-day", '2d' -> "two-day". isZero uses a small epsilon since the
// backend's before/after percentages can differ by float dust even when nothing changed.
function CoverageRow({ label, before, after, kind }) {
    const delta = after - before;
    const isZero = Math.abs(delta) < 0.05;
    const deltaColor = delta > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500';
    const deliveryWord = kind === '1d' ? 'next-day' : 'two-day';

    let annotation = null;
    if (isZero && before >= 95) {
        annotation = `already fully covered — this shipment doesn't change ${deliveryWord} reach.`;
    } else if (isZero) {
        annotation = `unchanged — none of these destinations shortens ${deliveryWord} delivery anywhere.`;
    } else if (delta > 0) {
        annotation = `+${delta.toFixed(1)}pp — about ${Math.round(delta)}% more of your demand would get ${deliveryWord} delivery.`;
    }

    // Task 24 (owner feedback 2026-08-16): slim before/after bar so the coverage change reads
    // visually, not just as numbers -- before-fill solid, the added delta segment in green.
    const barBeforeWidth = Math.max(0, Math.min(100, before));
    const barDeltaWidth = Math.max(0, Math.min(100 - barBeforeWidth, delta));

    return (
        <div className="text-sm">
            <div className="flex items-center justify-between">
                <span className="text-gray-500">{label}</span>
                <span className="font-mono tabular-nums flex items-center gap-1.5">
                    <span className="text-gray-800">{before.toFixed(1)}%</span>
                    <span className="text-gray-400">&rarr;</span>
                    <span className="text-gray-900 font-medium">{after.toFixed(1)}%</span>
                    <span className={`px-1.5 rounded text-xs ${deltaColor}`}>
                        {delta >= 0 ? '+' : ''}{delta.toFixed(1)}
                    </span>
                </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden flex mt-1">
                <div className="h-full bg-indigo-400" style={{ width: `${barBeforeWidth}%` }} />
                {barDeltaWidth > 0 && <div className="h-full bg-green-500" style={{ width: `${barDeltaWidth}%` }} />}
            </div>
            {annotation && <div className="text-xs text-gray-500 mt-0.5">{annotation}</div>}
        </div>
    );
}

// ---- Task 23: the compact before->after coverage block at the top of every scored card --
// replaces the old scattered "Coverage gain" / "Coverage after" lines with the explicit
// pre/post 1-day and 2-day numbers the owner asked for by name. The optional third line
// (persistence-discounted "effective" coverage) is a frontend-only derived display: avgAfterPersistence
// and persistenceDelta are both already on the score, so avgBeforePersistence = avgAfterPersistence -
// persistenceDelta, and "effective coverage" = raw coverage x persistence (mirrors the win-curve's
// own persistence-discount philosophy -- PlacementScoringService's class javadoc).
// Task 75 (owner, verbatim: "the freight option cards ... need to show the freight cost - remove
// value/mo and persistence since we do not know what those are"): on LTL cards the two coverage
// rows stay (they're factual before->after numbers), but the persistence-discounted "effective
// coverage" line and the $/mo value sentence below it are coverage-economics framing that doesn't
// apply to how the operator judges a freight shipment -- isLtl suppresses both. UPS/parcel cards
// are unaffected (isLtl defaults false).
function CoverageBlock({ score: s, isLtl = false }) {
    const hasPersistence = !isLtl && s.avgAfterPersistence != null;
    const avgBeforePersistence = hasPersistence ? s.avgAfterPersistence - s.persistenceDelta : null;
    // Task 24 (owner feedback 2026-08-16): "still not clear ... the impact of both 1 and 2 day
    // coverage" -- roll the two coverage lines up into one plain-language $-impact sentence.
    // Threshold (not a strict >0) absorbs float dust and near-zero lift; REPLENISHMENT shipments
    // naturally land here too (their valuePerMonth is already low by design), so this doesn't
    // contradict the decision strip / REPLENISHMENT banner, it just says the same thing here.
    const hasMeaningfulValue = (s.valuePerMonth ?? 0) >= 1;
    return (
        <div className="mb-3 space-y-2">
            <CoverageRow label="2-day coverage" before={s.covBefore2dPct} after={s.covAfter2dPct} kind="2d" />
            <CoverageRow label="1-day coverage" before={s.covBefore1dPct} after={s.covAfter1dPct} kind="1d" />
            {hasPersistence && (
                <div className="text-xs text-gray-400 font-mono tabular-nums">
                    effective 2-day {(avgBeforePersistence * s.covBefore2dPct).toFixed(0)}% &rarr; {(s.avgAfterPersistence * s.covAfter2dPct).toFixed(0)}%
                </div>
            )}
            {!isLtl && (
                <div className="text-xs text-gray-600 pt-1 border-t border-gray-100">
                    {hasMeaningfulValue
                        ? `These delivery-speed improvements are worth about $${s.valuePerMonth.toFixed(0)}/mo in expected sales.`
                        : 'No meaningful sales lift from delivery speed — this shipment is about depth, not coverage.'}
                </div>
            )}
        </div>
    );
}

// ---- Purpose tags (owner corrections 2026-08-17, case run 97 / B01M0TOP10): PlacementScoringService
// now emits a non-exclusive purposeTags array ('COVERAGE' and/or 'REPLENISHMENT', or ['WEAK'] alone)
// instead of the old single GROWTH/REPLENISHMENT/WEAK shipmentPurpose string -- a shipment can add
// both real coverage AND real depth at once ("we have 4 in stock - this is both a replenish and
// coverage"). purposeTags() below reads the new array with a fallback to the legacy shipmentPurpose
// field (renamed GROWTH -> COVERAGE) for older cached responses, so this keeps working either way.
function purposeTags(s) {
    if (!s) return [];
    if (Array.isArray(s.purposeTags) && s.purposeTags.length > 0) return s.purposeTags;
    if (s.weak) return ['WEAK'];
    if (s.shipmentPurpose === 'GROWTH') return ['COVERAGE'];
    if (s.shipmentPurpose) return [s.shipmentPurpose];
    return [];
}

const PURPOSE_TAG_CHIP_STYLES = {
    COVERAGE: 'bg-green-100 text-green-800',
    REPLENISHMENT: 'bg-indigo-100 text-indigo-800',
};
// One chip per non-WEAK tag, its own number attached -- "COVERAGE +14.1pp" / "REPLENISHMENT +120d
// runway" -- so a both-tagged option (run 97) shows both facts side by side instead of picking one.
function PurposeTagChips({ score: s, className = '' }) {
    const tags = purposeTags(s).filter((t) => t !== 'WEAK');
    if (tags.length === 0) return null;
    return (
        <div className={`flex flex-wrap gap-1 ${className}`}>
            {tags.map((tag) => (
                <span
                    key={tag}
                    className={`inline-block text-xs font-semibold px-2 py-0.5 rounded ${PURPOSE_TAG_CHIP_STYLES[tag] || 'bg-gray-100 text-gray-800'}`}
                >
                    {tag === 'COVERAGE' && `COVERAGE +${(s.demandCovGainPp ?? 0).toFixed(1)}pp`}
                    {tag === 'REPLENISHMENT' && `REPLENISHMENT +${(s.runwayDaysAdded ?? 0).toFixed(0)}d runway`}
                </span>
            ))}
        </div>
    );
}

// ---- Task 23: TAKE / CONSIDER / SKIP verdict strip -- the single most prominent element on the
// card, answering "do I ship this?" directly. decisionReason is rendered verbatim (backend already
// formats every number into the sentence).
const DECISION_STRIP_STYLES = {
    TAKE: 'bg-green-600 text-white',
    CONSIDER: 'bg-amber-500 text-white',
    SKIP: 'bg-gray-400 text-white',
};
function DecisionStrip({ decision, reason }) {
    if (!decision) return null;
    return (
        <div className={`rounded-md px-3 py-2 mb-3 ${DECISION_STRIP_STYLES[decision] || DECISION_STRIP_STYLES.SKIP}`}>
            <div className="text-xs font-bold tracking-wide">{decision}</div>
            {reason && <div className="text-xs mt-0.5 leading-snug">{reason}</div>}
        </div>
    );
}

// ---- Task 23: plan-level "your current position" header, rendered once above the option grid
// (both modes) so the pre-shipment state is stated plainly before any option card is read.
// currentRunwayDays/covBefore1dPct/covBefore2dPct are identical on every score in one analysis (they
// describe the CURRENT position, independent of which option ships), so any score in the list works.
//
// Task 46 (owner, questioning an opaque "60 days of stock": "why is it saying we have 2 months of
// inventory? should [show] the number of units in the current position section that we have in
// stock, the current sales rank, the estimated units to be sold and our actual units selling"):
// the header now renders a compact per-ASIN table of the runway math's INPUTS -- stock (+inbound),
// sales rank, the demand model's market estimate, our actual 28d sales, and the division itself
// ("4 ÷ 2/mo ≈ 60d") -- from AnalysisResponse.positions. The coverage summary line stays beneath.
// Older cached responses without positions fall back to the legacy one-line summary.
const POSITION_NUM_FMT = new Intl.NumberFormat('en-US');
const fmtPosNum = (v) => (v == null ? '—' : POSITION_NUM_FMT.format(v));
const RUNWAY_TOOLTIP = 'estimated market rate from the demand model; runway = (on hand + inbound) ÷ daily rate';
const DEMAND_DISAGREE_TOOLTIP = 'market estimate and actual sales disagree — runway may be off';

// Owner's implicit worry: runway is computed off the market ESTIMATE, which may not match what we
// actually sell. Monthly-ize the 28d actual and flag when the two rates differ by more than 2x
// either way. One side zero while the other is positive also disagrees (infinite ratio); both
// zero, or either side unknown, doesn't.
function demandDisagrees(estMo, sold28d) {
    if (estMo == null || sold28d == null) return false;
    const actualMo = sold28d * (30 / 28);
    if (estMo <= 0 && actualMo <= 0) return false;
    if (estMo <= 0 || actualMo <= 0) return true;
    const ratio = estMo > actualMo ? estMo / actualMo : actualMo / estMo;
    return ratio > 2;
}

function DemandDisagreeDot() {
    return (
        <span
            title={DEMAND_DISAGREE_TOOLTIP}
            className="inline-block h-2 w-2 rounded-full bg-amber-400 ml-1 align-middle cursor-help"
        />
    );
}

function PlanPositionHeader({ score: s, positions }) {
    if (!s) return null;
    const rows = Array.isArray(positions) ? positions : [];
    const runwayText = s.currentRunwayDays != null
        ? `${s.currentRunwayDays.toFixed(0)} days of stock`
        : 'stock runway unknown (market rate not available)';
    return (
        <div className="mb-3">
            {rows.length > 0 && (
                <div className="border border-gray-200 rounded-md overflow-x-auto mb-1.5">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-gray-500 bg-gray-50">
                                <th className="px-2 py-1.5 font-medium">Product</th>
                                <th className="px-2 py-1.5 font-medium text-right">In stock</th>
                                <th className="px-2 py-1.5 font-medium text-right">Sales rank</th>
                                <th className="px-2 py-1.5 font-medium text-right">Est. sales/mo (market)</th>
                                <th className="px-2 py-1.5 font-medium text-right">Our sold (28d)</th>
                                <th className="px-2 py-1.5 font-medium text-right" title={RUNWAY_TOOLTIP}>Runway</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => {
                                const total = (p.unitsOnHand ?? 0) + (p.unitsInTransit ?? 0);
                                const disagree = demandDisagrees(p.estMarketUnitsMo, p.ourUnitsSold28d);
                                return (
                                    <tr key={p.asin} className="border-t border-gray-100 align-top">
                                        <td className="px-2 py-1.5">
                                            <div className="text-gray-800 leading-snug">{p.name || p.asin}</div>
                                            <div className="text-[10px] text-gray-400 font-mono">{p.asin}</div>
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-gray-800 whitespace-nowrap">
                                            {fmtPosNum(p.unitsOnHand)}
                                            {(p.unitsInTransit ?? 0) > 0 && (
                                                <span className="text-indigo-600"> +{fmtPosNum(p.unitsInTransit)} inbound</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-gray-800">
                                            {p.salesRank != null
                                                ? fmtPosNum(p.salesRank)
                                                : <span className="text-gray-400 italic font-sans" title="Amazon has not reported a sales rank for this ASIN (typically a new listing)">new / no rank</span>}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-gray-800 whitespace-nowrap">
                                            {fmtPosNum(p.estMarketUnitsMo)}
                                            {disagree && <DemandDisagreeDot />}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-gray-800 whitespace-nowrap">
                                            {fmtPosNum(p.ourUnitsSold28d)}
                                            {disagree && <DemandDisagreeDot />}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-mono tabular-nums text-gray-800 whitespace-nowrap cursor-help" title={RUNWAY_TOOLTIP}>
                                            {p.runwayDays != null
                                                ? `${fmtPosNum(total)} ÷ ${fmtPosNum(p.estMarketUnitsMo)}/mo ≈ ${fmtPosNum(Math.round(p.runwayDays))}d`
                                                : 'n/a — no market estimate'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <div className="text-sm text-gray-600">
                Your current position: <span className="font-medium text-gray-800">{runwayText}</span>
                <span className="text-gray-300 mx-1.5">&middot;</span>
                2-day coverage <span className="font-medium text-gray-800">{Number.isFinite(s.covBefore2dPct) ? `${s.covBefore2dPct.toFixed(1)}%` : '—'}</span>
                <span className="text-gray-300 mx-1.5">&middot;</span>
                1-day <span className="font-medium text-gray-800">{Number.isFinite(s.covBefore1dPct) ? `${s.covBefore1dPct.toFixed(1)}%` : '—'}</span>
            </div>
        </div>
    );
}

// ---- Task 29 (draw-quality advisor -- owner question 2026-08-16: "how does this help us determine
// whether we should use a shipment plan or wait and regenerate at a later time with the hope of a
// different plan? if we generated 3 plans, 1 per day, are we then able to pick the best of the
// three?"): ONE plan-level strip under the position header (never per option) showing how today's
// draw ranks against the accumulated probe history for this composition, plus CONFIRM NOW / WAIT
// advice. Deliberately lighter than the per-option DecisionStrip (tinted border + background, not
// a solid fill) so the two never read as the same kind of verdict. Gray whenever the advisor
// couldn't rank the draw (insufficient history -> runway-only advice, or N/A on blocked options).
const DRAW_ADVICE_STYLES = {
    CONFIRM_NOW: 'border-green-300 bg-green-50 text-green-900',
    WAIT: 'border-indigo-300 bg-indigo-50 text-indigo-900',
    CONSIDER_WAITING: 'border-amber-300 bg-amber-50 text-amber-900',
};
const DRAW_ADVICE_LABELS = {
    CONFIRM_NOW: 'CONFIRM NOW',
    WAIT: 'WAIT',
    CONSIDER_WAITING: 'CONSIDER WAITING',
    'N/A': 'N/A',
};
function DrawQualityStrip({ dq }) {
    if (!dq) return null;
    const hasPercentile = dq.percentile != null;
    const style = hasPercentile && DRAW_ADVICE_STYLES[dq.advice]
        ? DRAW_ADVICE_STYLES[dq.advice]
        : 'border-gray-300 bg-gray-50 text-gray-700';
    const parts = [];
    if (hasPercentile) {
        parts.push(`better than ${Math.round(dq.percentile * 100)}% of the last ${dq.sampleSize} draws${dq.widened ? ' (similar plans)' : ''}`);
    } else {
        parts.push(`${dq.sampleSize} prior draw${dq.sampleSize === 1 ? '' : 's'} — not enough history to rank today's`);
    }
    if (dq.neededFcAppearanceRate != null && dq.sampleSize > 0) {
        parts.push(`needed FCs appeared in ${Math.round(dq.neededFcAppearanceRate * dq.sampleSize)} of ${dq.sampleSize}`);
    }
    if (dq.daysSinceBetterDraw != null) {
        parts.push(`last better draw ${dq.daysSinceBetterDraw} day${dq.daysSinceBetterDraw === 1 ? '' : 's'} ago`);
    }
    return (
        <div className={`rounded-md border px-3 py-2 mb-3 text-xs leading-snug ${style}`}>
            <span className="font-semibold">Draw quality:</span> {parts.join(' · ')}
            <span className="mx-1.5 opacity-40">·</span>
            <span className="font-bold tracking-wide">ADVICE: {DRAW_ADVICE_LABELS[dq.advice] || dq.advice}</span>
            {dq.adviceReason && <> — {dq.adviceReason}</>}
        </div>
    );
}

// ---- Task 35 (rescore-on-load, owner: "these pages should be the same"): subtle banner shown
// whenever the rendered result came from GET /runs/{id} (the backend stamps rescoredAt only on
// that path, never on a fresh analyze). Says what the operator is looking at: Amazon's stored
// options, re-scored against CURRENT inventory/demand at load time. When the stored options are
// older than ~2 days they may have expired on Amazon's side -- flag that acting requires a fresh
// analyze. Shared verbatim by PO mode and AsinModePanel so both loaded-run views label alike.
const RUN_OPTIONS_STALE_DAYS = 2;
function LoadedRunBanner({ result }) {
    if (!result || !result.rescoredAt) return null;
    const analyzed = result.analyzedAt ? new Date(result.analyzedAt) : null;
    const analyzedValid = analyzed && !isNaN(analyzed.getTime());
    const stale = analyzedValid
        && (Date.now() - analyzed.getTime()) > RUN_OPTIONS_STALE_DAYS * 86400000;
    return (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/70 px-3 py-2 mb-3 text-xs leading-snug text-indigo-800">
            <div className="flex items-center gap-1.5">
                <ClockIcon className="h-4 w-4 flex-shrink-0 text-indigo-500" />
                <span>
                    Loaded run #{result.runId}
                    {analyzedValid && <> (analyzed {analyzed.toLocaleDateString()} {analyzed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})</>}
                    {' '}— re-scored against current inventory and demand just now
                </span>
            </div>
            {stale && (
                <div className="flex items-center gap-1.5 mt-1 text-amber-700">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                    <span>Amazon options are over {RUN_OPTIONS_STALE_DAYS} days old and may have expired — re-run analyze to act.</span>
                </div>
            )}
        </div>
    );
}

// ---- Task 32 (owner request 2026-08-17: "select multiple lines from the PO, but have them
// processed as separate shipments? or to look at the difference for separate vs one massive
// combined shipment?"): comparison table for /analyze-split results -- one row per single-ASIN
// plan (+ the combined plan's row in COMPARE mode) and a plain-language rollup line. Every plan
// stays UNCONFIRMED; "Open" deep-links to the run so the operator confirms per plan.
const SPLIT_DECISION_PILL = {
    TAKE: 'bg-green-100 text-green-800',
    CONSIDER: 'bg-amber-100 text-amber-800',
    SKIP: 'bg-gray-200 text-gray-600',
};
function SplitRollupLine({ rollup }) {
    if (!rollup) return null;
    const fmt = (v) => (v != null ? `$${Math.round(v)}` : '$?');
    const moreFcs = rollup.distinctFcsSeparate - rollup.distinctFcsCombined;
    const extraCost = rollup.totalFreightSeparate != null && rollup.totalFreightCombined != null
        ? rollup.totalFreightSeparate - rollup.totalFreightCombined : null;
    return (
        <div className="text-sm text-gray-700 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 mb-3">
            <span className="font-medium">Separate:</span> {fmt(rollup.totalFreightSeparate)} freight+fees, {rollup.distinctFcsSeparate} distinct FC{rollup.distinctFcsSeparate !== 1 ? 's' : ''}, ${Math.round(rollup.totalValueSeparate || 0)}/mo
            <span className="text-gray-400 mx-1.5">&middot;</span>
            <span className="font-medium">Combined:</span> {fmt(rollup.totalFreightCombined)}, {rollup.distinctFcsCombined} FC{rollup.distinctFcsCombined !== 1 ? 's' : ''}, ${Math.round(rollup.totalValueCombined || 0)}/mo
            {moreFcs > 0 && extraCost != null && (
                <>
                    <span className="text-gray-400 mx-1.5">&middot;</span>
                    <span className="font-semibold text-indigo-800">
                        separate buys {moreFcs} more building{moreFcs !== 1 ? 's' : ''} for {extraCost >= 0 ? `$${Math.round(extraCost)}` : `$${Math.round(-extraCost)} LESS`}
                    </span>
                </>
            )}
            {rollup.freightUnknownAny && (
                <div className="text-xs text-amber-700 mt-1">Some plans returned no Amazon freight quote — freight totals are incomplete.</div>
            )}
        </div>
    );
}
function SplitResultRow({ r, isCombined }) {
    const pill = SPLIT_DECISION_PILL[r.decision] || SPLIT_DECISION_PILL.SKIP;
    return (
        <tr className={isCombined ? 'bg-indigo-50/60 font-medium' : ''}>
            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                {isCombined ? 'COMBINED' : r.asin}
                {r.note && (
                    <div className="text-[10px] text-amber-700 font-sans normal-case whitespace-normal max-w-[14rem]">{r.note}</div>
                )}
            </td>
            <td className="px-3 py-2 text-right">{r.qty}</td>
            {r.error ? (
                <td colSpan={7} className="px-3 py-2 text-xs text-red-600">{r.error}</td>
            ) : (
                <>
                    <td className="px-3 py-2 text-xs">{(r.destinationFcs || []).join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{r.bestScore != null ? r.bestScore.toFixed(0) : '—'}</td>
                    <td className="px-3 py-2 text-center">
                        {r.decision && <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${pill}`}>{r.decision}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">{r.covGain2dPp != null ? `+${r.covGain2dPp.toFixed(1)}pp` : '—'}</td>
                    <td className="px-3 py-2 text-right">
                        {(r.mpRelativeMetros ?? 0) > 0 ? `${r.mpFasterAfter}/${r.mpRelativeMetros}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                        {r.freightPerUnit != null ? `$${r.freightPerUnit.toFixed(2)}` : '—'}
                        {r.freightUnknown && <span className="text-amber-600" title="no Amazon freight quote — fee only">*</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                        {r.drawQuality?.percentile != null
                            ? <span title={`vs ${r.drawQuality.sampleSize} prior draws`}>p{Math.round(r.drawQuality.percentile * 100)}</span>
                            : '—'}
                    </td>
                </>
            )}
            <td className="px-3 py-2 text-right">
                {r.runId != null && (
                    <a
                        href={`/shipment-planner?run=${r.runId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                    >
                        Open
                    </a>
                )}
            </td>
        </tr>
    );
}
function SplitComparisonTable({ result }) {
    if (!result) return null;
    return (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                    {result.mode === 'COMPARE' ? 'Separate vs combined comparison' : 'Separate plans (one per ASIN)'}
                </h3>
                <span className="text-xs text-gray-400">all plans unconfirmed — open a row to confirm it</span>
            </div>
            <SplitRollupLine rollup={result.rollup} />
            {result.combinedError && (
                <div className="text-xs text-red-600 mb-2">Combined plan failed: {result.combinedError}</div>
            )}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">ASIN</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Destinations</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Score</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-500">Decision</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Cov 2d</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500" title="metros where our promise beats Marketplace Pros after / contested">vs MP</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500">Freight/u</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-500" title="draw percentile vs this composition's probe history">Draw</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {(result.perAsin || []).map((r) => <SplitResultRow key={r.asin} r={r} />)}
                        {result.combinedSummary && <SplitResultRow r={result.combinedSummary} isCombined />}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---- Task 14: shared scored placement-option card, used by both PO mode (ShipmentPlanner, when
// analysisResult.scores is present) and ASIN mode (AsinModePanel, always). Score cards show
// coverage before->after, $/mo, freight economics, and the recommended/confirm-gate flags that
// PlacementScoringService attaches -- the legacy (unscored) card stays a separate, simpler render
// in each caller.
/** The "Amazon never answered" banner for one placement option. Renders nothing for options that
 * were quoted, so it can be dropped into every option card unconditionally. */
export function TransportErrorNote({ option }) {
    const message = option?.transportError;
    if (!message) return null;
    return (
        <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800 flex items-start gap-2">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
                <span className="font-semibold">Not quoted.</span>{' '}{message}{' '}
                The destinations, freight and score shown for this option are placeholders, not results.
            </span>
        </div>
    );
}

export function ScoredOptionCard({ score: s, option: po, totalUnits, selected, onSelect, expanded, onToggleExpand,
                            palletEdit }) {
    const feePerUnit = totalUnits > 0 && po?.placementFee != null ? po.placementFee / totalUnits : null;
    const freightOnlyPerUnit = feePerUnit != null && Number.isFinite(s.freightPerUnit)
        ? Math.max(0, s.freightPerUnit - feePerUnit) : null;
    // Task 75: LTL cards lead with the Amazon freight quote instead of $/mo + persistence (coverage-
    // economics metrics the owner said don't apply to judging a freight shipment).
    const isLtl = isLtlOption(po);
    const freightTotals = isLtl ? optionFreightTotals(po, totalUnits) : null;
    const freightSummaryLine = isLtl ? formatFreightSummaryLine(freightTotals) : null;
    return (
        <div
            onClick={onSelect}
            className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                selected
                    ? 'border-indigo-500 bg-indigo-50/50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
            } ${isLtl && selected ? 'col-span-full' : ''} ${s.weak ? 'ring-1 ring-red-200' : purposeTags(s).includes('REPLENISHMENT') ? 'ring-1 ring-indigo-200' : ''}`}
        >
            <div className="flex items-center justify-between mb-1 gap-2">
                {s.recommended && (
                    <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded">Recommended</span>
                )}
                {s.weak && (
                    <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold px-2 py-0.5 rounded">WEAK</span>
                )}
                {!s.weak && <PurposeTagChips score={s} />}
            </div>

            {/* An option Amazon never quoted transport for. Everything below it — the coverage
                numbers, the $0 freight, the verdict — is computed from placeholders, so it is
                labelled at the TOP of the card rather than left to be inferred from a row of
                dashes. Before this, a failed generateTransportationOptions produced a card that
                read as a confident "no good options here". */}
            <TransportErrorNote option={po} />

            {/* Task 23: explicit pre->post 1d/2d coverage numbers, top of card. Task 75: LTL
                suppresses the persistence + $/mo sub-lines (isLtl), keeping the factual before/after
                rows.

                Withheld entirely on an option Amazon never quoted. With no destinations there is no
                after-state to compute, so this block rendered "86.3% -> 86.3% +0.0" and annotated it
                "unchanged -- none of these destinations shortens two-day delivery anywhere" (run
                #255): a measured-sounding finding about destinations that do not exist. The verdict
                fields behind it are already withdrawn server-side in
                ShipmentPlannerAnalysisService.withdrawUnquotedVerdicts; these four percentages are
                plain doubles that have no "unknown" value to carry, so the suppression is here. */}
            {po?.transportError
                ? <div className="mb-3 text-xs text-gray-500 italic">
                    Coverage impact unavailable — Amazon returned no destinations for this option.
                  </div>
                : (Number.isFinite(s.covBefore2dPct) && Number.isFinite(s.covAfter2dPct)
                    && Number.isFinite(s.covBefore1dPct) && Number.isFinite(s.covAfter1dPct))
                    ? <CoverageBlock score={s} isLtl={isLtl} />
                    : <div className="mb-3 text-xs text-gray-500 italic">
                        Coverage impact unavailable — this option has not been scored yet.
                      </div>}

            {/* Task 28: MP-relative overlay -- how many contested metros (MP has a fastest-promise
                observation for the plan's ASINs) our promise beats theirs in, before -> after this
                placement. Only rendered when MP contests at least one metro. */}
            {(s.mpRelativeMetros ?? 0) > 0 && (
                <div className="text-xs text-gray-600 mb-3">
                    vs Marketplace Pros: fastest in {s.mpFasterMetrosBefore} &rarr;{' '}
                    <span className={s.mpFasterMetrosAfter > s.mpFasterMetrosBefore ? 'text-green-700 font-semibold' : 'font-medium text-gray-800'}>
                        {s.mpFasterMetrosAfter}
                    </span>{' '}
                    of {s.mpRelativeMetros} contested metros
                </div>
            )}

            {/* Task 23: the take/skip verdict -- the single most prominent element on the card. */}
            <DecisionStrip decision={s.decision} reason={s.decisionReason} />

            {/* Task 75 (owner, verbatim: "the freight option cards ... need to show the freight
                cost"): the most prominent figure on an LTL card is now Amazon's freight quote --
                total across every shipment, carrier, placement fee, and per-unit.

                Milestone C task C4: an awaitingPallets option has no freight to show BECAUSE
                nothing has been measured yet -- that is a different fact than "Amazon simply
                hasn't answered", and reads as a call to action (measure the pallets) rather than
                a stall. */}
            {isLtl && (
                freightSummaryLine ? (
                    <div className="text-base font-bold text-gray-900 mb-3">{freightSummaryLine}</div>
                ) : po?.awaitingPallets ? (
                    <div className="text-xs text-amber-600 italic mb-3" data-testid={`measure-cta-${po.placementOptionId}`}>
                        Freight — measure the pallets to get Amazon&apos;s quote
                    </div>
                ) : (
                    <div className="text-xs text-amber-600 italic mb-3">no Amazon freight quote yet — see the shipment table below</div>
                )
            )}

            {/* Milestone C task C3/C4: Amazon's own deadline on this placement (present whether or
                not it is still awaiting pallets -- phase 2 needs it too, spec 3.7). Relative time,
                never a bare ISO string an operator has to do the subtraction on themselves. */}
            {isLtl && po?.placementExpiration && (
                <div className="text-[11px] text-amber-700 mb-2" data-testid={`placement-expiration-${po.placementOptionId}`}>
                    Quote window closes in {relativeTimeUntilLabel(po.placementExpiration)}
                </div>
            )}

            {/* No score when nothing was priced. A fabricated "0" reads as a measured verdict --
                the same failure mode the whole two-phase change exists to remove. */}
            {s.scoreKnown !== false && Number.isFinite(s.score) && (
                <>
                    <div className="text-2xl font-bold text-gray-900 mb-1">{s.score.toFixed(0)}</div>
                    <div className="text-xs text-gray-400 mb-3">score</div>
                </>
            )}

            {(!isLtl || purposeTags(s).includes('REPLENISHMENT')) && (
                <div className="space-y-1 text-sm">
                    {!isLtl && (
                        <div className="flex justify-between">
                            <span className="text-gray-500">Value / mo</span>
                            <span className="font-medium text-green-700">
                                {Number.isFinite(s.valuePerMonth) ? `$${s.valuePerMonth.toFixed(0)}/mo` : '—'}
                            </span>
                        </div>
                    )}
                    {!isLtl && s.avgAfterPersistence != null && (
                        <div className="flex justify-between">
                            <span className="text-gray-500">avg persistence after</span>
                            <span>{(s.avgAfterPersistence * 100).toFixed(0)}%{s.persistenceApprox && ' (approx)'}</span>
                        </div>
                    )}
                    {purposeTags(s).includes('REPLENISHMENT') && (
                        <div className="flex justify-between">
                            <span className="text-gray-500">runway added</span>
                            <span className="text-indigo-700">
                                {Number.isFinite(s.runwayDaysAdded) ? `+${s.runwayDaysAdded.toFixed(0)}d` : '—'}
                                {!isLtl && <> &middot; persistence {s.persistenceDelta >= 0 ? '+' : ''}{(s.persistenceDelta * 100).toFixed(0)}%</>}
                            </span>
                        </div>
                    )}
                    {!isLtl && (
                        <div className="flex justify-between">
                            <span className="text-gray-500">Placement Fee</span>
                            <span>{po?.placementFee != null ? `$${po.placementFee.toFixed(2)}` : '—'}</span>
                        </div>
                    )}
                    {!isLtl && (
                        <div className="flex justify-between">
                            <span className="text-gray-500">Fee (amortized)</span>
                            <span>{Number.isFinite(s.feeAmortized) ? `$${s.feeAmortized.toFixed(2)}/mo` : '—'}</span>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-3 pt-3 border-t space-y-1 text-xs">
                <div className="flex items-center justify-between">
                    <span className="text-gray-500 font-medium">Freight economics</span>
                    <ProfitabilityBadge profitability={s.profitability} freightUnknown={s.freightUnknown} />
                </div>
                {Number.isFinite(s.freightPerUnit) && (
                    <div className="text-gray-600">
                        {freightOnlyPerUnit != null && feePerUnit != null ? (
                            <>Freight ${freightOnlyPerUnit.toFixed(2)} + placement ${feePerUnit.toFixed(2)} = ${s.freightPerUnit.toFixed(2)}/unit</>
                        ) : (
                            <>${s.freightPerUnit.toFixed(2)}/unit</>
                        )}
                        {s.marginErosionPct != null && (
                            <> &middot; consumes {s.marginErosionPct.toFixed(0)}% of shipment margin</>
                        )}
                    </div>
                )}
                {/* Final-review Minor 5: this used to read "scored on placement fee only", which
                    stopped being true when the unpriced score was withheld entirely -- the card
                    showed no score directly above a sentence explaining how it had been scored. */}
                {s.freightUnknown && (
                    <div className="text-amber-600 italic">freight not quoted yet — no score or verdict until it is</div>
                )}
            </div>

            {/* Task 75: for LTL, destinations move into the per-shipment table below (with units,
                boxes, pallets, weight, freight class, carrier and cost) -- this simple list stays
                for parcel/UPS options, which have no such table. */}
            {!isLtl && (
                <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                    <div className="text-xs font-medium text-gray-500 mb-1">Destination FCs</div>
                    {(po?.shipments || []).map((sh, si) => {
                        const shipUnits = sh.items?.reduce((sum, i) => sum + i.quantity, 0) || 0;
                        return (
                            <div key={si} className="flex items-center justify-between text-xs">
                                <span className="font-medium text-gray-700">{sh.destinationFc}</span>
                                <span className="text-gray-400">{sh.destinationCity}, {sh.destinationState}</span>
                                <span className="text-gray-500">{shipUnits}u</span>
                            </div>
                        );
                    })}
                    {(!po?.shipments || po.shipments.length === 0) && <div className="text-xs text-gray-400 italic">no shipment detail</div>}
                </div>
            )}

            {/* 2026-08-23 (owner, live): the nine-column table only renders on the SELECTED card,
                which spans the full row -- unselected cards stay compact so the option choices lie
                horizontally without scrolling. */}
            {isLtl && !selected && (
                <div className="mt-3 pt-3 border-t text-xs">
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-gray-600">
                        {ltlPalletShipments(po).map(sh => (
                            <span key={sh.shipmentId} className="font-medium text-gray-700">
                                {sh.destinationFc || sh.shipmentId?.slice(-4)}
                            </span>
                        ))}
                    </div>
                    <div className="mt-1 text-indigo-600">Select to open the shipments & pallet builder</div>
                </div>
            )}
            {selected && <LtlShipmentsTable option={po} planId={palletEdit?.planId} planItems={palletEdit?.planItems}
                canEdit={!!palletEdit?.canEdit} onApplied={palletEdit?.onApplied}
                approvedOptionIds={palletEdit?.approvedOptionIds}
                onApproveBuild={palletEdit?.onApproveBuild}
                declaredPalletCount={palletEdit?.declaredPalletCount}
                savedMeasurements={palletEdit?.savedMeasurements}
                onMeasurementsChange={palletEdit?.onMeasurementsChange} />}

            {/* Part 2 (in-transit inventory layer, owner: "avoid a double stock at an FC") -- one
                line per (fc, asin) this option ships to that already has units on the way per
                amazon_shipment2/amazon_shipment_item2 (see InTransitInventoryService). */}
            {(s.alreadyInbound || []).length > 0 && (
                <div className="mt-3 pt-3 border-t space-y-1">
                    {s.alreadyInbound.map((w, wi) => (
                        <div key={wi} className="flex items-center gap-1 text-xs text-amber-700">
                            <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{w.fc} already receiving {w.units} unit{w.units === 1 ? '' : 's'} of {w.asin}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Task 75: the per-ASIN $/mo breakdown is the same coverage-economics metric as
                Value/mo, disaggregated -- hidden for LTL for the same reason. */}
            {!isLtl && (
                <>
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                        className="mt-3 text-xs text-indigo-600 hover:text-indigo-800"
                    >
                        {expanded ? 'Hide per-ASIN detail' : 'Show per-ASIN detail'}
                    </button>

                    {expanded && (
                        <div className="mt-2 pt-2 border-t text-xs space-y-1">
                            {Object.entries(s.perAsinValue || {}).map(([asin, val]) => (
                                <div key={asin} className="flex justify-between">
                                    <span className="font-mono text-gray-600">{asin}</span>
                                    <span className={val >= 0 ? 'text-green-700' : 'text-red-600'}>${val.toFixed(2)}/mo</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ---- Task 51: single source of truth for shipping results ----
// Margin color helpers, module-level: used by the unified fallback option card below and by the
// PO-mode items table / detailed-breakdown tables (which pass through as children).
const marginColor = (pct) => {
    if (pct == null) return 'text-gray-400';
    if (pct < 0) return 'text-red-700 font-bold';
    if (pct < 5) return 'text-red-500';
    if (pct < 20) return 'text-yellow-600';
    return 'text-green-600';
};

const marginBg = (pct) => {
    if (pct == null) return '';
    if (pct < 0) return 'bg-red-50';
    if (pct < 5) return 'bg-red-50/50';
    if (pct < 20) return 'bg-yellow-50/50';
    return '';
};

// Task 51 (owner request 2026-08-18: "add the FC distribution map to appear at the bottom on the
// shipping selection and recommendations ... a single source of truth page that is shared"): the
// per-ASIN "Current FC distribution" section rendered at the BOTTOM of ShipmentResultsView for
// EVERY entry path (PO fresh analyze, ASIN fresh analyze, loaded/watch-opened runs). One
// FcCoverageTable per plan ASIN — same table the War Room and the per-ASIN drawer use — fetched
// once per ASIN and cached for the life of the results view (fetchedRef guards re-fetch across
// re-analyzes of the same ASINs). Collapsible per ASIN when the plan has multiple ASINs
// (collapsed by default), expanded by default for a single-ASIN plan.
function FcDistributionSection({ items }) {
    const asins = useMemo(
        () => Array.from(new Set((items || []).map(i => i.asin).filter(Boolean))),
        [items]
    );
    const nameByAsin = useMemo(() => {
        const map = {};
        (items || []).forEach(i => { if (i.asin && !map[i.asin] && i.name) map[i.asin] = i.name; });
        return map;
    }, [items]);

    const [coverageByAsin, setCoverageByAsin] = useState({});
    const [openByAsin, setOpenByAsin] = useState({});
    const fetchedRef = useRef(new Set());

    useEffect(() => {
        asins.forEach(asin => {
            if (fetchedRef.current.has(asin)) return;
            fetchedRef.current.add(asin);
            setCoverageByAsin(prev => ({ ...prev, [asin]: { status: 'loading', rows: [] } }));
            ApiClient.get(`/api/war-room/fc-analysis/${asin}`)
                .then(res => setCoverageByAsin(prev => ({ ...prev, [asin]: { status: 'loaded', rows: res.data || [] } })))
                .catch(err => {
                    console.error(`Failed to load FC analysis for ${asin}:`, err);
                    setCoverageByAsin(prev => ({ ...prev, [asin]: { status: 'error', rows: [] } }));
                });
        });
    }, [asins]);

    if (asins.length === 0) return null;

    const multi = asins.length > 1;

    return (
        <div className="mb-6 space-y-3">
            {asins.map(asin => {
                const open = openByAsin[asin] ?? !multi;
                const cov = coverageByAsin[asin] || { status: 'loading', rows: [] };
                const title = `Current FC distribution — ${nameByAsin[asin] || asin} (${asin})`;
                return (
                    <div key={asin} className="bg-white shadow rounded-lg p-4">
                        {multi ? (
                            <button
                                onClick={() => setOpenByAsin(prev => ({ ...prev, [asin]: !open }))}
                                className="w-full flex items-center justify-between gap-2 text-left"
                            >
                                <h3 className="text-sm font-semibold text-gray-700 truncate">{title}</h3>
                                {open
                                    ? <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                            </button>
                        ) : (
                            <h3 className="text-sm font-semibold text-gray-700 truncate">{title}</h3>
                        )}
                        {open && (
                            <div className="mt-3">
                                <FcCoverageTable status={cov.status} rows={cov.rows} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}// ---- Task 65/72: what Amazon assigned to each LTL shipment, and the pallet the operator builds ----
//
// Task 65 (owner: "we dont know the height or weight until amazon tells us the sku and qty it
// wants on each shipment") computed the pallets after the split and surfaced them read-only,
// with a correction hidden behind a small "adjust" link and a modal.
//
// Task 72 (owner: "amazon should return the product mix and unit count per pallet, and allow us
// to build the pallet and enter the height"): each LTL shipment now shows Amazon's assignment as
// a staging list -- product, ASIN/SKU, units, boxes, box size and weight -- followed by a PALLET
// BUILDER seeded with the computed layout. The operator edits the pallets he actually builds
// (boxes on each, measured HEIGHT, weight, stackability), adds/removes pallets, redistributes
// boxes, and re-quotes on his own numbers. Nothing starts blank: the machine's answer is the
// default, and the operator corrects it.

/** The pallet-bearing shipments of an option (LTL only -- SPD options carry no pallet summary).
 *
 * Milestone C phase 1 (C2 reviewer finding, routed to C4): a phase-1 shipment has NO `pallets` --
 * that field only exists post-requote. Filtering on `sh.pallets` alone made every phase-1 LTL
 * analyze render as PARCEL (wrong layout, no builder, no requote entry). A shipment now also
 * counts when it carries a `projectedPallets` reference, or when the OPTION itself is flagged
 * `awaitingPallets` -- the option-level flag covers shipments the projection math skipped, so a
 * gap in the projection can never silently demote the whole option back to parcel. */
export function ltlPalletShipments(option) {
    return (option?.shipments || []).filter(sh => sh.pallets || sh.projectedPallets || option?.awaitingPallets);
}

/** True when this placement option is an LTL (pallet-freight) option rather than parcel. Plan-wide
 * mode never mixes (an analyze is either LTL or UPS), but this is checked per-option so callers
 * never have to assume that. */
export function isLtlOption(option) {
    return ltlPalletShipments(option).length > 0;
}

/** True when ANY placement option in the analysis is LTL -- drives the LTL-vs-parcel results
 * layout (owner, task 75: "arrange the data horizontally instead of vertically" for LTL). */
export function planIsLtlMode(analysisResult) {
    return (analysisResult?.placementOptions || []).some(isLtlOption);
}

/** "2 x 48x40x39" -- the pallet count + footprint, for the results-table Pallets column. */
export function shipmentPalletSummaryLabel(sh) {
    const p = sh?.pallets;
    if (!p) return '—';
    return `${p.quantity} × ${p.lengthIn}×${p.widthIn}×${p.heightIn}`;
}

/** Total shipment weight (all pallets), rounded -- "373 lb". */
export function shipmentWeightLabel(sh) {
    const p = sh?.pallets;
    if (!p || p.weightLbEach == null || !p.quantity) return '—';
    return `${Math.round(p.quantity * p.weightLbEach).toLocaleString()} lb`;
}

/** "FC 200 (density 4.3 lb/ft³)" -- freight class plus how it was derived, for the results table. */
export function shipmentFreightClassLabel(sh) {
    const p = sh?.pallets;
    if (!p) return '—';
    const src = p.freightClassSource === 'OVERRIDE' ? ' (override)'
        : p.freightClassSource === 'MANUAL' ? ' (manual)'
        : p.densityLbFt3 != null ? ` (density ${p.densityLbFt3} lb/ft³)` : '';
    return `FC ${freightClassLabel(p.freightClass)}${src}`;
}

/** This shipment's own partnered-LTL quote status: has a carrier/cost, or explicitly none offered
 * (mirrors ltlGapQuoteLabel's "never $0.00 for a missing offer" rule). */
export function shipmentHasFreightQuote(sh) {
    return sh?.carrierName != null || (sh?.transportCost != null && sh.transportCost > 0);
}

/** Every OTHER partnered option Amazon returned for this shipment, excluding whichever one is
 * already selected/quoted -- "alternate carrier quotes" for the row's expanded detail. */
export function shipmentAlternateOptions(sh) {
    return (sh?.availableTransportOptions || [])
        .filter(o => o.transportationOptionId !== sh.transportationOptionId);
}

/** Placement-option-level freight totals: the sum of every shipment's own partnered quote (never
 * derived from placementFee), the placement fee, the blended per-unit cost, and the carrier name
 * when every shipment shares one (Amazon Partnered LTL requires a single carrier per placement, so
 * this is the common case -- null/"mixed carriers" only for a part-quoted or own-carrier plan). */
export function optionFreightTotals(option, totalUnits) {
    const shipments = option?.shipments || [];
    const known = shipments.some(sh => sh.transportCost != null);
    const totalFreight = known ? shipments.reduce((sum, sh) => sum + (sh.transportCost || 0), 0) : null;
    const placementFee = option?.placementFee ?? null;
    const perUnit = totalFreight != null && totalUnits > 0
        ? (totalFreight + (placementFee || 0)) / totalUnits
        : null;
    const carriers = [...new Set(shipments.map(sh => sh.carrierName).filter(Boolean))];
    return {
        totalFreight,
        placementFee,
        perUnit,
        carrierName: carriers.length === 1 ? carriers[0] : null,
        mixedCarriers: carriers.length > 1,
    };
}

/** "$1,404.52" -- comma-grouped currency, 2 decimals always (freight totals run into the
 * thousands, and .toFixed(2) alone drops the thousands separator). */
export function fmtUsd(n) {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** "Freight $1,404.52 (ESTES) + $81.33 placement · $13.03/unit" -- the owner's exact requested
 * format (task 75) for the option-level totals line that leads an LTL card/table. Null when
 * freight is entirely unknown (no shipment ever quoted) rather than misreporting $0. */
export function formatFreightSummaryLine(totals) {
    if (!totals || totals.totalFreight == null) return null;
    const carrier = totals.carrierName ? ` (${totals.carrierName})` : totals.mixedCarriers ? ' (mixed carriers)' : '';
    const fee = totals.placementFee != null ? ` + $${fmtUsd(totals.placementFee)} placement` : '';
    const perUnit = totals.perUnit != null ? ` · $${fmtUsd(totals.perUnit)}/unit` : '';
    return `Freight $${fmtUsd(totals.totalFreight)}${carrier}${fee}${perUnit}`;
}

/** The single ASIN a shipment contains, or null when it is mixed/unknown. Persisting a pallet
 * build against an ASIN is only meaningful when that ASIN is the only thing on the pallet. */
export function shipmentSingleAsin(sh) {
    const asins = [...new Set((sh?.items || []).map(it => it.asin).filter(Boolean))];
    return asins.length === 1 ? asins[0] : null;
}

/** Amazon's assignment for one shipment, joined to the plan's packaging so the warehouse can
 * stage it: units, boxes (units / units_per_box), box dims and the computed loaded box weight. */
export function shipmentContents(sh, planItems) {
    const byAsin = {};
    (planItems || []).forEach(pi => { if (pi?.asin) byAsin[pi.asin] = pi; });
    return (sh?.items || []).map(it => {
        const pi = byAsin[it.asin] || {};
        const unitsPerBox = pi.unitsPerBox > 0 ? pi.unitsPerBox : 1;
        const units = it.quantity ?? 0;
        return {
            asin: it.asin,
            sku: it.sku || pi.sku || null,
            // Task 80: the FNSKU + condition Amazon will print for this line. Frozen into
            // analysis_json at analyze time; falls back to the plan item when an older run is
            // loaded (pre-task-80 runs have neither, and render as "unknown condition").
            fnsku: it.fnsku ?? pi.fnsku ?? null,
            conditionLabel: it.conditionLabel ?? pi.conditionLabel ?? null,
            newCondition: it.newCondition ?? pi.newCondition ?? null,
            name: pi.name || null,
            // 2026-08-21: the code his warehouse and the Schluter paperwork use. An ASIN is not
            // something anyone reads off a carton.
            itemCode: pi.itemCode || null,
            units,
            unitsPerBox,
            boxes: Math.ceil(units / unitsPerBox),
            boxLengthIn: pi.boxLengthIn ?? null,
            boxWidthIn: pi.boxWidthIn ?? null,
            boxHeightIn: pi.boxHeightIn ?? null,
            boxWeightLbs: pi.boxWeightLbs ?? null,
        };
    });
}

/** Boxes Amazon's split put on this shipment: the backend's own count when it echoed one,
 * else summed from the contents. */
export function shipmentBoxCount(sh, planItems) {
    if (sh?.pallets?.totalBoxes > 0) return sh.pallets.totalBoxes;
    return shipmentContents(sh, planItems).reduce((n, c) => n + c.boxes, 0);
}

/** Average loaded box weight and the pallet tare implied by the computed summary -- used to keep
 * each built pallet's weight honest as boxes move between pallets. Falls back to an even split of
 * the computed gross when the contents can't be priced out. */
export function shipmentWeightModel(sh, planItems) {
    // 2026-08-23 (owner, live): weights are COMPUTED -- units x unit weight + pallet tare -- and
    // the operator overrides by typing. Phase 1 has no sh.pallets, so the projection supplies the
    // tare; with no summary at all, boxed weight + the 50 lb GMA default still computes.
    const p = sh?.pallets || sh?.projectedPallets || {};
    const boxes = shipmentBoxCount(sh, planItems);
    const gross = (p.quantity || 0) * (p.weightLbEach || 0);
    const boxed = shipmentContents(sh, planItems)
        .reduce((w, c) => w + (c.boxWeightLbs > 0 ? c.boxes * c.boxWeightLbs : 0), 0);
    if (boxes > 0 && boxed > 0 && gross > 0 && p.quantity > 0) {
        const tare = Math.max(0, (gross - boxed) / p.quantity);
        return { perBoxLbs: boxed / boxes, tareLbs: Math.round(tare * 10) / 10 };
    }
    if (boxes > 0 && boxed > 0) {
        return { perBoxLbs: boxed / boxes, tareLbs: 50 };
    }
    return { perBoxLbs: boxes > 0 && gross > 0 ? gross / boxes : 0, tareLbs: 0 };
}

/** Spread `boxes` over `count` pallets as evenly as possible, heaviest first (11 + 10, never
 * 18 + 3). */
export function distributeBoxes(boxes, count) {
    const n = Math.max(1, Math.round(count || 1));
    const base = Math.floor(boxes / n);
    let extra = boxes - base * n;
    return Array.from({ length: n }, () => base + (extra-- > 0 ? 1 : 0));
}

/** The pallet builder's starting rows for a shipment: the computed layout, boxes balanced across
 * the computed pallet count, each pallet at the computed height and its own boxes' weight. */
export function defaultPalletRows(sh, planItems) {
    const p = sh?.pallets || {};
    const count = Math.max(1, p.quantity || 1);
    const boxes = shipmentBoxCount(sh, planItems);
    const { perBoxLbs, tareLbs } = shipmentWeightModel(sh, planItems);
    return distributeBoxes(boxes, count).map(n => ({
        boxes: n,
        lengthIn: p.lengthIn ?? 48,
        widthIn: p.widthIn ?? 40,
        heightIn: p.heightIn ?? null,
        weightLbEach: perBoxLbs > 0
            ? Math.round((n * perBoxLbs + tareLbs) * 10) / 10
            : (p.weightLbEach ?? null),
        // Owner, 2026-08-23, verbatim: "we do not send any stackable pallets." NON_STACKABLE is
        // the backend default too -- the checkbox is now an explicit declaration, not an assumption.
        stackable: p.stackable === true,
        weightTouched: false,
    }));
}

/** Milestone C phase 1 (spec §4): the pallet builder's starting rows when NOTHING has been
 * measured yet -- `n` blank rows (`n = declaredPalletCount || 1`). Boxes, height and weight start
 * blank; length/width start at 48x40 because those are warehouse CONSTANTS (the pallet footprint
 * the warehouse always builds on), not measurements -- prefilling them is not seeding a guess.
 * stackable starts false (owner default: "we do not send any stackable pallets"). The projection
 * (`sh.projectedPallets`) is deliberately NEVER read here -- it is a labelled reference the
 * operator checks their build against, not a default to override. Row shape is exactly Milestone
 * B's (`{boxes, lengthIn, widthIn, heightIn, weightLbEach, stackable, weightTouched}`) -- C5
 * whitelists these keys for restore, so no field may be added here. */
export function blankPalletRows(count) {
    const n = Math.max(1, count || 1);
    return Array.from({ length: n }, () => ({
        boxes: '', lengthIn: 48, widthIn: 40, heightIn: '', weightLbEach: '',
        stackable: false, weightTouched: false,
    }));
}

/** Milestone C task C5: rowsByShipment is keyed by shipmentId, which rotates on every re-analyze
 * -- so a build the operator measured is dead the moment the placement expires and gets
 * re-analyzed, unless it is persisted somewhere that survives the rotation. Physical pallets are
 * built per DESTINATION, and FCs recur across re-analyzes of the same composition, so the draft
 * persists measurements keyed by `destinationFc` instead. This is exactly Milestone B's row shape
 * (blankPalletRows' comment above) -- functions and touched-flags beyond `weightTouched` are
 * dropped on both save and restore so a stored draft can never carry forward something
 * unreproducible or, worse, a stale event/ref object. */
export const PALLET_MEASUREMENT_FIELDS = [
    'boxes', 'lengthIn', 'widthIn', 'heightIn', 'weightLbEach', 'stackable', 'weightTouched',
];

export function whitelistPalletMeasurementRow(row) {
    const out = {};
    PALLET_MEASUREMENT_FIELDS.forEach((key) => {
        if (row && Object.prototype.hasOwnProperty.call(row, key)) out[key] = row[key];
    });
    return out;
}

export function whitelistPalletMeasurementRows(rows) {
    return (Array.isArray(rows) ? rows : []).map(whitelistPalletMeasurementRow);
}

// The draft-level whitelist: { [destinationFc]: { rows, savedAt } }, dropping any FC entry that
// isn't a non-empty row array (a junk/empty entry restores identically to "never saved").
export function whitelistPalletMeasurementsMap(map) {
    const out = {};
    if (!map || typeof map !== 'object') return out;
    Object.keys(map).forEach((fc) => {
        const entry = map[fc];
        if (!fc || !entry || !Array.isArray(entry.rows) || entry.rows.length === 0) return;
        out[fc] = { rows: whitelistPalletMeasurementRows(entry.rows), savedAt: entry.savedAt || null };
    });
    return out;
}

/** "Blank" for restore purposes, spec's own test: no row has a finite heightIn or weightLbEach,
 * and no row has boxes typed (an explicit 0 counts as typed -- spec 6.1, it's the operator saying
 * "nothing on this pallet", not "I haven't looked"). This is deliberately a structural check on
 * the ROWS THEMSELVES rather than "does component state have an entry for this shipment yet" --
 * the two usually coincide, but checking the rows is what makes restoredPalletRows safe to call
 * from anywhere, including a row set built by redistribute/addPallet that never touched a height
 * or weight. */
export function palletRowsAreUnmeasured(rows) {
    // Seeded boxes/weights are DERIVED (2026-08-23) -- only a typed height or a touched weight
    // counts as the operator having measured something worth protecting from a restore.
    return (rows || []).every((r) => {
        const h = parseFloat(r?.heightIn);
        return !Number.isFinite(h) && !r?.weightTouched;
    });
}

/** The restore decision itself (spec §2's draft-restore lesson, applied to pallet measurements): a
 * corrected carton was once discarded by a restore that skipped state checks, so this returns the
 * saved rows ONLY when `currentRows` are still unmeasured -- never when the operator has already
 * put anything into them, however that happened. Returns null (no restore) when there is nothing
 * saved for this FC, or when the current rows are no longer blank. */
export function restoredPalletRows(currentRows, destinationFc, savedMeasurements) {
    const saved = savedMeasurements?.[destinationFc];
    if (!saved || !Array.isArray(saved.rows) || saved.rows.length === 0) return null;
    if (!palletRowsAreUnmeasured(currentRows)) return null;
    return whitelistPalletMeasurementRows(saved.rows);
}

/** The fallback rows for a shipment with nothing yet in component state: the computed layout when
 * Amazon has already priced this shipment's build (`sh.pallets` present -- post-requote, or a
 * legacy pre-Milestone-C run), or Milestone C phase 1's BLANK rows when nothing has been measured
 * yet. This is the one place that decision is made, so every consumer of the pallet rows -- the
 * builder itself, the requote payload, the "remember this build" saves, and the build-state gate
 * -- agrees on it; before this existed each of them called `defaultPalletRows` straight through
 * and would have quietly fabricated a "computed" build for a phase-1 shipment that was never
 * seeded with real projection data in the first place. */
/** Phase-1 starting rows (owner, live 2026-08-23): boxes = the shipment's units spread across
 * the pallet count, weight = boxes x per-box weight + tare, both COMPUTED and overridable.
 * Height stays blank -- it is the one true measurement the warehouse supplies. */
export function seededPalletRows(sh, planItems, declaredPalletCount) {
    const n = Math.max(1, declaredPalletCount || sh?.projectedPallets?.quantity || 1);
    const model = shipmentWeightModel(sh, planItems);
    const split = distributeBoxes(shipmentBoxCount(sh, planItems), n);
    return split.map(b => {
        const base = { boxes: b, lengthIn: 48, widthIn: 40, heightIn: '', weightLbEach: '',
            stackable: false, weightTouched: false };
        return model.perBoxLbs > 0 ? recomputeRowWeight(base, model, null) : base;
    });
}

function fallbackPalletRows(sh, planItems, declaredPalletCount) {
    return sh?.pallets ? defaultPalletRows(sh, planItems)
        : seededPalletRows(sh, planItems, declaredPalletCount);
}

/** Recompute a row's weight from the boxes on it (boxes x loaded box weight + tare) unless the
 * operator typed a weight himself. */
export function recomputeRowWeight(row, model, fallbackLbEach) {
    if (row.weightTouched) return row;
    const w = model.perBoxLbs > 0
        ? Math.round((row.boxes * model.perBoxLbs + model.tareLbs) * 10) / 10
        : (fallbackLbEach ?? row.weightLbEach);
    return { ...row, weightLbEach: w };
}

/** Live per-shipment truth for the built pallets: totals, the density over the ACTUAL cube (each
 * pallet's own height), the class that density implies (a MANUAL/OVERRIDE class always sticks),
 * and the box reconciliation that gates re-quoting. */
/**
 * 2026-08-23 (owner, live: "there is no clear message as to what is missing"): the re-quote
 * button needs EVERY pallet shipment complete -- Amazon takes them all in one request -- but the
 * per-shipment asks render inside collapsed sections, so an untouched shipment disabled the
 * button invisibly. This names each blocker next to the button itself.
 */
export function requoteBlockers(palletShipments, summaries) {
    const out = [];
    (palletShipments || []).forEach((sh, i) => {
        const s = summaries?.[i];
        if (!s || s.valid) return;
        const label = sh.destinationFc || sh.shipmentId?.slice(-4) || `shipment ${i + 1}`;
        const rows = s.count || 0;
        if (rows === 0) {
            out.push({ label, reason: 'no pallets entered yet' });
            return;
        }
        const missingDims = rows - Math.min(rows, s.heightsEntered ?? 0);
        if (!s.dimsValid) {
            out.push({ label, reason: 'every pallet needs a height and a weight' });
            return;
        }
        if (!s.boxesIncomplete && s.placedBoxes !== s.expectedBoxes) {
            out.push({ label, reason: `${s.placedBoxes} of ${s.expectedBoxes} boxes placed` });
        }
    });
    return out;
}

export function builtPalletSummary(sh, rows, planItems) {
    // Final-review Critical 2 (2026-08-23): in phase 1 the analyze-time summary lives on
    // projectedPallets -- reading only sh.pallets left freightClassSource undefined, so the
    // density branch never fired and a hardcoded FC_100 went to Amazon whatever the operator
    // measured. The projection here informs footprint defaults and class SOURCE only; the
    // measured rows still drive every number.
    const p = sh?.pallets || sh?.projectedPallets || {};
    const lengthIn = p.lengthIn || 48;
    const widthIn = p.widthIn || 40;
    const expectedBoxes = shipmentBoxCount(sh, planItems);
    const list = rows || [];
    const placedBoxes = list.reduce((n, r) => n + (parseInt(r.boxes, 10) || 0), 0);
    // Spec section 6.1: "Per-pallet box count becomes advisory ... A row with a blank box count is
    // valid and simply does not contribute to the reconciliation total, which is then reported as
    // INCOMPLETE rather than as a mismatch." Blank is not zero: an explicit 0 is the operator
    // saying "nothing on this pallet" and stays a real count that must reconcile.
    const boxesIncomplete = list.some(
        r => r.boxes === '' || r.boxes === null || r.boxes === undefined
            || Number.isNaN(parseInt(r.boxes, 10)));
    const heights = list.map(r => parseFloat(r.heightIn)).filter(h => Number.isFinite(h) && h > 0);
    const weights = list.map(r => parseFloat(r.weightLbEach)).filter(w => Number.isFinite(w) && w > 0);
    const dimsValid = list.length > 0 && heights.length === list.length && weights.length === list.length;
    const totalWeightLb = weights.reduce((a, b) => a + b, 0);
    // 2026-08-23, owner: pallets "may not be uniform in build size". Each pallet contributes its
    // OWN cube -- one shipment-wide footprint over a mixed build gives a wrong density and can
    // move the freight class a whole tier.
    const cubeFt3 = list.reduce((ft3, r) => {
        const h = parseFloat(r.heightIn);
        if (!Number.isFinite(h) || h <= 0) return ft3;
        const l = Number.isFinite(parseFloat(r.lengthIn)) && parseFloat(r.lengthIn) > 0
            ? parseFloat(r.lengthIn) : lengthIn;
        const w = Number.isFinite(parseFloat(r.widthIn)) && parseFloat(r.widthIn) > 0
            ? parseFloat(r.widthIn) : widthIn;
        return ft3 + (l * w * h) / 1728;
    }, 0);
    const density = dimsValid && cubeFt3 > 0 && totalWeightLb > 0 ? totalWeightLb / cubeFt3 : null;
    const freightClass = p.freightClassSource === 'DENSITY' && density != null
        ? freightClassFromDensity(density) : (p.freightClass || 'FC_100');
    return {
        count: list.length,
        lengthIn,
        widthIn,
        // Surfaced 2026-08-21: the operator enters a HEIGHT, and cube -> density -> freight class
        // is the entire consequence of that number. Computing it and not showing it is why the
        // height felt arbitrary.
        cubeFt3: cubeFt3 > 0 ? Math.round(cubeFt3 * 10) / 10 : null,
        tallestIn: heights.length ? Math.max(...heights) : null,
        avgWeightLbEach: weights.length ? Math.round(totalWeightLb / weights.length * 10) / 10 : null,
        totalWeightLb: Math.round(totalWeightLb * 10) / 10,
        density,
        freightClass,
        expectedBoxes,
        placedBoxes,
        // Three outcomes, not two: reconciled, mismatched, or incomplete. Only a MISMATCH is a
        // contradiction -- an unstated row is a question nobody has to answer, because the box
        // count is advisory and reaches Amazon in no field.
        boxesIncomplete,
        boxesReconcile: !boxesIncomplete && placedBoxes === expectedBoxes,
        dimsValid,
        // Dimensions still gate the re-quote (they are what gets quoted); box counts no longer do
        // when they are merely unstated.
        valid: dimsValid && list.length > 0 && (boxesIncomplete || placedBoxes === expectedBoxes),
    };
}

/**
 * 2026-08-21, owner: "why is it guessing it needs to ask" — the pallet half.
 *
 * The computation was never the problem. The problem was that its output was presented with the
 * same confidence as a confirmed fact: the plan guessed a pallet build, asked Amazon to price that
 * guess, and rendered a finished-looking quote with a small "adjust" affordance nobody had reason
 * to press. Run 266 shipped 11 pallets that way.
 *
 * So a build carries an explicit state. Until the operator approves it, the layout is a SUGGESTION
 * and the freight attached to it is INDICATIVE -- priced against numbers nobody has stood behind.
 * Approving re-quotes through the existing /requote-ltl path, so the figures the operator finally
 * confirms are Amazon's price for the build he actually signed off.
 *
 * @returns {{state: 'APPROVED'|'SUGGESTED'|'INVALID', reason: string|null}}
 */
export function palletBuildState(option, rowsByShipment, planItems, approvedOptionIds, declaredPalletCount) {
    if (!option || !isLtlOption(option)) return { state: 'APPROVED', reason: null };  // parcel: no pallet step
    const shipments = ltlPalletShipments(option);
    if (shipments.length === 0) return { state: 'APPROVED', reason: null };

    const unreconciled = shipments.filter(sh => {
        const rows = rowsByShipment?.[sh.shipmentId] || fallbackPalletRows(sh, planItems, declaredPalletCount);
        return !builtPalletSummary(sh, rows, planItems).valid;
    });
    if (unreconciled.length > 0) {
        return {
            state: 'INVALID',
            reason: `${unreconciled.length} shipment${unreconciled.length !== 1 ? 's have' : ' has'} `
                + 'boxes left to place, or a pallet with no height or weight.',
        };
    }
    if (approvedOptionIds?.[option.placementOptionId]) return { state: 'APPROVED', reason: null };
    return {
        state: 'SUGGESTED',
        reason: 'This pallet build is a suggestion — approve it so Amazon quotes the build you '
            + 'actually intend to ship.',
    };
}

/** One line of consequence for a pallet row: cube, density and the class they produce. */
export function palletConsequenceLabel(summary) {
    if (!summary || summary.density == null) return null;
    const parts = [];
    if (summary.cubeFt3 != null) parts.push(`${summary.cubeFt3} ft³`);
    parts.push(`${Math.round(summary.density * 10) / 10} lb/ft³`);
    parts.push(`class ${freightClassLabel(summary.freightClass)}`);
    return parts.join(' · ');
}

/** Milestone C phase 0->1 reconciliation (spec §2), ASYMMETRIC by design:
 *  - fewer built than declared usually means pallets were consolidated -- a normal, unremarkable
 *    outcome, so it reads as INFO, not a warning.
 *  - more built than declared is the surprising direction and gets a WARN, but nothing here
 *    blocks anything -- Amazon is asked for the build that was actually made, never for the
 *    number that was declared before the split even existed.
 * Null when nothing was declared, or when the two already agree -- no news is not a finding. */
export function declaredCountReconciliation(totalBuilt, declared) {
    if (declared == null || totalBuilt === declared) return null;
    if (totalBuilt < declared) {
        return {
            tone: 'info',
            text: `Built ${totalBuilt} of ${declared} declared — fewer usually means pallets were consolidated.`,
        };
    }
    return {
        tone: 'warn',
        text: `You declared ${declared}, built ${totalBuilt}. Nothing has been adjusted — continue, or revise.`,
    };
}

/** POST /requote-ltl body for one placement option. Amazon requires EVERY shipment of the
 * placement in one request, so untouched shipments ride along with their (computed) build. */
export function buildLtlRequotePayload(option, planId, rowsByShipment, planItems, declaredPalletCount) {
    return {
        planId,
        placementOptionId: option?.placementOptionId,
        shipments: ltlPalletShipments(option).map(sh => {
            const rows = rowsByShipment?.[sh.shipmentId] || fallbackPalletRows(sh, planItems, declaredPalletCount);
            const s = builtPalletSummary(sh, rows, planItems);
            return {
                shipmentId: sh.shipmentId,
                palletQuantity: s.count,
                palletLengthIn: s.lengthIn,
                palletWidthIn: s.widthIn,
                palletHeightIn: s.tallestIn,
                palletWeightLbEach: s.avgWeightLbEach,
                // Owner, 2026-08-23: "we do not send any stackable pallets." The shipment is
                // stackable only if some row explicitly declares it -- never assumed.
                stackable: rows.some(r => r.stackable === true),
                freightClass: s.freightClass,
                declaredValue: sh.pallets?.declaredValue ?? sh.projectedPallets?.declaredValue,
                pallets: rows.map(r => ({
                    boxes: parseInt(r.boxes, 10) || 0,
                    lengthIn: parseFloat(r.lengthIn),
                    widthIn: parseFloat(r.widthIn),
                    heightIn: parseFloat(r.heightIn),
                    weightLbEach: parseFloat(r.weightLbEach),
                    stackable: r.stackable === true,
                })),
            };
        }),
    };
}

/** "Remember for this product" -> POST /packaging-pallet bodies: the built layout saved back to
 * the ASIN so next time's DEFAULT is the real-world one. Single-ASIN shipments only. */
export function buildPalletDefaultSaves(option, rowsByShipment, remember, planItems, declaredPalletCount) {
    const out = [];
    ltlPalletShipments(option).forEach(sh => {
        if (!remember?.[sh.shipmentId]) return;
        const asin = shipmentSingleAsin(sh);
        if (!asin) return;
        const rows = rowsByShipment?.[sh.shipmentId] || fallbackPalletRows(sh, planItems, declaredPalletCount);
        const s = builtPalletSummary(sh, rows, planItems);
        if (!s.valid) return;
        const body = { asin, palletHeightIn: s.tallestIn };
        const maxBoxes = Math.max(...rows.map(r => parseInt(r.boxes, 10) || 0));
        if (maxBoxes > 0) body.boxesPerPallet = maxBoxes;
        out.push(body);
    });
    return out;
}

/** Amazon's assignment for one shipment, rendered as the staging list the warehouse works from. */
/**
 * The structured 400 the LTL build returns when a carton has not been confirmed (owner: "why is it
 * guessing it needs to ask").
 *
 * This is an ASK, not an error. Amazon's catalog UNIT dimensions are not a shipping carton, and
 * palletising from them is what produced 185 single-unit "cartons" and 11 pallets on run 266. The
 * backend now refuses rather than guessing, and hands back exactly which fields are missing for
 * which product -- so the operator can answer it here instead of being told no.
 *
 * `missing: ["confirmation"]` is deliberately a different sentence: every number is already there
 * and nobody has vouched for it. That reads "confirm these", never "enter these" -- asking someone
 * to re-type numbers already on screen is how you train them to click through the question.
 */
export function cartonGapFromError(err) {
    // Two shapes, one meaning: the synchronous endpoint's 400 body, and a background job that
    // re-attached the gap to its Error (the async path has no HTTP response to carry it).
    const gap = err?.response?.data?.cartonDataGap || err?.cartonDataGap;
    if (!gap || !Array.isArray(gap.items) || gap.items.length === 0) return null;
    return gap;
}

/** True when the only thing missing is a human saying yes. */
export function isConfirmationOnly(item) {
    const missing = item?.missing || [];
    return missing.length === 1 && missing[0] === 'confirmation';
}

const CARTON_FIELD_LABEL = {
    unitsPerBox: 'units per box',
    boxLengthIn: 'length',
    boxWidthIn: 'width',
    boxHeightIn: 'height',
    boxWeightLbs: 'weight',
};

export function missingFieldsLabel(item) {
    // Not "nothing missing" as though the values were suspect -- they may well be right. The only
    // outstanding thing is that a person has not said so.
    if (isConfirmationOnly(item)) return 'confirm these are the shipping carton’s dimensions';
    // A carton with NO stored row comes back missing all five measurements AND "confirmation".
    // Listing the pseudo-field alongside real ones would read "missing ... weight, confirmation",
    // as though confirming were a sixth number to type. It is implied by entering the rest.
    const missing = (item?.missing || [])
        .filter(f => f !== 'confirmation')
        .map(f => CARTON_FIELD_LABEL[f] || f);
    if (missing.length === 0) return 'carton details needed';
    return `missing ${missing.join(', ')}`;
}

export function CartonDataGapPanel({ gap, onFix }) {
    if (!gap) return null;
    const items = gap.items || [];
    const allConfirmationOnly = items.every(isConfirmationOnly);
    return (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-4" data-testid="carton-gap">
            <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-amber-900">
                        {allConfirmationOnly
                            ? 'Confirm these cartons before quoting freight'
                            : 'These cartons need details before freight can be quoted'}
                    </h3>
                    <p className="text-xs text-amber-800 mt-0.5">
                        {/* 2026-08-21, owner: "for individual units, each unit is a carton". One
                            unit per carton is a CORRECT answer for these products, not a fallback
                            that slipped through — so the ask must never imply he has to go find a
                            multi-unit box. What is being asked for is that someone has stood
                            behind the numbers, whatever they turn out to be. */}
                        {gap.message
                            || 'Pallets are built from the shipping carton’s real size. Amazon’s '
                               + 'catalog lists the PRODUCT’s dimensions, which may or may not be '
                               + 'the box it ships in — so the plan asks instead of assuming.'}
                    </p>
                    <p className="text-[11px] text-amber-800/80 mt-0.5">
                        If the unit ships in its own box, one unit per carton with the unit’s
                        dimensions is the right answer — just confirm it.
                    </p>
                    <ul className="mt-2 space-y-1.5">
                        {items.map(it => (
                            <li key={it.asin}
                                data-testid={`carton-gap-${it.asin}`}
                                className="flex flex-wrap items-baseline gap-x-2 text-xs bg-white/70 border border-amber-200 rounded px-2 py-1.5">
                                <span className="font-mono font-semibold text-amber-900">
                                    {it.itemCode || it.asin}
                                </span>
                                <span className="max-w-sm truncate text-gray-700" title={it.title || ''}>
                                    {it.title || it.asin}
                                </span>
                                {it.unitsInShipment > 0 && (
                                    <span className="text-gray-500">{it.unitsInShipment} units</span>
                                )}
                                <span className={isConfirmationOnly(it) ? 'text-amber-700' : 'text-red-700'}>
                                    {missingFieldsLabel(it)}
                                </span>
                                {/* Amazon's own suggestion, when it has one -- shown as a starting
                                    point to check, never applied for the operator. */}
                                {it.amazonSuggestion && (
                                    <span className="text-gray-500">
                                        · Amazon suggests {describeUnitBox(it.amazonSuggestion)}
                                    </span>
                                )}
                                {onFix && (
                                    <button
                                        onClick={() => onFix(it)}
                                        className="ml-auto flex-shrink-0 px-2 py-0.5 rounded border border-amber-400 text-amber-900 hover:bg-amber-100 font-medium"
                                    >
                                        {isConfirmationOnly(it) ? 'Review & confirm' : 'Enter carton'}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}

/** "1 unit/box, 24.8 x 16.5 x 4.3 in, 2.5 lb" -- Amazon's DerivedUnitBox, spelled out. */
export function describeUnitBox(d) {
    if (!d) return '';
    const dims = [d.boxLengthIn, d.boxWidthIn, d.boxHeightIn].filter(v => v != null);
    const parts = [];
    parts.push(`${d.unitsPerBox ?? 1} unit${(d.unitsPerBox ?? 1) !== 1 ? 's' : ''}/box`);
    if (dims.length === 3) parts.push(`${dims.join(' × ')} in`);
    if (d.boxWeightLbs != null) parts.push(`${d.boxWeightLbs} lb`);
    return parts.join(', ');
}

/**
 * 2026-08-21, owner verbatim: "the error should indicate on the UI once and needs to show the
 * product ASIN, name, and error currently the error is showing a couple dozen times."
 *
 * Amazon returns packing warnings PER BOX. Run 266 had 185 boxes on one shipment, so a single
 * problem — one product's carton weight — rendered as hundreds of identical lines. The information
 * content was one sentence.
 *
 * The server now groups them (ShipmentPlannerService.attributeWarnings); this renders one row per
 * distinct problem with the product named, the scale kept ("24 boxes"), and every raw line one
 * click away. Falls back to the flat list for stored runs analysed before grouping existed.
 *
 * These are ADVISORIES, not failures — the run-266 case is Amazon disagreeing with its own catalog
 * weights — so this stays amber and never reads as a blocked shipment.
 */
export function AmazonWarnings({ groups, warnings }) {
    const hasGroups = Array.isArray(groups) && groups.length > 0;
    const flat = Array.isArray(warnings) ? warnings : [];
    if (!hasGroups && flat.length === 0) return null;
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800"
            data-testid="amazon-warnings">
            <div className="flex items-center gap-1.5 font-medium mb-1">
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                Amazon warnings
                <span className="font-normal text-amber-700/80 text-xs">
                    · advisory, they do not block the shipment
                </span>
            </div>
            {hasGroups ? (
                <ul className="space-y-1.5">
                    {groups.map((g, i) => (
                        <li key={i} data-testid={`warning-group-${i}`}>
                            <div className="flex flex-wrap items-baseline gap-x-2">
                                {g.attributed ? (
                                    <>
                                        <span className="font-mono font-semibold text-amber-900">
                                            {g.itemCode || g.asin}
                                        </span>
                                        <span className="max-w-md truncate" title={g.productName || ''}>
                                            {g.productName || g.asin}
                                        </span>
                                        <span className="text-xs text-amber-700/70 font-mono">{g.asin}</span>
                                    </>
                                ) : (
                                    // Never guess a product. Saying which item is unknown is the
                                    // honest answer; dropping the warning would be worse.
                                    <span className="italic text-amber-700/80">
                                        Not tied to a specific product
                                    </span>
                                )}
                                {g.affectedBoxes > 0 && (
                                    <span className="text-xs text-amber-700/80">
                                        · {g.affectedBoxes} box{g.affectedBoxes !== 1 ? 'es' : ''}
                                    </span>
                                )}
                            </div>
                            <div className="text-[13px]">{g.message}</div>
                            {(g.rawMessages || []).length > 1 && (
                                <details className="text-[11px] mt-0.5">
                                    <summary className="cursor-pointer text-amber-700 hover:text-amber-900 select-none">
                                        show all {g.rawMessages.length} lines
                                    </summary>
                                    <ul className="mt-1 ml-3 max-h-40 overflow-auto font-mono text-amber-800/80">
                                        {g.rawMessages.map((r, ri) => <li key={ri}>{r}</li>)}
                                    </ul>
                                </details>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <ul className="list-disc ml-5 space-y-0.5">
                    {flat.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
            )}
        </div>
    );
}

/**
 * 2026-08-21, owner's question, verbatim: "how is the carrier selected currently so that a
 * confirmation can be generated?"
 *
 * The answer is that HE DOESN'T select it, and the UI previously did nothing to say so -- a carrier
 * name sitting next to a price reads like somebody's choice. Amazon requires ONE carrier across
 * every shipment in a placement option, so the server intersects the carriers each shipment was
 * quoted by, sums the cheapest quote per shipment, and takes the lowest total
 * (ShipmentPlannerService.pickSingleCarrierLtlOptionIds). At confirm time it re-runs against
 * freshly listed options, because Amazon's option ids rotate.
 *
 * Runners-up are listed with what they would have cost, so "cheapest" is something he can check
 * rather than something he has to trust.
 */
export function CarrierChoiceNote({ choice }) {
    if (!choice || !choice.selectedCarrier) return null;
    const alts = choice.alternatives || [];
    return (
        <div className="text-[11px] text-gray-600 bg-white border border-gray-200 rounded px-2 py-1.5"
            data-testid="carrier-choice">
            <div>
                <span className="font-medium text-gray-800">{choice.selectedCarrier}</span>
                {choice.selectedTotal != null && (
                    <span className="text-gray-500"> · ${fmtUsd(choice.selectedTotal)} total</span>
                )}
            </div>
            {/* Owner ruling 2026-08-29: Amazon assigns partnered carriers PER DESTINATION —
                one plan legitimately rides several. The old copy claimed one carrier was
                required for the whole option; that belief also drove the retired
                single-carrier intersection. */}
            <div className="text-gray-500 mt-0.5">
                Chosen automatically: the cheapest partnered carrier for each of the
                {choice.shipmentCount ? ` ${choice.shipmentCount} shipments` : ' shipments'}
                {' '}in this option — Amazon assigns carriers per destination, so one plan can ride
                several. This is not an operator choice.
            </div>
            {alts.length > 0 && (
                <details className="mt-1">
                    <summary className="cursor-pointer text-indigo-600 hover:text-indigo-800 select-none">
                        {alts.length} other carrier{alts.length !== 1 ? 's' : ''} could have covered it
                    </summary>
                    <ul className="mt-1 ml-3 space-y-0.5">
                        {alts.map(a => (
                            <li key={a.carrierName} className="flex justify-between max-w-xs">
                                <span>{a.carrierName}</span>
                                <span className="tabular-nums text-gray-500">
                                    ${fmtUsd(a.total)}
                                    {choice.selectedTotal != null && a.total != null && (
                                        <span className="text-gray-400">
                                            {' '}(+${fmtUsd(a.total - choice.selectedTotal)})
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}

/**
 * 2026-08-21, owner: "the list of asins and units for each FC should be clear and includ the
 * product title". Titles run long (Schluter names routinely exceed 60 characters), so the cell is
 * width-capped and truncated with the full title on hover -- an untruncated title column pushes
 * the units and boxes off the right edge, which is what made this table unreadable at 11 pallets.
 *
 * The ITEM CODE leads, because that is what the warehouse and the Schluter paperwork actually use;
 * the ASIN is there for the system, not for the person picking the carton.
 */
function ShipmentContentsTable({ contents }) {
    if (contents.length === 0) return <div className="text-[11px] text-gray-400 italic">no item detail</div>;
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-[11px]" data-testid="shipment-contents">
                <thead>
                    <tr className="text-gray-500 text-left">
                        <th className="pr-2 font-medium">Item code</th>
                        <th className="pr-2 font-medium">Product</th>
                        <th className="pr-2 font-medium">ASIN / SKU</th>
                        {/* Task 80: per-ASIN condition + FNSKU in the analyze results. */}
                        <th className="pr-2 font-medium">Condition / FNSKU</th>
                        <th className="pr-2 font-medium text-right">Units</th>
                        <th className="pr-2 font-medium text-right">Boxes</th>
                        <th className="pr-2 font-medium">Box</th>
                    </tr>
                </thead>
                <tbody>
                    {contents.map(c => (
                        <tr key={c.asin} className="text-gray-600 align-top">
                            <td className="pr-2 py-0.5 font-mono text-gray-800 whitespace-nowrap">
                                {c.itemCode || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="pr-2 py-0.5">
                                <span className="block max-w-[18rem] truncate" title={c.name || ''}>
                                    {c.name || '—'}
                                </span>
                            </td>
                            <td className="pr-2 py-0.5">
                                {c.asin}{c.sku ? <span className="text-gray-400"> / {c.sku}</span> : null}
                            </td>
                            <td className="pr-2 py-0.5">
                                <ConditionBadge
                                    conditionLabel={c.conditionLabel}
                                    fnsku={c.fnsku}
                                    newCondition={c.newCondition}
                                />
                            </td>
                            <td className="pr-2 py-0.5 text-right">{c.units}</td>
                            <td className="pr-2 py-0.5 text-right">
                                {c.boxes}
                                {c.unitsPerBox > 1 && <span className="text-gray-400"> ({c.unitsPerBox}/box)</span>}
                            </td>
                            <td className="pr-2 py-0.5">
                                {c.boxLengthIn ? `${c.boxLengthIn}×${c.boxWidthIn}×${c.boxHeightIn} in` : '—'}
                                {c.boxWeightLbs ? ` · ${c.boxWeightLbs} lb` : ''}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Task 75 (owner, verbatim: "it may be easiest for LTL to arrange the data horizontally instead of
// vertically ... that can expand to show the destination, cost, asins, units, freight class, etc")
// -- replaces the old vertical stack of per-shipment blocks with one row per shipment: destination,
// units, boxes, pallets, weight, freight class, carrier and freight cost all visible without
// expanding, chevron reveals the ASIN contents table + pallet builder + alternate carrier quotes.
// Renamed from LtlPalletBlock (task 65/72) -- same state/requote logic, table markup only.
const LTL_TABLE_HEAD_CLS = 'px-2 py-1.5 text-left font-medium text-gray-500 whitespace-nowrap';
function LtlShipmentsTable({ option, planId, planItems, canEdit = false, onApplied,
    approvedOptionIds, onApproveBuild, declaredPalletCount,
    // Milestone C task C5: the draft's saved-by-FC measurements ({ [destinationFc]: { rows, savedAt } })
    // and the upward report hook -- both optional, so callers with no persisted draft (PO mode)
    // simply render without restore/report.
    savedMeasurements, onMeasurementsChange }) {
    // Owner's question about carrier selection -- answered once per option, above the rows.
    const carrierChoice = option?.carrierChoice;
    const palletShipments = ltlPalletShipments(option);
    const [rowsByShipment, setRowsByShipment] = useState({});
    const [remember, setRemember] = useState({});
    const [open, setOpen] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [note, setNote] = useState(null);
    // Milestone C task C4: the requote came back because the PLACEMENT expired mid-build, not
    // because Amazon refused the quote (C3's classifyRequoteFailureAsExpiry). Kept separate from
    // `error` so it never renders in the same red "quote refusal" slot -- the remedy here is
    // re-analyze for a fresh assignment, not fix-and-retry.
    const [expiredNotice, setExpiredNotice] = useState(null);

    if (palletShipments.length === 0) return null;

    // Milestone C phase 1: a shipment with no rows in state yet falls back to the computed layout
    // ONLY when Amazon already priced it (sh.pallets present -- post-requote/legacy); otherwise it
    // falls back to blank rows seeded from the declared pallet count. See fallbackPalletRows.
    //
    // Milestone C task C5: BEFORE settling for blank, offer the operator's own measurements saved
    // under this shipment's destination FC (shipmentIds rotate every re-analyze; FCs recur).
    // Computed once per render, keyed by shipmentId, so rowsFor and the provenance banner below
    // agree on exactly the same restore decision. Restored ONLY while rowsByShipment holds nothing
    // for this shipment yet -- the instant it does (even a single keystroke), that entry is read
    // directly and this branch is never consulted again for it (see restoredPalletRows).
    const restoredMeasurementsByShipment = {};
    palletShipments.forEach((sh) => {
        if (Object.prototype.hasOwnProperty.call(rowsByShipment, sh.shipmentId)) return;
        const fallback = fallbackPalletRows(sh, planItems, declaredPalletCount);
        if (restoredPalletRows(fallback, sh.destinationFc, savedMeasurements)) {
            restoredMeasurementsByShipment[sh.shipmentId] = savedMeasurements[sh.destinationFc];
        }
    });
    const rowsFor = (sh) => rowsByShipment[sh.shipmentId]
        || (restoredMeasurementsByShipment[sh.shipmentId]
            ? whitelistPalletMeasurementRows(restoredMeasurementsByShipment[sh.shipmentId].rows)
            : fallbackPalletRows(sh, planItems, declaredPalletCount));
    const setRows = (sh, rows) => {
        setRowsByShipment(prev => ({ ...prev, [sh.shipmentId]: rows }));
        // Report upward so the panel can persist this build in the draft, keyed by destination FC
        // (buildPlannerDraftPayload's palletMeasurements) -- optional, PO mode has no draft to save into.
        if (onMeasurementsChange && sh.destinationFc) onMeasurementsChange(sh.destinationFc, rows);
    };

    const updateRow = (sh, i, field, value) => {
        const model = shipmentWeightModel(sh, planItems);
        setRows(sh, rowsFor(sh).map((r, ri) => {
            if (ri !== i) return r;
            if (field === 'boxes') {
                return recomputeRowWeight({ ...r, boxes: value }, model, sh.pallets?.weightLbEach ?? sh.projectedPallets?.weightLbEach);
            }
            if (field === 'weightLbEach') return { ...r, weightLbEach: value, weightTouched: true };
            return { ...r, [field]: value };
        }));
    };

    const addPallet = (sh) => {
        const rows = rowsFor(sh);
        const last = rows[rows.length - 1] || {};
        setRows(sh, [...rows, { boxes: 0,
            lengthIn: last.lengthIn ?? sh.pallets?.lengthIn ?? 48,
            widthIn: last.widthIn ?? sh.pallets?.widthIn ?? 40,
            heightIn: last.heightIn ?? sh.pallets?.heightIn ?? null,
            weightLbEach: shipmentWeightModel(sh, planItems).tareLbs || last.weightLbEach || null,
            // Owner, 2026-08-23: "we do not send any stackable pallets" -- carry forward only an
            // explicit true, never assume.
            stackable: last.stackable === true, weightTouched: false }]);
    };

    const removePallet = (sh) => {
        const rows = rowsFor(sh);
        if (rows.length <= 1) return;
        setRows(sh, rows.slice(0, -1));
    };

    const redistribute = (sh) => {
        const rows = rowsFor(sh);
        const model = shipmentWeightModel(sh, planItems);
        const split = distributeBoxes(shipmentBoxCount(sh, planItems), rows.length);
        setRows(sh, rows.map((r, i) => recomputeRowWeight({ ...r, boxes: split[i] }, model, sh.pallets?.weightLbEach)));
    };

    const summaries = palletShipments.map(sh => builtPalletSummary(sh, rowsFor(sh), planItems));
    const allValid = summaries.every(s => s.valid);
    // The whole point of this change: a build nobody signed off must never render as settled.
    const liveRows = {};
    palletShipments.forEach(sh => { liveRows[sh.shipmentId] = rowsFor(sh); });
    const build = palletBuildState(option, liveRows, planItems, approvedOptionIds, declaredPalletCount);
    // Milestone C task C4 (spec §2): plan-wide built-vs-declared count, checked above the requote
    // button -- informational only, see declaredCountReconciliation.
    const totalBuilt = summaries.reduce((n, s) => n + s.count, 0);
    const reconciliation = declaredCountReconciliation(totalBuilt, declaredPalletCount);

    const requote = async () => {
        setSaving(true);
        setError(null);
        setNote(null);
        setExpiredNotice(null);
        try {
            const built = {};
            palletShipments.forEach(sh => { built[sh.shipmentId] = rowsFor(sh); });
            const res = await ApiClient.post('/api/shipment-planner/requote-ltl',
                buildLtlRequotePayload(option, planId, built, planItems, declaredPalletCount), { timeout: 300000 });
            // Milestone C task C3/C4: the placement expired mid-build. This is not a quote refusal
            // -- the build is unchanged and there is nothing to fix and resubmit -- so it never
            // reaches the ordinary "remember the defaults, apply the response" path below. The
            // remedy is re-analyze for a fresh assignment, which the surfaced warning says.
            if (res.data?.placementExpired) {
                setExpiredNotice((res.data.warnings || []).find(w => /expir/i.test(w))
                    || 'This placement option expired — re-analyze to get a fresh assignment.');
                return;
            }
            // Persist the built layout for next time -- best effort: a failure here must not lose
            // the fresh quotes that just came back.
            const saved = [];
            const failures = [];
            for (const body of buildPalletDefaultSaves(option, built, remember, planItems, declaredPalletCount)) {
                try {
                    await ApiClient.post('/api/shipment-planner/packaging-pallet', body);
                    saved.push(body.asin);
                } catch (err) {
                    failures.push(`${body.asin}: ${err.response?.data?.error || err.message}`);
                }
            }
            const notes = [];
            if (saved.length > 0) notes.push(`Remembered this pallet build for ${saved.join(', ')}.`);
            if (failures.length > 0) notes.push(`Quotes updated, but could not remember ${failures.join('; ')}`);
            if (notes.length > 0) setNote(notes.join(' '));
            onApplied(option.placementOptionId, res.data);
            // Amazon has now priced THIS build, so the operator's sign-off applies to numbers that
            // came back from it -- not to the guess that preceded it.
            if (onApproveBuild) onApproveBuild(option.placementOptionId);
        } catch (err) {
            console.error('LTL requote failed:', err);
            setError(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 pt-3 border-t space-y-2" onClick={(e) => canEdit && e.stopPropagation()}>
            <span className="text-xs font-medium text-gray-500">
                Amazon&apos;s split — one row per shipment{canEdit ? ' (editable)' : ''}
            </span>

            {/* 2026-08-21 — the pallet build's own state, stated before any freight figure is read.
                Cartons are confirmed earlier (CartonDataGapPanel); this is the second and last
                thing the plan needs a human for, and the two are deliberately sequential: a
                shipment whose cartons are unconfirmed never reaches this panel at all. */}
            {build.state !== 'APPROVED' && (
                <div className={`rounded border px-2 py-1.5 text-[11px] ${build.state === 'INVALID'
                    ? 'border-red-300 bg-red-50 text-red-800'
                    : 'border-amber-300 bg-amber-50 text-amber-900'}`}
                    data-testid={`pallet-build-${build.state.toLowerCase()}`}>
                    <span className="font-semibold">
                        {build.state === 'INVALID' ? 'Build incomplete' : 'Suggested build — not approved'}
                    </span>{' '}
                    {build.reason}
                    {build.state === 'SUGGESTED' && (
                        <span className="block mt-0.5 text-amber-800/80">
                            The freight below is indicative: it prices the suggested layout, not one
                            you have stood behind.
                        </span>
                    )}
                    {build.state === 'SUGGESTED' && canEdit && (
                        <button
                            onClick={requote}
                            disabled={saving}
                            data-testid="approve-pallet-build"
                            className="mt-1.5 px-2.5 py-1 rounded-md bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-50"
                        >
                            {saving ? 'Getting quotes…' : 'Approve this build & re-quote'}
                        </button>
                    )}
                </div>
            )}
            {build.state === 'APPROVED' && isLtlOption(option) && (
                <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900"
                    data-testid="pallet-build-approved">
                    <span className="font-semibold">Build approved</span> — the freight below is
                    Amazon&apos;s price for this layout.
                </div>
            )}

            {carrierChoice && <CarrierChoiceNote choice={carrierChoice} />}
            <div className="overflow-x-auto">
                <table className="min-w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className={LTL_TABLE_HEAD_CLS}></th>
                            <th className={LTL_TABLE_HEAD_CLS}>Destination</th>
                            <th className={`${LTL_TABLE_HEAD_CLS} text-right`}>Units</th>
                            <th className={`${LTL_TABLE_HEAD_CLS} text-right`}>Boxes</th>
                            <th className={LTL_TABLE_HEAD_CLS}>Pallets</th>
                            <th className={`${LTL_TABLE_HEAD_CLS} text-right`}>Weight</th>
                            <th className={LTL_TABLE_HEAD_CLS}>Freight class</th>
                            <th className={LTL_TABLE_HEAD_CLS}>Carrier</th>
                            <th className={`${LTL_TABLE_HEAD_CLS} text-right`}>Freight cost</th>
                        </tr>
                    </thead>
                    {palletShipments.map((sh, i) => {
                        const rows = rowsFor(sh);
                        const s = summaries[i];
                        const contents = shipmentContents(sh, planItems);
                        const label = sh.destinationFc || sh.shipmentId?.slice(-4);
                        const asin = shipmentSingleAsin(sh);
                        const expanded = open[sh.shipmentId] ?? true;
                        const units = shipmentContents(sh, planItems).reduce((n, c) => n + c.units, 0);
                        const boxes = shipmentBoxCount(sh, planItems);
                        const hasQuote = shipmentHasFreightQuote(sh);
                        const alternates = shipmentAlternateOptions(sh);
                        return (
                            <tbody key={sh.shipmentId || i} data-testid={`ltl-shipment-${sh.shipmentId}`}
                                className="text-gray-700">
                                <tr className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="px-2 py-1.5">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setOpen(prev => ({ ...prev, [sh.shipmentId]: !expanded })); }}
                                            aria-label={`${expanded ? 'Hide' : 'Show'} details for ${label}`}
                                            className="text-gray-400 hover:text-indigo-600"
                                        >
                                            {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                                        </button>
                                    </td>
                                    <td className="px-2 py-1.5 whitespace-nowrap">
                                        <span className="font-medium text-gray-800">{label}</span>
                                        {(sh.destinationCity || sh.destinationState) && (
                                            <span className="text-gray-400"> · {sh.destinationCity}{sh.destinationCity && sh.destinationState ? ', ' : ''}{sh.destinationState}</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{units}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{boxes}</td>
                                    <td className="px-2 py-1.5 whitespace-nowrap">{shipmentPalletSummaryLabel(sh)}</td>
                                    <td className="px-2 py-1.5 text-right whitespace-nowrap">{shipmentWeightLabel(sh)}</td>
                                    <td className="px-2 py-1.5 whitespace-nowrap">
                                        {shipmentFreightClassLabel(sh)}
                                        {/* Live from the entered heights: cube -> density -> class.
                                            Entering a height is only meaningful if you can see what
                                            it does, and it should move as you adjust. */}
                                        {palletConsequenceLabel(s) && (
                                            <span className="block text-[10px] text-gray-400"
                                                data-testid={`pallet-consequence-${sh.shipmentId}`}>
                                                {palletConsequenceLabel(s)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2 py-1.5 whitespace-nowrap">{sh.carrierName || '—'}</td>
                                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                        {hasQuote ? `$${fmtUsd(sh.transportCost || 0)}` : (
                                            <span className="text-amber-600" title="no partnered LTL offering for this shipment">none offered</span>
                                        )}
                                        {/* Milestone C task C3/C4 (spec 3.7): the one-hour window a
                                            post-requote quote can still be voided in, without
                                            penalty -- only present once a requote has actually
                                            priced this shipment. */}
                                        {sh.quoteVoidableUntil && (
                                            <span className="block text-[10px] text-gray-400"
                                                data-testid={`quote-voidable-${sh.shipmentId}`}>
                                                Free to void until {relativeTimeUntilLabel(sh.quoteVoidableUntil)}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                {expanded && (
                                    <tr>
                                        <td></td>
                                        <td colSpan={8} className="px-2 pb-3 pt-1">
                                            <div className="space-y-2 border rounded p-2 bg-gray-50/50">
                                                <ShipmentContentsTable contents={contents} />

                                                {!hasQuote && (
                                                    <div className="text-amber-700 text-[11px]">
                                                        Amazon offered no partnered LTL for this shipment — see the carrier-gap panel above for remedies.
                                                    </div>
                                                )}
                                                {alternates.length > 0 && (
                                                    /* 2026-08-21, owner: "the frieght qoutes shoul be moved to a
                                                       tooltip ... maybe move the friehgts qoutes to a dropdown".
                                                       A DISCLOSURE, not a tooltip: this is multi-row numeric data
                                                       and a tooltip cannot be held open while comparing rows, cannot
                                                       be scrolled, and vanishes on mouse-out. The headline cost
                                                       stays inline on the row above; only the breakdown hides. */
                                                    <details className="text-[11px] text-gray-600"
                                                        data-testid={`alt-quotes-${sh.shipmentId}`}>
                                                        <summary className="cursor-pointer text-indigo-600 hover:text-indigo-800 select-none">
                                                            {alternates.length} other quote{alternates.length !== 1 ? 's' : ''} for this shipment
                                                        </summary>
                                                        <ul className="mt-1 ml-3 space-y-0.5">
                                                            {alternates.map((o, oi) => (
                                                                <li key={o.transportationOptionId || oi}
                                                                    className="flex justify-between max-w-sm">
                                                                    <span>
                                                                        {o.carrierName || shippingModeLabel(o.shippingMode)}
                                                                        {o.carrierName && o.shippingMode && (
                                                                            <span className="text-gray-400"> · {shippingModeLabel(o.shippingMode)}</span>
                                                                        )}
                                                                    </span>
                                                                    <span className="tabular-nums text-gray-500">
                                                                        {o.cost != null ? `$${fmtUsd(o.cost)}` : 'not quoted'}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </details>
                                                )}

                                                {/* Milestone C phase 1 (spec §4): the packer's
                                                    arithmetic PROJECTION -- a labelled reference the
                                                    operator checks their build against. It is never
                                                    a default (blankPalletRows never reads it) and
                                                    never reaches Amazon; only the operator's own
                                                    build does. */}
                                                {sh.projectedPallets && (
                                                    <div className="text-[11px] text-gray-500 italic"
                                                        data-testid={`pallet-projection-${sh.shipmentId}`}>
                                                        Projected: ~{sh.projectedPallets.quantity} pallets · tallest ~{sh.projectedPallets.heightIn}in — reference for the warehouse, not sent to Amazon
                                                    </div>
                                                )}
                                                {/* 2026-08-23 (owner): the Claude layout-planner
                                                    pre-calculation -- it interleaves SKUs, rotates
                                                    and splits columns, which the arithmetic packer
                                                    cannot. Same doctrine: reference only, never a
                                                    default, never sent to Amazon. */}
                                                {sh.aiProjection && (
                                                    <div className="text-[11px] text-indigo-700"
                                                        data-testid={`ai-projection-${sh.shipmentId}`}>
                                                        AI layout: {sh.aiProjection.palletCount != null
                                                            ? `~${sh.aiProjection.palletCount} pallet${sh.aiProjection.palletCount !== 1 ? 's' : ''}` : 'computed'}
                                                        {sh.aiProjection.overallHeightIn != null && ` · ~${sh.aiProjection.overallHeightIn}in overall`}
                                                        {sh.aiProjection.totalWeightLb != null && ` · ~${Math.round(sh.aiProjection.totalWeightLb)} lb`}
                                                        {(sh.aiProjection.warnings || []).length > 0 && (
                                                            <span className="text-amber-700"> · {sh.aiProjection.warnings.join(' · ')}</span>
                                                        )}
                                                        {sh.aiProjection.layoutText && (
                                                            <details className="mt-0.5 text-gray-600">
                                                                <summary className="cursor-pointer select-none text-indigo-600">show the layout plan</summary>
                                                                <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px] bg-gray-50 border rounded p-2 max-h-64 overflow-y-auto">{sh.aiProjection.layoutText}</pre>
                                                            </details>
                                                        )}
                                                    </div>
                                                )}

                                                {canEdit && (
                                                    <div data-testid={`pallet-builder-${sh.shipmentId}`} className="pt-1 border-t">
                                        {/* Milestone C task C5: the provenance line for a restore --
                                            only rendered when restoredMeasurementsByShipment actually
                                            fired for this shipment (see rowsFor above). Same relative-
                                            time cascade C4 reused (draftAgeLabel/relativeMagnitudeLabel),
                                            PAST-facing here ("from 2h ago"). */}
                                        {restoredMeasurementsByShipment[sh.shipmentId] && (
                                            <div className="mb-1 text-[11px] text-indigo-600 italic"
                                                data-testid={`measurements-restored-${sh.shipmentId}`}>
                                                <span className="text-amber-700 not-italic font-medium">
                                                    Restored your earlier measurements for {sh.destinationFc}
                                                </span>{' '}
                                                (from {draftAgeLabel({ savedAt: restoredMeasurementsByShipment[sh.shipmentId].savedAt })})
                                                {' — verify they match what you actually built, or '}
                                                <button
                                                    className="text-indigo-600 underline"
                                                    data-testid={`clear-restored-${sh.shipmentId}`}
                                                    onClick={(e) => { e.stopPropagation(); setRows(sh, seededPalletRows(sh, planItems, declaredPalletCount)); }}
                                                >start blank</button>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px] font-medium text-gray-500">
                                                Build the pallets ({s.expectedBoxes} box{s.expectedBoxes !== 1 ? 'es' : ''} to place)
                                            </span>
                                            <span className="flex items-center gap-2">
                                                <button onClick={(e) => { e.stopPropagation(); redistribute(sh); }}
                                                    className="text-indigo-600 hover:text-indigo-800">distribute evenly</button>
                                                <button onClick={(e) => { e.stopPropagation(); addPallet(sh); }}
                                                    className="text-indigo-600 hover:text-indigo-800">+ Add pallet</button>
                                                <button onClick={(e) => { e.stopPropagation(); removePallet(sh); }}
                                                    disabled={rows.length <= 1}
                                                    className="text-indigo-600 hover:text-indigo-800 disabled:text-gray-300">− Remove pallet</button>
                                            </span>
                                        </div>
                                        <div className="mt-1 space-y-1">
                                            {rows.map((r, ri) => (
                                                <div key={ri} className="flex flex-wrap items-center gap-2">
                                                    <span className="w-14 text-gray-500">Pallet {ri + 1}</span>
                                                    <label className="flex items-center gap-1">
                                                        <span className="text-gray-500">boxes</span>
                                                        <input type="number" min="0" aria-label={`Boxes on pallet ${ri + 1} for ${label}`}
                                                            value={r.boxes}
                                                            onChange={e => updateRow(sh, ri, 'boxes', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                                                            className="w-16 rounded border-gray-300 text-xs" />
                                                    </label>
                                                    <label className="flex items-center gap-1">
                                                        <span className="text-gray-500">L in</span>
                                                        <input type="number" step="0.1" min="1" aria-label={`Length of pallet ${ri + 1} for ${label}`}
                                                            value={r.lengthIn ?? ''}
                                                            onChange={e => updateRow(sh, ri, 'lengthIn', e.target.value)}
                                                            className="w-16 rounded border-gray-300 text-xs" />
                                                    </label>
                                                    <label className="flex items-center gap-1">
                                                        <span className="text-gray-500">W in</span>
                                                        <input type="number" step="0.1" min="1" aria-label={`Width of pallet ${ri + 1} for ${label}`}
                                                            value={r.widthIn ?? ''}
                                                            onChange={e => updateRow(sh, ri, 'widthIn', e.target.value)}
                                                            className="w-16 rounded border-gray-300 text-xs" />
                                                    </label>
                                                    <label className="flex items-center gap-1">
                                                        <span className="text-gray-500">height in</span>
                                                        <input type="number" step="0.1" min="1" aria-label={`Height of pallet ${ri + 1} for ${label}`}
                                                            value={r.heightIn ?? ''}
                                                            onChange={e => updateRow(sh, ri, 'heightIn', e.target.value)}
                                                            className="w-20 rounded border-gray-300 text-xs" />
                                                    </label>
                                                    <label className="flex items-center gap-1">
                                                        <span className="text-gray-500">lb</span>
                                                        <input type="number" step="0.1" min="1" aria-label={`Weight of pallet ${ri + 1} for ${label}`}
                                                            value={r.weightLbEach ?? ''}
                                                            onChange={e => updateRow(sh, ri, 'weightLbEach', e.target.value)}
                                                            className="w-20 rounded border-gray-300 text-xs" />
                                                    </label>
                                                    <label className="flex items-center gap-1 text-gray-500">
                                                        {/* Owner 2026-08-23 ("we do not send any stackable
                                                            pallets"): the four WRITE sites were flipped to
                                                            === true; this read site was missed, so a row with no
                                                            flag (a restored draft, say) rendered checked while
                                                            serializing stackable: false. */}
                                                        <input type="checkbox" checked={r.stackable === true}
                                                            aria-label={`Pallet ${ri + 1} stackable for ${label}`}
                                                            onChange={e => updateRow(sh, ri, 'stackable', e.target.checked)}
                                                            className="h-3 w-3 rounded border-gray-300" />
                                                        stackable
                                                    </label>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-1">
                                            {/* Spec section 6.1: an unstated box count is not a mismatch. It is
                                                reported, it does not block, and it never claims boxes are
                                                missing from a pallet nobody counted. */}
                                            {s.boxesIncomplete && (
                                                <div data-testid={`box-incomplete-${sh.shipmentId}`} className="text-gray-500">
                                                    reconciliation incomplete — {s.placedBoxes} box{s.placedBoxes !== 1 ? 'es' : ''} placed
                                                    of {s.expectedBoxes}, some rows unstated
                                                </div>
                                            )}
                                            {!s.boxesIncomplete && !s.boxesReconcile && (
                                                <div data-testid={`box-mismatch-${sh.shipmentId}`} className="text-amber-700">
                                                    {s.placedBoxes} box{s.placedBoxes !== 1 ? 'es' : ''} placed of {s.expectedBoxes} —
                                                    every box in the shipment must sit on a pallet before re-quoting.
                                                </div>
                                            )}
                                            {(s.boxesReconcile || s.boxesIncomplete) && !s.dimsValid && (
                                                <div className="text-amber-700">every pallet needs a positive height and weight</div>
                                            )}
                                            {s.valid && (
                                                <div className="text-gray-600">
                                                    → {s.count} pallet{s.count !== 1 ? 's' : ''} · {s.lengthIn}×{s.widthIn}×{s.tallestIn}
                                                    {' · '}{s.avgWeightLbEach} lb ea · {Math.round(s.totalWeightLb).toLocaleString()} lb total
                                                    {s.density != null && ` · density ${s.density.toFixed(1)} lb/ft³`}
                                                    {' → FC '}{freightClassLabel(s.freightClass)}
                                                </div>
                                            )}
                                        </div>

                                        <label className="mt-1 flex items-center gap-1 text-[11px] text-gray-500">
                                            <input type="checkbox" disabled={!asin}
                                                checked={!!remember[sh.shipmentId] && !!asin}
                                                onChange={e => setRemember(prev => ({ ...prev, [sh.shipmentId]: e.target.checked }))}
                                                className="h-3 w-3 rounded border-gray-300" />
                                            {asin
                                                ? `Remember this build for ${asin} (saves boxes per pallet + height on the product)`
                                                : 'Mixed-ASIN shipment — no single product to remember this build against'}
                                        </label>
                                    </div>
                                )}
                            </div>
                        </td>
                    </tr>
                )}
            </tbody>
                        );
                    })}
                </table>
            </div>
            {/* Milestone C task C4 (spec §2): declared-vs-built reconciliation, ABOVE the requote
                button and NEVER a gate on it -- see declaredCountReconciliation for the asymmetric
                info/warn split. */}
            {reconciliation && (
                <div data-testid="declared-reconciliation"
                    className={`rounded border px-2 py-1 text-[11px] ${reconciliation.tone === 'warn'
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
                    {reconciliation.text}
                </div>
            )}
            {canEdit && (
                <div className="pt-1">
                    <button
                        onClick={(e) => { e.stopPropagation(); requote(); }}
                        disabled={saving || !allValid}
                        className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
                        title="Send these pallets to Amazon and re-quote this plan's freight"
                    >
                        {saving ? 'Re-quoting with Amazon (can take a minute)...' : 'Re-quote with these pallets'}
                    </button>
                    {saving && (
                        <div className="mt-2 flex items-center gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800"
                            data-testid="requote-working">
                            <svg className="h-4 w-4 animate-spin text-indigo-600" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                            <span className="font-medium">Getting Amazon freight quotes…</span>
                            <span className="text-indigo-700/80">usually 30–90 seconds — leave this page open</span>
                        </div>
                    )}
                    {Object.keys(restoredMeasurementsByShipment).length > 0 && (
                        <div className="mt-1 text-[11px] text-amber-700 font-medium" data-testid="restored-verify-notice">
                            Heights for {palletShipments.filter(sh => restoredMeasurementsByShipment[sh.shipmentId])
                                .map(sh => sh.destinationFc || sh.shipmentId?.slice(-4)).join(', ')} were restored
                            from an earlier session — verify them against the physical pallets before quoting.
                        </div>
                    )}
                    {!allValid && (
                        <div className="mt-1 text-[11px] text-amber-700" data-testid="requote-blockers">
                            {'Cannot quote yet — '}
                            {requoteBlockers(palletShipments, summaries)
                                .map(b => `${b.label}: ${b.reason}`).join(' · ')}
                        </div>
                    )}
                    <span className="ml-2 text-[11px] text-gray-400">
                        Starts from the computed layout — change what you actually built.
                    </span>
                    {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
                    {note && <div className="mt-1 text-xs text-gray-600">{note}</div>}
                    {/* Milestone C task C3/C4: expiry is not a quote refusal -- a distinct amber
                        notice with the re-analyze remedy, never the red `error` slot above. */}
                    {expiredNotice && (
                        <div data-testid="requote-expired-notice"
                            className="mt-1 text-xs rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900">
                            {expiredNotice}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Task 51: the single shared results layout every path renders — PO-mode fresh analyze, ASIN-mode
// fresh analyze, and loaded runs (?run= deep link, Previous Runs, watch-opened) all funnel here.
// Owns: loaded-run banner, CONFIRMED actions (cancel shipment + print labels), plan-position
// header, draw-quality strip, confirm-gate reason line, the scored option cards (or the unified
// unscored fallback card for legacy runs / pre-scoring responses), then at the bottom the FC
// distribution map and the per-ASIN FC distribution section. Mode-specific extras (PO's detailed
// breakdown) render via children, between the cards and the FC distribution.
// State ownership: card expand/collapse is local here (purely presentational); option SELECTION
// stays lifted in the caller because each mode's confirm button reads it.
// Task 71 (owner blocked live on PO 5951): the amber panel that replaces the old silent $0 stub
// quotes. States the plan-wide fact, shows every shipment's OWN partnered-LTL quote (or "none
// offered") so the operator can see where the money and the gap actually are, and offers the two
// working remedies as one-click actions.
//
// Task 73 supersedes task 71's note here: the third remedy -- proceeding with
// USE_YOUR_OWN_CARRIER (non-partnered) transportation -- IS now offered, because
// confirmWithOwnCarrier() exists and can carry an unquoted own-carrier option all the way through
// confirmPlacementOption / delivery windows / confirmTransportationOptions. It is a separate,
// explicitly-acknowledged button rather than a variant of "Confirm & Send", because it commits the
// shipment with NO Amazon freight: the operator books and pays the carrier, and owes Amazon the
// tracking afterwards.
function LtlCarrierGapPanel({ gap: rawGap, planId, placementOptionId,
                             onExcludeAsins, onSwitchToUps, onConfirmOwnCarrier, busy = false }) {
    // Only the operator's OVERRIDES are state. The defaults are derived at render from the gap
    // itself, so the pickers are correct on the very first paint -- seeding them from an effect
    // meant a render where every select was empty and the confirm button was disabled, which is
    // both a flicker for the operator and a race for the tests.
    const [overrides, setOverrides] = useState({});
    const [overrideKey, setOverrideKey] = useState(null);
    // Freshly-listed options, overlaid on the (possibly pre-task-73, possibly stale) saved gap.
    const [liveOptions, setLiveOptions] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const gap = mergeOwnCarrierOptions(rawGap, liveOptions);

    // Identity of the option set on screen. When a re-analyze or a live refresh changes it, the
    // operator's picks are dropped rather than carried across -- Amazon's option ids rotate, so a
    // stale pick would only earn a confusing server-side refusal.
    const gapKey = (gap?.shipments || [])
        .map(s => `${s.shipmentId}:${(s.ownCarrierOptions || []).map(o => o.transportationOptionId).join(',')}`)
        .join('|');
    const activeOverrides = overrideKey === gapKey ? overrides : {};
    const ownSelections = { ...defaultOwnCarrierSelections(gap), ...activeOverrides };
    const setOwnCarrierFor = (shipmentId, optionId) => {
        setOverrides({ ...activeOverrides, [shipmentId]: optionId });
        setOverrideKey(gapKey);
    };

    const loadOwnCarrierOptions = async () => {
        if (!planId || !placementOptionId) return;
        setLoadingOptions(true);
        setLoadError(null);
        try {
            const res = await ApiClient.get(
                `/api/shipment-planner/own-carrier-options/${planId}`,
                { params: { placementOptionId }, timeout: 120000 });
            setLiveOptions(res.data?.optionsByShipment || {});
        } catch (err) {
            setLoadError(err.response?.data?.error || err.message || 'Could not list own-carrier options');
        }
        setLoadingOptions(false);
    };

    if (!gap) return null;
    const excludable = ltlGapExcludableAsins(gap);
    const quotes = gap.shipments || [];
    const ownServed = ownCarrierShipments(gap);
    const ownUnserved = ownCarrierUnservedShipments(gap);
    const ownComplete = ownCarrierSelectionComplete(gap, ownSelections);
    const canLoadOwnCarrier = !!planId && !!placementOptionId;

    return (
        <div
            data-testid="ltl-carrier-gap"
            className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4 text-sm text-amber-900"
        >
            <div className="flex items-start gap-2 font-semibold mb-1">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{gap.message}</span>
            </div>

            <p className="ml-7 text-amber-800 mb-3">
                Amazon Partnered LTL requires one carrier across every shipment in the plan, so the
                whole plan is unquotable until this shipment is out of it.
                {gap.coveringCarriers?.length > 0 && (
                    <> The other shipments <em>are</em> covered by {gap.coveringCarriers.join(', ')}.</>
                )}
            </p>

            {/* Requirement 3: per-shipment truth, even though the intersection failed. */}
            {quotes.length > 0 && (
                <div className="ml-7 mb-3 overflow-x-auto">
                    <table className="text-xs w-full max-w-2xl">
                        <thead>
                            <tr className="text-amber-700 text-left">
                                <th className="pr-4 pb-1 font-medium">Shipment</th>
                                <th className="pr-4 pb-1 font-medium">Contents</th>
                                <th className="pr-4 pb-1 font-medium">Best partnered LTL</th>
                                <th className="pb-1 font-medium">Carriers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.map(q => (
                                <tr
                                    key={q.shipmentId}
                                    className={q.partneredLtlOffered ? '' : 'text-red-700 font-medium'}
                                >
                                    <td className="pr-4 py-0.5 whitespace-nowrap">{ltlGapShipmentLabel(q)}</td>
                                    <td className="pr-4 py-0.5">
                                        {q.units > 0 ? `${q.units} units` : '—'}
                                        {q.asins?.length > 0 && (
                                            <span className="font-mono ml-1">({q.asins.join(', ')})</span>
                                        )}
                                        {q.pallets?.heightIn > 0 && (
                                            <span className="ml-1">
                                                · {q.pallets.heightIn.toFixed(0)} in
                                                {q.pallets.weightLbEach > 0 && ` / ${q.pallets.weightLbEach.toFixed(0)} lb`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="pr-4 py-0.5 whitespace-nowrap">{ltlGapQuoteLabel(q)}</td>
                                    <td className="py-0.5">{q.carrierCount > 0 ? q.carrierCount : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="ml-7 flex flex-wrap items-center gap-2">
                {excludable.length > 0 && onExcludeAsins && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onExcludeAsins(excludable)}
                        className="px-3 py-1.5 text-xs font-medium text-amber-900 bg-white border border-amber-400 rounded-md hover:bg-amber-100 disabled:opacity-50"
                        title="Drops these rows from the plan and re-runs the analysis. Your saved draft keeps the original composition, so the excluded ASIN can ship on its own plan."
                    >
                        Exclude {excludable.join(', ')} and re-analyze
                    </button>
                )}
                {onSwitchToUps && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onSwitchToUps}
                        className="px-3 py-1.5 text-xs font-medium text-amber-900 bg-white border border-amber-400 rounded-md hover:bg-amber-100 disabled:opacity-50"
                        title="Re-runs the same composition as UPS small parcel. Appropriate when the load is small enough to go by parcel."
                    >
                        Analyze as UPS parcel instead
                    </button>
                )}
            </div>

            {excludable.length > 0 && (
                <p className="ml-7 mt-2 text-xs text-amber-700">
                    Excluding drops {excludable.length === 1 ? 'that ASIN' : 'those ASINs'} from this plan
                    only — your saved draft still holds the full composition, so it can ship separately.
                </p>
            )}

            {/* Task 73: the third remedy. Actionable only when EVERY shipment has a pick. When the
                run predates this feature (the owner's blocked wf76d77a61) the saved gap carries no
                own-carrier options at all -- so the section still renders, with a button that lists
                them live rather than telling him to re-analyze and abandon the stuck plan. */}
            {(ownServed.length > 0 || canLoadOwnCarrier) && (
                <div data-testid="own-carrier-section" className="ml-7 mt-4 pt-3 border-t border-amber-300">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-semibold">Ship with your own carrier</span>
                        {canLoadOwnCarrier && (
                            <button
                                type="button"
                                data-testid="load-own-carrier-options"
                                disabled={busy || loadingOptions}
                                onClick={loadOwnCarrierOptions}
                                className="text-xs text-amber-800 underline hover:text-amber-900 disabled:opacity-50"
                                title="Lists the carriers Amazon offers for this placement right now. Amazon's option ids rotate, so this also refreshes stale ones."
                            >
                                {loadingOptions ? 'Listing carriers…'
                                    : ownServed.length > 0 ? 'refresh carriers from Amazon'
                                    : 'list carriers from Amazon'}
                            </button>
                        )}
                    </div>
                    {loadError && (
                        <p className="text-xs text-red-700 mb-2">{loadError}</p>
                    )}
                    {ownServed.length === 0 && !loadingOptions && !loadError && (
                        <p className="text-xs text-amber-700 mb-2">
                            This plan was analyzed before own-carrier support existed. List the carriers
                            to see what Amazon offers for it now — the plan itself is not touched.
                        </p>
                    )}
                    <p className="text-amber-800 mb-2">
                        Amazon offers these non-partnered carriers for this placement.{' '}
                        <strong>Amazon does not quote, book, bill or insure this freight</strong> — you
                        arrange the pickup and pay the carrier directly, then enter the BOL/PRO (or parcel
                        tracking) on the Confirmed Shipments page afterwards.
                    </p>

                    {/* Before anything is listed there is nothing to say about a shipment -- rendering
                        "no own-carrier option offered" for an unlisted plan would state an absence we
                        have not actually checked. */}
                    {ownServed.length > 0 && (
                    <>
                    <div className="space-y-1.5 mb-3">
                        {quotes.map(q => {
                            const options = q.ownCarrierOptions || [];
                            return (
                                <div key={q.shipmentId} className="flex flex-wrap items-center gap-2 text-xs">
                                    <span className="w-48 flex-shrink-0 truncate" title={q.shipmentId}>
                                        {ltlGapShipmentLabel(q)}
                                    </span>
                                    {options.length === 0 ? (
                                        <span className="text-red-700 font-medium">
                                            no own-carrier option offered
                                        </span>
                                    ) : (
                                        <select
                                            aria-label={`Own carrier for ${ltlGapShipmentLabel(q)}`}
                                            value={ownSelections[q.shipmentId] || ''}
                                            disabled={busy}
                                            onChange={e => setOwnCarrierFor(q.shipmentId, e.target.value)}
                                            className="px-2 py-1 border border-amber-400 rounded bg-white text-amber-900 max-w-xs"
                                        >
                                            <option value="">Select a carrier…</option>
                                            {options.map(o => (
                                                <option key={o.transportationOptionId} value={o.transportationOptionId}>
                                                    {ownCarrierOptionLabel(o)}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <span className="text-amber-700">no Amazon quote</span>
                                </div>
                            );
                        })}
                    </div>

                    {ownUnserved.length > 0 && (
                        <p className="text-xs text-red-700 mb-2">
                            {ownUnserved.length === 1 ? 'One shipment has' : `${ownUnserved.length} shipments have`}{' '}
                            no own-carrier option either, so this placement cannot be confirmed at all —
                            Amazon confirms transportation for every shipment in one call.
                        </p>
                    )}

                    {onConfirmOwnCarrier && (
                        <button
                            type="button"
                            data-testid="confirm-own-carrier"
                            disabled={busy || !ownComplete}
                            onClick={() => onConfirmOwnCarrier(ownSelections, gap)}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-amber-700 rounded-md hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={ownComplete
                                ? 'Confirms this placement with your own carrier. Amazon will not arrange or pay for the freight.'
                                : 'Pick a carrier for every shipment first — Amazon confirms the whole placement in one call.'}
                        >
                            Confirm with own carrier
                        </button>
                    )}
                    </>
                    )}
                </div>
            )}
        </div>
    );
}

function ShipmentResultsView({
    analysisResult,
    items,
    totalUnits,
    selectedOptionId,
    onSelectOption,
    onCancelConfirmed,
    noScoresNote = false,
    expandedPlacements,
    onToggleDetails,
    // Task 65: state setter for the analysis result -- required for the LTL "adjust pallets"
    // re-quote to land back on screen. When absent (callers that never produce LTL results),
    // the adjust affordance is hidden.
    onAnalysisUpdate,
    // Task 71: one-click remedies on the partnered-LTL carrier-gap panel. Both optional -- callers
    // that can't re-run an analyze (PO mode, loaded historical runs) render the panel read-only.
    onExcludeAsins,
    onSwitchToUps,
    // Task 73: the own-carrier confirm. Optional for the same reason -- callers that can't act on
    // a plan (PO mode, loaded historical runs) render the picker read-only rather than a button
    // that commits freight they can't follow through on.
    onConfirmOwnCarrier,
    remedyBusy = false,
    // 2026-08-21: pallet-build sign-off. Owned by the parent panel because the CONFIRM button
    // lives there and must be gated on it; this view only renders and reports it.
    approvedBuilds = {},
    onApproveBuild,
    // Milestone C task C5: the draft's saved-by-FC pallet measurements + the upward report hook.
    // Both optional -- callers with no persisted draft (PO mode) render without restore/report,
    // exactly like every other optional palletEdit field above.
    palletMeasurements,
    onMeasurementsChange,
    children,
}) {
    const [expandedCards, setExpandedCards] = useState({});
    // Task 72: everything the inline pallet override on an LTL card needs. Editing re-generates
    // transport for the plan, so it is only sane while the plan is still ACTIVE (and only when
    // the caller can take the updated analysis back).
    const palletEdit = {
        planId: analysisResult?.inboundPlanId,
        // Amazon's per-shipment assignment is joined to these for the staging list + weight model.
        planItems: analysisResult?.items || items,
        canEdit: !!onAnalysisUpdate && analysisResult?.status === 'ACTIVE',
        onApplied: (placementOptionId, resp) =>
            onAnalysisUpdate(mergeLtlRequoteIntoAnalysis(analysisResult, placementOptionId, resp)),
        approvedOptionIds: approvedBuilds,
        onApproveBuild,
        // Milestone C: the plan-wide count the operator declared before Amazon ever split the
        // plan (spec §0/§2) -- feeds blankPalletRows' starting row count and the reconciliation.
        declaredPalletCount: analysisResult?.declaredPalletCount,
        // Milestone C task C5: measurements persist by destination FC and survive a re-analyze.
        savedMeasurements: palletMeasurements,
        onMeasurementsChange,
    };

    const scores = analysisResult?.scores || [];
    const sortedScores = [...scores].sort((a, b) => b.score - a.score);
    const optionsById = {};
    (analysisResult?.placementOptions || []).forEach(po => { optionsById[po.placementOptionId] = po; });
    const selectedScore = scores.find(s => s.placementOptionId === selectedOptionId) || sortedScores[0] || null;
    // Any LTL option widens the option grid -- see the grid below for why.
    const anyLtlOption = (analysisResult?.placementOptions || []).some(isLtlOption);

    return (
        <>
            {/* Task 35: loaded-run label -- renders only when the result came from
                GET /runs/{id} (rescoredAt set), never after a fresh analyze. */}
            <LoadedRunBanner result={analysisResult} />

            {/* Task 71: partnered-LTL carrier gap -- distinct from the generic Amazon-warnings
                block below, and rendered FIRST because it explains why every option reads $0. */}
            <LtlCarrierGapPanel
                gap={ltlCarrierGapFrom(analysisResult)}
                /* Task 73: the plan + the placement THIS gap describes (matched by shipment id --
                   the response-level gap is the representative one, not necessarily the selected
                   option), so own-carrier options can be listed live and confirmed correctly. */
                planId={analysisResult?.inboundPlanId}
                placementOptionId={gapPlacementOptionId(analysisResult, ltlCarrierGapFrom(analysisResult))}
                onExcludeAsins={onExcludeAsins}
                onSwitchToUps={onSwitchToUps}
                onConfirmOwnCarrier={onConfirmOwnCarrier}
                busy={remedyBusy}
            />

            {/* Task 61: Amazon WARNING-severity operation problems (previously swallowed in
                pollOperation) -- e.g. "pallet and freight information has not been provided". */}
            <AmazonWarnings groups={analysisResult.warningGroups} warnings={analysisResult.warnings} />

            {/* Task 40 (owner requirement: "we should be able to cancel via the UI") --
                a loaded CONFIRMED run voided a real accepted shipment, so it gets the same
                deliberate cancel dialog as the Shipment Runs table; confirm-type actions in the
                caller's header stay disabled as-is (status isn't ACTIVE). Task 49: print belongs
                here too, beside Cancel shipment. */}
            {analysisResult.status === 'CONFIRMED' && (
                <div className="mb-3 flex items-center gap-3">
                    <button
                        onClick={onCancelConfirmed}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50"
                    >
                        Cancel shipment
                    </button>
                    <PrintLabelsAction
                        runId={analysisResult.runId}
                        planId={analysisResult.inboundPlanId}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50 disabled:opacity-50"
                    />
                </div>
            )}

            {/* Task 19/35: loaded run with no scores -- since rescore-on-load, this only
                happens when the on-load rescore itself failed (non-fatal fallback returns
                the stored snapshot unscored). Options still render, just unscored. */}
            {noScoresNote && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 italic mb-3">
                    <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
                    this run couldn&apos;t be scored on load — re-run analyze for scores
                </div>
            )}

            {/* Task 23: pre-state stated once, plainly, before any option card is read. */}
            {scores.length > 0 && <PlanPositionHeader score={sortedScores[0]} positions={analysisResult.positions} />}

            {/* Task 29: draw-quality advisor -- one plan-level strip, never per option. */}
            {scores.length > 0 && <DrawQualityStrip dq={analysisResult.drawQuality} />}

            {selectedScore && !selectedScore.confirmAllowed && (
                <div className="text-xs text-red-600 mb-3">{selectedScore.confirmDisabledReason || MIAMI_GATE_REASON}</div>
            )}

            {/* 2026-08-21, owner: "the layout for LTL should be horizontal - there is too much data
                for vertical". LTL carries strictly more per shipment than parcel does (pallet
                counts, heights, weights, freight class, declared value) and its per-shipment table
                is nine columns wide -- squeezed into a 1/3-width card on run 266 (11 pallets, 185
                boxes) it became a horizontal-scroll strip nobody could read. LTL options therefore
                get half the row each instead of a third, so the table has room to lay out
                horizontally; parcel keeps the denser 3-up grid it reads fine in. */}
            {/* 2026-08-23, owner (live): "the shipment choices are vertical and require scrolling
                — horizontal should display the data without any scrolling". LTL option cards are
                now COMPACT (no per-shipment table) and lie 3-4 across; selecting one expands it to
                the full row width, giving the nine-column shipments table the whole screen. */}
            <div className={`grid grid-cols-1 gap-4 mb-6 ${anyLtlOption
                ? 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {scores.length > 0 ? sortedScores.map((s) => (
                    <ScoredOptionCard
                        key={s.placementOptionId}
                        score={s}
                        option={optionsById[s.placementOptionId]}
                        totalUnits={totalUnits}
                        selected={selectedOptionId === s.placementOptionId}
                        onSelect={() => onSelectOption(s.placementOptionId)}
                        expanded={!!expandedCards[s.placementOptionId]}
                        onToggleExpand={() => setExpandedCards(prev => ({ ...prev, [s.placementOptionId]: !prev[s.placementOptionId] }))}
                        palletEdit={palletEdit}
                    />
                )) : (analysisResult.placementOptions || []).map((po) => {
                    const isLtl = isLtlOption(po);
                    const freightSummaryLine = isLtl ? formatFreightSummaryLine(optionFreightTotals(po, totalUnits)) : null;
                    return (
                    /* Unified unscored fallback card (was two divergent copies: PO-mode's rich
                       card and ASIN-mode's minimal one). Money fields render '—' when the run
                       snapshot doesn't carry them. */
                    <div
                        key={po.placementOptionId}
                        onClick={() => onSelectOption(po.placementOptionId)}
                        className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                            selectedOptionId === po.placementOptionId
                                ? 'border-indigo-500 bg-indigo-50/50 shadow-md'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        {analysisResult.recommendedPlacementId === po.placementOptionId && (
                            <div className="mb-2">
                                <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded">
                                    Recommended
                                </span>
                            </div>
                        )}
                        <div className="text-lg font-bold text-gray-900 mb-1">
                            {po.shipments?.length || 0} shipment{(po.shipments?.length || 0) !== 1 ? 's' : ''}
                        </div>

                        <TransportErrorNote option={po} />

                        {/* Task 75: LTL leads with the Amazon freight quote, above everything else. */}
                        {isLtl && (
                            freightSummaryLine ? (
                                <div className="text-base font-bold text-gray-900 mb-3">{freightSummaryLine}</div>
                            ) : (
                                <div className="text-xs text-amber-600 italic mb-3">no Amazon freight quote yet — see the shipment table below</div>
                            )
                        )}

                        {/* Destinations move into the per-shipment table below for LTL; this plain
                            list stays for parcel/UPS, which has no such table. */}
                        {!isLtl && (
                            <div className="text-sm text-gray-500 mb-3 space-y-1">
                                {(po.shipments || []).map((sh, si) => {
                                    const shipUnits = sh.items?.reduce((sum, i) => sum + i.quantity, 0) || 0;
                                    // Boxes for this shipment (only when the plan rows carry unitsPerBox)
                                    let shipBoxes = 0;
                                    if (sh.items) {
                                        for (const si2 of sh.items) {
                                            const planItem = (items || []).find(i => i.asin === si2.asin || (i.sku && i.sku === si2.sku));
                                            if (planItem?.unitsPerBox > 0) {
                                                shipBoxes += Math.ceil(si2.quantity / planItem.unitsPerBox);
                                            }
                                        }
                                    }
                                    return (
                                        <div key={si} className="flex items-center justify-between">
                                            <div>
                                                <span className="font-medium text-gray-700">{sh.destinationFc || sh.shipmentId?.slice(-4)}</span>
                                                {sh.destinationCity && (
                                                    <span className="text-gray-400 ml-1">({sh.destinationCity}, {sh.destinationState})</span>
                                                )}
                                            </div>
                                            <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                                {shipUnits} units{shipBoxes > 0 && ` / ${shipBoxes} box${shipBoxes !== 1 ? 'es' : ''}`}
                                            </span>
                                        </div>
                                    );
                                })}
                                {(!po.shipments || po.shipments.length === 0) && <div className="text-xs text-gray-400 italic">no shipment detail</div>}
                            </div>
                        )}

                        <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">Placement Fee</span>
                                <span>{po.placementFee != null ? `$${po.placementFee.toFixed(2)}` : '—'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Transport Cost</span>
                                <span>{po.totalTransportCost != null ? `$${po.totalTransportCost.toFixed(2)}` : '—'}</span>
                            </div>
                            <div className="flex justify-between font-bold border-t pt-1">
                                <span>Total Cost</span>
                                <span>{po.totalCost != null ? `$${po.totalCost.toFixed(2)}` : '—'}</span>
                            </div>
                        </div>

                        {/* Task 65/72/75: one row per shipment, chevron expands to contents +
                            pallet builder + alternate quotes. LTL only. */}
                        <LtlShipmentsTable option={po} planId={palletEdit?.planId} planItems={palletEdit?.planItems}
                            canEdit={!!palletEdit?.canEdit} onApplied={palletEdit?.onApplied}
                approvedOptionIds={palletEdit?.approvedOptionIds}
                onApproveBuild={palletEdit?.onApproveBuild}
                declaredPalletCount={palletEdit?.declaredPalletCount}
                savedMeasurements={palletEdit?.savedMeasurements}
                onMeasurementsChange={palletEdit?.onMeasurementsChange} />

                        {po.averageMarginPercent != null && (
                            <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Avg Margin</span>
                                    <span className={marginColor(po.averageMarginPercent)}>
                                        {po.averageMarginPercent?.toFixed(1)}%
                                    </span>
                                </div>
                                {po.negativeMarginCount > 0 && (
                                    <div className="flex justify-between text-red-600">
                                        <span>Negative Margin Items</span>
                                        <span className="font-bold">{po.negativeMarginCount}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {onToggleDetails && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleDetails(po.placementOptionId); }}
                                className="mt-3 text-xs text-indigo-600 hover:text-indigo-800"
                            >
                                {expandedPlacements?.[po.placementOptionId] ? 'Hide Details' : 'Show Details'}
                            </button>
                        )}
                    </div>
                    );
                })}
            </div>

            {/* Mode-specific extras (PO-mode detailed breakdown) */}
            {children}

            {/* === FC Distribution Map === */}
            <FcDistributionMap
                items={items}
                analysisResult={analysisResult}
                selectedPlacement={selectedOptionId}
            />

            {/* === Current FC distribution (per-ASIN, always at the bottom) === */}
            <FcDistributionSection items={items} />

        </>
    );
}

// === Task 101: never supersede a CONFIRMED plan ===
//
// "Supersede" sounds like bookkeeping. It is not: the backend follows the status flip with a real
// SP-API cancellation of the inbound plan, and Amazon honours that on confirmed plans too. Because
// re-analyze superseded whatever plan happened to be loaded, clicking it on a confirmed result
// cancelled seven live FBA shipments on 2026-08-20 (runs 211, 220, 222).
//
// The fix is narrow, because only half of re-analyze was ever dangerous. Creating a new plan
// alongside a confirmed one is harmless. Cancelling the confirmed one is the destructive half. So
// we skip the supersede and carry straight on to the new analysis, and say so.
export const CONFIRMED_PLAN_KEPT_NOTICE =
    'That plan is already confirmed — it was left intact, not cancelled. The new analysis creates a '
    + 'separate plan. To cancel the confirmed one, use Cancel on the Confirmed Shipments page.';

export function isConfirmedPlanStatus(status) {
    return String(status ?? '').trim().toUpperCase() === 'CONFIRMED';
}

/**
 * Supersede `planId` unless it is confirmed. Never throws — callers treat it as a best-effort
 * tidy-up step in front of a new analysis.
 *
 * @param planId  inbound plan id, or falsy for "nothing loaded"
 * @param status  the loaded run's status as the UI knows it (analysisResult.status)
 * @param notify  optional (message) => void for the operator-facing notice
 * @returns {Promise<boolean>} true when a confirmed plan was deliberately left intact
 */
export async function supersedePlanUnlessConfirmed(planId, status, notify) {
    if (!planId) return false;
    if (isConfirmedPlanStatus(status)) {
        if (notify) notify(CONFIRMED_PLAN_KEPT_NOTICE);
        return true;
    }
    try {
        await ApiClient.post(`/api/shipment-planner/supersede/${planId}`);
    } catch (e) {
        // 409 alreadyConfirmed is the backend reaching the same conclusion from fresher data than
        // the page holds (someone confirmed in another tab, or the loaded status is stale). It is
        // the guard working, not a failure: nothing was cancelled, so carry on with the new plan.
        if (e?.response?.status === 409 && e?.response?.data?.alreadyConfirmed) {
            if (notify) notify(CONFIRMED_PLAN_KEPT_NOTICE);
            return true;
        }
        console.error('Failed to supersede old plan:', e);
    }
    return false;
}

export default function ShipmentPlanner() {
    const [searchParams] = useSearchParams();
    // Owner, 2026-08-31 ("too many ways to access the shipment planner"): ASIN mode is THE
    // planner now — a bare URL lands there with the PO panel open. Legacy PO mode renders only
    // for its 13 historical runs (?run= flips the mode via syncMode) or an explicit ?mode=po.
    const [mode, setMode] = useState(() => (searchParams.get('mode') === 'po' ? 'po' : 'asin'));
    const autoImportDraft = searchParams.get('fromDraft') === '1';
    // Task 19 (task-18 concern #1): when a ?run= deep link resolves to an ASIN/PROBE-mode run,
    // this carries the loaded AnalysisResponse down to AsinModePanel so it can populate its own
    // item list + results instead of rendering empty while the mode toggle flips to ASIN.
    const [asinRunToLoad, setAsinRunToLoad] = useState(null);

    // PO selection state
    const [recentPOs, setRecentPOs] = useState([]);
    const [selectedPO, setSelectedPO] = useState('');
    const [items, setItems] = useState([]);
    const [loadingPO, setLoadingPO] = useState(false);

    // Product search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // Box dims editing state
    const [editingBoxDims, setEditingBoxDims] = useState(null); // asin of item being edited
    const [showingFcInventory, setShowingFcInventory] = useState(null); // asin of item showing FC inventory
    const saveTimerRef = useRef({});

    // Analysis state
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisStatus, setAnalysisStatus] = useState('');
    // Task 68: failures render in a persistent red banner (the old pattern left the failure text
    // inside the spinner banner, stuck at "analyzing" forever).
    const [analysisError, setAnalysisError] = useState(null);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [selectedPlacement, setSelectedPlacement] = useState(null);
    const [expandedPlacements, setExpandedPlacements] = useState({});

    // History state
    const [previousRuns, setPreviousRuns] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingRun, setLoadingRun] = useState(false);
    const [viewingHistorical, setViewingHistorical] = useState(false); // true when viewing a non-ACTIVE run
    const [confirming, setConfirming] = useState(false);
    // Task 80: the server-named used-condition SKUs blocking a confirm, plus the retry that runs
    // once the operator explicitly ticks "I intend to ship used condition". Null = no block.
    const [usedAckItems, setUsedAckItems] = useState(null);
    const [usedAckRetry, setUsedAckRetry] = useState(null);
    // Task 40 (owner requirement: "we should be able to cancel via the UI") -- controls the
    // deliberate cancel-confirmed-shipment dialog shown for a loaded CONFIRMED run.
    const [showCancelConfirmed, setShowCancelConfirmed] = useState(false);

    // Load recent POs on mount
    useEffect(() => {
        ApiClient.get('/api/shipment-planner/recent-pos')
            .then(res => setRecentPOs(res.data))
            .catch(err => console.error('Failed to load POs:', err));
        loadPreviousRuns();

        // Task 18 (Shipment Runs page): ?run=<id> deep-links here from the Runs page's "Open"
        // action. Load that run and, only for this deep-link path, sync the mode toggle to the
        // run's source_mode (ASIN runs open in ASIN mode; null/PO opens in PO mode) -- the existing
        // PO-mode Previous Runs button never does this (it stays in PO mode, unchanged).
        const runParam = searchParams.get('run');
        if (runParam) {
            loadRun(runParam, { syncMode: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Task 103 -- the point of the whole change, PO-mode half. On mount, ask the server whether
     * this panel already has an analyze in flight; if it does, put the spinner back up and follow
     * it to completion. The operator can now start an analyze, navigate away, come back, and see
     * exactly where it is instead of a blank page that pretends nothing happened.
     *
     * <p>Re-attachment is by SCOPE, not by a remembered job id -- a reloaded page has lost every
     * id it was holding. `cancelled` stops the POLLING when this component unmounts; it never
     * stops the JOB, which is the distinction the feature exists for.
     */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const job = await findActiveJob(SCOPE_PLANNER, SCOPE_KEY_PO_MODE);
            if (!job || cancelled) return;
            setAnalyzing(true);
            setAnalysisError(null);
            setAnalysisStatus(jobProgressLabel(job, {
                singleStepLabel: 'Resuming an analyze that is still running on the server…',
            }));
            try {
                const finished = await waitForJob(job.jobId, {
                    onProgress: (j) => { if (!cancelled) setAnalysisStatus(jobProgressLabel(j)); },
                    isCancelled: () => cancelled,
                });
                if (!finished || cancelled) return;
                applyPoAnalysis(await resolveFinishedJob(finished));
            } catch (err) {
                if (!cancelled) setAnalysisError(err.message || 'analyze failed');
            } finally {
                if (!cancelled) {
                    setAnalyzing(false);
                    setAnalysisStatus('');
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load previous runs
    const loadPreviousRuns = async () => {
        try {
            const res = await ApiClient.get('/api/shipment-planner/runs?limit=20');
            setPreviousRuns(res.data);
        } catch (err) {
            console.error('Failed to load previous runs:', err);
        }
    };

    // Task 43 (owner: "on the previous runs - it should be updated without requiring a page
    // refresh"): refresh the Previous Runs panel every 20s while it's open and the tab is visible;
    // paused when hidden, cleared on unmount/close. loadPreviousRuns never flips a loading flag,
    // so refreshes swap state in place with no panel flicker.
    useEffect(() => {
        if (!showHistory) return undefined;
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') loadPreviousRuns();
        }, 20000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showHistory]);

    // Load a specific previous run
    const loadRun = async (runId, opts = {}) => {
        setLoadingRun(true);
        try {
            const res = await ApiClient.get(`/api/shipment-planner/runs/${runId}`);
            setAnalysisResult(res.data);
            if (res.data.items) {
                setItems(res.data.items);
            }
            if (res.data.placementOptions && res.data.placementOptions.length > 0) {
                setSelectedPlacement(initialPlacementFor(res.data));
            }
            setViewingHistorical(res.data.status !== 'ACTIVE');
            setShowHistory(false);
            // Task 19: ASIN and PROBE runs both go through the ASIN-mode analyze path (task-18
            // report) -- either one deep-links into ASIN mode and hands its full response to
            // AsinModePanel so the scored cards render exactly as they did after a live analyze.
            const isAsinModeRun = res.data.sourceMode === 'ASIN' || res.data.sourceMode === 'PROBE';
            if (isAsinModeRun) {
                setAsinRunToLoad(res.data);
            }
            if (opts.syncMode) {
                setMode(isAsinModeRun ? 'asin' : 'po');
            }
        } catch (err) {
            console.error('Failed to load run:', err);
        }
        setLoadingRun(false);
    };

    // Load PO items
    const loadPOItems = async () => {
        if (!selectedPO) return;
        setLoadingPO(true);
        try {
            const res = await ApiClient.get(`/api/shipment-planner/po-items/${selectedPO}`);
            setItems(res.data);
            setAnalysisResult(null);
            setViewingHistorical(false);
        } catch (err) {
            console.error('Failed to load PO items:', err);
        }
        setLoadingPO(false);
    };

    // Product search
    const handleSearch = useCallback(async () => {
        if (!searchQuery || searchQuery.length < 2) return;
        setSearchLoading(true);
        try {
            const res = await ApiClient.get(`/api/shipment-planner/product-search?q=${encodeURIComponent(searchQuery)}`);
            setSearchResults(res.data);
        } catch (err) {
            console.error('Search failed:', err);
        }
        setSearchLoading(false);
    }, [searchQuery]);

    // Add item from search (with enrichment)
    const addSearchItem = async (product) => {
        const exists = items.find(i => i.asin === product.asin);
        if (exists) return;
        setSearchResults([]);
        setSearchQuery('');

        // Add immediately with basic data, then enrich
        const basicItem = {
            asin: product.asin,
            sku: product.sku,
            name: product.name,
            quantity: 1,
            weightLbs: null,
            cogs: null,
            fbaFees: null,
            sellingPrice: product.selling_price,
            missingWeight: true,
            missingCogs: true,
            fcInventory: []
        };
        setItems(prev => [...prev, basicItem]);

        try {
            const res = await ApiClient.get(`/api/shipment-planner/enrich?asin=${product.asin}`);
            const d = res.data;
            setItems(prev => prev.map(item => item.asin === product.asin ? {
                ...item,
                sku: d.sku || item.sku,
                name: d.name || item.name,
                weightLbs: d.weight_lbs ? parseFloat(d.weight_lbs) : null,
                lengthIn: d.length_in ? parseFloat(d.length_in) : null,
                widthIn: d.width_in ? parseFloat(d.width_in) : null,
                heightIn: d.height_in ? parseFloat(d.height_in) : null,
                cogs: d.price_list_cost ? parseFloat(d.price_list_cost) : null,
                sellingPrice: d.selling_price ? parseFloat(d.selling_price) : item.sellingPrice,
                fbaFees: d.fba_fees ? parseFloat(d.fba_fees) : null,
                referralFee: d.referral_fee ? parseFloat(d.referral_fee) : null,
                unitsPerBox: d.units_per_box ? parseInt(d.units_per_box) : null,
                boxLengthIn: d.box_length_in ? parseFloat(d.box_length_in) : null,
                boxWidthIn: d.box_width_in ? parseFloat(d.box_width_in) : null,
                boxHeightIn: d.box_height_in ? parseFloat(d.box_height_in) : null,
                boxWeightLbs: d.box_weight_lbs ? parseFloat(d.box_weight_lbs) : null,
                missingWeight: !d.weight_lbs,
                missingCogs: !d.price_list_cost,
                fcInventory: (d.fcInventory || []).map(fc => ({
                    fcCode: fc.fc_code,
                    city: fc.city,
                    state: fc.state,
                    zip: fc.zip,
                    quantity: fc.quantity
                }))
            } : item));
        } catch (err) {
            console.error('Failed to enrich item:', err);
        }
    };

    // Update item quantity
    const updateQuantity = (index, qty) => {
        setItems(prev => prev.map((item, i) => i === index ? { ...item, quantity: parseInt(qty) || 0 } : item));
    };

    // Remove item
    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    // Update units per box (with debounced save)
    const updateUnitsPerBox = (index, val) => {
        const parsed = parseInt(val) || null;
        setItems(prev => prev.map((item, i) => i === index ? { ...item, unitsPerBox: parsed } : item));
        // Debounce save
        const item = items[index];
        if (item && parsed && parsed > 0) {
            clearTimeout(saveTimerRef.current[item.asin]);
            saveTimerRef.current[item.asin] = setTimeout(() => {
                savePackagingDefault(item.asin, parsed, item.boxLengthIn, item.boxWidthIn, item.boxHeightIn, item.boxWeightLbs);
            }, 1000);
        }
    };

    // Update box dimension field
    const updateBoxDim = (index, field, val) => {
        const parsed = parseFloat(val) || null;
        setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: parsed } : item));
        const item = items[index];
        if (item && item.unitsPerBox > 0) {
            clearTimeout(saveTimerRef.current[item.asin + field]);
            saveTimerRef.current[item.asin + field] = setTimeout(() => {
                const updated = { ...item, [field]: parsed };
                savePackagingDefault(item.asin, item.unitsPerBox, updated.boxLengthIn, updated.boxWidthIn, updated.boxHeightIn, updated.boxWeightLbs);
            }, 1000);
        }
    };

    // Save packaging default to backend
    const savePackagingDefault = async (asin, unitsPerBox, boxLengthIn, boxWidthIn, boxHeightIn, boxWeightLbs) => {
        try {
            await ApiClient.post('/api/shipment-planner/packaging-default', {
                asin, unitsPerBox, boxLengthIn, boxWidthIn, boxHeightIn, boxWeightLbs
            });
        } catch (err) {
            console.error('Failed to save packaging default:', err);
        }
    };

    // Task 103: applying a finished PO-mode analyze to the panel. Extracted because BOTH the
    // kickoff path and the re-attach path (a reload landing on a job that is already running, or
    // has already finished) have to render the identical result -- a second copy would be a second
    // chance for the resumed view to disagree with the live one.
    const applyPoAnalysis = useCallback((analysis) => {
        if (!analysis) return;
        setAnalysisResult(analysis);
        if (analysis.placementOptions && analysis.placementOptions.length > 0) {
            setSelectedPlacement(initialPlacementFor(analysis));
        }
        // Update items with any enrichment from analysis
        if (analysis.items) {
            setItems(analysis.items);
        }
        loadPreviousRuns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Run analysis
    const analyzeShipment = async () => {
        if (items.length === 0) return;
        setAnalyzing(true);
        setAnalysisStatus('Creating inbound plan...');
        setAnalysisError(null);
        setAnalysisResult(null);
        setSelectedPlacement(null);
        setViewingHistorical(false);

        try {
            const payload = {
                items: items.map(i => ({ asin: i.asin, sku: i.sku, quantity: i.quantity }))
            };

            setAnalysisStatus('Getting placement & transportation options (this may take 30-60 seconds)...');
            // Task 103: the analyze runs on the SERVER now. The spinner and the wording are
            // unchanged, and a fast analyze still resolves in a poll or two -- what changed is that
            // closing this tab no longer throws the work away.
            const analysis = await runAnalyzeJob({
                url: '/api/shipment-planner/analyze/async',
                body: payload,
                scopeType: SCOPE_PLANNER,
                scopeKey: SCOPE_KEY_PO_MODE,
                label: `${items.length} line${items.length !== 1 ? 's' : ''}`,
                onProgress: (job) => setAnalysisStatus(jobProgressLabel(job, {
                    singleStepLabel: 'Getting placement & transportation options (this may take 30-60 seconds)...',
                })),
            });
            applyPoAnalysis(analysis);
        } catch (err) {
            console.error('Analysis failed:', err);
            // Task 68: surface the failure persistently and stop the spinner (the old code left
            // analyzing=true, wedging the button behind a stuck "Analysis failed" spinner).
            setAnalysisError(err.response?.data?.error || err.message);
            setAnalysisStatus('');
            setAnalyzing(false);
            return;
        }
        setAnalyzing(false);
        setAnalysisStatus('');
    };

    // Re-analyze: supersede old plan, then create new.
    // Task 101: "supersede" cancels the plan on Amazon, so a CONFIRMED plan is left alone --
    // see supersedePlanUnlessConfirmed. Re-analyzing still proceeds either way.
    const reAnalyze = async () => {
        await supersedePlanUnlessConfirmed(
            analysisResult?.inboundPlanId,
            analysisResult?.status,
            (msg) => toast(msg, { duration: 8000 }),
        );
        analyzeShipment();
    };

    // Cancel plan
    const cancelPlan = async () => {
        if (!analysisResult?.inboundPlanId) return;
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${analysisResult.inboundPlanId}`);
            setAnalysisResult(prev => prev ? { ...prev, status: 'CANCELLED' } : null);
            setViewingHistorical(true);
            loadPreviousRuns();
        } catch (err) {
            console.error('Cancel failed:', err);
        }
    };

    // Confirm placement with Amazon
    const confirmPlan = async (acknowledgeUsedCondition = false) => {
        // Task 87 defense-in-depth: a stray click event passed as this arg (bare `onClick={confirmPlan}`
        // wiring) must never be treated as "acknowledged" or reach the request body.
        acknowledgeUsedCondition = coerceAcknowledgeFlag(acknowledgeUsedCondition);
        if (!analysisResult?.inboundPlanId || !selectedPlacement) return;
        if (!acknowledgeUsedCondition && !window.confirm(
            'Confirm this placement option with Amazon?\n\n' +
            'This will:\n' +
            '- Lock in the selected placement option\n' +
            '- Confirm transportation with Amazon Partnered Carrier\n' +
            '- Make the workflow visible in Seller Central\n\n' +
            'This action cannot be undone.'
        )) return;

        setConfirming(true);
        // Task 87: built through the explicit whitelist serializer -- never planId/placementOptionId
        // spread from analysisResult/selectedPlacement wholesale -- with a last-chance stringify
        // check right before the request fires.
        const body = buildConfirmRequestBody({
            planId: analysisResult.inboundPlanId,
            placementOptionId: selectedPlacement,
            acknowledgeUsedCondition,
        });
        if (safeSerializationCheck(body, 'confirm')) {
            alert('Could not save — the confirm request could not be built. Please refresh and try again.');
            setConfirming(false);
            return;
        }
        try {
            const res = await ApiClient.post('/api/shipment-planner/confirm', body, { timeout: 120000 });
            setUsedAckItems(null);
            setAnalysisResult(prev => prev ? { ...prev, status: 'CONFIRMED', confirmedAt: new Date().toISOString() } : null);
            setViewingHistorical(true);
            loadPreviousRuns();
            alert(`Confirmed! ${res.data.shipmentCount} shipment(s) sent to Amazon.\nCheck Seller Central > Send to Amazon to continue.`);
        } catch (err) {
            // Task 80: used-condition refusal from the server -> explicit acknowledgement modal.
            const blocked = usedConditionBlockFrom(err);
            if (blocked) {
                setUsedAckItems(blocked);
                setUsedAckRetry(() => () => confirmPlan(true));
                setConfirming(false);
                return;
            }
            console.error('Confirm failed:', err);
            alert('Confirmation failed: ' + (err.response?.data?.error || err.message));
        }
        setConfirming(false);
    };

    // Cancel a plan from the history list
    const cancelHistoricalPlan = async (planId, e) => {
        e.stopPropagation();
        if (!window.confirm('Cancel this draft plan on Amazon?')) return;
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${planId}`);
            loadPreviousRuns();
            // If currently viewing this plan, update its status
            if (analysisResult?.inboundPlanId === planId) {
                setAnalysisResult(prev => prev ? { ...prev, status: 'CANCELLED' } : null);
                setViewingHistorical(true);
            }
        } catch (err) {
            console.error('Cancel failed:', err);
            alert('Cancel failed: ' + (err.response?.data?.error || err.message));
        }
    };

    // Toggle expanded placement detail
    const toggleExpanded = (placementId) => {
        setExpandedPlacements(prev => ({ ...prev, [placementId]: !prev[placementId] }));
    };

    // Status badge helper
    const statusBadge = (status) => {
        switch (status) {
            case 'ACTIVE':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>;
            case 'CONFIRMED':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Confirmed</span>;
            case 'CANCELLED':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Cancelled</span>;
            case 'SUPERSEDED':
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Superseded</span>;
            default:
                return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">{status}</span>;
        }
    };

    // Summary calculations
    const totalItems = items.length;
    const totalUnits = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
    const totalWeight = items.reduce((sum, i) => sum + ((i.weightLbs || 0) * (i.quantity || 0)), 0);
    const totalBoxes = items.reduce((sum, i) => sum + (i.unitsPerBox > 0 ? Math.ceil(i.quantity / i.unitsPerBox) : 0), 0);
    const warningCount = items.filter(i => i.missingWeight || i.missingCogs).length;

    // Task 14: PO-mode now gets the same scoring ASIN mode always had (backend: RouteController's
    // /api/shipment-planner/analyze calls the same scoreAndAttach ShipmentPlannerAnalysisService
    // exposes to analyze-asin-list). When scores are present, render the scored cards; otherwise
    // fall back to the legacy placement-option cards unchanged.
    const poScores = analysisResult?.scores || [];
    const sortedPoScores = [...poScores].sort((a, b) => b.score - a.score);
    const poOptionsById = {};
    (analysisResult?.placementOptions || []).forEach(po => { poOptionsById[po.placementOptionId] = po; });
    const selectedPoScore = sortedPoScores.find(s => s.placementOptionId === selectedPlacement) || sortedPoScores[0];

    return (
        // Owner, 2026-09-01 (layout, round two): ONE page width. The PO lines table needs
        // ~1800px on a wide monitor; giving ONLY the panel that width (the old translate/95vw
        // breakout) left the rest of the page in a narrow centered column — two widths, dead
        // margins, misaligned edges. So the page container itself is wide and every section
        // shares it; narrow content (search input, landing rows) caps its own width LEFT-aligned
        // inside, so edges line up down the page.
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1800px] mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <TruckIcon className="h-8 w-8 text-indigo-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Shipment Planner</h1>
                        <p className="text-sm text-gray-500">Analyze shipping costs and margins before creating FBA inbound shipments</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* The PO/ASIN toggle is gone (owner, 2026-08-31): ASIN mode is the planner.
                        The legacy view below still renders when a historical PO-mode run is
                        opened (?run= syncMode) — it just isn't a destination anymore. */}
                    {mode === 'po' && (
                        <button
                            onClick={() => setMode('asin')}
                            className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                            title="This legacy view only renders for old PO-mode runs"
                        >
                            ← Back to the planner
                        </button>
                    )}
                    {mode === 'po' && previousRuns.length > 0 && (
                        <button
                            onClick={() => setShowHistory(!showHistory)}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors ${
                                showHistory ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <ClockIcon className="h-4 w-4" />
                            Previous Runs ({previousRuns.length})
                        </button>
                    )}
                </div>
            </div>

            {mode === 'asin' ? (
                <AsinModePanel autoImportDraft={autoImportDraft} loadedRun={asinRunToLoad} />
            ) : (
            <>
            {/* === Previous Runs Panel === */}
            {showHistory && (
                <div className="bg-white shadow rounded-lg p-4 mb-6 border border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Analysis History</h3>
                    <div className="divide-y divide-gray-100 max-h-64 overflow-auto">
                        {previousRuns.map(run => (
                            <div
                                key={run.id}
                                className={`px-3 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-4 text-sm ${
                                    analysisResult?.runId === run.id ? 'bg-indigo-50' : ''
                                }`}
                            >
                                <button
                                    onClick={() => loadRun(run.id)}
                                    disabled={loadingRun}
                                    className="flex items-center gap-3 min-w-0 flex-1 text-left"
                                >
                                    {statusBadge(run.status)}
                                    <span className="text-gray-500 whitespace-nowrap">
                                        {new Date(run.created_at).toLocaleDateString()} {new Date(run.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <span className="text-gray-700 whitespace-nowrap">
                                        {run.total_items} items, {run.total_units} units
                                    </span>
                                    {/* Task 43: product name(s) + ASIN(s) + shipping-method chip on each run row */}
                                    {run.asins && run.asins.length > 0 && (
                                        <span
                                            className="text-gray-600 truncate min-w-0"
                                            title={run.asins.map(a => `${a.name ? a.name + ' — ' : ''}${a.asin}${a.qty != null ? ` ×${a.qty}` : ''}`).join('\n')}
                                        >
                                            {run.asins[0].name || run.asins[0].asin}
                                            {run.asins.length > 1 && <span className="text-gray-400"> +{run.asins.length - 1} more</span>}
                                            <span className="font-mono text-xs text-gray-400 ml-1.5">{run.asins[0].asin}</span>
                                        </span>
                                    )}
                                    {run.shippingMethod && (
                                        <span
                                            className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-medium whitespace-nowrap flex-shrink-0"
                                            title={run.shippingMethod}
                                        >
                                            {humanizeShippingMode(run.shippingMethod)}
                                        </span>
                                    )}
                                </button>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    {run.status === 'ACTIVE' && run.inbound_plan_id && (
                                        <button
                                            onClick={(e) => cancelHistoricalPlan(run.inbound_plan_id, e)}
                                            className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <span className="text-xs text-gray-400 font-mono">
                                        #{run.id}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* === Viewing Historical Banner === */}
            {viewingHistorical && analysisResult && (
                <div className={`rounded-lg p-3 mb-6 flex items-center justify-between text-sm ${
                    analysisResult.status === 'CANCELLED' ? 'bg-red-50 border border-red-200 text-red-700' :
                    analysisResult.status === 'CONFIRMED' ? 'bg-blue-50 border border-blue-200 text-blue-700' :
                    'bg-gray-50 border border-gray-200 text-gray-600'
                }`}>
                    <div className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4" />
                        Viewing {analysisResult.status?.toLowerCase()} run #{analysisResult.runId}
                        {analysisResult.status === 'CANCELLED' && ' — this plan was cancelled'}
                        {analysisResult.status === 'SUPERSEDED' && ' — this plan was replaced by a newer analysis'}
                        {analysisResult.status === 'CONFIRMED' && ' — this plan was confirmed and sent to Amazon'}
                    </div>
                    <button
                        onClick={() => { setAnalysisResult(null); setViewingHistorical(false); }}
                        className="text-xs underline hover:no-underline"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* === Item Selection Section === */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Items</h2>

                {/* PO Selector */}
                <div className="flex items-end gap-3 mb-4">
                    <div className="flex-1 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Load from Purchase Order</label>
                        <select
                            value={selectedPO}
                            onChange={e => setSelectedPO(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        >
                            <option value="">Select a PO...</option>
                            {recentPOs.map(po => (
                                <option key={po.poNumber} value={po.poNumber}>
                                    {po.poNumber} — {po.poDate} ({po.itemCount} items, {po.totalQuantity} units)
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={loadPOItems}
                        disabled={!selectedPO || loadingPO}
                        className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loadingPO ? 'Loading...' : 'Load PO Items'}
                    </button>
                </div>

                {/* Product Search */}
                <div className="flex items-end gap-3 mb-4">
                    <div className="flex-1 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add Individual Items</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                placeholder="Search by ASIN, SKU, or product name..."
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pr-10"
                            />
                            <button onClick={handleSearch} className="absolute right-2 top-1/2 -translate-y-1/2">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                    <div className="mb-4 border rounded-md divide-y max-h-48 overflow-auto">
                        {searchResults.map((p, idx) => (
                            <button
                                key={`${p.asin}-${p.sku || idx}`}
                                onClick={() => addSearchItem(p)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex justify-between"
                            >
                                <span className="truncate">{p.asin} — {p.name}</span>
                                <span className="text-gray-500 ml-2 flex-shrink-0">${p.selling_price}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Items Table */}
                {items.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500">ASIN</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Units/Box</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Weight (lbs)</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">COGS</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">FBA Fee</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Referral Fee</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Sell Price</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Est. Margin</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {items.map((item, idx) => {
                                    const estMargin = (item.sellingPrice || 0) - (item.cogs || 0) - (item.fbaFees || 0) - (item.referralFee || 0);
                                    const estMarginPct = item.sellingPrice > 0 ? (estMargin / item.sellingPrice) * 100 : 0;
                                    const boxCount = item.unitsPerBox > 0 ? Math.ceil(item.quantity / item.unitsPerBox) : null;
                                    return (
                                        <React.Fragment key={`${item.asin}-${idx}`}>
                                        <tr className={item.missingWeight || item.missingCogs ? 'bg-yellow-50/50' : ''}>
                                            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                                                <div className="flex items-center gap-1">
                                                    {item.asin}
                                                    {(item.missingWeight || item.missingCogs) && (
                                                        <ExclamationTriangleIcon className="inline h-4 w-4 text-yellow-500" title={
                                                            [item.missingWeight && 'Missing weight', item.missingCogs && 'Missing COGS'].filter(Boolean).join(', ')
                                                        } />
                                                    )}
                                                    {item.fcInventory && item.fcInventory.length > 0 && (
                                                        <button
                                                            onClick={() => setShowingFcInventory(showingFcInventory === item.asin ? null : item.asin)}
                                                            className={`p-0.5 rounded ${showingFcInventory === item.asin ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-gray-600'}`}
                                                            title={`${item.fcInventory.reduce((s, fc) => s + fc.quantity, 0)} units across ${item.fcInventory.length} FCs`}
                                                        >
                                                            <MapPinIcon className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 max-w-xs truncate">{item.name}</td>
                                            <td className="px-3 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={e => updateQuantity(idx, e.target.value)}
                                                    className="w-16 rounded border-gray-300 text-sm text-right"
                                                    disabled={viewingHistorical}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.unitsPerBox || ''}
                                                        onChange={e => updateUnitsPerBox(idx, e.target.value)}
                                                        placeholder="—"
                                                        className="w-14 rounded border-gray-300 text-sm text-right"
                                                        disabled={viewingHistorical}
                                                    />
                                                    <button
                                                        onClick={() => setEditingBoxDims(editingBoxDims === item.asin ? null : item.asin)}
                                                        className={`p-0.5 rounded ${editingBoxDims === item.asin ? 'text-indigo-600 bg-indigo-50' : 'text-gray-400 hover:text-gray-600'}`}
                                                        title="Edit box dimensions"
                                                    >
                                                        <CubeIcon className="h-4 w-4" />
                                                    </button>
                                                    {boxCount && (
                                                        <span className="text-xs text-gray-400 ml-0.5">{boxCount}box</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right">{item.weightLbs ? item.weightLbs.toFixed(2) : '—'}</td>
                                            <td className="px-3 py-2 text-right">{item.cogs ? `$${item.cogs.toFixed(2)}` : '—'}</td>
                                            <td className="px-3 py-2 text-right">{item.fbaFees ? `$${item.fbaFees.toFixed(2)}` : '—'}</td>
                                            <td className="px-3 py-2 text-right">{item.referralFee ? `$${item.referralFee.toFixed(2)}` : '—'}</td>
                                            <td className="px-3 py-2 text-right">{item.sellingPrice ? `$${item.sellingPrice.toFixed(2)}` : '—'}</td>
                                            <td className={`px-3 py-2 text-right ${marginColor(estMarginPct)}`}>
                                                {item.sellingPrice ? `$${estMargin.toFixed(2)} (${estMarginPct.toFixed(0)}%)` : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {!viewingHistorical && (
                                                    <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600">
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {editingBoxDims === item.asin && (
                                            <tr className="bg-gray-50/80">
                                                <td colSpan={2} className="px-3 py-2 text-xs text-gray-500 text-right">Box Dimensions:</td>
                                                <td colSpan={9} className="px-3 py-2">
                                                    <div className="flex items-center gap-3 text-xs">
                                                        <label className="flex items-center gap-1">
                                                            <span className="text-gray-500">L</span>
                                                            <input type="number" step="0.1" value={item.boxLengthIn || ''} onChange={e => updateBoxDim(idx, 'boxLengthIn', e.target.value)}
                                                                className="w-16 rounded border-gray-300 text-xs text-right" placeholder="in" disabled={viewingHistorical} />
                                                        </label>
                                                        <label className="flex items-center gap-1">
                                                            <span className="text-gray-500">W</span>
                                                            <input type="number" step="0.1" value={item.boxWidthIn || ''} onChange={e => updateBoxDim(idx, 'boxWidthIn', e.target.value)}
                                                                className="w-16 rounded border-gray-300 text-xs text-right" placeholder="in" disabled={viewingHistorical} />
                                                        </label>
                                                        <label className="flex items-center gap-1">
                                                            <span className="text-gray-500">H</span>
                                                            <input type="number" step="0.1" value={item.boxHeightIn || ''} onChange={e => updateBoxDim(idx, 'boxHeightIn', e.target.value)}
                                                                className="w-16 rounded border-gray-300 text-xs text-right" placeholder="in" disabled={viewingHistorical} />
                                                        </label>
                                                        <label className="flex items-center gap-1">
                                                            <span className="text-gray-500">Wt</span>
                                                            <input type="number" step="0.1" value={item.boxWeightLbs || ''} onChange={e => updateBoxDim(idx, 'boxWeightLbs', e.target.value)}
                                                                className="w-16 rounded border-gray-300 text-xs text-right" placeholder="lbs" disabled={viewingHistorical} />
                                                        </label>
                                                        {!viewingHistorical && <span className="text-gray-400">Auto-saves</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {showingFcInventory === item.asin && item.fcInventory && item.fcInventory.length > 0 && (
                                            <tr className="bg-blue-50/50">
                                                <td colSpan={2} className="px-3 py-2 text-xs text-gray-500 text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <MapPinIcon className="h-3.5 w-3.5" />
                                                        Current FCs:
                                                    </div>
                                                </td>
                                                <td colSpan={9} className="px-3 py-2">
                                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                                                        {[...item.fcInventory].sort((a, b) => b.quantity - a.quantity).map(fc => (
                                                            <span key={fc.fcCode} className="text-gray-600">
                                                                <span className="font-medium text-gray-800">{fc.fcCode}</span>
                                                                <span className="text-gray-400 ml-1">({fc.city}, {fc.state})</span>
                                                                <span className="font-semibold text-indigo-600 ml-1">{fc.quantity}</span>
                                                            </span>
                                                        ))}
                                                        <span className="text-gray-500 font-medium">
                                                            Total: {item.fcInventory.reduce((s, fc) => s + fc.quantity, 0)} units across {item.fcInventory.length} FCs
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Summary Row */}
                        <div className="flex gap-6 mt-3 text-sm text-gray-600 bg-gray-50 rounded p-3">
                            <span><strong>{totalItems}</strong> items</span>
                            <span><strong>{totalUnits}</strong> units</span>
                            <span><strong>{totalWeight.toFixed(1)}</strong> lbs total</span>
                            {totalBoxes > 0 && <span><strong>{totalBoxes}</strong> boxes</span>}
                            {warningCount > 0 && (
                                <span className="text-yellow-600">
                                    <ExclamationTriangleIcon className="inline h-4 w-4 mr-1" />
                                    {warningCount} items with missing data
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* === Analyze Button === */}
            {items.length > 0 && !viewingHistorical && (
                <div className="mb-6 flex items-center gap-3">
                    <button
                        onClick={analysisResult ? reAnalyze : analyzeShipment}
                        disabled={analyzing}
                        title={analyzing ? 'An analysis is already running — wait for it to finish' : ''}
                        className="w-full sm:w-auto px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {analyzing ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                {analysisStatus}
                            </>
                        ) : (
                            <>
                                <TruckIcon className="h-5 w-5" />
                                {analysisResult ? 'Re-analyze Shipping Options' : 'Get Shipping Options'}
                            </>
                        )}
                    </button>
                    {analysisResult && !analyzing && (
                        <span className="text-xs text-gray-500">Supersedes old plan and creates a new analysis</span>
                    )}
                </div>
            )}

            {/* Loading state */}
            {analyzing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-700">
                    {analysisStatus}
                </div>
            )}

            {/* Task 68: persistent, dismissable failure banner (see AsinModePanel counterpart). */}
            {analysisError && !analyzing && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>Analysis failed: {analysisError}</span>
                    </div>
                    <button onClick={() => setAnalysisError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* === Placement Option Comparison === */}
            {analysisResult && analysisResult.placementOptions && (
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Placement Options ({analysisResult.placementOptions.length})
                            </h2>
                            {analysisResult.runId && (
                                <span className="text-xs text-gray-400 font-mono">Run #{analysisResult.runId}</span>
                            )}
                            {analysisResult.inboundPlanId && (
                                <span
                                    className="text-xs text-gray-400 font-mono cursor-pointer hover:text-blue-500"
                                    title="Click to copy Workflow ID"
                                    onClick={() => {
                                        navigator.clipboard.writeText(analysisResult.inboundPlanId);
                                    }}
                                >
                                    {analysisResult.inboundPlanId}
                                </span>
                            )}
                            {analysisResult.status && statusBadge(analysisResult.status)}
                        </div>
                        <div className="flex items-center gap-2">
                            {analysisResult.status === 'ACTIVE' && selectedPlacement && !viewingHistorical && (
                                <button
                                    // Task 87: NEVER `onClick={confirmPlan}` -- confirmPlan's first
                                    // parameter is `acknowledgeUsedCondition`, and a bare handler
                                    // reference hands React's click SyntheticEvent straight to it. The
                                    // event was then truthy (skipping the confirmation dialog) AND
                                    // landing verbatim in the POST body, where its circular
                                    // DOM-fiber back-references broke JSON.stringify ("Converting
                                    // circular structure to JSON ... HTMLButtonElement"). Always call
                                    // with explicit arguments.
                                    onClick={() => confirmPlan()}
                                    disabled={confirming || (poScores.length > 0 && selectedPoScore && !selectedPoScore.confirmAllowed)}
                                    title={poScores.length > 0 && selectedPoScore && !selectedPoScore.confirmAllowed
                                        ? (selectedPoScore.confirmDisabledReason || MIAMI_GATE_REASON) : ''}
                                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {confirming ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            Confirming...
                                        </>
                                    ) : 'Confirm & Send to Amazon'}
                                </button>
                            )}
                            {analysisResult.status === 'ACTIVE' && !viewingHistorical && (
                                <button
                                    onClick={cancelPlan}
                                    disabled={confirming}
                                    className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                                >
                                    Cancel Plan
                                </button>
                            )}
                        </div>
                    </div>

                    {analysisResult.error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-700">
                            {analysisResult.error}
                        </div>
                    )}

                    {/* Recommendation Brief */}
                    {analysisResult.recommendationReason && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                    <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-indigo-900 mb-1">AI Recommendation</h3>
                                    <p className="text-sm text-indigo-800">{analysisResult.recommendationReason}</p>

                                    {/* Current inventory summary */}
                                    {items.some(i => i.fcInventory && i.fcInventory.length > 0) && (
                                        <div className="mt-3 pt-3 border-t border-indigo-200">
                                            <p className="text-xs font-medium text-indigo-700 mb-1">Current Inventory Distribution</p>
                                            {items.filter(i => i.fcInventory && i.fcInventory.length > 0).map(item => {
                                                const totalInv = item.fcInventory.reduce((s, fc) => s + fc.quantity, 0);
                                                const topFcs = [...item.fcInventory].sort((a, b) => b.quantity - a.quantity).slice(0, 5);
                                                return (
                                                    <div key={`brief-inv-${item.asin}`} className="text-xs text-indigo-600 mb-1">
                                                        <span className="font-medium">{item.asin}</span>
                                                        <span className="text-indigo-400 ml-1">({totalInv} units across {item.fcInventory.length} FCs)</span>
                                                        <span className="ml-1">
                                                            {topFcs.map(fc => `${fc.fcCode} ${fc.quantity}`).join(', ')}
                                                            {item.fcInventory.length > 5 && ` +${item.fcInventory.length - 5} more`}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Task 51: single shared results layout — banner, CONFIRMED actions,
                        position header, draw-quality strip, option cards, FC distribution map
                        and per-ASIN FC distribution all live in ShipmentResultsView. The
                        PO-mode detailed breakdown renders through it as children. */}
                    <ShipmentResultsView
                        analysisResult={analysisResult}
                        items={items}
                        totalUnits={totalUnits}
                        selectedOptionId={selectedPlacement}
                        onSelectOption={(id) => {
                            setSelectedPlacement(id);
                            // Persisted so the Shipment Runs FC column shows what the operator
                            // chose rather than what we recommended -- and so reopening this run
                            // restores their choice. Fire-and-forget by contract.
                            rememberSelectedPlacement(analysisResult?.runId, id);
                        }}
                        onCancelConfirmed={() => setShowCancelConfirmed(true)}
                        expandedPlacements={expandedPlacements}
                        onToggleDetails={toggleExpanded}
                        onAnalysisUpdate={setAnalysisResult}
                    >
                    {/* === Detailed Breakdown === */}
                    {analysisResult.placementOptions.map(po => (
                        expandedPlacements[po.placementOptionId] && (
                            <div key={`detail-${po.placementOptionId}`} className="bg-white shadow rounded-lg p-6 mb-6">
                                <h3 className="text-md font-semibold text-gray-900 mb-4">
                                    Detailed Breakdown — {po.shipments?.length} shipment{(po.shipments?.length || 0) !== 1 ? 's' : ''}
                                    <span className="text-sm font-normal text-gray-500 ml-2">
                                        (Total: ${(po.totalCost || 0).toFixed(2)})
                                    </span>
                                </h3>

                                {po.shipments?.map(shipment => (
                                    <div key={shipment.shipmentId} className="mb-6 last:mb-0">
                                        <div className="flex items-center gap-3 mb-2 bg-gray-50 rounded p-2">
                                            <span className="font-medium text-gray-900">
                                                {shipment.destinationFc || shipment.shipmentId}
                                            </span>
                                            {shipment.destinationCity && (
                                                <span className="text-sm text-gray-500">
                                                    {shipment.destinationCity}, {shipment.destinationState}
                                                </span>
                                            )}
                                            <span className="ml-auto text-sm">
                                                {shipment.shippingMode} via {shipment.carrierName || '—'}
                                            </span>
                                            <span className="font-medium">${(shipment.transportCost || 0).toFixed(2)}</span>
                                        </div>

                                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-500">ASIN</th>
                                                    <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Weight</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Ship Cost</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">COGS</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">FBA Fee</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Referral Fee</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Sell Price</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Margin</th>
                                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Margin %</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {po.perAsinBreakdown?.filter(b => b.shipmentId === shipment.shipmentId).map(b => (
                                                    <tr key={`${b.asin}-${b.shipmentId}`} className={marginBg(b.marginPercent)}>
                                                        <td className="px-3 py-2 font-mono text-xs">{b.asin}</td>
                                                        <td className="px-3 py-2 max-w-xs truncate">{b.name}</td>
                                                        <td className="px-3 py-2 text-right">{b.quantity}</td>
                                                        <td className="px-3 py-2 text-right">{b.weightLbs?.toFixed(2) || '—'}</td>
                                                        <td className="px-3 py-2 text-right">${(b.allocatedShippingCost || 0).toFixed(2)}</td>
                                                        <td className="px-3 py-2 text-right">{b.cogs ? `$${b.cogs.toFixed(2)}` : '—'}</td>
                                                        <td className="px-3 py-2 text-right">{b.fbaFees ? `$${b.fbaFees.toFixed(2)}` : '—'}</td>
                                                        <td className="px-3 py-2 text-right">{b.referralFee ? `$${b.referralFee.toFixed(2)}` : '—'}</td>
                                                        <td className="px-3 py-2 text-right">{b.sellingPrice ? `$${b.sellingPrice.toFixed(2)}` : '—'}</td>
                                                        <td className={`px-3 py-2 text-right font-medium ${marginColor(b.marginPercent)}`}>
                                                            {b.margin != null ? `$${b.margin.toFixed(2)}` : '—'}
                                                        </td>
                                                        <td className={`px-3 py-2 text-right font-medium ${marginColor(b.marginPercent)}`}>
                                                            {b.marginPercent != null ? `${b.marginPercent.toFixed(1)}%` : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}

                                {/* FC Inventory sidebar */}
                                {items.some(i => i.fcInventory && i.fcInventory.length > 0) && (
                                    <div className="mt-6 pt-4 border-t">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Current FC Inventory</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {items.filter(i => i.fcInventory && i.fcInventory.length > 0).map(item => (
                                                <div key={`inv-${item.asin}`} className="border rounded p-2 text-xs">
                                                    <div className="font-medium truncate mb-1">{item.asin} — {item.name}</div>
                                                    {item.fcInventory.map(fc => (
                                                        <div key={fc.fcCode} className="flex justify-between text-gray-500">
                                                            <span>{fc.fcCode} ({fc.city}, {fc.state})</span>
                                                            <span className="font-medium text-gray-700">{fc.quantity}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    ))}
                    </ShipmentResultsView>
                </div>
            )}

            {/* Task 80: explicit "I intend to ship used condition" gate. Opened only by the
                server refusing a confirm, so the SKUs listed are the real ones on the plan. */}
            <UsedConditionAckModal
                open={!!usedAckItems}
                items={usedAckItems || []}
                busy={confirming}
                onCancel={() => { setUsedAckItems(null); setUsedAckRetry(null); }}
                onAcknowledge={() => { if (usedAckRetry) usedAckRetry(); }}
            />
            <CancelConfirmedShipmentModal
                open={showCancelConfirmed}
                onClose={() => setShowCancelConfirmed(false)}
                planId={analysisResult?.inboundPlanId}
                runLabel={analysisResult?.runId ? `Run #${analysisResult.runId}` : null}
                units={totalUnits}
                asins={items.map(i => ({ asin: i.asin, qty: i.quantity }))}
                confirmedAt={analysisResult?.confirmedAt}
                onCancelled={() => {
                    setShowCancelConfirmed(false);
                    setAnalysisResult(prev => (prev ? { ...prev, status: 'CANCELLED' } : null));
                    setViewingHistorical(true);
                    toast.success('Shipment cancelled.');
                    loadPreviousRuns();
                }}
            />
            </>
            )}
        </div>
    );
}

// === ASIN Mode Panel (Task 7) ===
// Self-contained: manages its own item list, analyze/confirm/cancel flow, drawer and fallback
// ladder. Deliberately does not share state with the PO-mode flow above (PO mode stays untouched)
// but reuses the same backend confirm/cancel/supersede endpoints (planId-based, mode-agnostic).
/**
 * Task 80 — the used-condition acknowledgement modal (invariant 4).
 *
 * The backend refuses any confirm whose plan carries a non-New SKU, answering 400 with
 * `usedConditionBlock: true` and the exact offending SKUs/FNSKUs. This modal renders that server
 * answer and requires an explicit tick before the confirm is retried with
 * `acknowledgeUsedCondition: true`. Deliberately NOT a `window.confirm`: the operator has to see
 * which FNSKU would print and tick a box, so "I meant to" and "I didn't notice" can't be the same
 * gesture. Every acknowledged override is logged server-side.
 */
export function UsedConditionAckModal({ open, items, onCancel, onAcknowledge, busy }) {
    const [ticked, setTicked] = useState(false);
    useEffect(() => { if (!open) setTicked(false); }, [open]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                <div className="px-5 py-4 border-b">
                    <h3 className="text-base font-semibold text-red-700 flex items-center gap-2">
                        <span aria-hidden="true">&#9888;</span> This plan would ship USED condition
                    </h3>
                </div>
                <div className="px-5 py-4 space-y-3 text-sm text-gray-700">
                    <p>
                        Amazon derives the FNSKU and the condition line on every item label from the plan&apos;s
                        MSKU — there is no way to print a New label for these SKUs. Confirming sends them as-is.
                    </p>
                    <ul className="space-y-1">
                        {(items || []).map(it => (
                            <li key={it.sku} className="flex items-center gap-2 text-xs">
                                <span className="font-mono text-gray-900">{it.sku}</span>
                                <span className="font-mono text-gray-500">{it.fnsku || '—'}</span>
                                <span className="text-red-700 font-semibold">{it.conditionLabel}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="text-xs text-gray-500">
                        The safe fix is to cancel, re-create the plan (it will now resolve the New-condition SKU),
                        and confirm that one instead.
                    </p>
                    <label className="flex items-start gap-2 pt-1">
                        <input
                            type="checkbox"
                            checked={ticked}
                            onChange={e => setTicked(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span className="text-sm font-medium text-red-700">
                            I intend to ship used condition
                        </span>
                    </label>
                </div>
                <div className="px-5 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end gap-3">
                    <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
                        Cancel
                    </button>
                    <button
                        onClick={onAcknowledge}
                        disabled={!ticked || busy}
                        className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? 'Confirming…' : 'Confirm anyway'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Task 80: the server's used-condition refusal, or null when the error was something else. */
export function usedConditionBlockFrom(err) {
    const data = err?.response?.data;
    return data && data.usedConditionBlock ? (data.usedConditionItems || []) : null;
}

// ---- Task 87: explicit whitelist serializers for the confirm request bodies. ----
//
// The confirm endpoints only ever need a handful of primitive fields -- never the score/option
// objects, the analysisResult, or a whole row from state. Building the body through a pure function
// (rather than an inline object literal at each of the three confirm call sites) makes it
// structurally impossible for a future change to accidentally spread a non-serializable value (a
// synthetic event, a DOM ref, an axios response with circular config/request back-references) into
// what axios hands to JSON.stringify.

/** POST /api/shipment-planner/confirm body -- used by both the PO-mode and ASIN-mode partnered
 * confirm buttons (confirmPlan / confirmSelected) and the Recommended-board confirm. */
export function buildConfirmRequestBody({ planId, placementOptionId, acknowledgeUsedCondition = false }) {
    return {
        planId: planId ?? null,
        placementOptionId: placementOptionId ?? null,
        // coerceAcknowledgeFlag, not a bare `!!` -- an event/DOM-node-shaped value must resolve to
        // false, not to the true a plain double-negation would give it.
        acknowledgeUsedCondition: coerceAcknowledgeFlag(acknowledgeUsedCondition),
    };
}

/** POST /api/shipment-planner/confirm-own-carrier body (task 73). `selections` is reduced to
 * plain shipmentId -> transportationOptionId string pairs -- any entry that isn't exactly that
 * shape (e.g. a whole shipment/option object landing in state by mistake) is dropped rather than
 * carried through. */
export function buildOwnCarrierConfirmRequestBody({ planId, placementOptionId, selections, pickupDate,
    acknowledgeUsedCondition = false }) {
    const cleanSelections = {};
    Object.entries(selections || {}).forEach(([shipmentId, optionId]) => {
        if (typeof shipmentId !== 'string' && typeof shipmentId !== 'number') return;
        if (typeof optionId !== 'string' && typeof optionId !== 'number') return;
        cleanSelections[shipmentId] = String(optionId);
    });
    return {
        planId: planId ?? null,
        placementOptionId: placementOptionId ?? null,
        selections: cleanSelections,
        pickupDate: pickupDate || null,
        acknowledged: true,
        acknowledgeUsedCondition: coerceAcknowledgeFlag(acknowledgeUsedCondition),
    };
}

/**
 * PO SFP split (spec 2026-10-10 §E): non-blocking warning — received SFP units on this PO not yet
 * racked in an SFP bin. They are never planned into FBA, but the warehouse must set them aside.
 */
export function UnrackedSfpBadge({ units }) {
    if (!units || units <= 0) return null;
    return (
        <span className="inline-flex items-center rounded-full bg-orange-100 text-orange-800 text-[10px] font-medium px-2 py-0.5"
              title={`${units} SFP unit(s) received but not racked — set aside before packing. Rack them on Receive Inventory or Manage SFP Inventory → Put away.`}>
            {units} SFP unracked
        </span>
    );
}

/** Import-modal banner: SFP units on the PO received but not racked. Link opens a new tab so the open planner/draft survives. */
export function UnrackedSfpWarning({ poItems }) {
    const unracked = (poItems || []).reduce((s, l) => s + (l.sfpRackable || 0), 0);
    if (unracked <= 0) return null;
    return (
        <div className="mb-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
            {unracked} SFP unit(s) on this PO are received but not racked — set them aside before packing.{' '}
            <a href="/manage-sfp-inventory" target="_blank" rel="noopener noreferrer"
               title="Rack them on Manage SFP Inventory (opens in a new tab)" className="underline">Rack them</a>
        </div>
    );
}

/**
 * Task 80 — read-only "what will the item label say" badge.
 *
 * Owner rule: "all shipments created from a PO - are NEW products - THEY MUST NEVER HAVE SHIPMENTS
 * CREATED AS USED." Amazon's createMarketplaceItemLabels request has no condition parameter — the
 * FNSKU and the "New - " / "Used - Good - " line are derived entirely from the MSKU the plan was
 * created with. So this is the operator's only chance to see a used FNSKU coming, and used renders
 * red with a warning icon everywhere it appears.
 *
 * <p>Task 84: a line with NO condition data (an older run, a payload written before task 80, or a
 * resolver that came back empty) is neither New nor used -- it renders a neutral "condition unknown"
 * chip. Unknown is not blocked: only an explicit {@code blockedReason} stops a line, so a line whose
 * condition simply failed to resolve stays visible and selectable instead of vanishing.
 */
/**
 * Owner 2026-09-06: the PO list's SFP-listing badge. Green "SFP Ready" when every line ASIN has
 * an SFP-prefixed seller-fulfilled listing (report-synced, i.e. Amazon has processed it), amber
 * with the missing ASINs when any lacks one — the 6-hourly sweep creates those automatically, so
 * a persistent amber means the ASIN has no MAP on file. Unknown readiness renders NOTHING: an
 * absent badge must never masquerade as a green one.
 */
export function SfpReadyBadge({ sfpReady, sfpMissingAsins }) {
    if (sfpReady === true) {
        return (
            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-medium px-2 py-0.5"
                  title="Every ASIN on this PO has an SFP seller-fulfilled listing">
                SFP Ready
            </span>
        );
    }
    if (sfpReady === false) {
        const missing = sfpMissingAsins || [];
        return (
            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium px-2 py-0.5"
                  title={`No SFP listing yet: ${missing.join(', ')}. The sweep creates these automatically every 6h — an ASIN that stays amber has no MAP on file.`}>
                SFP: {missing.length} missing
            </span>
        );
    }
    return null;
}

export function ConditionBadge({ conditionLabel, fnsku, newCondition, blockedReason, className = '' }) {
    if (blockedReason) {
        return (
            <span className={`inline-flex items-center gap-1 text-xs text-red-700 ${className}`} title={blockedReason}>
                <span aria-hidden="true">&#9888;</span>
                <span>{blockedReason}</span>
            </span>
        );
    }
    if (!conditionLabel && !fnsku) {
        return (
            <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 border border-gray-300 ${className}`}
                title="this line's condition could not be resolved — it is not blocked, but nothing here proves it is New"
            >
                condition unknown
            </span>
        );
    }
    const isUsed = newCondition === false;
    return (
        <span
            className={`inline-flex items-center gap-1 text-xs ${isUsed ? 'text-red-700 font-semibold' : 'text-gray-600'} ${className}`}
            title={isUsed ? 'This item would ship on a USED-condition SKU — Amazon prints the condition from the MSKU' : undefined}
        >
            {isUsed && <span aria-hidden="true">&#9888;</span>}
            <span>{conditionLabel || 'unknown condition'}</span>
            {fnsku && <span className="font-mono text-gray-500">{fnsku}</span>}
        </span>
    );
}

/**
 * Task 80 — the per-row condition selector for manually-entered ASINs (owner: "If a user inputs an
 * asin into the ASIN input box, the system should ask the condition before proceeding with shipment
 * plans"). Lists only conditions that actually HAVE a SKU for this ASIN, New pre-selected when one
 * exists. When no New listing exists the select opens on an empty "choose a condition…" option, so
 * shipping used is always a deliberate act — never a fallback.
 */
export function ConditionCell({ asin, entry, chosenSku, onChange }) {
    if (entry === undefined) return <span className="text-xs text-gray-400">checking…</span>;
    // Null = the server returned no entry for this ASIN. Say so plainly rather than claiming the
    // ASIN has no listing; the backend still refuses to analyze anything it can't resolve to New.
    if (entry === null) return <span className="text-xs text-gray-400">—</span>;
    const options = entry?.options || [];
    if (options.length === 0) {
        return (
            <ConditionBadge blockedReason={entry?.blockedReason
                || 'no listing exists for this ASIN — create the listing before shipping'} />
        );
    }
    const chosen = options.find(o => o.sku === chosenSku);
    const isUsed = !!chosen && !chosen.newCondition;
    const mustChoose = !chosen;
    return (
        <div className="flex flex-col gap-0.5">
            <select
                aria-label={`Condition for ${asin}`}
                value={chosenSku || ''}
                onChange={e => onChange(e.target.value)}
                className={`rounded text-xs py-1 ${mustChoose || isUsed
                    ? 'border-red-400 text-red-700 bg-red-50 font-semibold'
                    : 'border-gray-300 text-gray-700'}`}
            >
                {mustChoose && <option value="">choose a condition…</option>}
                {options.map(o => (
                    <option key={o.sku} value={o.sku}>
                        {o.conditionLabel}{o.fnsku ? ` · ${o.fnsku}` : ''}
                    </option>
                ))}
            </select>
            {mustChoose ? (
                <span className="text-xs text-red-700">
                    &#9888; {entry?.blockedReason || 'no New-condition SKU exists for this ASIN — create the listing before shipping'}
                </span>
            ) : (
                <span className={`text-xs font-mono ${isUsed ? 'text-red-700' : 'text-gray-500'}`}>
                    {isUsed && <span aria-hidden="true">&#9888; </span>}{chosen.sku}
                </span>
            )}
        </div>
    );
}

/**
 * Task 91 (owner, verbatim: "on the shipment planner page - there needs to be a search bar") --
 * the ASIN-mode search-and-add bar. Sits above the staged rows as the fast path to find and add a
 * product by ASIN, seller SKU, item code, FNSKU, or product name -- the alternative to importing a
 * PO or pasting a list. Reuses {@code ConditionBadge} (task 80) so New vs. Used is visible on every
 * result, and refuses to add a blocked ASIN (no New-condition SKU) at all -- the same invariant
 * enforced everywhere else in the planner, just enforced one step earlier here.
 *
 * Deliberately additive, not a replacement: the existing "Add ASIN" quick-add row below (and PO
 * import / paste-list / drafts bar) keeps working exactly as before.
 */
export function SearchAddBar({ onAdd }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [moreAvailable, setMoreAvailable] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const [qtyByAsin, setQtyByAsin] = useState({});
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    // Debounced (~300ms) live search -- a stale in-flight response (a fast typist outrunning the
    // network) is dropped via requestIdRef rather than raced against the latest one.
    useEffect(() => {
        clearTimeout(debounceRef.current);
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            setMoreAvailable(false);
            setLoading(false);
            setSearched(false);
            return undefined;
        }
        setLoading(true);
        debounceRef.current = setTimeout(() => {
            const myRequestId = ++requestIdRef.current;
            ApiClient.get(`/api/shipment-planner/product-search-detailed?q=${encodeURIComponent(q)}`)
                .then(res => {
                    if (myRequestId !== requestIdRef.current) return;
                    setResults((res.data && res.data.items) || []);
                    setMoreAvailable(!!(res.data && res.data.moreAvailable));
                })
                .catch(err => {
                    console.error('Product search failed:', err);
                    if (myRequestId !== requestIdRef.current) return;
                    setResults([]);
                    setMoreAvailable(false);
                })
                .finally(() => {
                    if (myRequestId !== requestIdRef.current) return;
                    setLoading(false);
                    setSearched(true);
                });
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query]);

    useEffect(() => { setHighlighted(0); }, [results]);

    const addResult = (product) => {
        if (!product || product.blocked) return;
        const qty = parseInt(qtyByAsin[product.asin], 10) || 1;
        onAdd(product, qty);
        setQuery('');
        setResults([]);
        setSearched(false);
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (e.key === 'ArrowDown') {
            if (results.length === 0) return;
            e.preventDefault();
            setOpen(true);
            setHighlighted(i => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            if (results.length === 0) return;
            e.preventDefault();
            setHighlighted(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            if (results.length === 0) return;
            e.preventDefault();
            addResult(results[Math.min(highlighted, results.length - 1)]);
        }
    };

    const showDropdown = open && query.trim().length >= 2;

    return (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
            <label htmlFor="planner-search-add" className="block text-sm font-medium text-gray-900 mb-1">
                Find &amp; add a product
            </label>
            <div className="relative max-w-2xl">
                <MagnifyingGlassIcon className={`h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${loading ? 'animate-pulse' : ''}`} />
                <input
                    id="planner-search-add"
                    type="text"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls="planner-search-add-results"
                    aria-autocomplete="list"
                    autoComplete="off"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search by ASIN, SKU, item code, FNSKU, or product name"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pl-10 pr-3 py-2.5"
                />

                {showDropdown && (
                    <div id="planner-search-add-results" className="absolute z-20 mt-1 w-full border rounded-md bg-white shadow-lg divide-y max-h-96 overflow-auto">
                        {loading && results.length === 0 && (
                            <div className="px-3 py-3 text-sm text-gray-400">Searching…</div>
                        )}
                        {!loading && searched && results.length === 0 && (
                            <div className="px-3 py-3 text-sm text-gray-500">
                                No products match "{query.trim()}" — try the ASIN or item code.
                            </div>
                        )}
                        {results.map((p, idx) => {
                            const qty = qtyByAsin[p.asin] ?? 1;
                            return (
                                <div
                                    key={p.asin}
                                    className={`px-3 py-2 flex items-center justify-between gap-3 ${idx === highlighted ? 'bg-indigo-50' : ''}`}
                                    onMouseEnter={() => setHighlighted(idx)}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-gray-900 truncate">{p.name}</div>
                                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                                            <span className="font-mono">{p.asin}</span>
                                            {p.itemCode && <span>· item {p.itemCode}</span>}
                                            {p.sku && <span>· SKU {p.sku}</span>}
                                            {p.fbaStock != null && <span>· FBA stock {p.fbaStock}</span>}
                                        </div>
                                        <div className="mt-1">
                                            <ConditionBadge
                                                conditionLabel={p.conditionLabel}
                                                fnsku={p.fnsku}
                                                newCondition={p.newCondition}
                                                blockedReason={p.blocked
                                                    ? (p.blockedReason || 'no New-condition SKU exists for this ASIN — create the listing before shipping')
                                                    : null}
                                            />
                                        </div>
                                    </div>
                                    {p.blocked ? (
                                        <span className="text-xs text-gray-400 flex-shrink-0">can’t add</span>
                                    ) : (
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <input
                                                type="number"
                                                min="1"
                                                aria-label={`Quantity for ${p.asin}`}
                                                value={qty}
                                                onClick={e => e.stopPropagation()}
                                                onChange={e => setQtyByAsin(prev => ({ ...prev, [p.asin]: e.target.value }))}
                                                className="w-14 rounded border-gray-300 text-sm text-right"
                                            />
                                            <button
                                                onClick={() => addResult(p)}
                                                className="px-2 py-1 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {moreAvailable && results.length > 0 && (
                            <div className="px-3 py-1.5 text-xs text-gray-400 bg-gray-50">
                                Showing the top {results.length} matches — refine your search for more.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- Task 96: what the operator SEES while a split runs, and where the results live ----

// Live progress inside the split modal: the headline line plus one row per shipment, ticked off as
// each draft is written. On a failure it stops where it stopped and says so -- the rows themselves
// are the "which ones exist" answer.
function SplitProgressBlock({ progress }) {
    if (!progress) return null;
    const failed = progress.phase === 'failed';
    const done = progress.phase === 'done';
    const tone = failed ? 'border-red-200 bg-red-50' : (done ? 'border-emerald-200 bg-emerald-50' : 'border-indigo-200 bg-indigo-50');
    const headTone = failed ? 'text-red-800' : (done ? 'text-emerald-800' : 'text-indigo-800');
    return (
        <div className={`rounded-md border px-3 py-2 ${tone}`} data-testid="split-progress" aria-live="polite">
            <p className={`text-sm font-semibold ${headTone}`}>{splitProgressLabel(progress)}</p>
            <ul className="mt-1 space-y-0.5">
                {(progress.steps || []).map(step => (
                    <li key={step.index} className="flex items-center gap-2 text-xs text-gray-700">
                        <span className="w-4 text-center">
                            {step.status === 'done' ? '✓' : (step.status === 'failed' ? '✗' : (step.status === 'source' ? '•' : '·'))}
                        </span>
                        <span className={step.status === 'failed' ? 'text-red-700' : ''}>
                            {step.label} · {step.units} unit{step.units !== 1 ? 's' : ''}
                            {step.status === 'done' ? ' — created' : ''}
                            {step.status === 'failed' ? ' — not created' : ''}
                            {step.status === 'source' ? ' — the shipment you started from' : ''}
                            {step.status === 'pending' ? ' — waiting' : ''}
                        </span>
                    </li>
                ))}
            </ul>
            {failed && (
                <p className="mt-1 text-xs text-red-800">{splitPartialFailureNote(progress)}</p>
            )}
            {done && (
                <p className="mt-1 text-xs text-emerald-800">
                    Each shipment is analyzed and confirmed separately — open them from the Split set panel below.
                </p>
            )}
        </div>
    );
}

// The answer to "where do the results appear once it is complete": one row per shipment in the
// set, with its status and its actions. Rendered on the page (not in a modal) whenever the draft
// being edited belongs to a split, so it survives a reload and is reachable from any member.
function SplitSetPanel({ members, activeDraftId, analyzingId, runAll, summary, groupId,
    onSwitch, onAnalyze, onAnalyzeAll, busy }) {
    if (!members || members.length === 0) return null;
    const total = members[0]?.total || members.length;
    const analyzable = members.filter(m => m.exists && !m.outcome?.confirmed);
    // Task 97: this panel shows per-shipment STATUS; the assessment page shows the analyzed
    // RESULTS side by side. The link is only offered once at least one sibling has a run to
    // assess -- an all-draft set has nothing on the server to compare yet.
    const analyzedCount = members.filter(m => m.outcome?.runId != null).length;
    return (
        <div className="border border-indigo-200 bg-indigo-50/40 rounded-lg p-3 mb-4" data-testid="split-set-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-indigo-900">
                        Split set — {members.length} of {total} shipments
                    </h3>
                    <p className="text-xs text-indigo-800/80 mt-0.5">
                        Each shipment is analyzed and confirmed separately. Analyzing one never confirms anything.
                    </p>
                    {groupId && analyzedCount > 0 && (
                        <a
                            href={`/split-sets/${encodeURIComponent(groupId)}`}
                            className="inline-block mt-1 text-xs text-indigo-700 hover:text-indigo-900 underline"
                            data-testid="split-set-assess-link"
                            title="Compare every analyzed shipment in this set side by side"
                        >
                            Assess all {total} shipments on one page →
                        </a>
                    )}
                </div>
                <button
                    onClick={onAnalyzeAll}
                    disabled={busy || analyzable.length === 0}
                    className="px-3 py-1.5 text-xs rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50"
                    title="Run each shipment's analyze one after another — nothing is confirmed"
                >
                    {/* Task 103: queued and running are different states. A set waiting its turn
                        behind another analyze must not claim to be analyzing shipment 1 -- that is
                        exactly what made a 178-second wait look like a hang on 2026-08-21. */}
                    {runAll
                        ? (runAll.queued
                            ? (runAll.queuePosition > 0
                                ? `Queued — ${runAll.queuePosition} ahead…`
                                : 'Queued — starting next…')
                            : `Analyzing ${Math.min(runAll.done + 1, runAll.total)} of ${runAll.total}…`)
                        : `Analyze all in this set (${analyzable.length})`}
                </button>
            </div>
            {summary && (
                <p className="mt-2 text-xs text-indigo-900 bg-white/70 border border-indigo-200 rounded px-2 py-1"
                    data-testid="split-set-summary">
                    {summary}
                </p>
            )}
            <ul className="mt-2 divide-y divide-indigo-100">
                {members.map((m) => {
                    const status = splitMemberStatus({ ...m, analyzing: analyzingId === m.draftId });
                    const isActive = m.draftId === activeDraftId;
                    const statusTone = {
                        confirmed: 'text-emerald-700',
                        analyzed: 'text-indigo-700',
                        analyzing: 'text-indigo-500',
                        failed: 'text-red-700',
                        draft: 'text-gray-500',
                    }[status.kind] || 'text-gray-500';
                    return (
                        <li key={m.draftId} className="py-1.5 flex items-start justify-between gap-3 flex-wrap"
                            data-testid={`split-set-row-${m.index}`}>
                            <div className="min-w-0">
                                <div className="text-xs font-medium text-gray-900">
                                    <span className="inline-block px-1.5 py-0.5 mr-1.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-semibold">
                                        {`${m.index}/${m.total}`}
                                    </span>
                                    {m.name}
                                    {isActive && <span className="ml-1.5 text-[10px] text-indigo-600">(open now)</span>}
                                </div>
                                <div className="text-[11px] text-gray-600">
                                    {m.exists ? splitMemberContentsLabel(m) : 'draft closed — confirmed'}
                                    {' · '}
                                    <span className={statusTone}>{status.label}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {m.exists && !isActive && (
                                    <button
                                        onClick={() => onSwitch(m.draftId)}
                                        disabled={busy}
                                        className="px-2 py-1 text-[11px] rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
                                    >
                                        Switch to it
                                    </button>
                                )}
                                {m.exists && !m.outcome?.confirmed && (
                                    <button
                                        onClick={() => onAnalyze(m)}
                                        disabled={busy || analyzingId === m.draftId}
                                        className="px-2 py-1 text-[11px] rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                    >
                                        {analyzingId === m.draftId ? 'Analyzing…' : 'Analyze'}
                                    </button>
                                )}
                                {m.outcome?.runId != null && (
                                    <a
                                        href={`/shipment-planner?run=${m.outcome.runId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-2 py-1 text-[11px] rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                                    >
                                        Open latest run
                                    </a>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function AsinModePanel({ autoImportDraft, loadedRun }) {
    const [items, setItems] = useState([]); // { asin, sku, name, quantity, sellingPrice }
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [importingDraft, setImportingDraft] = useState(false);

    // Task 12: "Import from PO" modal
    const [showPoModal, setShowPoModal] = useState(false);
    // ?po= deep link (the workspace's back-to-open-lines path); consumed once on mount.
    const [poDeepLinkParams] = useSearchParams();
    const poDeepLinkDoneRef = useRef(false);
    // Owner, 2026-09-01 (landing redesign): "users should have the ability to access the draft,
    // but it should not be loaded by default. Users can pick from a list of drafts". A bare load
    // is a PICKER; deep links (?run=, ?po=, war-room ?fromDraft=1) carry their own intent and
    // land in the working state exactly as before.
    const hasEntryIntent = autoImportDraft
        || !!poDeepLinkParams.get('run') || !!poDeepLinkParams.get('po');
    const [plannerView, setPlannerView] = useState(hasEntryIntent ? 'working' : 'landing');
    const landingMountRef = useRef(!hasEntryIntent);
    const [poList, setPoList] = useState([]);
    const [poListLoading, setPoListLoading] = useState(false);
    const [poListError, setPoListError] = useState(null);
    // PO lifecycle (owner 2026-09-07): whether the list also shows closed POs (history).
    const [poShowClosed, setPoShowClosed] = useState(false);
    const [poSearch, setPoSearch] = useState('');
    const [selectedPoNumber, setSelectedPoNumber] = useState(null);
    const [poItems, setPoItems] = useState([]);
    // Owner, 2026-09-03: products can sell under multiple ASINs (the repair-kit case). Aliases +
    // per-ASIN context are fetched per PO so add-to-plan can open the split modal BEFORE staging.
    // A failed fetch leaves {} — the feature degrades to today's single-ASIN behavior.
    const [asinAliasesByItemCode, setAsinAliasesByItemCode] = useState({});
    // The split modal's working state: aliased rows still awaiting allocation, rows already
    // resolved (plain or allocated), and the current row's per-ASIN quantities.
    const [multiAsinSplit, setMultiAsinSplit] = useState(null);
    const [poItemsLoading, setPoItemsLoading] = useState(false);
    const [poItemsError, setPoItemsError] = useState(null);
    const [poLineChecked, setPoLineChecked] = useState({});
    const [poLineQty, setPoLineQty] = useState({});
    // Owner, 2026-09-01: PO lines can split units off to LOCAL (Orlando SFP) inventory —
    // opt-in per line, gated on an SFP listing existing (created at MAP, never a guessed price).
    const [poLineLocalQty, setPoLineLocalQty] = useState({});
    const [sfpStatusByAsin, setSfpStatusByAsin] = useState({});
    const [localAssignBusy, setLocalAssignBusy] = useState(false);

    // Task 88: the per-ASIN shipping designation (calibrate_asin_packaging_defaults.allowed_modes),
    // canonical 'UPS' | 'LTL' | 'BOTH' or null when nothing is on file. ONE map for the whole page:
    // it drives the PO-modal's per-line control + tab filtering, the confirm-step chips, and the
    // read-only chip on the ASIN-mode rows, and every GET /packaging response feeds it (an ASIN the
    // response omits is recorded as null so it isn't mistaken for "still loading").
    const [allowedModesByAsin, setAllowedModesByAsin] = useState({});
    // The "show all" escape from mode filtering, per PO-modal session.
    const [poShowAllModes, setPoShowAllModes] = useState(false);

    // Task 61 (owner: "in the import po modal, create a toggle for LTL or UPS shipment"): the
    // plan-wide shipment mode. Defaulted from the selected lines' allowed_modes (all LTL_ONLY ->
    // LTL) until the operator touches the toggle; rides with the imported rows through analyze.
    const [shipmentMode, setShipmentMode] = useState('UPS');
    const shipmentModeTouchedRef = useRef(false);
    // Raw GET /packaging rows for the selected PO's ASINs (allowed_modes + pallet columns) --
    // drives the toggle default, the pallet-count estimate, and the LTL form prefill.
    const [poPackagingMetaByAsin, setPoPackagingMetaByAsin] = useState({});
    // Task 65: shipment-level LTL inputs (entered ONCE in the confirm step, not per ASIN) --
    // ONLY what's knowable before Amazon splits the plan: pallet footprint + tare (warehouse
    // facts, prefilled with sane defaults), optional manual freight class (blank = auto density),
    // optional declared-value edit. Pallet count/height/weight are computed per shipment AFTER
    // the split and surfaced on the option cards. savePalletDefault persists the pallet fields to
    // each confirmed ASIN's calibrate_asin_packaging_defaults pallet columns.
    const [ltlForm, setLtlForm] = useState({ ...DEFAULT_LTL_FORM });

    // Milestone C task C5: the operator's pallet measurements, keyed by DESTINATION FC rather than
    // shipmentId -- shipmentIds rotate every re-analyze, FCs recur across re-analyzes of the same
    // composition. Shape: { [destinationFc]: { rows: [...], savedAt } }. Persisted in this draft
    // (buildPlannerDraftPayload/draftPayloadToPanelState) and marked dirty through the SAME
    // autosave path as ltlForm below -- see the debounced effect's dependency array.
    const [palletMeasurements, setPalletMeasurements] = useState({});
    const handleMeasurementsChange = (destinationFc, rows) => {
        if (!destinationFc) return;
        setPalletMeasurements(prev => ({
            ...prev,
            [destinationFc]: { rows: whitelistPalletMeasurementRows(rows), savedAt: new Date().toISOString() },
        }));
    };

    // Task 70b: the day the pallets are ready for the carrier. Sent as readyToShipDate, which the
    // backend turns into the readyToShipWindow on every shipment's transportation configuration.
    // Required for LTL (a freight carrier needs a pickup day); optional/secondary for UPS.
    const [pickupDate, setPickupDate] = useState(() => defaultPickupDate());

    // Task 34 (owner, verbatim: "when the add from PO modal is opened, and the asins selected, the
    // box sizes need to be confirmed that the warehouse manager wants the shipment planner to
    // use") -- step 2 of the Import-from-PO modal: one row per selected line, each must be
    // explicitly confirmed (green tick) before "Add N confirmed to plan" is enabled. Reuses
    // packagingByAsin/boxPresets/the existing packaging popover (openPackagingPopover) rather than
    // rebuilding a parallel form -- see poConfirmSourceLabel and applyPackaging's un-confirm hook.
    const [poConfirmStep, setPoConfirmStep] = useState(false);
    const [poConfirmRows, setPoConfirmRows] = useState([]); // [{asin, name, quantity}]
    const [poConfirmLoading, setPoConfirmLoading] = useState(false);
    // Raw stored-default row per asin (or null when none on file), fetched fresh on entry to step 2
    // purely to compute the "saved default" / "preset X" / "none on file" source label -- distinct
    // from packagingByAsin, which is the live *working* value (may already be edited this session).
    const [poConfirmDefaultsByAsin, setPoConfirmDefaultsByAsin] = useState({});
    const [poConfirmConfirmed, setPoConfirmConfirmed] = useState({}); // {asin: true}
    const [poConfirmSaveDefault, setPoConfirmSaveDefault] = useState({}); // {asin: true} "also save as default" toggle, off by default

    // Task 34: rows added to the plan via the PO-import confirm step render their packaging chip
    // in the green "confirmed" style. Rows added by any other path (manual add, paste-list, war
    // room draft) are untouched -- this map is the only thing that changes their rendering, so
    // extending confirmed-state to another entry path later is a one-line addition, not a rewrite.
    const [packagingConfirmedAsin, setPackagingConfirmedAsin] = useState({});
    // Readable from the draft-reconcile effect without making it re-run on every confirm.
    const packagingConfirmedAsinRef = useRef({});
    packagingConfirmedAsinRef.current = packagingConfirmedAsin;

    // Task 12: "Paste list + total" modal
    const [showAllocateModal, setShowAllocateModal] = useState(false);
    const [allocateAsinText, setAllocateAsinText] = useState('');
    const [allocateTotalQtyInput, setAllocateTotalQtyInput] = useState('');
    const [allocateLoading, setAllocateLoading] = useState(false);
    const [allocateError, setAllocateError] = useState(null);
    const [allocatePreview, setAllocatePreview] = useState(null); // { rows, totalAllocated, note }

    // Task 24 (owner feedback: "there is nothing to indicate i can add a new box configuration
    // ... the process needs to be clear and simple") -- packaging modal, redesigned as a two-step
    // "choose a configuration" / "details" flow. packagingByAsin[asin] is the five-field packaging
    // (known from the table and/or applied via the modal), or absent/undefined when nothing is
    // known yet ("packaging needed" chip). Not saved to the backend as the ASIN's default unless
    // the modal's "Use and make default" action (as opposed to "Use for this shipment") is used.
    const [packagingByAsin, setPackagingByAsin] = useState({});
    // Owner, 2026-09-01: product identity (manufacturer item code + resolved name) per staged
    // ASIN. Its own map, NOT packagingByAsin: identity is display info harvested from every
    // /packaging response UNCONDITIONALLY, while the carton map has confirmation/completeness
    // rules that would silently drop it.
    const [productIdentityByAsin, setProductIdentityByAsin] = useState({});
    const harvestProductIdentity = useCallback((rows) => {
        const found = (rows || []).filter(r => r && r.asin && (r.itemCode || r.productName));
        if (found.length === 0) return;
        setProductIdentityByAsin(prev => {
            const next = { ...prev };
            found.forEach(r => { next[r.asin] = { itemCode: r.itemCode || null, productName: r.productName || null }; });
            return next;
        });
    }, []);
    // Milestone C phase 0 (owner's Send to Amazon pattern, "Pallets: 1 = Boxes: 38 = Units: 38"):
    // the operator's typed pallet figure per ASIN, LTL mode only, for rows whose packaging carries
    // a boxes-per-pallet from the packing template. Units (ItemInput.quantity) stay the wire truth
    // -- this is only what the pallets input last showed. Shared across the direct ASIN-mode table
    // and the PO-confirm-step rows since both key off asin within this one panel.
    const [palletsByAsin, setPalletsByAsin] = useState({});
    // Task 80 (owner: "If a user inputs an asin into the ASIN input box, the system should ask the
    // condition before proceeding with shipment plans"). conditionsByAsin[asin] is the GET
    // /conditions entry -- { options: [{sku, conditionLabel, fnsku, newCondition}], hasNew,
    // defaultSku, blockedReason } -- or `null` once fetched and no listing exists (distinct from
    // "not yet fetched", i.e. absent, so it isn't re-fetched every render).
    // conditionSkuByAsin[asin] is the SKU actually chosen for the row: pre-set to the New SKU when
    // one exists, and left EMPTY when it does not, which is what forces an explicit choice.
    const [conditionsByAsin, setConditionsByAsin] = useState({});
    const [conditionSkuByAsin, setConditionSkuByAsin] = useState({});
    // Task 24/41/67/69/39/80: the box configuration modal's state (popover context, the
    // two-tier form, the shared box catalog, the "new box" inline editor) and handlers now live
    // in the useBoxConfig hook (Task 2 extraction) -- see the useBoxConfig(...) call below, once
    // packagingByAsin/isPackagingComplete/the pure helpers it depends on are all in scope.

    const [analyzing, setAnalyzing] = useState(false);
    const [analysisStatus, setAnalysisStatus] = useState('');
    // Task 68 (LTL "Score Placement Options" looked dead): analyze failures used to be written
    // into analysisStatus, whose banner only renders while analyzing === true -- the catch set
    // analyzing(false) in the same breath, so the message never appeared and a failed analyze
    // looked like a dead click. Failures now land here and render in a persistent red banner.
    const [analysisError, setAnalysisError] = useState(null);
    // 2026-08-21: the structured "this carton was never confirmed" refusal, rendered as an ask the
    // operator answers in place rather than a dead-end error.
    const [cartonGap, setCartonGap] = useState(null);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [selectedOptionId, setSelectedOptionId] = useState(null);
    const [confirming, setConfirming] = useState(false);
    // Task 80: the server-named used-condition SKUs blocking a confirm, plus the retry that runs
    // once the operator explicitly ticks "I intend to ship used condition". Null = no block.
    const [usedAckItems, setUsedAckItems] = useState(null);
    const [usedAckRetry, setUsedAckRetry] = useState(null);
    const [parking, setParking] = useState(false);
    // Task 40 (owner requirement: "we should be able to cancel via the UI") -- controls the
    // deliberate cancel-confirmed-shipment dialog shown for a loaded CONFIRMED run.
    const [showCancelConfirmed, setShowCancelConfirmed] = useState(false);

    const [toast, setToast] = useState(null);

    // Per-ASIN drawer
    const [drawerAsin, setDrawerAsin] = useState(null);
    const [demandRows, setDemandRows] = useState([]);
    const [demandLoading, setDemandLoading] = useState(false);
    const [planningByAsin, setPlanningByAsin] = useState(null);
    const [fcAnalysis, setFcAnalysis] = useState({ status: 'loading', rows: [] });

    // Split-by-size-tier fallback
    const [sizeTierByAsin, setSizeTierByAsin] = useState({});
    const [splitLoading, setSplitLoading] = useState(false);
    const [splitVariants, setSplitVariants] = useState(null);

    // Task 32 (owner request 2026-08-17): analyze mode -- 'combined' (default, the existing
    // single-plan analyze), 'separate' (one single-ASIN plan per line), 'compare' (both + rollup).
    const [analyzeMode, setAnalyzeMode] = useState('combined');
    const [splitResult, setSplitResult] = useState(null);
    const [splitAnalyzing, setSplitAnalyzing] = useState(false);

    // Task 33 (owner request 2026-08-17: "implement it as background job now, it will time out
    // with the browser"): POST the list to the watch endpoint and return immediately -- the
    // server draws each ASIN right away in the background and every 6h until confirmed.
    const [watchStarting, setWatchStarting] = useState(false);
    // Owner, 2026-09-01: the button must KNOW what is already queued — otherwise it re-enables
    // after a watch and looks like the click did nothing. Indication only: a failed read leaves
    // the button usable (the server would just upsert the same row).
    const [watchRows, setWatchRows] = useState([]);
    const refreshWatchRows = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/shipment-planner/watch');
            setWatchRows(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Failed to load watch list:', err);
        }
    }, []);
    useEffect(() => { refreshWatchRows(); }, [refreshWatchRows]);
    // Task 53: partition sweep (single-ASIN "explore box splits" -- async on the backend).
    const [sweepStarting, setSweepStarting] = useState(false);
    // Owner, 2026-09-01: the Score button asks HOW to submit — single shipment, single + per-box
    // breakup, or per-box breakup only. Last choice remembered for the session.
    const [scoreChoiceOpen, setScoreChoiceOpen] = useState(false);
    const [scoreChoice, setScoreChoice] = useState('SINGLE');
    // Owner 2026-09-04: the keep-checking toggle DEFAULTS ON every time the modal opens.
    const [keepChecking, setKeepChecking] = useState(true);

    // Task 90: manual "break ONE ASIN into N shipments" modal -- N separate drafts, one per
    // shipment (see planSplitDrafts for the source-draft rule).
    const [splitAsinModalOpen, setSplitAsinModalOpen] = useState(false);
    const [splitAsinTarget, setSplitAsinTarget] = useState(null);   // which staged ASIN is being split
    const [splitAsinCountInput, setSplitAsinCountInput] = useState('2');
    const [splitAsinUnitsInput, setSplitAsinUnitsInput] = useState('');
    const [splitAsinRemainderMode, setSplitAsinRemainderMode] = useState(REMAINDER_TO_LAST);
    const [splitAsinBusy, setSplitAsinBusy] = useState(false);
    // Task 94: the disabled attribute only lands a render later, so a double-click (or an Enter
    // held down) can fire the handler twice before it does. The ref closes that window.
    const splitAsinBusyRef = useRef(false);
    const [splitAsinError, setSplitAsinError] = useState(null);

    // Task 96: everything the operator can SEE about a split.
    // splitProgress -- the live "Creating shipment 2 of 3…" state inside the modal, with one step
    //   per draft (pending / done / failed) and a terminal phase ('done' | 'failed').
    // splitGroup    -- the stamp of the set THIS draft belongs to (restored from its payload).
    // splitSetPayloads / splitSetOutcomes -- the sibling drafts' payloads and what has happened to
    //   each of them; together they render the "Split set" results panel.
    const [splitProgress, setSplitProgress] = useState(null);
    const [splitGroup, setSplitGroup] = useState(null);
    const [splitSetPayloads, setSplitSetPayloads] = useState({});
    const [splitSetOutcomes, setSplitSetOutcomes] = useState({});
    // Owner addition to task 96 (verbatim): "on the modal for break asin into multiple shipments -
    // default a check box as selected for 'automatically submit to amazon'". "Submit" = ANALYZE
    // each shipment with Amazon (create the inbound plan, fetch scored placement options) right
    // after the split. It is NOT a confirm: nothing here can confirm a shipment, ever.
    const [splitAutoSubmit, setSplitAutoSubmit] = useState(true);
    const [splitSetAnalyzing, setSplitSetAnalyzing] = useState(null); // draftId currently analyzing
    const [splitSetRunAll, setSplitSetRunAll] = useState(null);       // {done, total} while "analyze all" runs
    const [splitSetSummary, setSplitSetSummary] = useState(null);     // "2 of 3 analyzed, 1 failed — …"
    const splitSetBusyRef = useRef(false);
    const splitGroupRef = useRef(null);
    splitGroupRef.current = splitGroup;

    const autoImportedRef = useRef(false);

    // ---- Task 69 + 83: durable autosave drafts of the working build. Task 69 gave the panel ONE
    // autosaved draft; task 83 makes it N, addressed by id, with a drafts bar above the panel.
    // draftBanner: shown after an automatic restore into an empty panel. resumeDraft: the stored
    // payload offered (not applied) when the panel already has rows -- never clobber them.
    const [draftBanner, setDraftBanner] = useState(null);
    const [resumeDraft, setResumeDraft] = useState(null);
    // Where the rows came from, for the banner label ("PO 5951", "War Room draft") -- persisted
    // with the draft, cosmetic only.
    const [planSource, setPlanSource] = useState(null);
    // Task 83: the open drafts (summaries, newest first), which one this panel is editing, and its
    // label. EVERY autosave/delete targets activeDraftId -- the other drafts are never touched.
    const [drafts, setDrafts] = useState([]);
    const [activeDraftId, setActiveDraftId] = useState('default');
    const [activeDraftName, setActiveDraftName] = useState('Shipment 1');
    const [draftMenuOpen, setDraftMenuOpen] = useState(false);
    const [draftRenaming, setDraftRenaming] = useState(false);
    const [draftNameInput, setDraftNameInput] = useState('');
    const [draftBusy, setDraftBusy] = useState(false);
    const [draftError, setDraftError] = useState(null);
    const draftReadyRef = useRef(false);        // autosave armed only after the initial GET settles
    const suppressNextAutosaveRef = useRef(false); // a restore/discard shouldn't immediately re-save
    const lastSavedDraftRef = useRef(null);     // last payload known to be on the server (confirm-clears rule)
    const activeDraftIdRef = useRef('default'); // readable from timers/async handlers, unlike state
    const draftExistsRef = useRef(false);       // is there a server row for the active id yet?
    const draftDebouncerRef = useRef(null);
    if (draftDebouncerRef.current == null) draftDebouncerRef.current = createDraftDebouncer();
    const itemsRef = useRef(items);
    itemsRef.current = items;

    // Task 95: provenance of the rows on the panel (see ROWS_FROM_* above). The REF is the source
    // of truth -- it is set synchronously by whichever handler put the rows there, so the autosave
    // effect (which runs in the very same commit as the setItems that triggered it) reads the new
    // origin rather than last render's. The state exists only to re-render the notice line.
    const rowsOriginRef = useRef(ROWS_FROM_DRAFT);
    const [rowsOrigin, setRowsOrigin] = useState(ROWS_FROM_DRAFT);
    const [loadedRunIdForNotice, setLoadedRunIdForNotice] = useState(null);
    const setRowsOriginNow = (origin) => {
        rowsOriginRef.current = origin;
        setRowsOrigin(origin);
        // Flipping away from the active draft must also kill anything already scheduled: a save
        // queued while the panel still held the draft's rows would otherwise fire ~2s later and
        // write the NEW (foreign) rows -- exactly the overwrite this task exists to stop.
        if (!autosaveAllowedFor(origin)) draftDebouncerRef.current.cancel();
    };

    const draftUrl = (id) => `/api/shipment-planner/drafts/${encodeURIComponent(id)}`;

    // Everything the draft payload is built from, mirrored into a ref so an async handler (switch /
    // new shipment) can flush the CURRENT working build without waiting for a re-render.
    const draftSnapshotRef = useRef(null);
    draftSnapshotRef.current = {
        items, packagingByAsin, packagingConfirmedAsin, shipmentMode, ltlForm, pickupDate, source: planSource,
        splitGroup, // task 96: re-stamped on every flush, or the set is lost on the next keystroke
        palletMeasurements, // task C5: same reasoning -- a switch/split flush must carry these too
    };

    const applyDraftToPanel = (state, { reset = false } = {}) => {
        suppressNextAutosaveRef.current = true;
        // Task 95: these rows ARE the active draft's contents (restore / resume / switch), so the
        // panel is back to case (a) and autosave is armed again.
        setRowsOriginNow(ROWS_FROM_DRAFT);
        setItems(state.items);
        // Draft packaging wins over anything fetched meanwhile -- it carries the operator's
        // confirmed/edited working values, which are exactly what the resume must reproduce.
        setPackagingByAsin(prev => ({ ...prev, ...state.packagingByAsin }));
        setPackagingConfirmedAsin(state.packagingConfirmedAsin);
        if (state.shipmentMode) {
            shipmentModeTouchedRef.current = true; // don't let the PO-modal default effect fight the restore
            setShipmentMode(state.shipmentMode);
        }
        // Task 83: on a draft SWITCH the shipment-level settings must come wholly from the draft
        // being opened -- carrying the previous draft's pallet/pickup config across would quietly
        // quote the wrong freight. On a plain restore (task 69) they merge, as before.
        if (reset) {
            setLtlForm({ ...DEFAULT_LTL_FORM, ...(state.ltlForm || {}) });
            setPickupDate(state.pickupDate || defaultPickupDate());
        } else {
            if (state.ltlForm) setLtlForm(prev => ({ ...prev, ...state.ltlForm }));
            // Task 70b: only adopt a still-valid stored pickup date; draftPayloadToPanelState
            // already nulls out ones that have gone stale, leaving today's default in place.
            if (state.pickupDate) setPickupDate(state.pickupDate);
        }
        setPlanSource(state.source);
        // Task 96: a draft minted by a split carries its set stamp -- adopting it is what makes the
        // "Split set" panel appear when the operator opens any member (including after a reload).
        setSplitGroup(state.splitGroup || null);
        // Task C5: the draft's own saved-by-FC measurements, wholesale -- on both a plain restore
        // and a switch there is nothing of a DIFFERENT draft's to merge in (unlike ltlForm/pickupDate,
        // which are prefilled sane defaults that a switch must not let a previous draft override).
        setPalletMeasurements(state.palletMeasurements || {});
    };

    // Task 83: wipe the working build (used by "+ New shipment" and by opening an empty draft).
    // Analyze output goes too -- results belong to the composition that produced them.
    const clearPanelForDraft = () => {
        suppressNextAutosaveRef.current = true;
        // Task 95: an empty panel belongs to the draft about to be opened/created -- case (a).
        setRowsOriginNow(ROWS_FROM_DRAFT);
        setItems([]);
        setPackagingConfirmedAsin({});
        setPlanSource(null);
        shipmentModeTouchedRef.current = false;
        setShipmentMode('UPS');
        setLtlForm({ ...DEFAULT_LTL_FORM });
        setPickupDate(defaultPickupDate());
        setAnalysisResult(null);
        setAnalysisError(null);
        setSelectedOptionId(null);
        setSplitResult(null);
        setSplitGroup(null); // task 96: an empty/new shipment belongs to no split set
        setPalletMeasurements({}); // task C5: a brand-new/empty shipment carries no other draft's measurements
        setDraftBanner(null);
        setResumeDraft(null);
        lastSavedDraftRef.current = null;
    };

    // 2026-08-23 (owner): see draftIsSubmitted. Reads the stored payload and PUTs it back with
    // the stamp toggled -- deliberately not routed through the autosave debouncer, which could
    // overwrite the stamp with pre-submission state milliseconds later.
    const markActiveDraftSubmitted = async (submitted) => {
        const id = activeDraftIdRef.current;
        if (!id) return;
        try {
            const res = await ApiClient.get(draftUrl(id));
            const payload = res?.data?.payload || {};
            if (submitted) payload.submittedForAnalysis = { at: new Date().toISOString() };
            else delete payload.submittedForAnalysis;
            await ApiClient.put(draftUrl(id), payload);
            loadDraftList();
        } catch (e) {
            console.error('Could not update the draft submission stamp:', e);
        }
    };

    const loadDraftList = () => {
        const req = ApiClient.get('/api/shipment-planner/drafts');
        if (!req || typeof req.then !== 'function') return Promise.resolve([]);
        return req.then(res => {
            const list = Array.isArray(res.data) ? res.data : [];
            setDrafts(list);
            return list;
        }).catch(err => { console.error('Failed to list planner drafts:', err); return []; });
    };

    const rememberActiveDraft = (id, name) => {
        activeDraftIdRef.current = id;
        setActiveDraftId(id);
        if (name != null) setActiveDraftName(name);
        try { window.localStorage.setItem(ACTIVE_DRAFT_STORAGE_KEY, id); } catch (e) { /* private mode */ }
    };

    // Push the current working build to its draft NOW (no debounce). Called before every switch /
    // new-shipment so the draft being left behind keeps the operator's last keystroke.
    const flushActiveDraft = () => {
        draftDebouncerRef.current.cancel();
        // Task 95 (requirement 4): a switch / "+ New shipment" / split flushes only LEGITIMATE
        // edits. When the panel is holding a loaded run (or rows a resume-offer declined to
        // clobber), there is nothing of this draft's to strand -- and flushing would write the
        // foreign build into the draft being left behind, which is the bug.
        if (!autosaveAllowedFor(rowsOriginRef.current)) return Promise.resolve();
        const id = activeDraftIdRef.current;
        const payload = buildPlannerDraftPayload({
            ...draftSnapshotRef.current, allowEmpty: draftExistsRef.current,
        });
        if (!id || !payload) return Promise.resolve();
        // Task 87: belt-and-braces -- buildPlannerDraftPayload is an explicit whitelist, but verify
        // the result really is stringify-able before it ever reaches axios, and fail loudly rather
        // than silently if it somehow isn't.
        if (safeSerializationCheck(payload, 'draft flush')) {
            setToast("Couldn't save draft — the draft could not be built. Please refresh and try again.");
            return Promise.resolve();
        }
        lastSavedDraftRef.current = payload;
        draftExistsRef.current = true;
        const req = ApiClient.put(draftUrl(id), payload);
        if (!req || typeof req.then !== 'function') return Promise.resolve();
        return req.catch(err => console.error('Planner draft flush failed:', err));
    };

    // On mount: list the drafts, open the last one this browser worked on (else the most recently
    // updated), and restore it automatically ONLY into an empty panel; if rows already exist
    // (loaded run, war-room auto-import) offer a resume affordance instead.
    useEffect(() => {
        let cancelled = false;
        // Landing: list the drafts for the picker and STOP. Nothing opens until the operator
        // picks — the silent auto-restore this replaced is what dumped a split set on first load.
        if (landingMountRef.current) {
            // No draft is active on the picker — the refs default to 'default'/'Shipment 1',
            // and leaving them would make picking the draft literally named "default"
            // early-return in switchToDraft as "already active" (and would aim autosave at a
            // draft the operator never opened).
            activeDraftIdRef.current = null;
            setActiveDraftId(null);
            setActiveDraftName('');
            draftExistsRef.current = false;
            ApiClient.get('/api/shipment-planner/drafts')
                .then(res => { if (!cancelled) setDrafts(Array.isArray(res.data) ? res.data : []); })
                .catch(err => console.error('Failed to list planner drafts:', err))
                .finally(() => { draftReadyRef.current = true; });
            return () => { cancelled = true; };
        }
        let stored = null;
        try { stored = window.localStorage.getItem(ACTIVE_DRAFT_STORAGE_KEY); } catch (e) { /* private mode */ }
        ApiClient.get('/api/shipment-planner/drafts')
            .then(res => {
                if (cancelled) return null;
                const list = Array.isArray(res.data) ? res.data : [];
                setDrafts(list);
                const id = pickActiveDraftId(list, stored);
                const entry = list.find(d => d && d.id === id);
                activeDraftIdRef.current = id;
                setActiveDraftId(id);
                setActiveDraftName(entry?.name || 'Shipment 1');
                draftExistsRef.current = !!entry;
                return entry ? ApiClient.get(draftUrl(id)) : null;
            })
            .then(res => {
                if (cancelled || !res) return;
                const payload = res.data?.payload;
                const currentCount = autoImportDraft ? Math.max(1, itemsRef.current.length) : itemsRef.current.length;
                const { action, state } = draftRestoreAction(payload, currentCount);
                if (action === 'restore') {
                    applyDraftToPanel(state);
                    setDraftBanner({ label: draftSummaryLabel(payload), savedAt: state.savedAt });
                    lastSavedDraftRef.current = payload;
                } else if (action === 'offer') {
                    setResumeDraft(payload);
                    lastSavedDraftRef.current = payload;
                    // Task 95: the panel already held rows and we deliberately did NOT clobber
                    // them -- which means the rows on screen are not this draft's contents. Before
                    // this task the autosave then wrote them over the very draft this banner is
                    // offering to resume. Mark them foreign; nothing autosaves until the operator
                    // resumes the draft or explicitly copies these rows into one.
                    // (A loaded run is a more specific origin and already says so -- keep it.)
                    if (rowsOriginRef.current === ROWS_FROM_DRAFT) setRowsOriginNow(ROWS_FROM_FOREIGN);
                }
            })
            .catch(err => console.error('Failed to load planner draft:', err))
            .finally(() => { draftReadyRef.current = true; });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced autosave: any change to the working build (rows / packaging / confirmed flags /
    // mode / LTL config) re-snapshots the whole draft ~2s after the last keystroke, into the ACTIVE
    // draft only. Analyze results are NOT part of the snapshot -- they re-generate. Emptying the
    // rows of an existing draft saves an empty build rather than deleting it (task 83: the draft is
    // a named container; only the bar's Delete retires it) -- but a panel that never had a draft
    // stays unsaved, so merely visiting the page never mints a row.
    //
    // Task 95: and ONLY when the rows on screen are that draft's own build (or edits made on top of
    // it). A loaded run -- or rows the resume-offer declined to clobber -- never writes here; see
    // ROWS_FROM_* and the notice line rendered beside the drafts bar. Checked twice, because the
    // origin can flip during the ~2s the save is in flight: once when scheduling, once when firing.
    useEffect(() => {
        if (!draftReadyRef.current) return undefined;
        if (suppressNextAutosaveRef.current) {
            suppressNextAutosaveRef.current = false;
            return undefined;
        }
        if (!autosaveAllowedFor(rowsOriginRef.current)) return undefined;
        draftDebouncerRef.current.schedule(() => {
            if (!autosaveAllowedFor(rowsOriginRef.current)) return;
            const id = activeDraftIdRef.current;
            const payload = buildPlannerDraftPayload({
                items, packagingByAsin, packagingConfirmedAsin, shipmentMode, ltlForm, pickupDate,
                source: planSource, splitGroup: splitGroupRef.current,
                allowEmpty: draftExistsRef.current,
                palletMeasurements, // task C5: an edit here marks the draft dirty exactly like ltlForm does
            });
            if (!id || !payload) return;
            // Task 87: the background autosave must never fail silently -- if the whitelisted
            // payload somehow still can't be stringified, say so loudly instead of letting axios's
            // JSON.stringify throw into an unhandled rejection the operator never sees.
            if (safeSerializationCheck(payload, 'draft autosave')) {
                setToast("Couldn't save draft — please refresh and try again.");
                return;
            }
            fireDraftRequest(ApiClient.put(draftUrl(id), payload), () => {
                lastSavedDraftRef.current = payload;
                draftExistsRef.current = true;
                setDrafts(prev => applyDraftSummaryUpdate(prev, id, payload, activeDraftName));
            }, 'Planner draft autosave failed:');
        });
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, packagingByAsin, packagingConfirmedAsin, shipmentMode, ltlForm, pickupDate, planSource,
        activeDraftId, rowsOrigin, palletMeasurements]);

    // Unmount: drop any pending autosave timer (the last completed save stands).
    useEffect(() => {
        const debouncer = draftDebouncerRef.current;
        return () => debouncer.cancel();
    }, []);

    // Explicit discard of the ACTIVE draft's server row (banner/affordance actions). Leaves panel
    // rows alone, and leaves every OTHER draft alone.
    const deleteServerDraft = () => {
        draftDebouncerRef.current.cancel();
        const id = activeDraftIdRef.current;
        lastSavedDraftRef.current = null;
        draftExistsRef.current = false;
        setDraftBanner(null);
        setResumeDraft(null);
        setDrafts(prev => (prev || []).filter(d => d && d.id !== id));
        // Task 95: "Discard saved draft — keep the rows below" is the operator's explicit decision
        // that the rows on screen are now the build. There is no longer anything to clobber, so the
        // panel goes back to case (a) and autosave resumes (re-creating the row under this id).
        setRowsOriginNow(ROWS_FROM_DRAFT);
        fireDraftRequest(ApiClient.delete(draftUrl(id)), null, 'Failed to delete planner draft:');
    };

    // Banner discard: the restore wasn't wanted -- clear the restored rows AND the server draft.
    const discardRestoredDraft = () => {
        deleteServerDraft();
        suppressNextAutosaveRef.current = true;
        setItems([]);
        setPackagingConfirmedAsin({});
        setPlanSource(null);
    };

    // Resume affordance: the operator explicitly chose the saved draft over the current rows.
    const resumeSavedDraft = () => {
        const { state } = draftRestoreAction(resumeDraft, 0);
        if (state) {
            applyDraftToPanel(state);
            setDraftBanner({ label: draftSummaryLabel(resumeDraft), savedAt: state.savedAt });
        }
        setResumeDraft(null);
    };

    // ---- Task 95: the two explicit ways OUT of a suspended-autosave panel ----

    // What the rows on screen are, in words, for the notice line / confirm text / toasts.
    const provenanceNotice = panelProvenanceNotice({
        origin: rowsOrigin, runId: loadedRunIdForNotice, draftName: activeDraftName,
    });

    // A name for the copy. A run copies as "Run #222", anything else borrows the plan source
    // ("PO 5951", "War Room draft"). Clamped to the column width.
    const provenanceCopyName = () => {
        const base = rowsOriginRef.current === ROWS_FROM_RUN && loadedRunIdForNotice != null
            ? `Run #${loadedRunIdForNotice}`
            : (planSource || 'Copied build');
        return base.slice(0, SPLIT_DRAFT_NAME_MAX);
    };

    // "Copy this run into a new draft": mints a NEW draft holding exactly what is on screen and
    // makes it active. Nothing existing is overwritten, so this needs no confirmation -- it is the
    // safe default action and is listed first.
    const copyPanelIntoNewDraft = async () => {
        setDraftMenuOpen(false);
        setDraftBusy(true);
        setDraftError(null);
        try {
            const payload = buildPlannerDraftPayload({ ...draftSnapshotRef.current });
            if (!payload) {
                setDraftError('There are no rows to copy into a draft.');
                return;
            }
            if (safeSerializationCheck(payload, 'copy build into a new draft')) {
                setDraftError('Could not copy this build — please refresh and try again.');
                return;
            }
            const name = provenanceCopyName();
            const res = await ApiClient.post('/api/shipment-planner/drafts', { name, payload });
            const id = res?.data?.id;
            if (!id) throw new Error('no draft id returned');
            lastSavedDraftRef.current = payload;
            draftExistsRef.current = true;
            setResumeDraft(null);
            setDraftBanner(null);
            setRowsOriginNow(ROWS_FROM_DRAFT); // the rows are now a draft's own contents
            setLoadedRunIdForNotice(null);
            rememberActiveDraft(id, res?.data?.name || name);
            await loadDraftList();
            setToast(`Copied ${rowsCountLabel(payload.rows)} into a new shipment draft `
                + `“${res?.data?.name || name}” — it autosaves from here.`);
        } catch (err) {
            const msg = err?.response?.data?.error;
            console.error('Failed to copy the build into a new draft:', err);
            setDraftError(msg || 'Could not copy this build into a new shipment draft.');
        } finally {
            setDraftBusy(false);
        }
    };

    // "Copy into “PO 5951”": overwrite the ACTIVE draft with what is on screen. This is the
    // destructive one, so (requirement 3) it re-reads what that draft really holds -- the server
    // row is the authority, not lastSavedDraftRef, which can be stale (task 94's rule) -- and
    // confirms, naming both sides with their ASIN/unit counts. An empty draft, or one that already
    // holds exactly these contents, has nothing to lose and is written without a prompt.
    const copyPanelIntoActiveDraft = async () => {
        setDraftMenuOpen(false);
        const id = activeDraftIdRef.current;
        if (!id) return;
        setDraftBusy(true);
        setDraftError(null);
        try {
            const payload = buildPlannerDraftPayload({ ...draftSnapshotRef.current, allowEmpty: true });
            if (!payload || safeSerializationCheck(payload, 'copy build into the active draft')) {
                setDraftError('Could not copy this build — please refresh and try again.');
                return;
            }
            let savedRows = [];
            try {
                const res = await ApiClient.get(draftUrl(id));
                savedRows = res?.data?.payload?.rows || [];
            } catch (err) {
                console.error('Could not re-read the draft before overwriting it:', err);
                setDraftError('Could not check what that shipment draft holds — nothing was changed.');
                return;
            }
            if (draftContentsDiffer(savedRows, payload.rows)) {
                const ok = window.confirm(overwriteDraftConfirmMessage({
                    draftName: activeDraftName,
                    savedRows,
                    incomingRows: payload.rows,
                    incomingLabel: provenanceNotice?.incomingLabel || 'the rows on screen',
                }));
                if (!ok) return; // nothing written, panel untouched, still suspended
            }
            await ApiClient.put(draftUrl(id), payload);
            lastSavedDraftRef.current = payload;
            draftExistsRef.current = true;
            setDrafts(prev => applyDraftSummaryUpdate(prev, id, payload, activeDraftName));
            setResumeDraft(null);
            setRowsOriginNow(ROWS_FROM_DRAFT);
            setLoadedRunIdForNotice(null);
            setToast(`Draft “${activeDraftName}” now holds ${rowsCountLabel(payload.rows)} — `
                + 'autosaving from here.');
        } catch (err) {
            const msg = err?.response?.data?.error;
            console.error('Failed to copy the build into the active draft:', err);
            setDraftError(msg || 'Could not write this build into that shipment draft.');
        } finally {
            setDraftBusy(false);
        }
    };

    // ---- Task 83: drafts-bar actions ----

    // Open another draft: flush the current one first (nothing typed is stranded), then load the
    // target's payload into the panel. An empty target clears the panel instead of leaving the
    // previous draft's rows on screen.
    const switchToDraft = async (id) => {
        setDraftMenuOpen(false);
        if (!id || id === activeDraftIdRef.current) return;
        setDraftBusy(true);
        setDraftError(null);
        try {
            await flushActiveDraft();
            const res = await ApiClient.get(draftUrl(id));
            const payload = res?.data?.payload;
            const { state } = draftRestoreAction(payload, 0);
            setAnalysisResult(null);
            setAnalysisError(null);
            setSelectedOptionId(null);
            setSplitResult(null);
            if (state) {
                applyDraftToPanel(state, { reset: true });
                setDraftBanner({ label: draftSummaryLabel(payload), savedAt: state.savedAt });
            } else {
                clearPanelForDraft();
            }
            lastSavedDraftRef.current = payload || null;
            draftExistsRef.current = true;
            rememberActiveDraft(id, res?.data?.name || (drafts.find(d => d && d.id === id)?.name) || 'Shipment');
        } catch (err) {
            console.error('Failed to switch planner draft:', err);
            setDraftError('Could not open that shipment draft — nothing was changed.');
        } finally {
            setDraftBusy(false);
        }
    };

    // "+ New shipment": flush the current draft, ask the server for a new id (it enforces the cap),
    // then start from an empty panel. The previous build stays exactly as it was, in its own draft.
    // Landing -> working: the pick. switchToDraft no-ops when the id is already active
    // (returning to the same draft after "All plans"), so the view flip is separate.
    const resumeDraftFromLanding = async (id) => {
        await switchToDraft(id);
        setPlannerView('working');
    };

    // Owner, 2026-09-01: "add a trashcan so that the plan can be discarded". A split-set row
    // discards ALL member drafts. Honesty requirement: discarding drafts does NOT cancel any
    // Amazon-side runs — the placement workspace's remove-plans feature does that — and the
    // dialog says so. Discarding never resumes; the picker just refreshes.
    const discardLandingRow = async (r) => {
        const ids = r.kind === 'split' ? (r.memberIds || []) : [r.id];
        if (ids.length === 0) return;
        const msg = `Discard '${r.name}' — ${ids.length} shipment draft${ids.length !== 1 ? 's' : ''}. `
            + 'Analyzed plans already on the Shipment Runs page are not affected.';
        if (!window.confirm(msg)) return;
        setDraftBusy(true);
        try {
            for (const id of ids) {
                await ApiClient.delete(draftUrl(id));
            }
            try {
                if (ids.includes(window.localStorage.getItem(ACTIVE_DRAFT_STORAGE_KEY))) {
                    window.localStorage.removeItem(ACTIVE_DRAFT_STORAGE_KEY);
                }
            } catch (e) { /* private mode */ }
            setToast(`Discarded ${ids.length} draft${ids.length !== 1 ? 's' : ''}.`);
        } catch (err) {
            console.error('Draft discard failed:', err);
            setToast('Discard failed: ' + (err.response?.data?.error || err.message));
        }
        await loadDraftList();
        setDraftBusy(false);
    };

    // Working -> landing. Flush first so the draft keeps the last keystroke, kill the debounced
    // autosave, DETACH the active draft before clearing (an attached autosave of the cleared
    // panel would empty the draft), then clear so the picker never shows draft contents.
    const backToAllPlans = async () => {
        await flushActiveDraft();
        draftDebouncerRef.current.cancel();
        activeDraftIdRef.current = null;
        setActiveDraftId(null);
        setActiveDraftName('');
        draftExistsRef.current = false;
        clearPanelForDraft();
        await loadDraftList();
        setPlannerView('landing');
    };

    // Landing -> working via "start new": the first rows arriving (PO import / search add) flip
    // the view; with no active draft yet one is minted, so autosave has somewhere to write.
    useEffect(() => {
        if (plannerView !== 'landing' || items.length === 0) return;
        setPlannerView('working');
        if (!activeDraftIdRef.current) {
            // Defensive transport handling, same as loadDraftList: a throwing/misbehaving client
            // must never crash the panel — the rows stay usable, they just don't autosave yet.
            let req = null;
            try { req = ApiClient.post('/api/shipment-planner/drafts', {}); }
            catch (err) { console.error('Could not mint a draft for the new rows:', err); }
            if (req && typeof req.then === 'function') {
                req.then(res => {
                    const id = res?.data?.id;
                    if (!id) return;
                    draftExistsRef.current = true;
                    rememberActiveDraft(id, res?.data?.name || 'New shipment');
                    loadDraftList();
                }).catch(err => console.error('Could not mint a draft for the new rows:', err));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plannerView, items.length]);

    const startNewShipment = async () => {
        setDraftMenuOpen(false);
        setDraftBusy(true);
        setDraftError(null);
        try {
            await flushActiveDraft();
            const res = await ApiClient.post('/api/shipment-planner/drafts', {});
            const id = res?.data?.id;
            if (!id) throw new Error('no draft id returned');
            clearPanelForDraft();
            draftExistsRef.current = true;
            rememberActiveDraft(id, res?.data?.name || 'New shipment');
            await loadDraftList();
        } catch (err) {
            const msg = err?.response?.data?.error;
            console.error('Failed to start a new planner draft:', err);
            setDraftError(msg || 'Could not start another shipment draft.');
        } finally {
            setDraftBusy(false);
        }
    };

    // Delete the ACTIVE draft, then fall back to the most recent remaining one (or an empty panel).
    const deleteActiveDraft = async () => {
        setDraftMenuOpen(false);
        const id = activeDraftIdRef.current;
        if (!window.confirm(`Delete the shipment draft "${activeDraftName}"? This cannot be undone.`)) return;
        setDraftBusy(true);
        setDraftError(null);
        draftDebouncerRef.current.cancel();
        try {
            await ApiClient.delete(draftUrl(id));
            lastSavedDraftRef.current = null;
            draftExistsRef.current = false;
            const list = await loadDraftList();
            const next = (list || []).find(d => d && d.id !== id);
            if (next) {
                activeDraftIdRef.current = null; // force switchToDraft to load it
                await switchToDraft(next.id);
            } else {
                clearPanelForDraft();
                rememberActiveDraft('default', 'Shipment 1');
                draftExistsRef.current = false;
            }
        } catch (err) {
            console.error('Failed to delete planner draft:', err);
            setDraftError('Could not delete that shipment draft.');
        } finally {
            setDraftBusy(false);
        }
    };

    const beginRenameDraft = () => {
        setDraftNameInput(activeDraftName || '');
        setDraftRenaming(true);
        setDraftMenuOpen(false);
    };

    const commitRenameDraft = () => {
        const name = (draftNameInput || '').trim();
        setDraftRenaming(false);
        if (!name || name === activeDraftName) return;
        setActiveDraftName(name);
        setDrafts(prev => (prev || []).map(d => (d && d.id === activeDraftIdRef.current ? { ...d, name } : d)));
        fireDraftRequest(ApiClient.post(`${draftUrl(activeDraftIdRef.current)}/rename`, { name }),
            null, 'Failed to rename planner draft:');
    };

    // ---- Task 90: break ONE staged ASIN into N shipments (N separate drafts) ----

    // Live math for the modal, recomputed every render so the numbers move with the inputs.
    const splitAsinRow = items.find(it => it && it.asin === splitAsinTarget) || null;
    const splitAsinPlan = computeAsinSplit({
        totalQty: splitAsinRow?.quantity,
        shipments: splitAsinCountInput,
        unitsPerShipment: splitAsinUnitsInput,
        remainderMode: splitAsinRemainderMode,
    });
    const splitAsinDecision = planSplitDrafts({
        plan: splitAsinPlan,
        rows: items,
        asin: splitAsinTarget,
        openDraftCount: drafts.length,
        baseLabel: planSource || activeDraftName,
    });

    const chooseSplitAsin = (asin) => {
        const row = items.find(it => it && it.asin === asin) || null;
        setSplitAsinTarget(asin || null);
        setSplitAsinError(null);
        setSplitAsinUnitsInput(row
            ? String(defaultSplitUnitsPerShipment(row.quantity, splitAsinCountInput)) : '');
    };

    const openSplitAsinModal = () => {
        const rows = items.filter(it => it && it.asin);
        // Unambiguous single row -> pre-filled; several rows -> the modal opens on its ASIN picker.
        const target = rows.length === 1 ? rows[0].asin
            : (rows.some(r => r.asin === splitAsinTarget) ? splitAsinTarget : null);
        const row = rows.find(r => r.asin === target) || null;
        setSplitAsinTarget(target);
        setSplitAsinCountInput('2');
        setSplitAsinUnitsInput(row ? String(defaultSplitUnitsPerShipment(row.quantity, 2)) : '');
        setSplitAsinRemainderMode(REMAINDER_TO_LAST);
        setSplitAsinError(null);
        setSplitProgress(null); // task 96: a fresh open never shows the last split's progress
        setSplitAsinModalOpen(true);
    };

    // Materialise the split. Order is deliberate: the new drafts are created FIRST, and the source
    // draft is only rewritten once every creation succeeded -- a cap refusal (409) or a network
    // failure therefore never costs the operator the build they were standing on.
    //
    // Task 94 (owner, live prod evidence: three 2-unit drafts carved out of a draft that held 2
    // units): the split is re-decided AT THE MOMENT OF ACTION and validated against the units the
    // DRAFT IT IS ABOUT TO REWRITE actually holds -- never against a number captured when the modal
    // opened, and never against the rows on screen alone. The rows on screen are not guaranteed to
    // be that draft's contents: a run loaded from the Runs list, a re-import, or the "a saved draft
    // exists" resume-offer all leave the panel holding a build that came from somewhere else while
    // the draft bookkeeping points at another (possibly already-split) draft. Splitting is
    // destructive and rewrites a server row, so the saved draft is the authority and the smaller of
    // the two numbers wins.
    const runAsinSplit = async () => {
        // Double-submit: a ref, not the busy state, because two clicks in the same tick both see
        // the pre-update state value (the disabled attribute lands a render later).
        if (splitAsinBusyRef.current) return;

        // Re-read the panel state NOW rather than trusting the render-time capture.
        const asin = splitAsinTarget;
        const liveRows = (itemsRef.current || []).filter(r => r && r.asin);
        const row = liveRows.find(r => r.asin === asin) || null;
        if (!row) {
            setSplitAsinError('That ASIN is no longer staged on this build — close this and try again.');
            return;
        }
        const inputs = {
            shipments: splitAsinCountInput,
            unitsPerShipment: splitAsinUnitsInput,
            remainderMode: splitAsinRemainderMode,
        };
        const panelQty = Math.max(0, Math.floor(Number(row.quantity) || 0));
        let plan = computeAsinSplit({ ...inputs, totalQty: panelQty });
        if (!plan.valid) { setSplitAsinError(plan.error); return; }

        splitAsinBusyRef.current = true;
        setSplitAsinBusy(true);
        setSplitAsinError(null);
        const activeId = activeDraftIdRef.current;
        // Owner addition: when "automatically submit to Amazon" is ticked, the members created
        // below are analyzed (never confirmed) as soon as the drafts exist. Collected here and run
        // AFTER the finally block, so the split's own busy flag is already down.
        // Task 103: the group id to hand to the server-side "analyze all" once the drafts exist
        // (this used to be the in-browser member list the page then looped over itself).
        let autoSubmitMembers = null;
        try {
            // ---- The guard: what does the draft being rewritten REALLY hold right now? ----
            // Owner bug 2026-09-01: cancelling the pending autosave and reading the store made
            // the fresh-rows path (landing → PO add → split) fail FOREVER — the minted draft is
            // empty until that autosave lands, and each retry re-cancelled the save it was
            // waiting for. Flush FIRST instead: for the draft's OWN rows the screen is the truth
            // and the store catches up; for foreign/run rows flushActiveDraft deliberately
            // no-ops, so the guard below still refuses to split a draft whose stored build is
            // not what is on screen — the case it was built for.
            if (activeId) {
                await flushActiveDraft();
                let savedQty = null;
                try {
                    const res = await ApiClient.get(draftUrl(activeId));
                    savedQty = draftStagedQuantity(res?.data?.payload, asin);
                } catch (err) {
                    console.error('Could not re-read the draft before splitting it:', err);
                    setSplitAsinError('Could not check what this shipment draft holds — nothing was '
                        + 'changed. Please try again.');
                    return;
                }
                if (savedQty < panelQty) {
                    // The panel and the draft disagree: re-decide against the draft's own units.
                    plan = computeAsinSplit({ ...inputs, totalQty: savedQty });
                    if (!plan.valid) {
                        setSplitAsinError(`${plan.error} The rows on screen (${panelQty} `
                            + `unit${panelQty !== 1 ? 's' : ''}) are not what this draft holds — reopen it `
                            + 'from the Switch menu and split from there.');
                        return;
                    }
                }
            }
            const rowsForSplit = liveRows.map(r => (r.asin === asin ? { ...r, quantity: plan.total } : r));
            const splitRowNow = rowsForSplit.find(r => r.asin === asin);
            const decision = planSplitDrafts({
                plan,
                rows: rowsForSplit,
                asin,
                openDraftCount: drafts.length,
                baseLabel: planSource || activeDraftName,
            });
            if (!decision.capOk) { setSplitAsinError(decision.capMessage); return; }

            // Nothing typed on the current build may be stranded by what follows.
            await flushActiveDraft();

            // ---- Task 96: the set + the progress indicator ----
            // One group id for this split; every shipment it produces (including the source draft
            // when it becomes 1/N) is stamped with it, so the set can be rebuilt from the server
            // rows alone. The step list is what the modal ticks off as each POST returns.
            const groupId = newSplitGroupId();
            const total = plan.shipments.length;
            const stampFor = (index) => ({ id: groupId, index, total, asin });
            const stepLabel = (index) => `Shipment ${index}/${total}`;
            setSplitProgress({
                phase: 'creating',
                total,
                created: 0,
                steps: plan.shipments.map(sh => ({
                    index: sh.index,
                    label: stepLabel(sh.index),
                    units: sh.units,
                    status: 'pending',
                })),
            });
            const markStep = (index, status) => setSplitProgress(prev => (prev ? {
                ...prev,
                created: status === 'done' ? prev.created + 1 : prev.created,
                steps: prev.steps.map(s => (s.index === index ? { ...s, status } : s)),
            } : prev));
            // The source draft's own step is settled before any POST fires when it is reused: it
            // already exists, so "created" is honest only for the drafts actually being written.
            if (decision.reuseSource) markStep(1, 'source');

            const created = [];
            for (const sh of decision.newShipments) {
                // Task 80 carry-through: the row is copied wholesale (asin/sku/name/sellingPrice)
                // and ONLY the quantity changes, so the New-condition SKU + the packaging entry
                // (and its confirmed flag) reach every shipment exactly as they are here.
                const payload = buildPlannerDraftPayload({
                    items: [{ ...splitRowNow, quantity: sh.units }],
                    packagingByAsin,
                    packagingConfirmedAsin,
                    shipmentMode,
                    ltlForm,
                    pickupDate,
                    source: planSource,
                    splitGroup: stampFor(sh.index),
                });
                if (!payload || safeSerializationCheck(payload, 'asin split')) {
                    throw new Error('The split could not be built — please refresh and try again.');
                }
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const res = await ApiClient.post('/api/shipment-planner/drafts', { name: sh.name, payload });
                    const id = res?.data?.id;
                    if (!id) throw new Error('no draft id returned');
                    created.push({ id, name: sh.name, index: sh.index, units: sh.units });
                    markStep(sh.index, 'done');
                } catch (err) {
                    // Task 96: STOP here. Say which shipments exist and which do not, and leave the
                    // source draft exactly as it was -- the rewrite below never runs.
                    markStep(sh.index, 'failed');
                    setSplitProgress(prev => (prev ? { ...prev, phase: 'failed' } : prev));
                    throw err;
                }
            }

            setSplitProgress(prev => (prev ? { ...prev, phase: 'source' } : prev));

            // The source draft: either shipment 1/N (renamed in the same PUT) or the leftovers.
            // Task 96: it is stamped into the set ONLY when it becomes shipment 1/N -- a source
            // draft that merely keeps its other ASINs is not a member of the split.
            const sourceStamp = decision.reuseSource ? stampFor(1) : null;
            const sourcePayload = buildPlannerDraftPayload({
                items: decision.sourceRows,
                packagingByAsin,
                packagingConfirmedAsin,
                shipmentMode,
                ltlForm,
                pickupDate,
                source: planSource,
                splitGroup: sourceStamp,
                allowEmpty: true,
            });
            const sourceUrl = decision.reuseSource
                ? `${draftUrl(activeId)}?name=${encodeURIComponent(decision.sourceName)}`
                : draftUrl(activeId);
            await ApiClient.put(sourceUrl, sourcePayload);
            lastSavedDraftRef.current = sourcePayload;
            draftExistsRef.current = true;
            if (decision.reuseSource) markStep(1, 'done');

            // Keep the flush-on-switch snapshot honest: switchToDraft below flushes from this ref
            // before the re-render lands, and it must carry the POST-split rows, not the old ones.
            draftSnapshotRef.current = {
                ...draftSnapshotRef.current, items: decision.sourceRows, splitGroup: sourceStamp,
            };
            suppressNextAutosaveRef.current = true;
            // Task 95: the rows now on screen are EXACTLY what was just PUT to the source draft, so
            // the panel is back to case (a) regardless of where the pre-split rows came from.
            setRowsOriginNow(ROWS_FROM_DRAFT);
            setLoadedRunIdForNotice(null);
            setItems(decision.sourceRows);
            setAnalysisResult(null);
            setAnalysisError(null);
            setSelectedOptionId(null);
            setSplitResult(null);
            setSplitGroup(sourceStamp);
            if (decision.reuseSource) setActiveDraftName(decision.sourceName);

            // Task 96: the panel now knows the whole set without another round trip -- seed the
            // payload cache so the "Split set" panel renders the moment the modal's success state
            // appears, and remember each member's shipment index for the outcome ledger.
            cacheDraftPayloads([
                ...(sourceStamp ? [[activeId, sourcePayload]] : []),
                ...created.map(c => ([c.id, {
                    ...sourcePayload,
                    splitGroup: stampFor(c.index),
                    rows: [{
                        asin: splitRowNow.asin,
                        sku: splitRowNow.sku ?? null,
                        name: splitRowNow.name ?? null,
                        sellingPrice: splitRowNow.sellingPrice ?? null,
                        packaging: whitelistPackagingForDraft(packagingByAsin?.[asin]),
                        packagingConfirmed: !!packagingConfirmedAsin?.[asin],
                        quantity: c.units,
                    }],
                }])),
            ]);
            const setMembers = [
                ...(sourceStamp ? [{ draftId: activeId, name: decision.sourceName, index: 1 }] : []),
                ...created.map(c => ({ draftId: c.id, name: c.name, index: c.index })),
            ].map(m => ({ ...m, total, asin, groupId }));
            setSplitSetOutcomes(() => {
                let ledger = {};
                setMembers.forEach((m) => {
                    ledger = writeSplitOutcome(groupId, m.draftId,
                        { name: m.name, index: m.index, total, asin }, undefined);
                });
                return ledger;
            });
            if (splitAutoSubmit) autoSubmitMembers = groupId;

            await loadDraftList();
            setSplitProgress(prev => (prev ? { ...prev, phase: 'done' } : prev));

            const n = plan.shipments.length;
            const sizes = plan.shipments.map(s => s.units).join(' + ');
            if (decision.reuseSource) {
                // Shipment 1/N IS this draft -- stay on it; the rest are in the drafts bar.
                setToast(`Split ${asin} into ${n} shipments (${sizes} units). This draft is now `
                    + `“${decision.sourceName}” — the other ${n - 1} are in the drafts bar.`);
            } else {
                await switchToDraft(created[0].id);
                const leftPart = decision.leftover > 0
                    ? ` keeps ${decision.leftover} unassigned unit${decision.leftover !== 1 ? 's' : ''}`
                    : ' keeps its other ASINs';
                setToast(`Split ${asin} into ${n} shipments (${sizes} units) — opened `
                    + `“${created[0].name}”. The original draft${leftPart}.`);
            }
        } catch (err) {
            console.error('Failed to break the ASIN into multiple shipments:', err);
            setSplitAsinError(err?.response?.data?.error || err?.message
                || 'Could not create the shipment drafts — nothing on this build was changed.');
            // Task 96: whatever went wrong, the modal stays open on a FAILED progress state that
            // names the shipments that exist and the ones that do not. Never a silent half-split.
            setSplitProgress(prev => (prev ? { ...prev, phase: 'failed' } : prev));
            loadDraftList();
        } finally {
            splitAsinBusyRef.current = false;
            setSplitAsinBusy(false);
        }
        // Owner addition: "automatically submit to amazon" -- analyze every shipment the split just
        // made, one at a time, with the per-draft progress and results the Split set panel shows.
        // A failure on one shipment is recorded on its row and the rest still run. NO confirm.
        // Task 103: the sequence runs on the SERVER now, so closing this modal (or the tab) while
        // it works no longer abandons the shipments that have not been reached yet.
        if (autoSubmitMembers) await startSplitSetJob(autoSubmitMembers);
    };

    // ---- Task 96: the split set (results panel + "analyze all") ----

    // Payload cache for the sibling drafts. The drafts LIST endpoint returns summaries only, so the
    // set is rebuilt by reading each open draft's payload once and keeping the ones stamped with
    // this group. Nothing is fetched unless the active draft actually belongs to a split, so the
    // normal planner page makes no extra requests at all.
    // The cache is REF-first: an analyze kicked off in the same tick as the split that created the
    // drafts must see the payloads immediately, and React state is a render behind.
    const splitSetPayloadsRef = useRef({});

    const cacheDraftPayloads = (entries) => {
        const next = { ...splitSetPayloadsRef.current };
        (entries || []).forEach(([id, payload]) => { next[id] = payload; });
        splitSetPayloadsRef.current = next;
        setSplitSetPayloads(next);
        return next;
    };

    // Read the payload of every open draft we haven't seen yet (once each). Used by the set panel
    // and by the drafts-bar dropdown's "2/3" chips -- a draft summary carries no split stamp.
    const hydrateDraftPayloads = (list) => {
        const targets = (list || drafts || []).filter(d => d && d.id
            && !Object.prototype.hasOwnProperty.call(splitSetPayloadsRef.current, d.id));
        if (targets.length === 0) return Promise.resolve(null);
        return Promise.all(targets.map(d => Promise.resolve(ApiClient.get(draftUrl(d.id)))
            .then(res => [d.id, res?.data?.payload || null])
            .catch(() => [d.id, null])))
            .then((entries) => {
                cacheDraftPayloads(entries);
                return entries;
            });
    };

    useEffect(() => {
        const gid = splitGroup?.id;
        if (!gid) return;
        setSplitSetOutcomes(readSplitOutcomes(gid));
        hydrateDraftPayloads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [splitGroup?.id, drafts]);

    const splitSetMembers = useMemo(() => buildSplitSetMembers({
        groupId: splitGroup?.id,
        drafts,
        payloadsById: splitSetPayloads,
        outcomes: splitSetOutcomes,
    }), [splitGroup, drafts, splitSetPayloads, splitSetOutcomes]);

    // Remember what happened to one member (analyzed / confirmed), in the browser-local ledger and
    // on screen. Never touches the draft itself.
    const recordSplitOutcome = (draftId, patch, groupIdOverride) => {
        const gid = groupIdOverride || splitGroupRef.current?.id;
        if (!gid || !draftId) return;
        const group = writeSplitOutcome(gid, draftId, patch);
        if (gid === splitGroupRef.current?.id) setSplitSetOutcomes(group);
    };

    // The FBA-prefixed shipment ids of a confirmed run, best-effort: the confirmed-shipments
    // endpoint already resolves (and caches) them. A failure just means the row reads "Confirmed"
    // without ids -- it must never break the confirm that already happened.
    const fetchConfirmedFbaIds = async (runId) => {
        if (runId == null) return [];
        try {
            const res = await ApiClient.get('/api/shipment-planner/confirmed');
            const rows = Array.isArray(res?.data) ? res.data : [];
            const match = rows.find(r => r && String(r.runId) === String(runId));
            return (match?.shipments || []).map(s => s?.shipmentConfirmationId).filter(Boolean);
        } catch (err) {
            return [];
        }
    };

    // Analyze ONE member without opening it (the per-row "Analyze" button). Task 103: this is a
    // server-side job too, scoped to that member's DRAFT -- so a single-member analyze survives
    // navigation exactly as the whole-set run does. Confirming is never automated: the operator
    // opens the shipment and confirms it there, exactly as before.
    const analyzeSplitMember = async (member, groupIdHint) => {
        if (!member || !member.draftId) return null;
        // The group the outcome belongs to: an explicit hint (the auto-submit run, which starts
        // before the panel state has caught up) beats the panel's current stamp.
        const gid = groupIdHint || member.groupId || splitGroupRef.current?.id;
        setSplitSetAnalyzing(member.draftId);
        try {
            let payload = splitSetPayloadsRef.current[member.draftId];
            if (!payload) {
                const res = await ApiClient.get(draftUrl(member.draftId));
                payload = res?.data?.payload || null;
                cacheDraftPayloads([[member.draftId, payload]]);
            }
            const body = splitSetAnalyzePayload(payload);
            if (!body) throw new Error('this shipment has no rows to analyze');
            const data = await runAnalyzeJob({
                url: '/api/shipment-planner/analyze-asin-list/async',
                body,
                scopeType: SCOPE_DRAFT,
                scopeKey: member.draftId,
                label: member.name,
            });
            recordSplitOutcome(member.draftId, {
                name: member.name,
                index: member.index,
                total: member.total,
                asin: member.asin,
                runId: data?.runId ?? null,
                decision: bestAnalysisDecision(data),
                error: null,
            }, gid);
            return { ok: true, data: data || null, error: null };
        } catch (err) {
            // Row-level failure, never a crash: Amazon errors, the New-condition invariant, an
            // incomplete packaging entry and the plan-creation budget all land here, get written to
            // this member's row, and the caller carries on with the next shipment.
            console.error('Split-set analyze failed:', err);
            const error = err?.response?.data?.error || err?.message || 'analyze failed';
            recordSplitOutcome(member.draftId, {
                name: member.name,
                index: member.index,
                total: member.total,
                asin: member.asin,
                error,
            }, gid);
            return { ok: false, data: null, error };
        } finally {
            setSplitSetAnalyzing(null);
        }
    };

    /**
     * Task 103 -- the whole set, analyzed BY THE SERVER.
     *
     * This used to be a `for` loop in this component awaiting one analyze after another. It worked
     * only while the tab stayed open: navigating away or refreshing abandoned every member that
     * had not been requested yet, silently, with no evidence on return that anything had been in
     * progress. Now one POST enqueues the whole set as a multi-step job and this function's only
     * remaining responsibility is to WATCH it -- which is why walking away is now harmless.
     *
     * Still sequential (the runner analyzes members one at a time: Amazon rate-limits plan
     * creation), still isolates a failing member from the rest, and still NEVER confirms -- no code
     * path from here can reach a confirm endpoint.
     */
    const startSplitSetJob = async (gid) => {
        const groupId = gid || splitGroupRef.current?.id;
        if (!groupId || splitSetBusyRef.current) return null;
        splitSetBusyRef.current = true;
        setSplitSetSummary(null);
        try {
            const job = await startJob(
                `/api/shipment-planner/split-sets/${encodeURIComponent(groupId)}/analyze`,
                {},
                { params: { scopeType: SCOPE_SPLIT_GROUP, scopeKey: groupId } });
            applySplitSetJob(job);
            return watchSplitSetJob(groupId, job);
        } catch (err) {
            console.error('Split-set analyze-all failed to start:', err);
            setSplitSetSummary(err?.response?.data?.error || err?.message || 'could not start the analyze');
            return null;
        } finally {
            splitSetBusyRef.current = false;
        }
    };

    // Push one server job reading onto the panel: progress button, per-row states, the summary
    // line. The panel's own presentation is untouched -- it just reads server facts now.
    const applySplitSetJob = useCallback((job) => {
        if (!job) return;
        setSplitSetRunAll(runAllProgress(job));
        setSplitSetAnalyzing(runningDraftId(job));
        setSplitSetOutcomes(prev => splitOutcomesFromJob(job, prev));
        if (!jobIsActive(job)) {
            const summary = job.result?.summary
                || (job.phase === 'INTERRUPTED' || job.phase === 'FAILED' ? job.error : null);
            if (summary) setSplitSetSummary(summary);
        }
    }, []);

    // Poll one split-set job to completion. Cancels its POLLING on unmount; the job itself keeps
    // running server-side, which is the entire point.
    const splitSetPollRef = useRef({ cancelled: false, jobId: null });
    const watchSplitSetJob = async (groupId, job) => {
        if (!job?.jobId || !jobIsActive(job)) return job;
        splitSetPollRef.current.jobId = job.jobId;
        const finished = await waitForJob(job.jobId, {
            onProgress: applySplitSetJob,
            isCancelled: () => splitSetPollRef.current.cancelled
                || splitSetPollRef.current.jobId !== job.jobId,
        });
        if (finished) {
            applySplitSetJob(finished);
            // Sibling runs landed while this page was watching -- refresh the ledger-backed rows.
            hydrateDraftPayloads();
        }
        return finished;
    };

    /**
     * Task 103 -- "come back and see the current status", split-set half. On mount and whenever the
     * active split group changes, ask the server for that group's job. An ACTIVE job re-raises the
     * live progress and is followed to completion; a FINISHED one still repaints the per-member
     * outcomes, so a set analyzed in a tab that has since been closed is not a blank panel.
     */
    useEffect(() => {
        const gid = splitGroup?.id;
        if (!gid) return undefined;
        splitSetPollRef.current = { cancelled: false, jobId: null };
        (async () => {
            const job = await fetchSplitSetStatus(gid);
            if (!job || job.phase === 'NONE' || splitSetPollRef.current.cancelled) return;
            applySplitSetJob(job);
            if (jobIsActive(job)) await watchSplitSetJob(gid, job);
        })();
        return () => { splitSetPollRef.current.cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [splitGroup?.id]);

    // "Analyze all in this set" (the panel button): the server decides which members are still
    // analyzable (a confirmed member has no draft, so it is never re-analyzed).
    const analyzeAllInSplitSet = () => startSplitSetJob();

    // Confirm-success hook: a confirmed plan retires the draft it was built from (plan ASINs ⊆
    // draft rows -- see confirmedPlanClearsDraft). Only the ACTIVE draft is a candidate: the panel
    // that produced the confirmed plan IS the active draft, so the operator's other in-progress
    // shipments are never touched. Cancels any in-flight autosave first so the delete can't be
    // immediately overwritten.
    const clearDraftAfterConfirm = (planItems) => {
        if (!confirmedPlanClearsDraft((planItems || []).map(i => i.asin), lastSavedDraftRef.current)) return;
        draftDebouncerRef.current.cancel();
        const id = activeDraftIdRef.current;
        lastSavedDraftRef.current = null;
        draftExistsRef.current = false;
        setDraftBanner(null);
        setResumeDraft(null);
        setDrafts(prev => (prev || []).filter(d => d && d.id !== id));
        fireDraftRequest(ApiClient.delete(draftUrl(id)), null, 'Failed to clear planner draft after confirm:');
    };

    // Task 96: a confirmed member must not vanish from its split set. The draft row is deleted by
    // the hook above (that is task 69's rule and stays), so the confirm is written to the ledger
    // instead -- the set panel then shows it as "Confirmed · FBA…" alongside its still-open
    // siblings. Best-effort ids: the confirm response carries a shipment COUNT, and the
    // confirmed-shipments endpoint resolves the FBA-prefixed ids Amazon assigns afterwards.
    const recordSplitConfirm = async (shipmentCount) => {
        const g = splitGroupRef.current;
        if (!g) return;
        const draftId = activeDraftIdRef.current;
        const runId = analysisResult?.runId ?? null;
        const base = { name: activeDraftName, index: g.index, total: g.total, asin: g.asin, runId };
        const at = new Date().toISOString();
        recordSplitOutcome(draftId, { ...base, confirmed: { at, shipmentCount: shipmentCount ?? null, ids: [] } }, g.id);
        const ids = await fetchConfirmedFbaIds(runId);
        if (ids.length > 0) {
            recordSplitOutcome(draftId, { ...base, confirmed: { at, shipmentCount: shipmentCount ?? null, ids } }, g.id);
        }
    };

    useEffect(() => {
        if (autoImportDraft && !autoImportedRef.current) {
            autoImportedRef.current = true;
            importDraft();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoImportDraft]);

    // Task 19 (task-18 concern #1): populate this panel from a run loaded via the outer
    // component's ?run= deep link (source_mode ASIN/PROBE). Guarded by runId so it only fires
    // once per loaded run (idempotent under React 18 StrictMode's double-invoke, and doesn't
    // fight a subsequent live analyze). Legacy runs saved before scoring existed carry
    // placementOptions but no scores -- flagged so the results section can show a note instead
    // of silently rendering nothing.
    const loadedRunIdRef = useRef(null);
    const [legacyRunNoScores, setLegacyRunNoScores] = useState(false);
    useEffect(() => {
        if (!loadedRun || loadedRunIdRef.current === loadedRun.runId) return;
        loadedRunIdRef.current = loadedRun.runId;

        // Task 95: THE fix. These rows came from a run, not from the active draft -- record that
        // BEFORE setItems so the autosave effect (which runs in the same commit as this state
        // update) sees the new origin and stays out. setRowsOriginNow also cancels any save already
        // queued from the draft rows this run is replacing.
        setRowsOriginNow(ROWS_FROM_RUN);
        setLoadedRunIdForNotice(loadedRun.runId ?? null);

        const { items: parsedItems, packagingByAsin: parsedPackaging } = parseRunItemsForAsinPanel(loadedRun.items);
        setItems(parsedItems);
        setPackagingByAsin(prev => ({ ...prev, ...parsedPackaging }));
        setAnalysisResult(loadedRun);

        const scores = loadedRun.scores || [];
        setLegacyRunNoScores(scores.length === 0);
        // The operator's own last choice wins over the top-scored option -- reopening a run must
        // not silently revert what they picked.
        const storedChoice = initialPlacementFor(loadedRun);
        if (loadedRun.selectedPlacementId && storedChoice) {
            setSelectedOptionId(storedChoice);
        } else if (scores.length > 0) {
            setSelectedOptionId(scores[0].placementOptionId);
        } else if (loadedRun.placementOptions && loadedRun.placementOptions.length > 0) {
            setSelectedOptionId(loadedRun.placementOptions[0].placementOptionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedRun]);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 5000);
        return () => clearTimeout(t);
    }, [toast]);

    // Task 14: whenever the plan grows (Import from PO / Paste list+total / War Room draft / a
    // single ASIN added via search), fetch packaging defaults for any ASIN not yet checked, in one
    // batched GET. A row with no calibrate_asin_packaging_defaults entry is recorded as `null`
    // (distinct from "not yet fetched", i.e. absent) so it isn't re-fetched every render.
    useEffect(() => {
        const missing = Array.from(new Set(
            items.map(it => it.asin).filter(asin => asin && !(asin in packagingByAsin))));
        if (missing.length === 0) return;
        ApiClient.get(`/api/shipment-planner/packaging?asins=${encodeURIComponent(missing.join(','))}`)
            .then(res => {
                const byAsin = {};
                (res.data || []).forEach(row => { byAsin[row.asin] = row; });
                setPackagingByAsin(prev => {
                    const next = { ...prev };
                    missing.forEach(asin => {
                        next[asin] = packagingEntryFromRow(byAsin[asin]);
                    });
                    return next;
                });
                // Task 88: same response already carries allowed_modes -- feed the shared map so
                // the ASIN-mode rows can show the read-only designation chip. No extra request.
                setAllowedModesByAsin(prev => mergeAllowedModes(prev, missing, res.data || []));
                harvestProductIdentity(res.data);
            })
            .catch(err => {
                console.error('Failed to fetch packaging defaults:', err);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    /**
     * 2026-08-21 (run 266): a draft carries its own copy of each row's packaging, and the fetch
     * above deliberately skips any ASIN already present -- so a draft built before a carton was
     * corrected kept the OLD numbers forever. The operator stored a real 4-units/box carton for
     * B0DJG4N9M7 and B0F1Z6HL52 at 18:45, re-analyzed at 18:51, and the run still went to Amazon
     * declaring 1 unit per box: the request's packaging override outranks the stored table, and
     * nothing ever re-read the table. That is what produced 185 single-unit "cartons" and 11
     * pallets on a shipment that should be a fraction of that -- the arithmetic was right and the
     * inputs were stale.
     *
     * So: whenever a draft is restored, re-read the stored defaults for its ASINs and let a
     * complete stored carton replace a copy nobody CONFIRMED. A confirmed row is the operator's
     * own statement about a physical carton and is never overwritten; an unconfirmed one is at
     * best a suggestion, and the stored table is the better suggestion.
     */
    const reconciledDraftRef = useRef(null);
    useEffect(() => {
        const draftId = activeDraftId;
        const asins = Array.from(new Set(items.map(it => it.asin).filter(Boolean)));
        if (!draftId || asins.length === 0 || reconciledDraftRef.current === draftId) return;
        reconciledDraftRef.current = draftId;
        ApiClient.get(`/api/shipment-planner/packaging?asins=${encodeURIComponent(asins.join(','))}`)
            .then(res => {
                harvestProductIdentity(res.data);
                const byAsin = {};
                (res.data || []).forEach(row => { byAsin[row.asin] = row; });
                setPackagingByAsin(prev => {
                    const next = { ...prev };
                    let changed = false;
                    asins.forEach(asin => {
                        if (packagingConfirmedAsinRef.current?.[asin]) return;  // operator's own word
                        const stored = packagingEntryFromRow(byAsin[asin]);
                        if (!stored || !isPackagingComplete(stored)) return;
                        if (packagingValuesMatch(stored, next[asin])) return;
                        next[asin] = stored;
                        changed = true;
                    });
                    return changed ? next : prev;
                });
            })
            .catch(err => {
                // Best effort: a failed reconcile leaves the draft's own copy in place, which is
                // the behaviour that existed before this effect.
                console.error('Failed to reconcile draft packaging against stored defaults:', err);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDraftId, items]);

    // Task 80: the same batched-prefetch shape as packaging above, for SKU condition. Whenever the
    // plan grows, fetch the conditions that actually have a SKU for each new ASIN. New is
    // pre-selected when one exists; when it does NOT, the row's selector is deliberately left
    // empty so the operator has to choose, and the analyze gate holds until they do. Nothing here
    // ever picks a used SKU on the operator's behalf.
    useEffect(() => {
        const missing = Array.from(new Set(
            items.map(it => it.asin).filter(asin => asin && !(asin in conditionsByAsin))));
        if (missing.length === 0) return;
        ApiClient.get(`/api/shipment-planner/conditions?asins=${encodeURIComponent(missing.join(','))}`)
            .then(res => {
                const data = res.data || {};
                setConditionsByAsin(prev => {
                    const next = { ...prev };
                    missing.forEach(asin => { next[asin] = data[asin] || null; });
                    return next;
                });
                setConditionSkuByAsin(prev => {
                    const next = { ...prev };
                    missing.forEach(asin => {
                        if (next[asin]) return; // never overwrite an operator's explicit choice
                        const entry = data[asin];
                        // defaultSku is the New SKU, or null when the ASIN has no New listing --
                        // in which case the row stays unresolved on purpose.
                        if (entry && entry.defaultSku) next[asin] = entry.defaultSku;
                    });
                    return next;
                });
            })
            .catch(err => {
                console.error('Failed to fetch SKU conditions:', err);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    // ---- item list ----

    const handleSearch = useCallback(async () => {
        if (!searchQuery || searchQuery.length < 2) return;
        setSearchLoading(true);
        try {
            const res = await ApiClient.get(`/api/shipment-planner/product-search?q=${encodeURIComponent(searchQuery)}`);
            setSearchResults(res.data);
        } catch (err) {
            console.error('Search failed:', err);
        }
        setSearchLoading(false);
    }, [searchQuery]);

    const addItem = (product) => {
        setItems(prev => (prev.find(i => i.asin === product.asin) ? prev : [...prev, {
            asin: product.asin,
            sku: product.sku,
            name: product.name,
            quantity: 1,
            sellingPrice: product.selling_price,
        }]));
        setSearchResults([]);
        setSearchQuery('');
    };

    // Task 91: adds a result from the new SearchAddBar (product-search-detailed shape --
    // camelCase sellingPrice/fnsku/conditionLabel/newCondition, unlike addItem's product-search
    // rows above). Appends exactly like the other entry paths, then pre-seeds conditionsByAsin /
    // conditionSkuByAsin from the search result so the row's condition chip and the task-80 gate
    // render immediately instead of waiting on the batched /conditions round trip -- same shape
    // that fetch would have produced. SearchAddBar itself already refuses to call this for a
    // blocked result, so a blocked ASIN never reaches here.
    const addFromSearchBar = (product, qty) => {
        const parsedQty = Math.max(1, parseInt(qty, 10) || 1);
        setItems(prev => (prev.find(i => i.asin === product.asin) ? prev : [...prev, {
            asin: product.asin,
            sku: product.sku,
            name: product.name,
            quantity: parsedQty,
            sellingPrice: product.sellingPrice,
        }]));
        if (product.sku) {
            setConditionsByAsin(prev => (product.asin in prev ? prev : {
                ...prev,
                [product.asin]: {
                    options: [{
                        sku: product.sku,
                        conditionLabel: product.conditionLabel,
                        fnsku: product.fnsku,
                        newCondition: product.newCondition,
                    }],
                    hasNew: !!product.newCondition,
                    defaultSku: product.sku,
                    blockedReason: null,
                },
            }));
            setConditionSkuByAsin(prev => (product.asin in prev ? prev : { ...prev, [product.asin]: product.sku }));
        }
    };

    const updateQty = (idx, qty) => {
        // Milestone C phase 0: units are the wire truth -- typing a unit quantity directly clears
        // any pallets figure showing for this row, so the two inputs never silently disagree.
        const asin = items[idx]?.asin;
        if (asin) {
            setPalletsByAsin(prev => {
                if (!(asin in prev)) return prev;
                const next = { ...prev };
                delete next[asin];
                return next;
            });
        }
        setItems(prev => prev.map((it, i) => (i === idx ? { ...it, quantity: parseInt(qty, 10) || 0 } : it)));
    };

    // Milestone C phase 0: pallets -> units for a direct ASIN-mode row. Recorded in palletsByAsin
    // purely for display (the derivation line + the declared-total prefill); the wire value the
    // row actually carries is the derived unit quantity.
    const updatePalletsForAsin = (asin, idx, pallets) => {
        setPalletsByAsin(prev => ({ ...prev, [asin]: pallets }));
        const pkg = packagingByAsin[asin];
        const units = palletsToUnits(pallets, pkg);
        setItems(prev => prev.map((it, i) => (i === idx ? { ...it, quantity: units } : it)));
    };

    const removeItemAt = (idx) => {
        const removedAsin = items[idx]?.asin;
        setItems(prev => prev.filter((_, i) => i !== idx));
        // Task 34: drop the stale green-confirmed flag once the row is actually gone, so a later
        // re-add via a non-PO path (which shouldn't be marked confirmed) never inherits it.
        if (removedAsin && items.filter(it => it.asin === removedAsin).length === 1) {
            setPackagingConfirmedAsin(prev => {
                if (!(removedAsin in prev)) return prev;
                const next = { ...prev };
                delete next[removedAsin];
                return next;
            });
        }
    };

    const importDraft = async () => {
        setImportingDraft(true);
        try {
            const res = await ApiClient.get('/api/war-room/planning');
            const rows = (res.data && res.data.rows) || [];
            const draftRows = rows.filter(r => r.draft_qty != null && r.draft_qty > 0);
            if (draftRows.length === 0) {
                setToast('No War Room PO draft lines found — build a draft on the War Room Planning tab first.');
            } else {
                setItems(draftRows.map(r => ({
                    asin: r.asin,
                    sku: r.item_code || null,
                    name: r.title,
                    quantity: r.draft_qty,
                    sellingPrice: null,
                })));
                setPlanSource('War Room draft'); // Task 69: draft-banner source label
                setToast(`Imported ${draftRows.length} line${draftRows.length !== 1 ? 's' : ''} from the War Room PO draft.`);
            }
        } catch (err) {
            console.error('Failed to import War Room draft:', err);
            setToast('Failed to import War Room draft: ' + (err.response?.data?.error || err.message));
        }
        setImportingDraft(false);
    };

    // Merges new (asin, quantity) lines into the plan, summing qty into any ASIN already present.
    const mergeItemsIntoPlan = (newLines) => {
        setItems(prev => {
            const byAsin = new Map(prev.map(it => [it.asin, { ...it }]));
            newLines.forEach(nl => {
                if (!nl.asin || !nl.quantity) return;
                if (byAsin.has(nl.asin)) {
                    const existing = byAsin.get(nl.asin);
                    existing.quantity = (existing.quantity || 0) + nl.quantity;
                    byAsin.set(nl.asin, existing);
                } else {
                    byAsin.set(nl.asin, {
                        asin: nl.asin,
                        sku: nl.sku || null,
                        name: nl.name || nl.asin,
                        quantity: nl.quantity,
                        sellingPrice: null,
                    });
                }
            });
            return Array.from(byAsin.values());
        });
    };

    // ---- Task 24/41: packaging modal (two-tier box model) ----
    // isPackagingComplete moved to module scope above (Task 3) so PO Creation can import it too;
    // it is pure (only reads `pkg`), so the relocation changes nothing about its behaviour.

    // Task 24/41/67/69/39/80: the box-configuration modal's validation, box-catalog CRUD, and
    // apply/save logic now live in useBoxConfig (Task 2 extraction) -- packagingByAsin/
    // poConfirmDefaultsByAsin/poPackagingMetaByAsin/setPackagingByAsin/setToast/
    // setPoConfirmConfirmed/setPackagingConfirmedAsin/shipmentMode are genuinely planner-wide
    // state the hook does not own, so they (and the handful of pure helpers the hook's logic
    // calls, several of which -- like isPackagingComplete -- are also used well outside the
    // modal and unit-tested by importing them from this file directly) are passed in rather than
    // duplicated or imported, which would create a pages/hooks import cycle.
    const {
        packagingPopoverAsin, packagingPopoverTitle, packagingPopoverQty,
        packagingForm, packagingFieldErrors, packagingFormValid, packagingAttemptedSubmit,
        packagingStyleChoice, packagingSaving, packagingLoadedLbs,
        packagingModalActivePkg, packagingModalDerived, packagingModalLegacyOnly, packagingModalTotals,
        boxCatalog, choosePackagingStyle, closePackagingPopover, applyPackaging, updatePackagingField,
        newBoxOpen, newBoxForm, newBoxSaving, editingBoxId, boxError, boxNote, selectedBox,
        updateNewBoxField, saveNewBoxToCatalog, renderCatalogBoxCard,
        setEditingBoxId, setNewBoxForm, setBoxError, setNewBoxOpen, setSelectedBoxId,
        openPackagingPopover, loadBoxCatalog,
    } = useBoxConfig({
        shipmentMode,
        packagingByAsin,
        poConfirmDefaultsByAsin,
        poPackagingMetaByAsin,
        setPackagingByAsin,
        setToast,
        setPoConfirmConfirmed,
        setPackagingConfirmedAsin,
        isPackagingComplete,
        fmtLbsShort,
        computeLoadedBoxWeightLbs,
        deriveLegacyPackagingFields,
        computeShipmentTotals,
        boxFormFromCatalogBox,
        boxCatalogErrorText,
        unitBoxEntryFromDerived,
        unitBoxMatchesDerived,
        defaultPackagingStyle,
        onSaved: () => {},
    });

    // ---- Task 12: "Import from PO" ----

    // PO lifecycle (owner 2026-09-07): the list is open POs by default; the show-closed toggle
    // refetches with includeClosed=1 so history stays reachable without polluting on-order.
    const refreshPoList = async (showClosed) => {
        setPoListLoading(true);
        setPoListError(null);
        try {
            const res = await ApiClient.get(
                `/api/shipment-planner/supplier-pos${showClosed ? '?includeClosed=1' : ''}`);
            setPoList(res.data || []);
        } catch (err) {
            console.error('Failed to load supplier POs:', err);
            setPoListError(err.response?.data?.error || err.message);
        }
        setPoListLoading(false);
    };

    const openPoModal = async () => {
        setShowPoModal(true);
        setSelectedPoNumber(null);
        setPoItems([]);
        setPoShowAllModes(false); // Task 88: the mode filter is on by default, every session
        await refreshPoList(poShowClosed);
    };

    // Closing removes the PO's remaining units from on-order everywhere; the PO stays visible in
    // history (show-closed toggle) and can be reopened -- idempotent both ways, always asked.
    const setPoClosed = async (po, closed) => {
        const msg = closed
            ? `Close PO ${po.poNumber}? This removes its remaining ${po.totalOpenUnits} not-yet-received unit${po.totalOpenUnits !== 1 ? 's' : ''} `
                + 'from on-order everywhere. The PO stays visible in history and can be reopened.'
            : `Reopen PO ${po.poNumber}? Its open units will count as on-order again.`;
        if (!window.confirm(msg)) return;
        try {
            await ApiClient.post(
                `/api/shipment-planner/supplier-pos/${encodeURIComponent(po.poNumber)}/close`,
                { closed });
            await refreshPoList(poShowClosed);
        } catch (err) {
            console.error('Failed to update PO lifecycle:', err);
            setToast(`Could not ${closed ? 'close' : 'reopen'} PO ${po.poNumber}: `
                + (err.response?.data?.error || err.message));
        }
    };

    const closePoModal = () => {
        setShowPoModal(false);
        setSelectedPoNumber(null);
        setPoItems([]);
        setPoSearch('');
        setPoConfirmStep(false);
        setPoConfirmRows([]);
        setPoConfirmDefaultsByAsin({});
        setPoConfirmConfirmed({});
        setPoConfirmSaveDefault({});
        setPoShowAllModes(false);
    };

    const selectPo = async (poNumber) => {
        setSelectedPoNumber(poNumber);
        setPoShowAllModes(false); // Task 88: a fresh PO starts filtered to the current tab
        setPoItemsLoading(true);
        setPoItemsError(null);
        setPoItems([]);
        setAsinAliasesByItemCode({});
        try {
            const res = await ApiClient.get(`/api/shipment-planner/supplier-pos/${encodeURIComponent(poNumber)}/items`);
            const lines = res.data || [];
            setPoItems(lines);
            // Multi-ASIN awareness (owner 2026-09-03): one batched fetch per PO; absent = single.
            const itemCodes = [...new Set(lines.map(l => l.itemCode).filter(Boolean))];
            if (itemCodes.length > 0) {
                ApiClient.get(`/api/shipment-planner/asin-aliases?itemCodes=${encodeURIComponent(itemCodes.join(','))}`)
                    .then(r => setAsinAliasesByItemCode(r.data?.aliases || {}))
                    .catch(() => { /* degrades to single-ASIN behavior */ });
            }
            const checked = {};
            const qty = {};
            lines.forEach(l => {
                if (l.asin) {
                    // Owner REVERSAL 2026-09-01 ("when a PO is loaded ... all row checkboxes are
                    // automatically selected, they should all be unselected"): every line loads
                    // UNCHECKED and the operator opts in — per line or via select-all, which
                    // still skips blocked/fully-sent/spoken-for lines (the 2026-08-18/task-84/
                    // task-85 rules live on there and in the per-line disable).
                    checked[l.asin] = false;
                    // Task 85: default the ship qty to what is actually left over.
                    qty[l.asin] = poLineDefaultShipQty(l);
                }
            });
            setPoLineChecked(checked);
            setPoLineQty(qty);
            // Owner 2026-09-01: local (Orlando) split — fresh PO starts with no local intent,
            // and the SFP-listing gate data loads for every line in one batched GET.
            setPoLineLocalQty({});
            const gateAsins = lines.filter(l => l.asin).map(l => l.asin);
            if (gateAsins.length > 0) {
                ApiClient.get(`/api/local-sfp/status?asins=${encodeURIComponent(gateAsins.join(','))}`)
                    .then(r => setSfpStatusByAsin(r.data || {}))
                    .catch(() => setSfpStatusByAsin({}));
            } else {
                setSfpStatusByAsin({});
            }
            // Task 61: allowed_modes + pallet meta for the toggle default and the LTL step.
            const asins = lines.filter(l => l.asin).map(l => l.asin);
            if (asins.length > 0) {
                ApiClient.get(`/api/shipment-planner/packaging?asins=${encodeURIComponent(asins.join(','))}`)
                    .then(r => {
                        const meta = {};
                        (r.data || []).forEach(row => { meta[row.asin] = row; });
                        setPoPackagingMetaByAsin(meta);
                        // Task 88: the designation for every line of this PO, in the same batched
                        // GET that already ran -- the per-line control and the tab filter read it.
                        setAllowedModesByAsin(prev => mergeAllowedModes(prev, asins, r.data || []));
                    })
                    .catch(() => { /* toggle just stays on its manual/default value */ });
            } else {
                setPoPackagingMetaByAsin({});
            }
        } catch (err) {
            console.error('Failed to load PO items:', err);
            setPoItemsError(err.response?.data?.error || err.message);
        }
        setPoItemsLoading(false);
    };

    // Owner, 2026-08-26: the workspace's "back to the open lines" link lands on ?po=<n> -- reopen
    // the panel on that PO's lines directly. Once only: after that the operator owns the panel.
    // Owner, 2026-08-31: a BARE landing (no run to load, no draft to import) opens the panel at
    // the PO list -- the default view IS where "load from purchase order" starts.
    useEffect(() => {
        if (poDeepLinkDoneRef.current) return;
        poDeepLinkDoneRef.current = true;
        const po = poDeepLinkParams.get('po');
        if (po) {
            openPoModal();
            selectPo(po);
        } else if (!autoImportDraft && !poDeepLinkParams.get('run') && !poDeepLinkParams.get('mode')) {
            // Bare landing only: an explicit ?mode=asin (Shipment Runs "New analysis", deep
            // links) still gets the blank panel -- those navigations carry their own intent.
            openPoModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Task 61: keep the toggle on its allowed_modes-derived default until the operator touches it.
    // Owner 2026-09-01 (lines now load UNCHECKED): with nothing checked yet, derive the default
    // from ALL unblocked lines instead of the empty selection — otherwise an LTL-only PO would
    // load in UPS mode with every line hidden by the task-88 tab filter, unselectable.
    useEffect(() => {
        if (!showPoModal || shipmentModeTouchedRef.current) return;
        const selected = poItems.filter(l => l.asin && poLineChecked[l.asin]).map(l => l.asin);
        const basis = selected.length > 0
            ? selected
            : poItems.filter(l => l.asin && !l.blocked).map(l => l.asin);
        setShipmentMode(defaultShipmentModeForAsins(basis, poPackagingMetaByAsin));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPoModal, poItems, poLineChecked, poPackagingMetaByAsin]);

    // Task 88: which PO lines the current tab shows. The owner's rule -- UPS tab keeps UPS +
    // UPS/LTL, LTL tab keeps LTL + UPS/LTL -- with unset/unknown counted as UPS/LTL so nothing can
    // vanish silently. Lines with no ASIN can't carry a designation and are never hidden.
    const poLinePartition = useMemo(
        () => partitionPoLinesByMode(poItems, shipmentMode, allowedModesByAsin, poShowAllModes),
        [poItems, shipmentMode, allowedModesByAsin, poShowAllModes]);
    const poVisibleAsins = useMemo(
        () => new Set(poLinePartition.visible.map(l => l.asin).filter(Boolean)),
        [poLinePartition]);

    const chooseShipmentMode = (mode) => {
        shipmentModeTouchedRef.current = true;
        setShipmentMode(mode);
        // Task 88: switching the tab re-filters live AND de-selects any line the new tab hides --
        // a hidden line can never enter a plan, and the "N lines selected" count drops immediately
        // so the operator sees it happen. (goToPoConfirmStep filters again as belt-and-braces.)
        if (poShowAllModes) return;
        setPoLineChecked(prev => {
            let changed = false;
            const next = { ...prev };
            poItems.forEach(l => {
                if (!l.asin || !next[l.asin]) return;
                if (!lineAllowedInMode(allowedModesFor(l.asin, allowedModesByAsin), mode)) {
                    next[l.asin] = false;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    };

    // Task 88: persist one ASIN's designation the moment the operator picks it. Optimistic -- the
    // row (and the tab filter) reflect the new value with no reload -- and rolled back with a
    // visible toast if the POST fails, so the UI never claims a save that didn't happen.
    const setAsinAllowedModes = async (asin, value) => {
        if (!asin) return;
        const previous = allowedModesByAsin[asin] ?? null;
        const next = normalizeAllowedModes(value);
        if (previous === next) return;
        setAllowedModesByAsin(prev => ({ ...prev, [asin]: next }));
        // Keep the toggle-default source in step too, so the UPS|LTL default stays coherent with
        // what the operator just designated (only matters while the toggle is untouched).
        setPoPackagingMetaByAsin(prev => (prev[asin]
            ? { ...prev, [asin]: { ...prev[asin], allowedModes: next } }
            : prev));
        // A line the current tab no longer shows must not stay selected.
        if (!poShowAllModes && !lineAllowedInMode(next, shipmentMode)) {
            setPoLineChecked(prev => (prev[asin] ? { ...prev, [asin]: false } : prev));
        }
        try {
            await ApiClient.post('/api/shipment-planner/packaging-mode', { asin, allowedModes: next });
        } catch (err) {
            console.error('Failed to save shipping designation:', err);
            setAllowedModesByAsin(prev => ({ ...prev, [asin]: previous }));
            setPoPackagingMetaByAsin(prev => (prev[asin]
                ? { ...prev, [asin]: { ...prev[asin], allowedModes: previous } }
                : prev));
            setToast(`Couldn't save the shipping designation for ${asin} — it has been put back.`);
        }
    };

    // Task 61 guard rail: split/compare, watch, and partition sweep stay UPS-only (v1) -- an LTL
    // plan always analyzes as one combined plan through the mode-aware analyze path.
    useEffect(() => {
        if (shipmentMode === 'LTL') setAnalyzeMode('combined');
    }, [shipmentMode]);

    const togglePoLine = (asin) => {
        setPoLineChecked(prev => ({ ...prev, [asin]: !prev[asin] }));
    };

    // Select / deselect all PO lines (only those with an ASIN are selectable). Owner addition
    // 2026-08-18: select-all does NOT check fully-sent lines -- they join only via manual click
    // (their current manual state is preserved on check-all); deselect-all clears everything.
    const setAllPoLines = (checked) => {
        setPoLineChecked(prev => {
            const next = {};
            poItems.forEach(l => {
                if (!l.asin) return;
                // Task 88: select-all never reaches a line the current tab hides.
                if (!poVisibleAsins.has(l.asin)) {
                    next[l.asin] = false;
                // Task 80: never sweep in a line that has no New-condition SKU.
                } else if (l.blocked) {
                    next[l.asin] = false;
                } else if (isPoLineFullySent(l) || isPoLineSpokenFor(l)) {
                    // Task 85: spoken-for lines join only by manual click, like fully-sent ones.
                    next[l.asin] = checked ? !!prev[l.asin] : false;
                } else {
                    next[l.asin] = checked;
                }
            });
            return next;
        });
    };

    const updatePoLineQty = (asin, qty) => {
        setPoLineQty(prev => ({ ...prev, [asin]: parseInt(qty, 10) || 0 }));
    };

    // Owner 2026-09-01: local (Orlando) split. Ship + local share ONE honest pool (the line's
    // remaining units): the local qty clamps to remaining, and typing it auto-reduces the ship
    // qty when the two together would exceed the pool — the ship default is the full remainder,
    // so without this the local input could never take a value.
    const onPoLocalQtyChange = (asin, qty, line) => {
        const remaining = poLineRemaining(line) ?? 0;
        const wanted = Math.max(0, parseInt(qty, 10) || 0);
        const local = Math.min(wanted, remaining);
        setPoLineLocalQty(prev => ({ ...prev, [asin]: local }));
        const ship = poLineQty[asin] != null ? poLineQty[asin] : poLineDefaultShipQty(line);
        if ((ship || 0) + local > remaining) {
            setPoLineQty(prev => ({ ...prev, [asin]: Math.max(0, remaining - local) }));
        }
    };

    // The lines the operator has marked for local — and the subset whose ASIN still has no SFP
    // listing (the gate that must clear before assignment).
    const localLines = poItems.filter(l => l.asin && (poLineLocalQty[l.asin] || 0) > 0);
    const localMissingSfp = localLines.filter(l => !sfpStatusByAsin[l.asin]?.sfpSku);
    const localUnitsTotal = localLines.reduce((s, l) => s + (poLineLocalQty[l.asin] || 0), 0);

    const createSfpListingsForLocal = async () => {
        const asins = localMissingSfp.map(l => l.asin);
        if (asins.length === 0) return;
        setLocalAssignBusy(true);
        try {
            // Existing endpoint: creates SFP listings priced at MAP, refusing ASINs without MAP.
            await ApiClient.post('/api/sfp-inventory/create-listings', { asins });
            const r = await ApiClient.get(`/api/local-sfp/status?asins=${encodeURIComponent(
                poItems.filter(l => l.asin).map(l => l.asin).join(','))}`);
            setSfpStatusByAsin(r.data || {});
        } catch (err) {
            setToast('SFP listing creation failed: ' + (err.response?.data?.error || err.message));
        }
        setLocalAssignBusy(false);
    };

    const assignLocalUnits = async () => {
        if (localLines.length === 0 || localMissingSfp.length > 0) return;
        setLocalAssignBusy(true);
        try {
            const res = await ApiClient.post('/api/local-sfp/assign', {
                poNumber: selectedPoNumber,
                lines: localLines.map(l => ({ asin: l.asin, qty: poLineLocalQty[l.asin] })),
            });
            const errors = (res.data?.lines || []).filter(l => l.error);
            setToast(errors.length === 0
                ? `Assigned ${res.data?.created ?? localUnitsTotal} unit(s) to local (Orlando) — they go live on Amazon when a warehouse space is assigned.`
                : `Assigned ${res.data?.created ?? 0} unit(s); ${errors.length} line(s) refused: ${errors.map(e => `${e.asin}: ${e.error}`).join('; ')}`);
            setPoLineLocalQty({});
            // Refresh the lines so the new local bucket shows in assigned/remaining.
            await selectPo(selectedPoNumber);
        } catch (err) {
            setToast('Local assignment failed: ' + (err.response?.data?.error || err.message));
        }
        setLocalAssignBusy(false);
    };

    // Task 34 step 1 -> step 2: "Next: confirm box sizes". Snapshots the checked lines as
    // poConfirmRows, then batch-fetches their stored packaging defaults (for the source label --
    // "saved default" / "edited" / "none on file") plus the shared box catalog (task 41),
    // pre-filling packagingByAsin for any ASIN this session doesn't already have a live working
    // value for. The GET carries each row's qty so the response rows arrive with server-computed
    // boxCount/totalShipmentWeightLbs.
    const goToPoConfirmStep = async () => {
        const rows = poItems
            // Task 80: a PO line with no New-condition SKU can never enter a plan. It is excluded
            // here as well as un-checkable in the picker — everything on a PO is a new product.
            // Task 88: nor can a line the current tab hides (UPS-only line in LTL mode, or vice
            // versa). chooseShipmentMode already de-selects those; this is the structural backstop
            // that makes "a hidden line can never enter a plan" true no matter how state got here.
            .filter(l => l.asin && poLineChecked[l.asin] && !l.blocked && poVisibleAsins.has(l.asin))
            .map(l => ({
                asin: l.asin,
                name: l.description || l.asin,
                // Task 85: same default as the input box -- remaining when the server reports one.
                quantity: poLineQty[l.asin] != null ? poLineQty[l.asin] : poLineDefaultShipQty(l),
                // Task 80: the resolved New SKU + the FNSKU Amazon will print, carried through the
                // confirm step so the operator sees the condition before boxes are configured.
                sku: l.sku,
                fnsku: l.fnsku,
                conditionLabel: l.conditionLabel,
                newCondition: l.newCondition,
            }))
            .filter(l => l.quantity > 0);
        // Task 84: a fully-received PO comes back with openQty 0 on every line, so every ship-qty
        // box defaults to 0 and this used to return silently -- the "Next" button looked dead.
        // Say what happened instead; the operator can type the quantity they actually want to ship.
        if (rows.length === 0) {
            setToast('No line has a ship quantity yet — enter a quantity on at least one selected line.');
            setTimeout(() => setToast(null), 6000);
            return;
        }

        setPoConfirmRows(rows);
        setPoConfirmConfirmed({});
        setPoConfirmSaveDefault({});
        setPoConfirmStep(true);
        setPoConfirmLoading(true);
        // Task 61/65: prefill the shipment-level LTL form from the selected ASINs' stored pallet
        // defaults (first configured tare; NON_STACKABLE anywhere -> not stackable; uniform
        // freight_class_override -> preselected). Count/height are computed post-split, never here.
        if (shipmentMode === 'LTL') {
            const metas = rows.map(r => poPackagingMetaByAsin[r.asin]).filter(Boolean);
            const firstPos = (get) => { for (const m of metas) { const v = get(m); if (v > 0) return String(v); } return ''; };
            const fcSet = new Set(metas.map(m => m.freightClassOverride).filter(Boolean));
            setLtlForm(prev => ({
                ...prev,
                palletTareLbs: firstPos(m => m.palletTareLbs) || prev.palletTareLbs,
                stackable: metas.some(m => m.stackability === 'NON_STACKABLE') ? false : prev.stackable,
                freightClass: prev.freightClass || (fcSet.size === 1 ? [...fcSet][0] : ''),
            }));
        }
        try {
            const asins = rows.map(r => r.asin);
            const qtys = rows.map(r => r.quantity);
            const [, pkgRes] = await Promise.all([
                loadBoxCatalog(),
                ApiClient.get(`/api/shipment-planner/packaging?asins=${encodeURIComponent(asins.join(','))}`
                    + `&qtys=${encodeURIComponent(qtys.join(','))}`),
            ]);
            const defaultsByAsin = {};
            (pkgRes.data || []).forEach(row => { defaultsByAsin[row.asin] = row; });
            setPoConfirmDefaultsByAsin(defaultsByAsin);
            // Task 88: keep the shared designation map current for the confirm-step chips.
            setAllowedModesByAsin(prev => mergeAllowedModes(prev, asins, pkgRes.data || []));
            setPackagingByAsin(prev => {
                const next = { ...prev };
                rows.forEach(r => {
                    if (next[r.asin] != null) return; // keep a live edited/known value already in this session
                    next[r.asin] = packagingEntryFromRow(defaultsByAsin[r.asin]);
                });
                return next;
            });
        } catch (err) {
            console.error('Failed to load packaging for PO confirm step:', err);
            setToast('Failed to load saved packaging for these lines — you can still enter values manually.');
        }
        setPoConfirmLoading(false);
    };

    const backToPoLines = () => {
        setPoConfirmStep(false);
        setPoConfirmRows([]);
        setPoConfirmConfirmed({});
    };

    // Source label for a PO-confirm-step row: "saved default" (matches the value fetched from
    // calibrate_asin_packaging_defaults) > "edited" (complete but doesn't match) > amber "none on
    // file". (Task 41 removed the "preset X" bucket along with ASIN-scoped presets.)
    const poConfirmSourceLabel = (asin) => {
        const defaultRow = poConfirmDefaultsByAsin[asin];
        const working = packagingByAsin[asin];
        if (!isPackagingComplete(working)) return { text: 'none on file', tone: 'amber' };
        // Task 67: an untouched Amazon unit-box prefill on a row with no saved default -- the
        // zero-data-entry path, distinct from both "saved default" and a manual "edited".
        if (working.style === 'UNIT' && working.unitBoxSource === 'AMAZON' && !(defaultRow?.unitsPerBox > 0)) {
            return { text: 'unit = box (Amazon)', tone: 'blue' };
        }
        if (defaultRow && packagingValuesMatch(defaultRow, working)) return { text: 'saved default', tone: 'gray' };
        return { text: 'edited', tone: 'gray' };
    };

    const confirmPoRow = (asin) => {
        if (!isPackagingComplete(packagingByAsin[asin])) return;
        setPoConfirmConfirmed(prev => ({ ...prev, [asin]: true }));
    };

    const unconfirmPoRow = (asin) => {
        setPoConfirmConfirmed(prev => {
            if (!(asin in prev)) return prev;
            const next = { ...prev };
            delete next[asin];
            return next;
        });
    };

    const togglePoConfirmSaveDefault = (asin) => {
        setPoConfirmSaveDefault(prev => ({ ...prev, [asin]: !prev[asin] }));
    };

    // Spec: "Optionally allow deselecting a problem ASIN at step 2 (removes it from the add)."
    const removePoConfirmRow = (asin) => {
        setPoConfirmRows(prev => prev.filter(r => r.asin !== asin));
        unconfirmPoRow(asin);
        setPoConfirmSaveDefault(prev => {
            if (!(asin in prev)) return prev;
            const next = { ...prev };
            delete next[asin];
            return next;
        });
    };

    // Task 67 bulk action ("Apply 'unit = box' to all unconfigured (N)"): fills each eligible row
    // with the Amazon unit-box suggestion.
    //
    // 2026-08-21: it no longer CONFIRMS them. Filling and confirming in one click is how run 266
    // ended up declaring 185 single-unit "cartons" and 11 pallets -- Amazon's catalog guess was
    // accepted in bulk without anyone looking at a carton. Filling is a helpful head start;
    // confirming is a statement about physical reality and stays per-row and deliberate. The
    // footer gate (all rows confirmed before "Add to plan") is what makes this safe rather than
    // annoying: the operator still cannot proceed until each row has been looked at.
    const unitBoxBulkTargets = poConfirmRows.filter(r => isUnitBoxBulkTarget(
        poConfirmDefaultsByAsin[r.asin], packagingByAsin[r.asin],
        !!poConfirmConfirmed[r.asin], isPackagingComplete(packagingByAsin[r.asin])));

    const applyUnitBoxToAllUnconfigured = () => {
        if (unitBoxBulkTargets.length === 0) return;
        setPackagingByAsin(prev => {
            const next = { ...prev };
            unitBoxBulkTargets.forEach(r => {
                next[r.asin] = unitBoxEntryFromDerived(poConfirmDefaultsByAsin[r.asin]?.derivedUnitBox);
            });
            return next;
        });
        // Deliberately NO setPoConfirmConfirmed here -- see the note above.
    };

    const allPoConfirmRowsConfirmed = poConfirmRows.length > 0
        && poConfirmRows.every(r => poConfirmConfirmed[r.asin]);

    // Task 65: LTL requirements before "Add … to plan" enables -- just the warehouse facts:
    // a positive pallet footprint and tare (both defaulted, so this is normally always ready).
    // Count/height/weight are computed per shipment after Amazon returns the split.
    const ltlReady = shipmentMode !== 'LTL' || (
        parseFloat(ltlForm.palletLengthIn) > 0 && parseFloat(ltlForm.palletWidthIn) > 0
        && parseFloat(ltlForm.palletTareLbs) > 0);
    // Task 70b: LTL without a pickup day is not analyzable -- block with an explanation rather
    // than letting the backend silently pick one.
    const pickupBlocked = pickupDateBlocksAnalyze(shipmentMode, pickupDate);

    const updateLtlField = (field, value) => {
        setLtlForm(prev => ({ ...prev, [field]: value }));
    };

    // Milestone C phase 0: pallets -> units for a PO-confirm-step row. The raw packaging (including
    // boxesPerPallet) is available here -- it was fetched into packagingByAsin at goToPoConfirmStep.
    const updatePoConfirmRowPallets = (asin, pallets) => {
        setPalletsByAsin(prev => ({ ...prev, [asin]: pallets }));
        const pkg = packagingByAsin[asin];
        const units = palletsToUnits(pallets, pkg);
        setPoConfirmRows(prev => prev.map(r => (r.asin === asin ? { ...r, quantity: units } : r)));
    };

    // Milestone C phase 0: "Declared pallet total" prefills with the sum of the per-row pallet
    // entries once EVERY confirm-step row is pallet-denominated -- never forcing a zero for a
    // declaration nobody made (spec §2). Only fires while the field is still blank, so it never
    // overwrites an operator's own edit.
    useEffect(() => {
        if (shipmentMode !== 'LTL' || poConfirmRows.length === 0) return;
        if (ltlForm.declaredPalletCount !== '' && ltlForm.declaredPalletCount != null) return;
        const perRowPallets = poConfirmRows.map(r => {
            const pkg = packagingByAsin[r.asin];
            if (!(pkg?.boxesPerPallet > 0)) return null;
            const p = parseFloat(palletsByAsin[r.asin]);
            return Number.isFinite(p) && p > 0 ? p : null;
        });
        if (perRowPallets.some(p => p == null)) return;
        const total = perRowPallets.reduce((sum, p) => sum + p, 0);
        setLtlForm(prev => (prev.declaredPalletCount ? prev : { ...prev, declaredPalletCount: String(total) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poConfirmRows, packagingByAsin, palletsByAsin, shipmentMode]);

    // Footer action: confirmed values flow into the plan as this-shipment overrides via the
    // existing packagingByAsin mechanism (regardless of whether they were also saved as the ASIN
    // default) -- what the warehouse manager confirmed is what analyze/watch uses.
    const addConfirmedPoRowsToPlan = async () => {
        const rows = poConfirmRows.filter(r => poConfirmConfirmed[r.asin]);
        if (rows.length === 0) return;

        // Task 41: the save-as-default POST carries the two-tier config, so only rows whose
        // working value has one (computed) can persist -- the checkbox is disabled for legacy
        // rows in the UI, this filter is the belt-and-braces.
        // Task 67: single-unit (unit = box) rows are save-eligible too -- their POST carries the
        // UNIT style + the unit's own dims instead of a catalog carton.
        const toSaveAsDefault = rows.filter(r => {
            const pkg = packagingByAsin[r.asin];
            if (!poConfirmSaveDefault[r.asin]) return false;
            if (pkg?.style === 'UNIT') return isPackagingComplete(pkg);
            return pkg?.computed && pkg.unitWeightLbs > 0 && pkg.boxCatalogId != null;
        });
        if (toSaveAsDefault.length > 0) {
            await Promise.all(toSaveAsDefault.map(async (r) => {
                const pkg = packagingByAsin[r.asin];
                try {
                    await ApiClient.post('/api/shipment-planner/packaging', pkg.style === 'UNIT'
                        ? {
                            asin: r.asin,
                            packagingStyle: 'UNIT',
                            unitsPerBox: 1,
                            unitWeightLbs: pkg.unitWeightLbs ?? pkg.boxWeightLbs,
                            boxLengthIn: pkg.boxLengthIn,
                            boxWidthIn: pkg.boxWidthIn,
                            boxHeightIn: pkg.boxHeightIn,
                            mode: shipmentMode,
                        }
                        : {
                            asin: r.asin,
                            packagingStyle: 'CARTON',
                            unitsPerBox: pkg.unitsPerBox,
                            unitWeightLbs: pkg.unitWeightLbs,
                            boxCatalogId: pkg.boxCatalogId,
                            mode: shipmentMode, // Task 61: LTL saves skip the parcel ceiling server-side
                        });
                } catch (err) {
                    console.error(`Failed to save default packaging for ${r.asin}:`, err);
                }
            }));
        }

        // Task 61: "save pallet settings as default" -- persist the shipment-level pallet fields
        // to every confirmed ASIN's calibrate_asin_packaging_defaults pallet columns.
        if (shipmentMode === 'LTL' && ltlForm.savePalletDefault) {
            const ltl = buildLtlRequestPayload(ltlForm);
            await Promise.all(rows.map(async (r) => {
                try {
                    await ApiClient.post('/api/shipment-planner/packaging-pallet', {
                        asin: r.asin,
                        palletLengthIn: ltl.palletLengthIn,
                        palletWidthIn: ltl.palletWidthIn,
                        // Task 65: no pallet height upfront -- it's computed per shipment after
                        // the split; null keeps any previously stored value (COALESCE upsert).
                        palletTareLbs: ltl.palletTareLbs,
                        stackability: ltl.stackable ? 'STACKABLE' : 'NON_STACKABLE',
                        freightClassOverride: ltl.freightClass,
                    });
                } catch (err) {
                    console.error(`Failed to save pallet defaults for ${r.asin}:`, err);
                }
            }));
        }

        // Owner, 2026-09-03: a row whose product sells under MULTIPLE ASINs must pass through the
        // split modal BEFORE anything is staged — the operator allocates units per ASIN with the
        // context (rank, velocity, stock, inboundability) in front of them. Rows without aliases
        // stage exactly as before.
        const entryFor = (r) => ({
            asin: r.asin, name: r.name, quantity: r.quantity, sku: r.sku, packagingConfirmed: true,
        });
        const aliasedRows = rows.filter(r => {
            const line = poItems.find(l => l.asin === r.asin);
            return line?.itemCode && (asinAliasesByItemCode[line.itemCode] || []).length >= 2;
        });
        if (aliasedRows.length > 0) {
            const first = aliasedRows[0];
            const firstLine = poItems.find(l => l.asin === first.asin);
            setMultiAsinSplit({
                pending: aliasedRows,
                resolved: rows.filter(r => !aliasedRows.includes(r)).map(entryFor),
                aliases: asinAliasesByItemCode[firstLine.itemCode],
                allocations: defaultAllocations(asinAliasesByItemCode[firstLine.itemCode], first),
            });
            return;
        }
        stageEntriesIntoPlan(rows.map(entryFor));
    };

    /** Default split: every unit to the first inboundable alias (AUTHORIZED sorts first). */
    const defaultAllocations = (aliases, row) => {
        const target = (aliases || []).find(a => a.inboundable)?.asin;
        const out = {};
        (aliases || []).forEach(a => { out[a.asin] = a.asin === target ? row.quantity : 0; });
        return out;
    };

    // The staging tail shared by the plain path and the modal path — one implementation so the
    // two can never drift on condition seeding, source labels, or the confirmation map.
    const stageEntriesIntoPlan = (entries) => {
        mergeItemsIntoPlan(entries.map(e => ({ asin: e.asin, name: e.name, quantity: e.quantity })));
        // Task 80: PO-sourced rows arrive with their New-condition SKU already resolved by the
        // server. Seed the plan row's condition choice with it so the row shows New immediately
        // (rather than flickering through the prefetch) and analyze sends that exact SKU. An
        // ALTERNATE-ASIN allocation carries no sku — the normal condition prefetch resolves it.
        setConditionSkuByAsin(prev => {
            const next = { ...prev };
            entries.forEach(e => { if (e.sku) next[e.asin] = e.sku; });
            return next;
        });
        // Task 69: draft-banner source label -- keep it honest when rows come from several places.
        setPlanSource(prev => {
            const label = `PO ${selectedPoNumber}`;
            return prev && prev !== label ? 'multiple sources' : label;
        });
        setPackagingConfirmedAsin(prev => {
            const next = { ...prev };
            entries.forEach(e => { if (e.packagingConfirmed) next[e.asin] = true; });
            return next;
        });
        setToast(`Added ${entries.length} confirmed line${entries.length !== 1 ? 's' : ''} from PO ${selectedPoNumber}.`);
        closePoModal();
    };

    /** Modal confirm for the row at multiAsinSplit.pending[0]: bank its allocations, advance. */
    const confirmMultiAsinAllocation = () => {
        const s = multiAsinSplit;
        if (!s) return;
        const row = s.pending[0];
        const allocated = (s.aliases || [])
            .filter(a => (Number(s.allocations[a.asin]) || 0) > 0)
            .map(a => ({
                asin: a.asin,
                name: a.title || row.name,
                quantity: Number(s.allocations[a.asin]),
                // The original PO row's resolved SKU + carton confirmation apply only to ITS asin;
                // an alternate ASIN resolves its own SKU and (for LTL) confirms its own carton.
                sku: a.asin === row.asin ? row.sku : undefined,
                packagingConfirmed: a.asin === row.asin,
            }));
        const remaining = s.pending.slice(1);
        const resolved = [...s.resolved, ...allocated];
        if (remaining.length === 0) {
            setMultiAsinSplit(null);
            stageEntriesIntoPlan(resolved);
            return;
        }
        const nextRow = remaining[0];
        const nextLine = poItems.find(l => l.asin === nextRow.asin);
        const nextAliases = asinAliasesByItemCode[nextLine.itemCode];
        setMultiAsinSplit({
            pending: remaining,
            resolved,
            aliases: nextAliases,
            allocations: defaultAllocations(nextAliases, nextRow),
        });
    };

    const filteredPoList = poList.filter(po =>
        !poSearch || (po.poNumber || '').toLowerCase().includes(poSearch.toLowerCase()));

    // ---- Task 12: "Paste list + total" ----

    const openAllocateModal = () => {
        setShowAllocateModal(true);
        setAllocateAsinText('');
        setAllocateTotalQtyInput('');
        setAllocateError(null);
        setAllocatePreview(null);
    };

    const closeAllocateModal = () => {
        setShowAllocateModal(false);
        setAllocatePreview(null);
        setAllocateError(null);
    };

    const parseAsinList = (text) => {
        const tokens = text.split(/[\s,]+/).map(t => t.trim().toUpperCase()).filter(Boolean);
        const seen = new Set();
        const asins = [];
        tokens.forEach(t => {
            if (!seen.has(t)) {
                seen.add(t);
                asins.push(t);
            }
        });
        return asins;
    };

    const runAllocate = async () => {
        const asins = parseAsinList(allocateAsinText);
        const totalQty = parseInt(allocateTotalQtyInput, 10);
        if (asins.length === 0) {
            setAllocateError('Enter at least one ASIN.');
            return;
        }
        if (!totalQty || totalQty <= 0) {
            setAllocateError('Enter a total quantity greater than 0.');
            return;
        }
        const looksInvalid = asins.filter(a => !/^[A-Z0-9]{10}$/.test(a));
        setAllocateLoading(true);
        setAllocateError(null);
        try {
            const res = await ApiClient.post('/api/shipment-planner/allocate-total', { asins, totalQty });
            setAllocatePreview({
                rows: (res.data.rows || []).map(r => ({ ...r })),
                totalAllocated: res.data.totalAllocated,
                note: res.data.note,
            });
            if (looksInvalid.length > 0) {
                setAllocateError(`Note: ${looksInvalid.join(', ')} don't look like standard 10-character ASINs — double-check before adding.`);
            }
        } catch (err) {
            console.error('allocate-total failed:', err);
            setAllocateError(err.response?.data?.error || err.message);
        }
        setAllocateLoading(false);
    };

    const updateAllocatePreviewQty = (asin, qty) => {
        setAllocatePreview(prev => (prev ? {
            ...prev,
            rows: prev.rows.map(r => (r.asin === asin ? { ...r, qty: parseInt(qty, 10) || 0 } : r)),
        } : prev));
    };

    const addAllocateRowsToPlan = () => {
        if (!allocatePreview) return;
        const lines = allocatePreview.rows
            .filter(r => r.qty > 0)
            .map(r => ({ asin: r.asin, name: r.asin, quantity: r.qty }));
        mergeItemsIntoPlan(lines);
        setToast(`Added ${lines.length} ASIN${lines.length !== 1 ? 's' : ''} from the allocated split.`);
        closeAllocateModal();
    };

    // ---- analyze ----

    /**
     * Task 103: applying a finished ASIN-mode analyze. Shared by the kickoff path and the
     * re-attach path so a resumed analyze renders identically to a live one -- including the
     * split-set bookkeeping, which must happen whether or not the browser was watching when the
     * server finished.
     */
    const applyAsinAnalysis = useCallback((analysis) => {
        if (!analysis) return;
        setAnalysisResult(analysis);
        // Task 96: when this draft is one shipment of a split, its run lands on the set panel
        // so the operator can see, from any member, which siblings have been analyzed.
        if (splitGroupRef.current) {
            const g = splitGroupRef.current;
            recordSplitOutcome(activeDraftIdRef.current, {
                name: activeDraftName,
                index: g.index,
                total: g.total,
                asin: g.asin,
                runId: analysis?.runId ?? null,
                decision: bestAnalysisDecision(analysis),
                error: null,
            }, g.id);
        }
        const scores = analysis.scores || [];
        if (scores.length > 0) {
            setSelectedOptionId(scores[0].placementOptionId);
        } else if (analysis.placementOptions && analysis.placementOptions.length > 0) {
            setSelectedOptionId(analysis.placementOptions[0].placementOptionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDraftName]);

    /**
     * Task 103 -- ASIN-mode half of "navigate away and come back". Whenever the open draft
     * changes (including on mount, after a reload), ask the server whether THAT draft has an
     * analyze in flight and, if so, re-raise the spinner and follow it to completion.
     */
    useEffect(() => {
        const draftId = activeDraftId;
        if (!draftId) return undefined;
        let cancelled = false;
        (async () => {
            const job = await findActiveJob(SCOPE_DRAFT, draftId);
            if (!job || cancelled) return;
            setAnalyzing(true);
            setAnalysisError(null);
            setAnalysisStatus(jobProgressLabel(job, {
                singleStepLabel: 'Resuming an analyze that is still running on the server…',
            }));
            try {
                const finished = await waitForJob(job.jobId, {
                    onProgress: (j) => { if (!cancelled) setAnalysisStatus(jobProgressLabel(j)); },
                    isCancelled: () => cancelled,
                });
                if (!finished || cancelled) return;
                applyAsinAnalysis(await resolveFinishedJob(finished));
            } catch (err) {
                if (!cancelled) setAnalysisError(err.message || 'analyze failed');
            } finally {
                if (!cancelled) {
                    setAnalyzing(false);
                    setAnalysisStatus('');
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDraftId]);

    // Task 71: `modeOverride` lets the carrier-gap remedies re-run immediately with a different
    // shipping mode. setShipmentMode() is async, so reading `shipmentMode` from the closure right
    // after flipping it would send the OLD mode -- the override is passed explicitly instead.
    const analyzeAsinList = async (list, modeOverride) => {
        const useItems = list || items;
        const effectiveMode = modeOverride || shipmentMode;
        if (useItems.length === 0) return;
        setAnalyzing(true);
        setAnalysisStatus('Creating inbound plan...');
        setAnalysisError(null);
        setCartonGap(null);
        setAnalysisResult(null);
        setSelectedOptionId(null);
        setSplitVariants(null);
        setLegacyRunNoScores(false); // a fresh live analyze always scores -- clear any stale loaded-run note
        try {
            // Task 14: always send whatever's in the packaging popover's fields for this ASIN --
            // takes precedence over the calibrate_asin_packaging_defaults table row on the
            // backend, so edited-but-unsaved values flow through without requiring a save first.
            const payload = {
                items: useItems.map(i => {
                    const pkg = packagingByAsin[i.asin];
                    const line = { asin: i.asin, qty: i.quantity };
                    // Task 80: the row's chosen condition, expressed as its SKU. When absent the
                    // backend resolves New or blocks the line -- it never falls through to used.
                    if (conditionSkuByAsin[i.asin]) line.sku = conditionSkuByAsin[i.asin];
                    if (pkg) {
                        line.unitsPerBox = pkg.unitsPerBox;
                        line.boxLengthIn = pkg.boxLengthIn;
                        line.boxWidthIn = pkg.boxWidthIn;
                        line.boxHeightIn = pkg.boxHeightIn;
                        line.boxWeightLbs = pkg.boxWeightLbs;
                    }
                    // 2026-08-21: whether a HUMAN has vouched for this carton, as opposed to it
                    // being Amazon's catalog guess or a stale draft copy. The LTL build refuses to
                    // palletise an unconfirmed carton rather than guessing one -- absent/false is
                    // the safe default, so an older client can never assert a confirmation it does
                    // not have.
                    line.cartonConfirmed = !!packagingConfirmedAsin[i.asin];
                    return line;
                }),
            };
            // Task 61: the mode chosen in the Import-from-PO modal rides the whole flow -- LTL
            // sends FREIGHT_LTL plus the shipment-level pallet/freight inputs.
            if (effectiveMode === 'LTL') {
                payload.preferredShippingMode = 'FREIGHT_LTL';
                payload.ltl = buildLtlRequestPayload(ltlForm);
            }
            // Task 70b: becomes readyToShipWindow.start on every shipment's transportation
            // configuration. Sent in UPS mode too -- harmless there, and it keeps the date the
            // operator sees identical to the one Amazon quotes against.
            // ...EXCEPT for a split member. Owner, 2026-08-21: "remove the ready date from the
            // split shipment process" -- see splitSetAnalyzePayload for why. Analyzing shipment 2
            // of a split from its own panel must produce the same request the set panel sends.
            if (pickupDate && !splitGroupRef.current) payload.readyToShipDate = pickupDate;
            // Task 97: same stamp the sibling path sends -- analyzing a split member from its own
            // panel must land in the same server-side set as analyzing it from the set panel.
            if (splitGroupRef.current) {
                const g = splitGroupRef.current;
                const stamp = splitGroupStamp(g.id, g.index, g.total, g.asin);
                if (stamp) payload.splitGroup = stamp;
            }
            setAnalysisStatus('Scoring placement options against regional demand (this may take 30-60 seconds)...');
            // 2026-08-23 (owner): stamp the draft the moment it is SUBMITTED, so it leaves the
            // saved-planners list -- the run in the Shipment Queue is its identity from here on.
            // Failure un-stamps in the catch below, so a refused analyze reappears as saved work.
            markActiveDraftSubmitted(true);
            // Task 103: server-side job instead of a 5-minute open request. Scoped to the DRAFT,
            // which is this workspace's durable identity -- so a reload of this page finds the
            // running analyze again (see the re-attach effect below).
            const analysis = await runAnalyzeJob({
                url: '/api/shipment-planner/analyze-asin-list/async',
                body: payload,
                // 2026-09-18: since the landing redesign, no draft is active until the operator
                // picks one — activeDraftIdRef is NULL when rows arrived via PO import, and a
                // null scopeKey was dropped from the query string, failing the backend's
                // required-param check ("Analysis failed ... 403" on PO 5953). The default
                // workspace is a real, durable scope: re-attach after a reload still works.
                scopeKey: activeDraftIdRef.current || 'default',
                scopeType: SCOPE_DRAFT,
                label: activeDraftName || undefined,
                onProgress: (job) => setAnalysisStatus(jobProgressLabel(job)),
            });
            applyAsinAnalysis(analysis);
        } catch (err) {
            console.error('ASIN analysis failed:', err);
            markActiveDraftSubmitted(false);
            // 2026-08-21: a carton-confirmation refusal is a QUESTION, not a failure. Render the
            // structured ask; the plain error banner is for everything else.
            const gap = cartonGapFromError(err);
            setCartonGap(gap);
            // Task 68: analysisStatus only renders while the spinner is up -- a failure written
            // there vanished the instant analyzing flipped false. Surface it persistently instead.
            setAnalysisError(gap ? null : (err.response?.data?.error || err.message));
            setAnalysisStatus('');
            setAnalyzing(false);
            return;
        }
        setAnalyzing(false);
        setAnalysisStatus('');
    };

    // Task 101: see the PO-mode reAnalyze -- a CONFIRMED plan is never superseded, because
    // superseding cancels its shipments on Amazon. The new analysis runs regardless.
    const reAnalyze = async () => {
        await supersedeCurrentPlan();
        analyzeAsinList();
    };

    // ---- Task 71: partnered-LTL carrier-gap remedies ----
    // Both supersede the dead plan first (it can never be confirmed) so the account doesn't
    // accumulate orphans, exactly as reAnalyze does.

    const supersedeCurrentPlan = async () => {
        // Task 101: a confirmed plan is left intact -- the gap remedies below then simply build
        // their replacement plan alongside it rather than voiding a live shipment.
        await supersedePlanUnlessConfirmed(
            analysisResult?.inboundPlanId,
            analysisResult?.status,
            setToast,
        );
    };

    // Remedy (a): drop the ASIN(s) Amazon offered no partnered LTL for and re-run in LTL mode.
    // The task-69 draft autosave still holds the ORIGINAL composition, so the excluded ASIN is not
    // lost -- it goes out on its own plan afterwards.
    const excludeAsinsAndReanalyze = async (asins) => {
        const remaining = excludeAsinsFromRows(items, asins);
        if (remaining.length === 0) {
            setAnalysisError('Excluding those ASINs would empty the plan — remove the LTL mode instead.');
            return;
        }
        setItems(remaining);
        setToast(`Excluded ${asins.join(', ')} — re-analyzing ${remaining.length} ASIN${remaining.length !== 1 ? 's' : ''}.`);
        await supersedeCurrentPlan();
        await analyzeAsinList(remaining, 'LTL');
    };

    // Task 73: confirm the placement with the operator's own (non-partnered) carrier.
    //
    // This is deliberately NOT folded into confirmSelected(). A partnered confirm hands the
    // freight to Amazon; this one commits the shipment with no Amazon freight at all, and leaves
    // the operator owing a carrier booking and a tracking number. The dialog says so in plain
    // words, and the request carries `acknowledged: true` -- the backend refuses without it, so
    // there is no code path (watch, probe, sweep, retry) that can reach a confirm by accident.
    // `panelGap` is the panel's own view of the gap -- the saved one with any freshly-listed
    // own-carrier options overlaid. The dialog must describe the carriers the operator actually
    // sees, which after a live refresh are not the ones the saved run holds.
    const confirmOwnCarrier = async (selections, panelGap, acknowledgeUsedCondition = false) => {
        // Task 87 defense-in-depth (see confirmPlan's identical note).
        acknowledgeUsedCondition = coerceAcknowledgeFlag(acknowledgeUsedCondition);
        if (!analysisResult?.inboundPlanId) return;
        const gap = panelGap || ltlCarrierGapFrom(analysisResult);
        // The placement the GAP describes, not the highlighted card: the response-level gap is the
        // representative one (fewest blocking ASINs) and its shipments may belong to a different
        // option entirely. Confirming the wrong placement would commit the wrong freight.
        const placementOptionId = gapPlacementOptionId(analysisResult, gap);
        if (!placementOptionId) {
            alert('Could not tell which placement option these shipments belong to — re-analyze and try again.');
            return;
        }
        const lines = Object.entries(selections || {}).map(([shipmentId, optionId]) => {
            const q = (gap?.shipments || []).find(s => s.shipmentId === shipmentId);
            const opt = (q?.ownCarrierOptions || []).find(o => o.transportationOptionId === optionId);
            return `  • ${ltlGapShipmentLabel(q) || shipmentId}: ${opt ? ownCarrierOptionLabel(opt) : optionId}`;
        });
        if (!acknowledgeUsedCondition && !window.confirm(
            'Confirm this placement with YOUR OWN carrier?\n\n' +
            lines.join('\n') + '\n\n' +
            'Amazon will NOT charge you for freight and will NOT arrange the pickup.\n' +
            'You must:\n' +
            '  1. Book and pay these carriers yourself\n' +
            '  2. Print the box labels (and the BOL for freight) from Confirmed Shipments\n' +
            '  3. Enter the BOL/PRO or parcel tracking on Confirmed Shipments once it ships\n\n' +
            'This confirmation cannot be undone.'
        )) return;

        setConfirming(true);
        // Task 87: built through the explicit whitelist serializer -- `selections` is reduced to
        // plain shipmentId -> transportationOptionId strings rather than spread wholesale, so a
        // stray non-string value (an option/shipment object, an event) can never reach the body.
        const body = buildOwnCarrierConfirmRequestBody({
            planId: analysisResult.inboundPlanId,
            placementOptionId,
            selections,
            // Task 70b's operator-chosen ready-for-pickup date doubles as the delivery-window
            // hint: a window that closes before the truck is even loaded is unusable.
            pickupDate,
            acknowledgeUsedCondition,
        });
        if (safeSerializationCheck(body, 'confirm-own-carrier')) {
            alert('Could not save — the confirm request could not be built. Please refresh and try again.');
            setConfirming(false);
            return;
        }
        try {
            const res = await ApiClient.post('/api/shipment-planner/confirm-own-carrier', body, { timeout: 180000 });
            setUsedAckItems(null);
            setAnalysisResult(prev => (prev ? { ...prev, status: 'CONFIRMED', confirmedAt: new Date().toISOString() } : null));
            clearDraftAfterConfirm(items);
            const windows = (res.data?.shipments || []).filter(s => s.deliveryWindowOptionId).length;
            setToast(`Confirmed with your own carrier — ${res.data?.shipmentCount ?? 0} shipment(s)` +
                (windows > 0 ? `, ${windows} delivery window(s) booked` : '') +
                '. Book the carriers, then enter tracking on Confirmed Shipments.');
        } catch (err) {
            // Task 80: same used-condition gate as the partnered path — own carrier is still a
            // real shipment, and the labels still come from the plan's MSKU.
            const blocked = usedConditionBlockFrom(err);
            if (blocked) {
                setUsedAckItems(blocked);
                setUsedAckRetry(() => () => confirmOwnCarrier(selections, panelGap, true));
                setConfirming(false);
                return;
            }
            console.error('Own-carrier confirm failed:', err);
            alert('Own-carrier confirmation failed: ' + (err.response?.data?.error || err.message));
        }
        setConfirming(false);
    };

    // Remedy (b): same composition, UPS small parcel. Appropriate when the load is small enough.
    const reanalyzeAsUps = async () => {
        setShipmentMode('UPS');
        setToast('Re-analyzing as UPS small parcel.');
        await supersedeCurrentPlan();
        await analyzeAsinList(items, 'UPS');
    };

    // Task 68: single kickoff for the analyze button. The async functions above catch their own
    // request failures; this wrapper is the belt-and-braces for anything thrown OUTSIDE those try
    // blocks (state-shape regressions, request assembly bugs) -- without it, such an exception
    // dies in the console and the click looks like it did nothing.
    // Owner, 2026-09-01 submission-mode modal: the per-box breakup rides the partition sweep
    // with an explicit variants override — one variant of N single-box chunks. The combined
    // plan (when asked for) is the normal interactive analyze; both land on Shipment Runs.
    const postBoxSweep = async (plan) => {
        setSweepStarting(true);
        try {
            await ApiClient.post('/api/shipment-planner/partition-sweep', {
                asin: plan.asin,
                qty: plan.quantity,
                variants: [plan.chunks],
                shipmentMode: shipmentMode === 'LTL' ? 'LTL' : 'UPS',
            });
            setToast(`Box breakup running in background — ${plan.boxes} single-box plans; results `
                + 'appear under "Partition sweeps" on the Shipment Runs page.');
        } catch (err) {
            console.error('box-breakup sweep failed:', err);
            setToast('Box breakup failed: ' + (err.response?.data?.error || err.message));
        }
        setSweepStarting(false);
    };

    // Owner 2026-09-04 (dual cadence): quiet watch registration -- the analyze/sweep IS the
    // first draw (kickoff:false), so nothing extra runs now. The FULL row re-draws every 6h;
    // the SPLIT row re-creates the whole broken-up set every 12h. Registration failure is
    // non-fatal: the plans the operator asked for are already on their way.
    const registerKeepChecking = async (choice, plan) => {
        try {
            const base = {
                source: 'score modal',
                shipmentMode: shipmentMode === 'LTL' ? 'LTL' : 'UPS',
                kickoff: false,
                items: buildWatchItems(),
            };
            if (choice === 'SINGLE' || choice === 'BOTH') {
                await ApiClient.post('/api/shipment-planner/watch', base);
            }
            if ((choice === 'BOXES' || choice === 'BOTH') && plan?.eligible) {
                await ApiClient.post('/api/shipment-planner/watch', {
                    ...base,
                    chunkSpec: `${plan.boxes}x${plan.chunks[0]}`,
                    drawIntervalHours: 12,
                });
            }
            refreshWatchRows();
        } catch (err) {
            console.error('keep-checking registration failed:', err);
        }
    };

    const runScoreChoice = (choice, plan) => {
        setScoreChoiceOpen(false);
        if (choice === 'SINGLE' || choice === 'BOTH') startAnalysis();
        if ((choice === 'BOXES' || choice === 'BOTH') && plan?.eligible) postBoxSweep(plan);
        if (keepChecking) registerKeepChecking(choice, plan);
    };

    const startAnalysis = () => {
        try {
            const kickoff = analyzeMode !== 'combined'
                ? analyzeSplitPlans()
                : (analysisResult ? reAnalyze() : analyzeAsinList());
            if (kickoff && typeof kickoff.catch === 'function') {
                kickoff.catch(err => {
                    console.error('Analysis kickoff failed:', err);
                    setAnalysisError("couldn't start analysis: " + (err?.message || String(err)));
                    setAnalyzing(false);
                    setSplitAnalyzing(false);
                });
            }
        } catch (err) {
            console.error('Analysis kickoff failed:', err);
            setAnalysisError("couldn't start analysis: " + (err?.message || String(err)));
            setAnalyzing(false);
            setSplitAnalyzing(false);
        }
    };

    // Task 32: SEPARATE/COMPARE fan-out via /analyze-split. Sequential on the backend (one real
    // Amazon plan per ASIN at ~1-2 min each); results arrive as one response, so a single spinner
    // with an expectation-setting message stands in for per-ASIN streaming.
    const analyzeSplitPlans = async () => {
        if (items.length === 0) return;
        const distinctAsins = new Set(items.map(i => i.asin)).size;
        const planCount = analyzeMode === 'compare' ? distinctAsins + 1 : distinctAsins;
        setSplitAnalyzing(true);
        setSplitResult(null);
        setAnalysisError(null);
        setAnalysisResult(null);
        try {
            const payload = {
                mode: analyzeMode === 'separate' ? 'SEPARATE' : 'COMPARE',
                items: items.map(i => {
                    const pkg = packagingByAsin[i.asin];
                    const line = { asin: i.asin, qty: i.quantity };
                    if (pkg) {
                        line.unitsPerBox = pkg.unitsPerBox;
                        line.boxLengthIn = pkg.boxLengthIn;
                        line.boxWidthIn = pkg.boxWidthIn;
                        line.boxHeightIn = pkg.boxHeightIn;
                        line.boxWeightLbs = pkg.boxWeightLbs;
                    }
                    // 2026-08-21: whether a HUMAN has vouched for this carton, as opposed to it
                    // being Amazon's catalog guess or a stale draft copy. The LTL build refuses to
                    // palletise an unconfirmed carton rather than guessing one -- absent/false is
                    // the safe default, so an older client can never assert a confirmation it does
                    // not have.
                    line.cartonConfirmed = !!packagingConfirmedAsin[i.asin];
                    return line;
                }),
            };
            // Generous timeout: up to 12 single-ASIN plans + a combined plan at ~2 min each.
            const res = await ApiClient.post('/api/shipment-planner/analyze-split', payload, { timeout: 1800000 });
            setSplitResult(res.data);
            const failed = (res.data.perAsin || []).filter(r => r.error).length;
            setToast(failed > 0
                ? `Split analysis done — ${planCount - failed} of ${planCount} plans created (${failed} failed). All plans are UNCONFIRMED.`
                : `Split analysis done — ${planCount} plan${planCount !== 1 ? 's' : ''} created, all UNCONFIRMED. Open a row to confirm it.`);
        } catch (err) {
            console.error('analyze-split failed:', err);
            setToast('Split analysis failed: ' + (err.response?.data?.error || err.message));
        }
        setSplitAnalyzing(false);
    };

    // Task 34: carry each ASIN's current packaging override (e.g. values confirmed via the
    // PO-import flow but not necessarily saved as the ASIN default) onto the watch row -- the
    // backend persists them so the background draws use the confirmed values too. Shared by the
    // Watch button and the score modal's keep-checking registration (owner 2026-09-04), so the
    // two paths can never drift on what a watch row knows.
    const buildWatchItems = () => items.map(i => {
        const pkg = packagingByAsin[i.asin];
        const line = { asin: i.asin, qty: i.quantity };
        if (pkg) {
            line.unitsPerBox = pkg.unitsPerBox;
            line.boxLengthIn = pkg.boxLengthIn;
            line.boxWidthIn = pkg.boxWidthIn;
            line.boxHeightIn = pkg.boxHeightIn;
            line.boxWeightLbs = pkg.boxWeightLbs;
        }
        // 2026-08-21: whether a HUMAN has vouched for this carton, as opposed to it being
        // Amazon's catalog guess or a stale draft copy. The LTL build refuses to palletise an
        // unconfirmed carton rather than guessing one -- absent/false is the safe default, so an
        // older client can never assert a confirmation it does not have.
        line.cartonConfirmed = !!packagingConfirmedAsin[i.asin];
        return line;
    });

    // Task 33: kick off a background watch -- no spinner, the HTTP call returns immediately
    // ({watching, started}) while the server runs the first draw cycle off-thread.
    const watchInBackground = async () => {
        if (items.length === 0) return;
        const distinct = new Set(items.map(i => i.asin)).size;
        setWatchStarting(true);
        try {
            const payload = {
                source: 'manual',
                // Owner 2026-08-30: LTL watches draw as Phase-1 LTL analyzes in the background
                // (FC split + placement fee; freight only after pallets are measured).
                shipmentMode: shipmentMode === 'LTL' ? 'LTL' : 'UPS',
                items: buildWatchItems(),
            };
            await ApiClient.post('/api/shipment-planner/watch', payload);
            setToast(`Watching ${distinct} ASIN${distinct !== 1 ? 's' : ''} — drawing every 6h until each shipment is confirmed. Track progress on Shipment Runs.`);
            // The refreshed rows flip the button to its disabled "Watching ✓" state.
            await refreshWatchRows();
        } catch (err) {
            console.error('watch failed:', err);
            setToast('Watch failed: ' + (err.response?.data?.error || err.message));
        }
        setWatchStarting(false);
    };

    // Task 53 (owner: "we need the ability to look at shipments broken apart and together to
    // examine the best placement ... same 24 units just broken apart"): partition sweep -- ONE
    // ASIN's qty split into box-multiple chunks, each chunk analyzed as its own plan, fully async
    // on the backend (browser-timeout lesson). Preview first so the confirm dialog states exactly
    // how many real plans the sweep will create.
    const exploreBoxSplits = async () => {
        if (items.length !== 1) return;
        const { asin, quantity } = items[0];
        setSweepStarting(true);
        try {
            const preview = await ApiClient.get(
                `/api/shipment-planner/partition-sweep-preview?asin=${encodeURIComponent(asin)}&qty=${quantity}`);
            const { planCount, variants, remainderNote } = preview.data;
            const variantLines = (variants || [])
                .map(v => `  ${v.name}: [${(v.chunks || []).join(', ')}]`).join('\n');
            const ok = window.confirm(
                `Explore box splits for ${asin} × ${quantity}?\n\n` +
                `This creates ${planCount} REAL Amazon inbound plans (all left unconfirmed):\n` +
                `${variantLines}\n` +
                (remainderNote ? `\nNote: ${remainderNote}\n` : '') +
                `\nRuns in the background (~1–2 min per plan) — you'll get an email when it's done.`);
            if (!ok) {
                setSweepStarting(false);
                return;
            }
            await ApiClient.post('/api/shipment-planner/partition-sweep', {
                asin, qty: quantity,
                // Owner 2026-08-30 ("break apart LTL shipments ... to get different FCs"):
                // LTL sweeps analyze every chunk as a Phase-1 LTL plan.
                shipmentMode: shipmentMode === 'LTL' ? 'LTL' : 'UPS',
            });
            setToast('Partition sweep running in background — you\'ll get an email when it finishes; results appear under "Partition sweeps" on the Shipment Runs page.');
        } catch (err) {
            console.error('partition sweep failed:', err);
            setToast('Partition sweep failed: ' + (err.response?.data?.error || err.message));
        }
        setSweepStarting(false);
    };

    // ---- confirm / park & retry (reuses the same plan-id endpoints PO mode uses) ----

    const scores = useMemo(() => analysisResult?.scores || [], [analysisResult]);
    // Defensive re-sort: DTO contract says scores arrive score-descending, but the WEAK banner and
    // confirm gate hinge on "the best option", so don't trust ordering blindly.
    const sortedScores = useMemo(() => [...scores].sort((a, b) => b.score - a.score), [scores]);
    const bestScore = sortedScores.length > 0 ? sortedScores[0] : null;
    const selectedScore = scores.find(s => s.placementOptionId === selectedOptionId) || bestScore;
    // 2026-08-21: which LTL options have had their pallet build signed off. Reset by every fresh
    // analyze -- a new split is a new build, and carrying an approval across would recreate the
    // exact "looks settled, nobody checked" state this exists to remove.
    const [approvedBuilds, setApprovedBuilds] = useState({});
    useEffect(() => { setApprovedBuilds({}); }, [analysisResult?.inboundPlanId]);
    const approveBuild = useCallback(
        (placementOptionId) => setApprovedBuilds(prev => ({ ...prev, [placementOptionId]: true })), []);
    const optionsById = useMemo(() => {
        const map = {};
        (analysisResult?.placementOptions || []).forEach(po => { map[po.placementOptionId] = po; });
        return map;
    }, [analysisResult]);

    // Parcel returns APPROVED, so small-parcel plans gain no pallet step. Declared after
    // optionsById because it reads it.
    const selectedBuildState = palletBuildState(
        optionsById[selectedScore?.placementOptionId], null, analysisResult?.items || items, approvedBuilds,
        analysisResult?.declaredPalletCount);

    const confirmSelected = async (acknowledgeUsedCondition = false) => {
        // Task 87 defense-in-depth (see confirmPlan's identical note).
        acknowledgeUsedCondition = coerceAcknowledgeFlag(acknowledgeUsedCondition);
        if (!analysisResult?.inboundPlanId || !selectedOptionId) return;
        if (selectedScore && !selectedScore.confirmAllowed) return;
        if (!acknowledgeUsedCondition && !window.confirm(
            'Confirm this placement option with Amazon?\n\n' +
            'This will:\n' +
            '- Lock in the selected placement option\n' +
            '- Confirm transportation with Amazon Partnered Carrier\n' +
            '- Make the workflow visible in Seller Central\n\n' +
            'This action cannot be undone.'
        )) return;

        setConfirming(true);
        // Task 87: built through the explicit whitelist serializer -- never selectedOptionId /
        // analysisResult spread wholesale -- with a last-chance stringify check right before the
        // request fires.
        const body = buildConfirmRequestBody({
            planId: analysisResult.inboundPlanId,
            placementOptionId: selectedOptionId,
            acknowledgeUsedCondition,
        });
        if (safeSerializationCheck(body, 'confirm')) {
            setToast("Couldn't save — the confirm request could not be built. Please refresh and try again.");
            setConfirming(false);
            return;
        }
        try {
            const res = await ApiClient.post('/api/shipment-planner/confirm', body, { timeout: 120000 });
            setUsedAckItems(null);
            setAnalysisResult(prev => (prev ? { ...prev, status: 'CONFIRMED', confirmedAt: new Date().toISOString() } : null));
            // Task 96: record it in the split set FIRST -- the hook below deletes the draft row.
            recordSplitConfirm(res.data?.shipmentCount);
            // Task 69: a confirmed plan retires the in-progress draft it was built from.
            clearDraftAfterConfirm(items);
            setToast(`Confirmed! ${res.data.shipmentCount} shipment(s) sent to Amazon. Check Seller Central > Send to Amazon to continue.`);
        } catch (err) {
            // Task 80: the server refused because the plan carries used-condition SKUs. Show the
            // exact SKUs/FNSKUs it named and require an explicit tick before retrying.
            const blocked = usedConditionBlockFrom(err);
            if (blocked) {
                setUsedAckItems(blocked);
                setUsedAckRetry(() => () => confirmSelected(true));
                setConfirming(false);
                return;
            }
            console.error('Confirm failed:', err);
            alert('Confirmation failed: ' + (err.response?.data?.error || err.message));
        }
        setConfirming(false);
    };

    const cancelPlan = async () => {
        if (!analysisResult?.inboundPlanId) return;
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${analysisResult.inboundPlanId}`);
            setAnalysisResult(prev => (prev ? { ...prev, status: 'CANCELLED' } : null));
        } catch (err) {
            console.error('Cancel failed:', err);
            alert('Cancel failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const parkAndRetry = async () => {
        if (!analysisResult?.inboundPlanId) return;
        setParking(true);
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${analysisResult.inboundPlanId}`);
            setAnalysisResult(prev => (prev ? { ...prev, status: 'CANCELLED' } : null));
            setToast('Parked — plan voided on Amazon. The nightly placement-probe job will retry this composition and log results to probe history.');
        } catch (err) {
            console.error('Park & retry failed:', err);
            setToast('Failed to park plan: ' + (err.response?.data?.error || err.message));
        }
        setParking(false);
    };

    // ---- split by size tier fallback ----

    const ensureSizeTiers = useCallback(async (asinList) => {
        const missing = asinList.filter(a => !(a in sizeTierByAsin));
        if (missing.length === 0) return sizeTierByAsin;
        const updates = {};
        await Promise.all(missing.map(async (asin) => {
            try {
                const res = await ApiClient.get(`/api/product-size/${asin}`);
                updates[asin] = res.data && res.data.success ? res.data.sizeTier : null;
            } catch (e) {
                updates[asin] = null;
            }
        }));
        const merged = { ...sizeTierByAsin, ...updates };
        setSizeTierByAsin(merged);
        return merged;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizeTierByAsin]);

    const splitBySizeTier = async () => {
        setSplitLoading(true);
        try {
            const tiers = await ensureSizeTiers(items.map(i => i.asin));
            const standard = items.filter(it => tiers[it.asin] === 'Standard-Size');
            const oversizeXl = items.filter(it => tiers[it.asin] !== 'Standard-Size');
            setSplitVariants({ standard, oversizeXl });
        } catch (err) {
            console.error('Split by size tier failed:', err);
            setToast('Failed to fetch size tiers for split.');
        }
        setSplitLoading(false);
    };

    const reanalyzeVariant = (list) => {
        if (!list || list.length === 0) return;
        setSplitVariants(null);
        setItems(list);
        analyzeAsinList(list);
    };

    // Origin note: fetch tiers in the background once a WEAK verdict appears, purely to evaluate
    // the spec's "all dest FCs east of -95 AND plan contains ExtraLarge" condition.
    useEffect(() => {
        // Bounded auto-fetch: the origin-note needs size tiers, but each miss can hit a live
        // Amazon Catalog fallback — only auto-fire for small plans; larger lists resolve tiers
        // when the user explicitly clicks Split by size tier. (Task 7 review, 2026-08-15.)
        if (bestScore && bestScore.weak && items.length > 0 && items.length <= 15) {
            ensureSizeTiers(items.map(i => i.asin));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bestScore?.weak, items.length]);

    const originNote = useMemo(() => {
        if (!selectedScore) return null;
        const po = optionsById[selectedScore.placementOptionId];
        const shipments = po?.shipments || [];
        if (shipments.length === 0) return null;
        const allEast = shipments.every(s => s.lng != null && s.lng > -95);
        const containsXl = items.some(i => sizeTierByAsin[i.asin] === 'Extra-Large');
        if (allEast && containsXl) {
            return 'All destination FCs sit east of -95° longitude and this plan includes Extra-Large item(s) — Amazon may be routing east because of the origin, not demand. Consider shipping from a more central/western origin if one is available.';
        }
        return null;
    }, [selectedScore, optionsById, items, sizeTierByAsin]);

    // ---- per-ASIN drawer ----

    const openDrawer = async (asin) => {
        setDrawerAsin(asin);
        setDemandLoading(true);
        setFcAnalysis({ status: 'loading', rows: [] });
        try {
            const res = await ApiClient.get(`/api/shipment-planner/demand/${asin}`);
            setDemandRows(res.data || []);
        } catch (err) {
            console.error('Failed to load demand rows:', err);
            setDemandRows([]);
        }
        setDemandLoading(false);

        ApiClient.get(`/api/war-room/fc-analysis/${asin}`)
            .then((res) => setFcAnalysis({ status: 'loaded', rows: res.data || [] }))
            .catch((err) => {
                console.error('Failed to load FC analysis:', err);
                setFcAnalysis({ status: 'error', rows: [] });
            });

        if (!planningByAsin) {
            try {
                const res2 = await ApiClient.get('/api/war-room/planning');
                const rows = (res2.data && res2.data.rows) || [];
                const map = {};
                rows.forEach(r => { map[r.asin] = r; });
                setPlanningByAsin(map);
            } catch (err) {
                console.error('Failed to load planning rows for drawer:', err);
                setPlanningByAsin({});
            }
        }
    };

    const drawerPlanningRow = drawerAsin && planningByAsin ? planningByAsin[drawerAsin] : null;

    // ---- render ----

    const totalUnits = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
    // Task 14: fail-fast gate for the Analyze button -- replaces the old analyze-time refusal
    // (a 500 mid-flow after createInboundPlan already ran) with an up-front, listable check.
    const incompletePackagingAsins = items
        .filter(it => !isPackagingComplete(packagingByAsin[it.asin]))
        .map(it => it.asin);
    // Task 80: the same fail-fast shape for condition. A row is unresolved while its ASIN has been
    // fetched and still has no chosen SKU -- i.e. there is no New listing and the operator has not
    // picked a condition. Rows still loading (asin absent from conditionsByAsin) don't gate.
    // A `null` entry means the server answered without an entry for this ASIN (malformed/failed
    // response). That is not evidence of anything, so it does not gate here -- the backend's own
    // NEW_ONLY resolution and blocked-line refusal are the real enforcement; this gate is UX.
    const unresolvedConditionAsins = items
        .filter(it => conditionsByAsin[it.asin] && !conditionSkuByAsin[it.asin])
        .map(it => it.asin);
    // Rows the operator deliberately set to a used condition -- allowed, but called out loudly so
    // "I meant to do that" and "I didn't notice" can never look the same on screen.
    const usedConditionAsins = items.filter(it => {
        const chosen = conditionSkuByAsin[it.asin];
        if (!chosen) return false;
        const opt = (conditionsByAsin[it.asin]?.options || []).find(o => o.sku === chosen);
        return opt && !opt.newCondition;
    }).map(it => it.asin);

    // Task 41: packagingModalActivePkg/packagingModalLegacyOnly/packagingModalTotals and
    // renderCatalogBoxCard are computed inside useBoxConfig now (Task 2 extraction) -- see the
    // hook call above.

    return (
        <div>
            {toast && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-6 text-sm text-indigo-800 flex items-center justify-between">
                    <span>{toast}</span>
                    <button onClick={() => setToast(null)} className="text-indigo-400 hover:text-indigo-600">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Owner, 2026-09-15: the landing picker. "Start new" leads into the ASIN List
                below it; the "Working plans" resume list sits UNDER that list. */}
            {plannerView === 'landing' && (
                <div className="bg-white shadow rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Start new</h2>
                    <p className="text-sm text-gray-500">From a purchase order below, or search an ASIN to build a plan by hand.</p>
                </div>
            )}
            {plannerView === 'working' && (
                <button
                    onClick={backToAllPlans}
                    disabled={draftBusy}
                    className="mb-3 text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                >
                    ← All plans
                </button>
            )}
            {/* Task 83 (owner: "we should be able to work on multiple shipments simultaneously"):
                the drafts bar -- which shipment build this panel is editing, what else is open,
                and the create/rename/delete affordances. Autosave always targets the one named
                here. */}
            {plannerView === 'working' && (
            <div className="bg-white shadow rounded-lg px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex-shrink-0">
                    Shipment draft
                </span>
                {draftRenaming ? (
                    <input
                        autoFocus
                        value={draftNameInput}
                        maxLength={80}
                        onChange={(e) => setDraftNameInput(e.target.value)}
                        onBlur={commitRenameDraft}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRenameDraft();
                            if (e.key === 'Escape') setDraftRenaming(false);
                        }}
                        aria-label="Shipment draft name"
                        className="px-2 py-1 border border-indigo-300 rounded text-sm font-medium text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                ) : (
                    <button
                        onClick={beginRenameDraft}
                        title="Rename this shipment draft"
                        className="group flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-indigo-700"
                    >
                        {activeDraftName}
                        <PencilIcon className="h-3.5 w-3.5 text-gray-300 group-hover:text-indigo-500" />
                    </button>
                )}
                {/* live contents of the build on screen (no age -- it's being edited right now) */}
                <span className="text-xs text-gray-500">
                    {draftEntryLabel({
                        itemCount: items.length,
                        units: items.reduce((s, it) => s + (Number(it.quantity) || 0), 0),
                        mode: shipmentMode,
                        source: planSource,
                    })}
                </span>

                <div className="relative">
                    <button
                        onClick={() => { setDraftMenuOpen(o => !o); hydrateDraftPayloads(); }}
                        disabled={draftBusy}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        title="Switch to another in-progress shipment"
                    >
                        Switch{drafts.length > 1 ? ` (${drafts.length})` : ''}
                        <ChevronDownIcon className="h-3.5 w-3.5" />
                    </button>
                    {draftMenuOpen && (
                        <div className="absolute left-0 mt-1 z-30 w-96 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                            {drafts.filter(d => d && d.id !== activeDraftId).length === 0 && (
                                <div className="px-3 py-2 text-xs text-gray-500">
                                    No other shipment drafts — use “+ New shipment” to start one.
                                </div>
                            )}
                            {drafts.filter(d => d && d.id !== activeDraftId && !draftIsSubmitted(d)).map(d => {
                                // Task 96: a draft minted by a split shows its "2/3" chip and is
                                // indented under the set it belongs to, so the operator can see the
                                // set exists without opening anything.
                                const stamp = splitGroupFromPayload(splitSetPayloads[d.id]);
                                const sameSet = stamp && splitGroup && stamp.id === splitGroup.id;
                                return (
                                    <button
                                        key={d.id}
                                        onClick={() => switchToDraft(d.id)}
                                        className={`w-full text-left px-3 py-2 hover:bg-indigo-50 ${stamp ? 'border-l-2 border-indigo-300 pl-5' : ''}`}
                                    >
                                        <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                            {stamp && (
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${sameSet
                                                    ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-600'}`}>
                                                    {`${stamp.index}/${stamp.total}`}
                                                </span>
                                            )}
                                            {d.name}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {draftEntryLabel(d)}
                                            {stamp ? ` · split of ${stamp.asin || 'one ASIN'}` : ''}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <button
                    onClick={startNewShipment}
                    disabled={draftBusy || drafts.length >= MAX_OPEN_DRAFTS}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
                    title={drafts.length >= MAX_OPEN_DRAFTS
                        ? `${MAX_OPEN_DRAFTS} shipment drafts are already open — delete one first`
                        : 'Save this build and start another shipment alongside it'}
                >
                    <PlusIcon className="h-3.5 w-3.5" />
                    New shipment
                </button>
                <button
                    onClick={deleteActiveDraft}
                    disabled={draftBusy}
                    className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                    title="Delete this shipment draft"
                >
                    Delete
                </button>
                {draftError && <span className="text-xs text-red-600">{draftError}</span>}

                {/* Task 95: when the rows on screen did NOT come from the draft named above, say so
                    plainly and offer the two explicit ways to keep them. Before this, the panel
                    looked identical either way while the autosave quietly overwrote the draft. */}
                {provenanceNotice && (
                    <div
                        className="basis-full w-full mt-1 pt-2 border-t border-amber-200 flex items-center gap-2 flex-wrap"
                        title={provenanceNotice.title}
                    >
                        <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            {provenanceNotice.text}
                        </span>
                        <button
                            onClick={copyPanelIntoNewDraft}
                            disabled={draftBusy || items.length === 0 || drafts.length >= MAX_OPEN_DRAFTS}
                            className="px-2 py-1 text-xs rounded border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                            title={drafts.length >= MAX_OPEN_DRAFTS
                                ? `${MAX_OPEN_DRAFTS} shipment drafts are already open — delete one first`
                                : 'Keep these rows as a new shipment draft and carry on working on them'}
                        >
                            {provenanceNotice.newDraftLabel}
                        </button>
                        <button
                            onClick={copyPanelIntoActiveDraft}
                            disabled={draftBusy || items.length === 0}
                            className="px-2 py-1 text-xs rounded border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                            title={`Overwrite draft “${activeDraftName}” with these rows — you will be `
                                + 'asked to confirm, and told exactly what is being replaced'}
                        >
                            {provenanceNotice.intoDraftLabel}
                        </button>
                    </div>
                )}
            </div>
            )}

            {/* Task 96: the split's results live HERE -- one row per sibling shipment, with its
                status and its actions. Shown whenever the draft on screen belongs to a split set,
                so it is there after a reload, from any member, until every one is confirmed. */}
            {plannerView === 'working' && splitGroup && !splitAsinModalOpen && (
                <SplitSetPanel
                    members={splitSetMembers}
                    activeDraftId={activeDraftId}
                    analyzingId={splitSetAnalyzing}
                    runAll={splitSetRunAll}
                    summary={splitSetSummary}
                    groupId={splitGroup?.id}
                    onSwitch={switchToDraft}
                    onAnalyze={analyzeSplitMember}
                    onAnalyzeAll={analyzeAllInSplitSet}
                    busy={draftBusy || !!splitSetRunAll || !!splitSetAnalyzing}
                />
            )}

            {/* Task 69: automatic restore of the in-progress build (dismissable; discard clears it) */}
            {plannerView === 'working' && draftBanner && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-6 text-sm text-emerald-800 flex items-center justify-between gap-3">
                    <span>
                        Restored in-progress shipment ({draftBanner.label})
                        {draftSavedAtLabel(draftBanner.savedAt) ? ` from ${draftSavedAtLabel(draftBanner.savedAt)}` : ''}
                        {' '}— it autosaves as you work.
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={discardRestoredDraft}
                            className="px-2 py-1 text-xs rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                            title="Throw away the saved draft and start with an empty plan"
                        >
                            Discard draft
                        </button>
                        <button onClick={() => setDraftBanner(null)} className="text-emerald-400 hover:text-emerald-600" title="Dismiss">
                            <XMarkIcon className="h-4 w-4" />
                        </button>
                    </span>
                </div>
            )}

            {/* Task 69: a saved draft exists but the panel already has rows -- never clobber them */}
            {plannerView === 'working' && resumeDraft && !draftBanner && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-sm text-amber-800 flex items-center justify-between gap-3">
                    <span>
                        A saved in-progress shipment exists ({draftSummaryLabel(resumeDraft)})
                        {draftSavedAtLabel(resumeDraft.savedAt) ? ` from ${draftSavedAtLabel(resumeDraft.savedAt)}` : ''}.
                        Resuming replaces the rows below.
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={resumeSavedDraft}
                            className="px-2 py-1 text-xs rounded border border-amber-300 text-amber-800 hover:bg-amber-100 font-medium"
                        >
                            Resume saved draft
                        </button>
                        <button
                            onClick={deleteServerDraft}
                            className="px-2 py-1 text-xs rounded border border-amber-300 text-amber-700 hover:bg-amber-100"
                            title="Delete the saved draft and keep the rows below"
                        >
                            Discard saved draft
                        </button>
                    </span>
                </div>
            )}

            {/* Task 91: prominent find-and-add search bar, above the staged rows. Additive to the
                "Add ASIN" quick-add row inside the Item Selection card below -- both stay working. */}
            <SearchAddBar onAdd={addFromSearchBar} />

            {/* === Item Selection === */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">ASIN List</h2>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={importDraft}
                            disabled={importingDraft}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ArrowUpTrayIcon className="h-4 w-4" />
                            {importingDraft ? 'Importing...' : 'Import War Room PO draft'}
                        </button>
                        <button
                            onClick={() => (showPoModal ? closePoModal() : openPoModal())}
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border ${
                                showPoModal
                                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        >
                            <DocumentTextIcon className="h-4 w-4" />
                            {showPoModal ? 'Hide purchase order' : 'Load from purchase order'}
                        </button>
                        <button
                            onClick={openAllocateModal}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                            <ClipboardDocumentListIcon className="h-4 w-4" />
                            Paste list + total
                        </button>
                    </div>
                </div>

            {/* Owner, 2026-08-25: the PO panel lives IN the page, under the toolbar that opens it --
                    not in an overlay. Same component, inline shell. */}
            {showPoModal && (
                <ImportPoModal
                    inline
                    onClose={closePoModal}
                    step={poConfirmStep ? 'confirm' : (selectedPoNumber ? 'lines' : 'list')}
                    poList={filteredPoList}
                    poListLoading={poListLoading}
                    poListError={poListError}
                    onSetPoClosed={setPoClosed}
                    showClosedPos={poShowClosed}
                    onToggleShowClosedPos={() => {
                        const next = !poShowClosed;
                        setPoShowClosed(next);
                        refreshPoList(next);
                    }}
                    poSearch={poSearch}
                    setPoSearch={setPoSearch}
                    selectedPoNumber={selectedPoNumber}
                    onSelectPo={selectPo}
                    onBack={() => { setSelectedPoNumber(null); setPoItems([]); }}
                    poItems={poItems}
                    poItemsLoading={poItemsLoading}
                    poItemsError={poItemsError}
                    poLineChecked={poLineChecked}
                    poLineQty={poLineQty}
                    poLineLocalQty={poLineLocalQty}
                    sfpStatusByAsin={sfpStatusByAsin}
                    onLocalQtyChange={onPoLocalQtyChange}
                    localControls={{
                        units: localUnitsTotal,
                        missingCount: localMissingSfp.length,
                        busy: localAssignBusy,
                        onCreateListings: createSfpListingsForLocal,
                        onAssign: assignLocalUnits,
                    }}
                    onToggleLine={togglePoLine}
                    onToggleAll={setAllPoLines}
                    onQtyChange={updatePoLineQty}
                    onNextConfirm={goToPoConfirmStep}
                    /* Task 34: step 2 -- confirm box configurations */
                    poConfirmRows={poConfirmRows}
                    poConfirmLoading={poConfirmLoading}
                    packagingByAsin={packagingByAsin}
                    isPackagingComplete={isPackagingComplete}
                    poConfirmSourceLabel={poConfirmSourceLabel}
                    poConfirmConfirmed={poConfirmConfirmed}
                    onConfirmRow={confirmPoRow}
                    onUnconfirmRow={unconfirmPoRow}
                    onEditRow={openPackagingPopover}
                    /* Task 67: bulk "unit = box" action for unconfigured rows */
                    unitBoxBulkCount={unitBoxBulkTargets.length}
                    onApplyUnitBoxBulk={applyUnitBoxToAllUnconfigured}
                    poConfirmSaveDefault={poConfirmSaveDefault}
                    onToggleSaveDefault={togglePoConfirmSaveDefault}
                    onRemoveConfirmRow={removePoConfirmRow}
                    onBackToLines={backToPoLines}
                    allPoConfirmRowsConfirmed={allPoConfirmRowsConfirmed}
                    onAddConfirmedToPlan={addConfirmedPoRowsToPlan}
                    /* Task 61: UPS|LTL toggle + shipment-level LTL pallet/freight section */
                    shipmentMode={shipmentMode}
                    onShipmentModeChange={chooseShipmentMode}
                    ltlForm={ltlForm}
                    onLtlFieldChange={updateLtlField}
                    ltlReady={ltlReady}
                    /* Task 70b: pickup day, entered alongside the pallet inputs it travels with */
                    pickupDate={pickupDate}
                    onPickupDateChange={setPickupDate}
                    /* Task 88: per-ASIN UPS / LTL / UPS-LTL designation + the tab filter it drives */
                    allowedModesByAsin={allowedModesByAsin}
                    onAllowedModesChange={setAsinAllowedModes}
                    poVisibleLines={poLinePartition.visible}
                    poHiddenLineCount={poLinePartition.hidden.length}
                    showAllModes={poShowAllModes}
                    onShowAllModes={setPoShowAllModes}
                                    /* Milestone C phase 0: pallet-denominated confirm-step rows -- 
                       re-attached after the inline-shell merge (2026-08-26). */
                    palletsByAsin={palletsByAsin}
                    onUpdatePoConfirmRowPallets={updatePoConfirmRowPallets}
                />
            )}

                <div className="flex items-end gap-3 mb-4">
                    <div className="flex-1 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add ASIN</label>
                        <div className="relative">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                placeholder="Search by ASIN, SKU, or product name..."
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm pr-10"
                            />
                            <button onClick={handleSearch} disabled={searchLoading} className="absolute right-2 top-1/2 -translate-y-1/2">
                                <MagnifyingGlassIcon className={`h-5 w-5 text-gray-400 hover:text-gray-600 ${searchLoading ? 'animate-pulse' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>

                {searchResults.length > 0 && (
                    <div className="mb-4 border rounded-md divide-y max-h-48 overflow-auto">
                        {searchResults.map((p, idx) => (
                            <button
                                key={`${p.asin}-${p.sku || idx}`}
                                onClick={() => addItem(p)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex justify-between"
                            >
                                <span className="truncate">{p.asin} — {p.name}</span>
                                <span className="text-gray-500 ml-2 flex-shrink-0">${p.selling_price}</span>
                            </button>
                        ))}
                    </div>
                )}

                {items.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500">ASIN</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                                    <th className="px-3 py-2 text-right font-medium text-gray-500">Qty</th>
                                    {/* Task 80: condition is decided HERE, before any label prints —
                                        Amazon derives the FNSKU and the "New - "/"Used - " line on the
                                        item label entirely from the MSKU. */}
                                    <th className="px-3 py-2 text-left font-medium text-gray-500">Condition / FNSKU</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-500">Packaging</th>
                                    <th className="px-3 py-2"></th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {items.map((item, idx) => {
                                    const pkg = packagingByAsin[item.asin];
                                    const complete = isPackagingComplete(pkg);
                                    // Owner, 2026-09-01: manufacturer item SKU + resolved product
                                    // name on every staged row (packaging batch carries identity).
                                    const identity = productIdentityByAsin[item.asin];
                                    const displayName = stagedDisplayName(item, identity);
                                    return (
                                    <tr key={`${item.asin}-${idx}`}>
                                        <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                                            {identity?.itemCode && (
                                                <div className="font-medium text-gray-800">{identity.itemCode}</div>
                                            )}
                                            <div className={identity?.itemCode ? 'text-gray-500' : ''}>{item.asin}</div>
                                        </td>
                                        <td className="px-3 py-2 max-w-xs truncate" title={displayName}>{displayName}</td>
                                        <td className="px-3 py-2 text-right">
                                            <input
                                                type="number"
                                                min="1"
                                                aria-label={`Planned quantity for ${item.asin}`}
                                                value={item.quantity}
                                                onChange={e => updateQty(idx, e.target.value)}
                                                className="w-20 rounded border-gray-300 text-sm text-right"
                                            />
                                            {/* Milestone C phase 0 (owner's Send to Amazon pattern):
                                                quantity entry denominated in pallets for a row whose
                                                packing template carries a boxes-per-pallet. Units stay
                                                the wire truth -- typing here just derives them. */}
                                            {shipmentMode === 'LTL' && pkg?.boxesPerPallet > 0 && (
                                                <div className="mt-1 text-right">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        placeholder="pallets"
                                                        value={palletsByAsin[item.asin] ?? ''}
                                                        onChange={e => updatePalletsForAsin(item.asin, idx, e.target.value)}
                                                        aria-label={`Pallets for ${item.asin}`}
                                                        className="w-20 rounded border-gray-300 text-xs text-right"
                                                    />
                                                    {palletDerivationLabel(palletsByAsin[item.asin], pkg) && (
                                                        <div
                                                            data-testid={`pallet-qty-derivation-${item.asin}`}
                                                            className="text-[11px] text-gray-500 whitespace-nowrap"
                                                        >
                                                            {palletDerivationLabel(palletsByAsin[item.asin], pkg)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <ConditionCell
                                                asin={item.asin}
                                                entry={conditionsByAsin[item.asin]}
                                                chosenSku={conditionSkuByAsin[item.asin]}
                                                onChange={sku => setConditionSkuByAsin(prev => ({ ...prev, [item.asin]: sku }))}
                                            />
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {/* Task 24 (owner feedback: "there is nothing to indicate i can add a new
                                                box configuration") -- pencil icon so the chip visibly reads as clickable,
                                                not just a status pill. Task 34: rows added via the PO-import confirm
                                                step render green (warehouse-manager confirmed); everything else keeps
                                                the prior gray/amber styling. */}
                                            <button
                                                onClick={() => openPackagingPopover(item.asin, item.name, item.quantity)}
                                                title={packagingConfirmedAsin[item.asin]
                                                    ? 'Confirmed at PO import — click to edit box configuration'
                                                    : 'Edit box configuration'}
                                                className={packagingConfirmedAsin[item.asin] && complete
                                                    ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300 hover:bg-green-200'
                                                    : complete
                                                    ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                                                    : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'}
                                            >
                                                {packagingConfirmedAsin[item.asin] && complete && (
                                                    <span aria-hidden="true">&#10003;</span>
                                                )}
                                                {complete
                                                    ? packagingSummaryLine(pkg)
                                                    : 'packaging needed'}
                                                <PencilIcon className="h-3 w-3 flex-shrink-0 opacity-60" />
                                            </button>
                                            {/* Task 88: the ASIN's shipping designation, read-only
                                                on the plan rows -- it is set on the PO-import lines
                                                step and belongs to the ASIN, not to this plan. */}
                                            <ModeChip value={allowedModesByAsin[item.asin]} className="ml-1.5" />
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                onClick={() => openDrawer(item.asin)}
                                                className="text-gray-400 hover:text-indigo-600"
                                                title="Demand / coverage detail"
                                            >
                                                <InformationCircleIcon className="h-4 w-4" />
                                            </button>
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <button onClick={() => removeItemAt(idx)} className="text-red-400 hover:text-red-600">
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className="flex gap-6 mt-3 text-sm text-gray-600 bg-gray-50 rounded p-3">
                            <span><strong>{items.length}</strong> items</span>
                            <span><strong>{totalUnits}</strong> units</span>
                        </div>
                        {/* Task 80: a deliberately-chosen used condition is allowed, but it is never
                            quiet. The confirm step will ask again before anything reaches Amazon. */}
                        {usedConditionAsins.length > 0 && (
                            <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                                <span aria-hidden="true">&#9888;</span>{' '}
                                <strong>{usedConditionAsins.length}</strong> line
                                {usedConditionAsins.length !== 1 ? 's are' : ' is'} set to a USED condition
                                ({usedConditionAsins.join(', ')}). Amazon prints the condition from the SKU, so these
                                item labels will say used. Switch to New above unless this is intentional.
                            </div>
                        )}
                        {unresolvedConditionAsins.length > 0 && (
                            <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                                <span aria-hidden="true">&#9888;</span>{' '}
                                Choose a condition for <strong>{unresolvedConditionAsins.join(', ')}</strong> — no
                                New-condition SKU exists for {unresolvedConditionAsins.length !== 1 ? 'these ASINs' : 'this ASIN'},
                                so the condition has to be picked deliberately. Analyze is blocked until then.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Owner, 2026-09-15: "place the working plans section under the ASIN list
                section". Same landing-only gate and same card styling as before -- only the
                position changed. "Start new" deliberately stays above, because its own copy
                ("From a purchase order below") introduces the list that follows it. */}
            {plannerView === 'landing' && (
                <div className="bg-white shadow rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Working plans</h2>
                    <p className="text-sm text-gray-500 mb-3">Pick a plan to resume — nothing loads until you choose.</p>
                    {(() => {
                        let lastOpened = null;
                        try { lastOpened = window.localStorage.getItem(ACTIVE_DRAFT_STORAGE_KEY); } catch (e) { /* private mode */ }
                        const rows = groupDraftsForLanding(drafts, lastOpened);
                        if (rows.length === 0) {
                            return <div className="text-sm text-gray-400 italic">No working plans — start one below.</div>;
                        }
                        return (
                            // Readability on the wide page: the resume list caps its width but
                            // stays LEFT-aligned with the container edge, so edges line up.
                            <div className="border rounded-md divide-y max-w-4xl">
                                {rows.map(r => (
                                    /* Owner 2026-09-01: the trashcan must not trigger resume —
                                       so the row is a div holding TWO buttons, never nested. */
                                    <div key={r.id} className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50">
                                        <button
                                            onClick={() => resumeDraftFromLanding(r.id)}
                                            disabled={draftBusy}
                                            className="flex-1 min-w-0 text-left flex items-center justify-between gap-3 disabled:opacity-50"
                                        >
                                            <span className="min-w-0">
                                                <span className="font-medium text-gray-900">{r.name}</span>
                                                {r.kind === 'split' && (
                                                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 whitespace-nowrap">
                                                        {r.total}-shipment split · {r.memberCount} open
                                                    </span>
                                                )}
                                                {r.lastOpened && (
                                                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">
                                                        last opened
                                                    </span>
                                                )}
                                                {/* Owner 2026-09-01: manufacturer SKU + product
                                                    name; a draft whose ASIN resolved to nothing
                                                    simply shows no second line. */}
                                                {(r.itemCode || r.productName) && (
                                                    <span className="block text-xs text-gray-500 truncate">
                                                        {r.itemCode && <span className="font-mono">{r.itemCode}</span>}
                                                        {r.itemCode && r.productName ? ' — ' : ''}
                                                        {r.productName}
                                                        {(r.itemCount || 0) > 1 && ` +${r.itemCount - 1} more`}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-xs text-gray-500 flex-shrink-0">
                                                {r.units || 0} units · <ModeChip value={r.mode} />
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => discardLandingRow(r)}
                                            disabled={draftBusy}
                                            aria-label={`Discard ${r.name}`}
                                            title="Discard this plan's draft(s). Analyzed plans on the Shipment Runs page are not affected."
                                            className="flex-shrink-0 p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* === Analyze Button + Task 32 mode selector + Task 33 background watch === */}
            {/* Owner, 2026-09-03: the multi-ASIN split modal — appears BEFORE staging when
                an added PO row's product sells under several ASINs. Context per ASIN is
                what makes the split an informed choice; an uninboundable listing (the
                repair-kit case: merchant-channel) is disabled with the reason, never
                silently allocatable. */}
            {multiAsinSplit && (() => {
                const s = multiAsinSplit;
                const row = s.pending[0];
                const total = row.quantity;
                const sum = (s.aliases || [])
                    .reduce((t, a) => t + (Number(s.allocations[a.asin]) || 0), 0);
                const lineCode = poItems.find(l => l.asin === row.asin)?.itemCode;
                return (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-5">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Split {total} units across ASINs
                            </h3>
                            <p className="text-sm text-gray-600 mt-0.5">
                                {row.name}{lineCode ? ` (${lineCode})` : ''} sells under {s.aliases.length} ASINs —
                                choose how many units ship under each.
                            </p>
                            <div className="mt-4 space-y-3">
                                {s.aliases.map(a => (
                                    <div key={a.asin}
                                         className={`border rounded-md px-4 py-3 ${
                                             a.inboundable ? 'border-gray-200' : 'border-red-200 bg-red-50/40'}`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-sm text-gray-900">{a.asin}</span>
                                                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                                        a.role === 'AUTHORIZED'
                                                            ? 'bg-indigo-100 text-indigo-700'
                                                            : 'bg-gray-100 text-gray-600'}`}>
                                                        {a.role === 'AUTHORIZED' ? 'authorized' : 'alternate'}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-700 truncate" title={a.title || row.name}>
                                                    {a.title || row.name}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {a.salesRank != null
                                                        ? `rank #${Number(a.salesRank).toLocaleString()}`
                                                        : 'rank —'}
                                                    {' · '}{a.orders90d ?? 0} orders/90d
                                                    {' · '}{a.fbaStock ?? 0} in FBA
                                                    {a.sku ? ` · ${a.sku}` : ''}
                                                </div>
                                                {a.note && (
                                                    <div className="text-xs text-gray-400 mt-0.5">{a.note}</div>
                                                )}
                                                {!a.inboundable && (
                                                    <div className="text-xs text-red-700 mt-1">
                                                        {a.sku
                                                            ? 'merchant-channel listing — cannot inbound until converted to FBA'
                                                            : 'no listing — cannot inbound'}
                                                    </div>
                                                )}
                                            </div>
                                            <input
                                                type="number" min="0"
                                                aria-label={`Units for ${a.asin}`}
                                                disabled={!a.inboundable}
                                                value={s.allocations[a.asin] ?? 0}
                                                onChange={e => setMultiAsinSplit(prev => ({
                                                    ...prev,
                                                    allocations: { ...prev.allocations, [a.asin]: e.target.value },
                                                }))}
                                                className="w-20 rounded border-gray-300 text-sm text-right shrink-0 disabled:bg-gray-100"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                                <span className={`text-sm ${sum === total ? 'text-emerald-700' : 'text-amber-700'}`}>
                                    {sum} of {total} units allocated
                                    {s.pending.length > 1 ? ` · ${s.pending.length - 1} more product${s.pending.length > 2 ? 's' : ''} after this` : ''}
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => setMultiAsinSplit(null)}
                                            className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                                        Cancel
                                    </button>
                                    <button onClick={confirmMultiAsinAllocation}
                                            disabled={sum !== total}
                                            title={sum !== total ? `Allocations must total exactly ${total} units` : undefined}
                                            className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                                        Stage allocations
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
            {items.length > 0 && (
                <div className="mb-6">
                {new Set(items.map(i => i.asin)).size > 4 && (
                    <p className="text-xs text-amber-600 mb-2">
                        Large lists are better as a background watch — the synchronous modes keep this tab open ~1–2 min per ASIN.
                    </p>
                )}
                {/* Task 61: the shipment mode rides with the imported rows -- shown (and still
                    switchable) on the main panel. Manual/non-PO flows keep the UPS default. */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-gray-700">Shipment type:</span>
                    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm" role="group">
                        {[['UPS', 'UPS small parcel'], ['LTL', 'LTL freight']].map(([value, label], idx) => (
                            <button
                                key={value}
                                onClick={() => chooseShipmentMode(value)}
                                disabled={analyzing || splitAnalyzing}
                                title={(analyzing || splitAnalyzing) ? 'An analysis is already running — wait for it to finish' : ''}
                                className={`px-3 py-1.5 ${idx > 0 ? 'border-l border-gray-300' : ''} ${shipmentMode === value
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:opacity-50`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {shipmentMode === 'LTL' && (
                        <span className="text-xs text-gray-500">
                            partnered LTL quote — pallet details from the PO-import step (or saved defaults); watch &amp; box-split sweeps run as Phase-1 LTL draws; split/compare stays UPS-only
                        </span>
                    )}
                </div>
                {/* Task 70b (owner: "amazon may also require a pickup date to be specified for the
                    ltl - we need a way to indicate that"). Amazon quotes partnered-LTL carrier
                    appointments against this date; before this it was an invisible backend default.

                    Split members don't get the control at all. Owner, 2026-08-21: "remove the ready
                    date from the split shipment process" -- leaving a live date picker on a panel
                    whose analyze deliberately discards it would be a control that lies. */}
                {splitGroup ? (
                    <div className="mb-3 text-xs text-gray-500">
                        Ready-for-pickup date is not used for split shipments — Amazon is quoted
                        against its own default window.
                    </div>
                ) : (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <label htmlFor="pickup-date" className="text-sm font-medium text-gray-700">
                        Ready for pickup
                        {shipmentMode === 'LTL'
                            ? <span className="text-red-600" title="Required for LTL"> *</span>
                            : <span className="text-gray-400 font-normal"> (optional)</span>}
                    </label>
                    <input
                        id="pickup-date"
                        type="date"
                        value={pickupDate}
                        min={minPickupDate()}
                        max={maxPickupDate()}
                        onChange={e => setPickupDate(e.target.value)}
                        aria-label="Ready for pickup date"
                        className={`px-2 py-1 border rounded text-sm ${pickupBlocked ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                    />
                    {pickupDate && !pickupBlocked && (
                        <span className="text-xs text-gray-500">{formatPickupLabel(pickupDate)}</span>
                    )}
                    {pickupBlocked && (
                        <span className="text-xs text-red-600">
                            required for LTL — the carrier needs a day to collect the pallets
                        </span>
                    )}
                    {shipmentMode !== 'LTL' && (
                        <span className="text-xs text-gray-400">Amazon schedules parcel pickup itself</span>
                    )}
                </div>
                )}
                {/* Task 90 (owner: "before analyze shipping options, add another button for break
                    single asin into multiple"): sits ABOVE the analyze row because it restructures
                    what will be analyzed. Enabled with a single staged row (pre-filled) or several
                    (the modal opens on its ASIN picker). */}
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    <button
                        onClick={openSplitAsinModal}
                        disabled={analyzing || splitAnalyzing || draftBusy || splitAsinBusy || items.length === 0}
                        title={(analyzing || splitAnalyzing)
                            ? 'An analysis is already running — wait for it to finish'
                            : 'Break one ASIN\'s quantity into several smaller shipments — each becomes its own shipment draft you can analyze and confirm separately'}
                        className="px-4 py-2 border border-amber-300 text-amber-800 font-medium rounded-lg text-sm hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Squares2X2Icon className="h-4 w-4" />
                        Break ASIN into multiple shipments…
                    </button>
                    <span className="text-xs text-gray-500">
                        e.g. 8 units as 2 shipments of 4 — creates a separate draft per shipment, analyzed one at a time
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => {
                            if (analyzeMode === 'combined') {
                                setKeepChecking(true);   // owner 2026-09-04: default ON per open
                                setScoreChoiceOpen(true);
                            } else {
                                startAnalysis();
                            }
                        }}
                        disabled={analyzing || splitAnalyzing || incompletePackagingAsins.length > 0
                            || unresolvedConditionAsins.length > 0 || pickupBlocked}
                        title={(analyzing || splitAnalyzing)
                            ? 'An analysis is already running — wait for it to finish'
                            : incompletePackagingAsins.length > 0
                                ? `Packaging needed for: ${incompletePackagingAsins.join(', ')}`
                                : unresolvedConditionAsins.length > 0
                                    ? `Choose a condition for: ${unresolvedConditionAsins.join(', ')} — no New-condition SKU exists, so the condition must be picked deliberately`
                                    : pickupBlocked
                                        ? 'Set a ready-for-pickup date — LTL carriers need a day to collect the pallets'
                                        : ''}
                        className="w-full sm:w-auto px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {(analyzing || splitAnalyzing) ? (
                            <>
                                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                {splitAnalyzing
                                    ? `Analyzing ${new Set(items.map(i => i.asin)).size + (analyzeMode === 'compare' ? 1 : 0)} plans (~1–2 min each)...`
                                    : analysisStatus}
                            </>
                        ) : (
                            <>
                                <TruckIcon className="h-5 w-5" />
                                {analyzeMode === 'separate' ? 'Analyze Separate Plans (one per ASIN)'
                                    : analyzeMode === 'compare' ? 'Compare Separate vs Combined'
                                    : (analysisResult ? 'Re-analyze Placement Options' : 'Score Placement Options')}
                            </>
                        )}
                    </button>
                    {/* Owner, 2026-09-01: "open a modal and ask if the shipment should be
                        submitted as a single shipment only, as a single shipment & separate
                        shipments broken up by box, or just separate broken up by box". Only the
                        combined analyzeMode opens this — Separate/Compare already ARE an explicit
                        submission choice (Task 32). */}
                    {scoreChoiceOpen && (() => {
                        const boxPlan = scoreSubmissionPlan(items, packagingByAsin);
                        const boxLabel = boxPlan.eligible
                            ? `${boxPlan.boxes} single-box plans of ${boxPlan.chunks[0]} unit${boxPlan.chunks[0] !== 1 ? 's' : ''} each`
                            : boxPlan.reason;
                        const remainderNote = boxPlan.eligible && boxPlan.remainder > 0
                            ? `${boxPlan.remainder} unit${boxPlan.remainder !== 1 ? 's' : ''} beyond whole boxes ride only in a combined plan`
                            : null;
                        const choices = [
                            { value: 'SINGLE', label: 'Single shipment only', enabled: true,
                              detail: '1 combined plan — today\'s behavior. Amazon may still split it across FCs, and at 5+ boxes the placement fee is waived.' },
                            { value: 'BOTH', label: 'Single shipment + broken up by box', enabled: boxPlan.eligible,
                              detail: boxPlan.eligible
                                  ? `1 combined plan AND ${boxLabel} — compare together vs broken apart on the Shipment Runs page. Single-box plans each forfeit Amazon's ~5-box placement-fee waiver; fees show per variant.${remainderNote ? ` ${remainderNote}.` : ''}`
                                  : boxPlan.reason },
                            { value: 'BOXES', label: 'Separate shipments, broken up by box only', enabled: boxPlan.eligible,
                              detail: boxPlan.eligible
                                  ? `${boxLabel}, each with its own FC draw — no combined plan. Each forfeits the ~5-box placement-fee waiver.${remainderNote ? ` Note: ${remainderNote} — with no combined plan they stay unanalyzed.` : ''}`
                                  : boxPlan.reason },
                        ];
                        const effective = choices.find(c => c.value === scoreChoice && c.enabled)
                            ? scoreChoice : 'SINGLE';
                        return (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-1">How should this be submitted?</h3>
                                    <p className="text-sm text-gray-500 mb-4">
                                        All choices create real, unconfirmed Amazon plans — nothing is confirmed from here.
                                    </p>
                                    <div className="space-y-2">
                                        {choices.map(c => (
                                            <label key={c.value}
                                                   className={`flex items-start gap-3 border rounded-md px-4 py-3 ${
                                                       c.enabled ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'} ${
                                                       effective === c.value ? 'border-indigo-400 bg-indigo-50/50' : 'border-gray-200'}`}>
                                                <input
                                                    type="radio"
                                                    name="score-submission-choice"
                                                    className="mt-1"
                                                    checked={effective === c.value}
                                                    disabled={!c.enabled}
                                                    onChange={() => setScoreChoice(c.value)}
                                                    aria-label={c.label}
                                                />
                                                <span>
                                                    <span className="block font-medium text-gray-900">{c.label}</span>
                                                    <span className="block text-xs text-gray-600 mt-0.5">{c.detail}</span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    {/* Owner 2026-09-04 (dual cadence): default ON. The analyze
                                        is the first draw -- registration is quiet (kickoff:false),
                                        the FULL shape re-draws every 6h, a box breakup re-draws as
                                        a whole set every 12h. */}
                                    <label className="mt-4 flex items-start gap-2 text-sm cursor-pointer border-t border-gray-100 pt-4">
                                        <input
                                            type="checkbox"
                                            className="mt-0.5"
                                            checked={keepChecking}
                                            onChange={() => setKeepChecking(v => !v)}
                                            aria-label="Keep checking until confirmed"
                                        />
                                        <span>
                                            <span className="font-medium text-gray-900">Keep checking until confirmed</span>
                                            <span className="block text-xs text-gray-600 mt-0.5">
                                                The full shipment re-draws every 6 hours
                                                {boxPlan.eligible ? '; a box breakup re-draws as a whole set every 12 hours' : ''}
                                                {' '}until confirmed or stopped — this request is the first draw, so nothing extra runs now.
                                            </span>
                                        </span>
                                    </label>
                                    <div className="mt-5 flex justify-end gap-2">
                                        <button onClick={() => setScoreChoiceOpen(false)}
                                                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                                            Cancel
                                        </button>
                                        <button onClick={() => runScoreChoice(effective, boxPlan)}
                                                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700">
                                            Request shipments
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    {/* Task 32 mode selector (owner request 2026-08-17: individual UPS-ground
                        shipments to diversify coverage, or a side-by-side vs one combined plan). */}
                    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm" role="group">
                        {[['combined', 'Combined'], ['separate', 'Separate per ASIN'], ['compare', 'Compare']].map(([value, label], idx) => (
                            <button
                                key={value}
                                onClick={() => setAnalyzeMode(value)}
                                disabled={analyzing || splitAnalyzing || (shipmentMode === 'LTL' && value !== 'combined')}
                                title={(analyzing || splitAnalyzing)
                                    ? 'An analysis is already running — wait for it to finish'
                                    : shipmentMode === 'LTL' && value !== 'combined' ? 'UPS-only — LTL analyzes as one combined plan' : ''}
                                className={`px-3 py-2 ${idx > 0 ? 'border-l border-gray-300' : ''} ${analyzeMode === value
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'} disabled:opacity-50`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    {analyzeMode !== 'combined' && (
                        <span className="text-xs text-gray-500">
                            creates {new Set(items.map(i => i.asin)).size + (analyzeMode === 'compare' ? 1 : 0)} real plans (max 12 ASINs) — all stay unconfirmed
                        </span>
                    )}
                    {/* Task 33: background watch -- no spinner, response is immediate.
                        Owner, 2026-09-01: when the CURRENT asin/qty set is already actively
                        watched, the button says so and refuses a duplicate click instead of
                        silently re-upserting the same queue entry. A different qty is a
                        different configuration — watching again updates the queued qty. */}
                    {(() => {
                        const coverage = watchCoverage(items, watchRows);
                        const alreadyWatched = coverage === 'EXACT';
                        return (
                    <button
                        onClick={watchInBackground}
                        disabled={watchStarting || analyzing || splitAnalyzing || alreadyWatched}
                        title={alreadyWatched
                            ? 'This ASIN/qty set is already in the background queue — drawing every 6 hours. Manage or stop it on the Shipment Runs page.'
                            : coverage === 'DIFFERENT_QTY'
                                ? 'Already watched at a different quantity — watching again updates the queued quantity.'
                                : shipmentMode === 'LTL'
                                    ? 'Draws a Phase-1 LTL plan (FC split + placement fee) for each ASIN every 6 hours until confirmed — freight is priced when you measure pallets'
                                    : (analyzing || splitAnalyzing)
                                        ? 'An analysis is already running — wait for it to finish'
                                        : watchStarting
                                            ? 'Watch is starting…'
                                            : 'Draws each ASIN in the background now and every 6 hours until its shipment is confirmed — no need to keep this tab open. Progress lives on the Shipment Runs page.'}
                        className="px-4 py-3 border border-purple-300 text-purple-700 font-medium rounded-lg text-sm hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <EyeIcon className="h-4 w-4" />
                        {alreadyWatched ? 'Watching in background ✓'
                            : watchStarting ? 'Starting watch…'
                            : coverage === 'DIFFERENT_QTY' ? 'Update watch (qty changed)'
                            : 'Watch in background'}
                    </button>
                        );
                    })()}
                    {/* Task 53: partition sweep -- single-ASIN only (it partitions ONE ASIN's qty
                        into box-multiple chunks as separate plans). Async; results by email + on
                        the Runs page's "Partition sweeps" section. */}
                    {new Set(items.map(i => i.asin)).size === 1 && items.length === 1 && (
                        <button
                            onClick={exploreBoxSplits}
                            disabled={sweepStarting || analyzing || splitAnalyzing}
                            title={shipmentMode === 'LTL'
                                ? 'Breaks the quantity into box-multiple chunks, each a Phase-1 LTL plan with its own FC split and placement fee — compare reach and fees now, price freight after measuring the kept chunks'
                                : (analyzing || splitAnalyzing)
                                    ? 'An analysis is already running — wait for it to finish'
                                    : sweepStarting
                                        ? 'Sweep is starting…'
                                        : 'Analyzes this ASIN\'s quantity together AND broken into box-multiple chunks (e.g. 5 boxes + 1 box as separate shipments) to compare placement, freight, and the 5-box placement-fee break. Runs in the background.'}
                            className="px-4 py-3 border border-teal-300 text-teal-700 font-medium rounded-lg text-sm hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {sweepStarting ? 'Starting sweep…' : 'Explore box splits…'}
                        </button>
                    )}
                </div>
                </div>
            )}

            {analyzing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-700">
                    {analysisStatus}
                </div>
            )}

            {/* 2026-08-21: the carton-confirmation ask. Sits ABOVE the failure banner and replaces
                it (the catch clears analysisError when a gap is present), because "we need this
                number from you" is a different thing from "the analyze broke". Clicking a row
                opens the same packaging popover the rest of the page uses -- one editor, not a
                second copy of one. */}
            {!analyzing && (
                <CartonDataGapPanel
                    gap={cartonGap}
                    onFix={(it) => openPackagingPopover(
                        it.asin,
                        it.title || it.asin,
                        it.unitsInShipment || (items.find(i => i.asin === it.asin)?.quantity ?? 0),
                    )}
                />
            )}

            {/* Task 68: persistent, dismissable failure banner -- analyze errors used to be
                written into analysisStatus (visible only WHILE analyzing) and were never seen. */}
            {analysisError && !analyzing && !splitAnalyzing && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>Analysis failed: {analysisError}</span>
                    </div>
                    <button onClick={() => setAnalysisError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {splitAnalyzing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-700">
                    Creating one inbound plan per ASIN{analyzeMode === 'compare' ? ' plus the combined plan' : ''} — Amazon
                    processes these sequentially (~1–2 minutes each). Leave this tab open.
                </div>
            )}

            {/* === Task 32: split/compare results === */}
            {splitResult && !splitAnalyzing && <SplitComparisonTable result={splitResult} />}

            {analysisResult && analysisResult.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">
                    {analysisResult.error}
                </div>
            )}

            {/* === Approximation note === */}
            {analysisResult && analysisResult.approximation && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-sm text-amber-800 flex items-start gap-2">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>Per-shipment item breakout unavailable for at least one option — scores approximate (every ASIN is credited with all of that option&apos;s destinations).</span>
                </div>
            )}

            {/* === Purpose-tag banner (owner corrections 2026-08-17, run 97): renders whenever the
                best option is tagged COVERAGE and/or REPLENISHMENT -- chips carry each tag's own
                number so a both-tagged option ("this is both a replenish and coverage") shows both
                facts, not just one. Renders instead of the red WEAK banner below (mutually
                exclusive -- WEAK is only ever the sole tag). No fallback ladder here -- this isn't
                a bad shipment, just possibly a lower-value one (see the decision strip per card). */}
            {bestScore && !bestScore.weak && purposeTags(bestScore).length > 0 && (
                <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <InformationCircleIcon className="h-6 w-6 text-indigo-500 flex-shrink-0" />
                        <div className="flex-1">
                            <PurposeTagChips score={bestScore} className="mb-2" />
                            <p className="text-sm text-indigo-800">
                                {purposeTags(bestScore).includes('COVERAGE') && purposeTags(bestScore).includes('REPLENISHMENT')
                                    ? `Adds ${bestScore.demandCovGainPp.toFixed(1)}pp of 2-day coverage and ${bestScore.runwayDaysAdded.toFixed(0)} days of runway.`
                                    : purposeTags(bestScore).includes('COVERAGE')
                                        ? `Adds ${bestScore.demandCovGainPp.toFixed(1)}pp of 2-day coverage.`
                                        : `Adds ${bestScore.runwayDaysAdded.toFixed(0)} days of runway.`}
                                {' '}See the decision below for whether it's worth taking.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* === Weak placement banner + fallback ladder === */}
            {bestScore && bestScore.weak && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                        <div className="flex-1">
                            <h3 className="text-sm font-bold text-red-800 mb-1">WEAK PLACEMENT</h3>
                            <p className="text-sm text-red-700 mb-3">
                                Best option's expected lift is only ${bestScore.valuePerMonth.toFixed(0)}/mo with a
                                {' '}{bestScore.demandCovGainPp.toFixed(1)}pp demand-coverage gain — below the
                                {' '}$50/mo or 5pp confirmation floor. Confirming anyway sends stock somewhere demand doesn't support.
                            </p>
                            {originNote && (
                                <p className="text-xs text-red-600 bg-red-100 rounded p-2 mb-3">{originNote}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={parkAndRetry}
                                    disabled={parking || !analysisResult?.inboundPlanId}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                                >
                                    <ArrowPathIcon className="h-4 w-4" />
                                    {parking ? 'Parking...' : 'Park & retry'}
                                </button>
                                <button
                                    onClick={splitBySizeTier}
                                    disabled={splitLoading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                                >
                                    <Squares2X2Icon className="h-4 w-4" />
                                    {splitLoading ? 'Splitting...' : 'Split by size tier'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* === Split-by-size-tier variants === */}
            {splitVariants && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {[{ label: 'Standard', list: splitVariants.standard }, { label: 'Oversize / Extra-Large', list: splitVariants.oversizeXl }].map(v => (
                        <div key={v.label} className="bg-white shadow rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-gray-800">{v.label}</h4>
                                <span className="text-xs text-gray-500">{v.list.length} item{v.list.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="text-xs text-gray-500 mb-3 max-h-24 overflow-auto">
                                {v.list.map(i => <div key={i.asin}>{i.asin} — qty {i.quantity}</div>)}
                                {v.list.length === 0 && <div className="italic">none</div>}
                            </div>
                            <button
                                onClick={() => reanalyzeVariant(v.list)}
                                disabled={v.list.length === 0 || analyzing}
                                className="w-full px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                                Re-analyze this group
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* === Scored option cards (or a legacy pre-scoring fallback for an old loaded run) === */}
            {analysisResult && (scores.length > 0 || (analysisResult.placementOptions || []).length > 0) && (
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Placement Options ({scores.length > 0 ? scores.length : (analysisResult.placementOptions || []).length})
                            </h2>
                            {analysisResult.runId && <span className="text-xs text-gray-400 font-mono">Run #{analysisResult.runId}</span>}
                            {analysisResult.status && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{analysisResult.status}</span>
                            )}
                            {/* Task 70b: the pickup day these quotes were priced against -- carrier
                                appointments move with it, so it belongs next to the results. */}
                            {pickupDate && formatPickupLabel(pickupDate) && (
                                <span
                                    title="Ready-for-pickup date sent to Amazon for these quotes"
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700"
                                >
                                    Pickup: {formatPickupLabel(pickupDate)}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {analysisResult.status === 'ACTIVE' && selectedOptionId && (
                                <button
                                    // Task 87: see the identical note on the PO-mode confirm button --
                                    // never a bare handler reference here either.
                                    onClick={() => confirmSelected()}
                                    // 2026-08-21: an LTL plan cannot be confirmed on a pallet build
                                    // nobody approved. Parcel is unaffected -- palletBuildState
                                    // returns APPROVED for a non-LTL option, so small-parcel plans
                                    // gain no extra step.
                                    disabled={confirming
                                        || (selectedScore && !selectedScore.confirmAllowed)
                                        || selectedBuildState.state !== 'APPROVED'}
                                    title={selectedScore && !selectedScore.confirmAllowed
                                        ? (selectedScore.confirmDisabledReason || MIAMI_GATE_REASON)
                                        : (selectedBuildState.reason || '')}
                                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {confirming ? 'Confirming...' : 'Confirm & Send to Amazon'}
                                </button>
                            )}
                            {analysisResult.status === 'ACTIVE' && (
                                <button
                                    onClick={cancelPlan}
                                    disabled={confirming}
                                    className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                                >
                                    Cancel Plan
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Task 51: single shared results layout (see ShipmentResultsView) — same
                        component PO mode renders, so banner, CONFIRMED actions, position header,
                        draw-quality strip, option cards, FC distribution map and per-ASIN FC
                        distribution stay identical across every entry path. */}
                    <ShipmentResultsView
                        analysisResult={analysisResult}
                        items={items}
                        totalUnits={totalUnits}
                        approvedBuilds={approvedBuilds}
                        onApproveBuild={approveBuild}
                        selectedOptionId={selectedOptionId}
                        onSelectOption={(id) => {
                            setSelectedOptionId(id);
                            rememberSelectedPlacement(analysisResult?.runId, id);
                        }}
                        onCancelConfirmed={() => setShowCancelConfirmed(true)}
                        noScoresNote={legacyRunNoScores}
                        onAnalysisUpdate={setAnalysisResult}
                        /* Task 71: one-click remedies for the partnered-LTL carrier gap. */
                        onExcludeAsins={excludeAsinsAndReanalyze}
                        onSwitchToUps={reanalyzeAsUps}
                        /* Task 73: the own-carrier confirm, offered only while the plan is still
                           actionable -- a confirmed or cancelled plan has nothing left to commit. */
                        onConfirmOwnCarrier={analysisResult?.status === 'ACTIVE' ? confirmOwnCarrier : undefined}
                        remedyBusy={analyzing || splitAnalyzing || confirming}
                        /* Task C5: measurements persist by destination FC and survive a re-analyze. */
                        palletMeasurements={palletMeasurements}
                        onMeasurementsChange={handleMeasurementsChange}
                    />
                </div>
            )}

            {/* === Per-ASIN drawer === */}
            {drawerAsin && (
                <div className="fixed inset-0 z-40 flex justify-end">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setDrawerAsin(null)} />
                    <div className="relative bg-white w-full max-w-md h-full shadow-xl p-6 overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 font-mono">{drawerAsin}</h3>
                            <button onClick={() => setDrawerAsin(null)} className="text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Top demand metros</h4>
                        {demandLoading ? (
                            <div className="text-sm text-gray-400">Loading...</div>
                        ) : demandRows.length === 0 ? (
                            <div className="text-sm text-gray-400 italic mb-4">No demand history for this ASIN yet.</div>
                        ) : (
                            <div className="space-y-1 mb-4">
                                {demandRows.slice(0, 5).map(r => (
                                    <div key={r.metro} className="flex items-center justify-between text-sm">
                                        <span className="font-mono text-gray-700">{r.metro}</span>
                                        <span className="text-gray-500">{((r.weight || 0) * 100).toFixed(1)}%</span>
                                        <span className="text-xs text-gray-400">n={((r.n_recent || 0) + (r.n_lifetime || 0))}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h4 className="text-sm font-semibold text-gray-700 mb-2 pt-2 border-t">Current coverage &amp; runway</h4>
                        {!planningByAsin ? (
                            <div className="text-sm text-gray-400">Loading...</div>
                        ) : drawerPlanningRow ? (
                            <div className="space-y-1 text-sm">
                                <div className="flex justify-between"><span className="text-gray-500">Cov 1d</span><span>{drawerPlanningRow.cov_1d_pct != null ? `${drawerPlanningRow.cov_1d_pct}%` : '—'}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Cov 2d</span><span>{drawerPlanningRow.cov_2d_pct != null ? `${drawerPlanningRow.cov_2d_pct}%` : '—'}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Runway (days)</span><span>{drawerPlanningRow.runway_days ?? '—'}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">FC count</span><span>{drawerPlanningRow.fc_count ?? '—'}</span></div>
                            </div>
                        ) : (
                            <div className="text-sm text-gray-400 italic">
                                No War Room scorecard row for this ASIN (not currently stocked, or scorecard hasn't run yet).
                            </div>
                        )}

                        <h4 className="text-sm font-semibold text-gray-700 mb-2 pt-4 border-t mt-4">FC Coverage</h4>
                        <FcCoverageTable
                            status={fcAnalysis.status}
                            rows={fcAnalysis.rows}
                            dwCov1dPct={drawerPlanningRow?.dw_cov_1d_pct}
                            dwCov2dPct={drawerPlanningRow?.dw_cov_2d_pct}
                            dwCov1dEffPct={drawerPlanningRow?.dw_cov_1d_eff_pct}
                            dwCov2dEffPct={drawerPlanningRow?.dw_cov_2d_eff_pct}
                        />
                    </div>
                </div>
            )}

            {/* === Task 12: Paste list + total modal === */}
            {/* === Task 90: break ONE ASIN into N shipments (N drafts) === */}
            {splitAsinModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg my-8">
                        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
                            <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900">Break one ASIN into multiple shipments</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Each shipment becomes its own shipment draft — analyze and confirm them one at a time.
                                </p>
                            </div>
                            <button
                                onClick={() => setSplitAsinModalOpen(false)}
                                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                                aria-label="Close"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            {/* Task 96: the progress / success / partial-failure indicator. */}
                            <SplitProgressBlock progress={splitProgress} />

                            {/* Task 96: once the drafts exist the form is done -- the modal shows the
                                set that was created instead of inviting another split of rows that
                                have already moved. */}
                            {splitProgress?.phase === 'done' && (
                                <SplitSetPanel
                                    members={splitSetMembers}
                                    activeDraftId={activeDraftId}
                                    analyzingId={splitSetAnalyzing}
                                    runAll={splitSetRunAll}
                                    summary={splitSetSummary}
                                    groupId={splitGroup?.id}
                                    onSwitch={(id) => { setSplitAsinModalOpen(false); switchToDraft(id); }}
                                    onAnalyze={analyzeSplitMember}
                                    onAnalyzeAll={analyzeAllInSplitSet}
                                    busy={!!splitSetRunAll || !!splitSetAnalyzing}
                                />
                            )}

                            {splitProgress?.phase !== 'done' && (<>
                            {/* Which ASIN: pre-filled and stated when there's only one staged row,
                                a picker when the build has several. */}
                            {items.filter(it => it && it.asin).length > 1 ? (
                                <div>
                                    <label htmlFor="split-asin-target" className="block text-xs font-medium text-gray-700 mb-1">
                                        ASIN to break up
                                    </label>
                                    <select
                                        id="split-asin-target"
                                        aria-label="ASIN to break up"
                                        value={splitAsinTarget || ''}
                                        onChange={e => chooseSplitAsin(e.target.value)}
                                        className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
                                    >
                                        <option value="">Choose an ASIN…</option>
                                        {items.filter(it => it && it.asin).map(it => (
                                            <option key={it.asin} value={it.asin}>
                                                {it.asin} — {stagedDisplayName(it, productIdentityByAsin[it.asin]) || 'product'} ({it.quantity || 0} units)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-700">
                                    <span className="font-mono">{splitAsinTarget || '—'}</span>
                                    {splitAsinRow ? <span className="text-gray-500"> · {stagedDisplayName(splitAsinRow, productIdentityByAsin[splitAsinRow.asin])}</span> : null}
                                    <span className="text-gray-500"> · {splitAsinRow?.quantity || 0} units staged</span>
                                </p>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label htmlFor="split-shipment-count" className="block text-xs font-medium text-gray-700 mb-1">
                                        Number of shipments
                                    </label>
                                    <input
                                        id="split-shipment-count"
                                        aria-label="Number of shipments"
                                        type="number" min="2" max={MAX_OPEN_DRAFTS} step="1"
                                        value={splitAsinCountInput}
                                        onChange={e => { setSplitAsinCountInput(e.target.value); setSplitAsinError(null); }}
                                        className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="split-units-per" className="block text-xs font-medium text-gray-700 mb-1">
                                        Units per shipment
                                    </label>
                                    <input
                                        id="split-units-per"
                                        aria-label="Units per shipment"
                                        type="number" min="1" step="1"
                                        value={splitAsinUnitsInput}
                                        onChange={e => { setSplitAsinUnitsInput(e.target.value); setSplitAsinError(null); }}
                                        className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
                                    />
                                </div>
                            </div>

                            {/* Live math — the owner's "2 shipments of 4 units total ... per 1 box" */}
                            <div className={`rounded-md border px-3 py-2 text-sm ${splitAsinPlan.valid
                                ? 'border-gray-200 bg-gray-50 text-gray-800'
                                : 'border-red-200 bg-red-50 text-red-700'}`}>
                                <div className="font-medium">{splitMathLabel(splitAsinPlan)}</div>
                                {splitAsinPlan.valid && splitBoxMathLabel(splitAsinPlan.unitsPerShipment,
                                    packagingByAsin[splitAsinTarget]?.unitsPerBox) && (
                                    <div className="text-xs text-gray-600 mt-0.5">
                                        {splitBoxMathLabel(splitAsinPlan.unitsPerShipment,
                                            packagingByAsin[splitAsinTarget]?.unitsPerBox)}
                                    </div>
                                )}
                                {splitAsinPlan.valid && !packagingByAsin[splitAsinTarget]?.unitsPerBox && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        Box count unknown — this ASIN has no packaging configured yet.
                                    </div>
                                )}
                                {splitAsinPlan.error && <div className="text-xs mt-0.5">{splitAsinPlan.error}</div>}
                            </div>

                            {/* Remainder: never silently absorbed — the operator picks. */}
                            {splitAsinPlan.remainder > 0 && (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                                    <p className="text-xs font-semibold text-amber-900 mb-1">
                                        {splitAsinPlan.remainder} unit{splitAsinPlan.remainder !== 1 ? 's' : ''} left over
                                    </p>
                                    {[[REMAINDER_TO_LAST, `Add ${splitAsinPlan.remainder === 1 ? 'it' : 'them'} to the last shipment `
                                        + `(it ships ${splitAsinPlan.unitsPerShipment + splitAsinPlan.remainder} units)`],
                                      [REMAINDER_UNASSIGNED, `Leave ${splitAsinPlan.remainder} unit${splitAsinPlan.remainder !== 1 ? 's' : ''} `
                                        + 'unassigned — they stay on this draft']].map(([value, label]) => (
                                        <label key={value} className="flex items-start gap-2 text-xs text-amber-900 py-0.5">
                                            <input
                                                type="radio"
                                                name="split-remainder-mode"
                                                value={value}
                                                checked={splitAsinRemainderMode === value}
                                                onChange={() => setSplitAsinRemainderMode(value)}
                                                className="mt-0.5"
                                            />
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {/* Per-shipment preview */}
                            {splitAsinPlan.valid && (
                                <div className="flex flex-wrap gap-1.5">
                                    {splitAsinPlan.shipments.map(sh => {
                                        const boxes = splitBoxesForUnits(sh.units, packagingByAsin[splitAsinTarget]?.unitsPerBox);
                                        return (
                                            <span key={sh.index} className="inline-flex items-center gap-1 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs text-indigo-800">
                                                {sh.index}/{splitAsinPlan.shipments.length} · {sh.units} units
                                                {boxes != null ? ` · ${boxes} box${boxes !== 1 ? 'es' : ''}` : ''}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {/* What happens to the draft on screen — stated before the operator commits. */}
                            {splitAsinPlan.valid && (
                                <p className="text-xs text-gray-600">
                                    {splitAsinDecision.reuseSource
                                        ? `This draft becomes shipment 1/${splitAsinPlan.shipments.length} `
                                            + `(${splitAsinPlan.shipments[0].units} units, renamed “${splitAsinDecision.sourceName}”); `
                                            + `${splitAsinDecision.newDraftCount} new draft${splitAsinDecision.newDraftCount !== 1 ? 's' : ''} `
                                            + 'will be created for the rest.'
                                        : `${splitAsinDecision.newDraftCount} new draft${splitAsinDecision.newDraftCount !== 1 ? 's' : ''} `
                                            + 'will be created — one per shipment. This draft stays open with '
                                            + (splitAsinDecision.leftover > 0
                                                ? `${splitAsinDecision.leftover} unassigned unit${splitAsinDecision.leftover !== 1 ? 's' : ''}`
                                                : 'its other ASINs')
                                            + '. Packaging, shipment type and pickup date carry through unchanged.'}
                                </p>
                            )}

                            {splitAsinPlan.valid && !splitAsinDecision.capOk && (
                                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                    {splitAsinDecision.capMessage}
                                </p>
                            )}

                            {/* Owner addition: submit = ANALYZE each shipment with Amazon. Never a
                                confirm -- that stays one deliberate human act per shipment. */}
                            <label className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                <input
                                    type="checkbox"
                                    checked={splitAutoSubmit}
                                    onChange={e => setSplitAutoSubmit(e.target.checked)}
                                    disabled={splitAsinBusy}
                                    className="mt-0.5"
                                />
                                <span className="text-xs text-gray-800">
                                    <span className="font-medium">Automatically submit to Amazon</span>
                                    <span className="block text-gray-600">
                                        analyzes each shipment with Amazon right after the split — you still
                                        choose which to confirm.
                                    </span>
                                </span>
                            </label>

                            {/* The owner's question, answered in the modal itself. */}
                            <p className="text-xs text-gray-600">
                                Each shipment is analyzed and confirmed separately. Nothing is ever confirmed
                                automatically — you open each shipment and confirm it yourself.
                            </p>
                            </>)}
                            {splitAsinError && (
                                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                    {splitAsinError}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50 rounded-b-lg">
                            {splitProgress?.phase === 'done' ? (
                                // Task 96: the split is finished -- the modal's job now is to hand the
                                // operator over to the set it created.
                                <button
                                    onClick={() => setSplitAsinModalOpen(false)}
                                    className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700"
                                >
                                    Done — show me the shipments
                                </button>
                            ) : (<>
                                <button
                                    onClick={() => setSplitAsinModalOpen(false)}
                                    disabled={splitAsinBusy}
                                    className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                >
                                    {splitProgress?.phase === 'failed' ? 'Close' : 'Cancel'}
                                </button>
                                <button
                                    onClick={runAsinSplit}
                                    disabled={splitAsinBusy || !splitAsinRow || !splitAsinPlan.valid || !splitAsinDecision.capOk}
                                    className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {splitAsinBusy ? splitProgressLabel(splitProgress) || 'Creating shipments…'
                                        : `Break into ${splitAsinPlan.valid ? splitAsinPlan.shipments.length : 0} shipments`}
                                </button>
                            </>)}
                        </div>
                    </div>
                </div>
            )}

            {showAllocateModal && (
                <AllocateTotalModal
                    onClose={closeAllocateModal}
                    asinText={allocateAsinText}
                    setAsinText={setAllocateAsinText}
                    totalQtyInput={allocateTotalQtyInput}
                    setTotalQtyInput={setAllocateTotalQtyInput}
                    onRun={runAllocate}
                    loading={allocateLoading}
                    error={allocateError}
                    preview={allocatePreview}
                    onRowQtyChange={updateAllocatePreviewQty}
                    onBack={() => setAllocatePreview(null)}
                    onAddToPlan={addAllocateRowsToPlan}
                />
            )}

            {/* === Task 24/41: packaging modal -- two-tier box model (owner, verbatim: "the user
                should specify the weight of the unit, and also specify the weight and qty of boxes
                to make the shipment. and the total is added together ... the box size and weights
                can be shared across asins, the shipment configuration should be asin specific
                though"). Step 1 = the ASIN-specific numbers (unit weight, units per box); Step 2 =
                pick a SHARED catalog carton (or create one inline); the loaded box weight is never
                typed -- it's computed live with the math spelled out. Footer keeps the two named
                actions from task 24. */}
            <BoxConfigModal
                packagingPopoverAsin={packagingPopoverAsin}
                packagingPopoverTitle={packagingPopoverTitle}
                packagingPopoverQty={packagingPopoverQty}
                packagingForm={packagingForm}
                packagingFieldErrors={packagingFieldErrors}
                packagingFormValid={packagingFormValid}
                packagingAttemptedSubmit={packagingAttemptedSubmit}
                packagingStyleChoice={packagingStyleChoice}
                packagingSaving={packagingSaving}
                packagingLoadedLbs={packagingLoadedLbs}
                packagingSummaryLine={packagingSummaryLine}
                packagingModalActivePkg={packagingModalActivePkg}
                packagingModalDerived={packagingModalDerived}
                packagingModalLegacyOnly={packagingModalLegacyOnly}
                packagingModalTotals={packagingModalTotals}
                boxCatalog={boxCatalog}
                choosePackagingStyle={choosePackagingStyle}
                closePackagingPopover={closePackagingPopover}
                applyPackaging={applyPackaging}
                updatePackagingField={updatePackagingField}
                fmtLbsShort={fmtLbsShort}
                newBoxOpen={newBoxOpen}
                newBoxForm={newBoxForm}
                newBoxSaving={newBoxSaving}
                editingBoxId={editingBoxId}
                boxError={boxError}
                boxNote={boxNote}
                selectedBox={selectedBox}
                updateNewBoxField={updateNewBoxField}
                saveNewBoxToCatalog={saveNewBoxToCatalog}
                renderCatalogBoxCard={renderCatalogBoxCard}
                setEditingBoxId={setEditingBoxId}
                setNewBoxForm={setNewBoxForm}
                setBoxError={setBoxError}
                setNewBoxOpen={setNewBoxOpen}
                setSelectedBoxId={setSelectedBoxId}
            />

            {/* Task 80: explicit "I intend to ship used condition" gate. Opened only by the
                server refusing a confirm, so the SKUs listed are the real ones on the plan. */}
            <UsedConditionAckModal
                open={!!usedAckItems}
                items={usedAckItems || []}
                busy={confirming}
                onCancel={() => { setUsedAckItems(null); setUsedAckRetry(null); }}
                onAcknowledge={() => { if (usedAckRetry) usedAckRetry(); }}
            />
            <CancelConfirmedShipmentModal
                open={showCancelConfirmed}
                onClose={() => setShowCancelConfirmed(false)}
                planId={analysisResult?.inboundPlanId}
                runLabel={analysisResult?.runId ? `Run #${analysisResult.runId}` : null}
                units={totalUnits}
                asins={items.map(i => ({ asin: i.asin, qty: i.quantity }))}
                confirmedAt={analysisResult?.confirmedAt}
                onCancelled={() => {
                    setShowCancelConfirmed(false);
                    setAnalysisResult(prev => (prev ? { ...prev, status: 'CANCELLED' } : null));
                    // Note: this component's `toast` identifier is its own local banner state
                    // (const [toast, setToast] = useState(null)), not the react-hot-toast import
                    // used elsewhere on this page -- setToast keeps this consistent with every
                    // other success/failure message in ASIN mode.
                    setToast('Shipment cancelled.');
                    setTimeout(() => setToast(null), 5000);
                }}
            />
        </div>
    );
}

// === Task 88: per-ASIN shipping designation ===
//
// Read-only chip. Used where the designation is information, not a decision: the PO-import confirm
// step rows and the ASIN-mode plan rows. Unset renders as a muted "UPS/LTL · not set" so the
// operator can tell "nobody has said" from "somebody said both".
export function ModeChip({ value, className = '' }) {
    const set = isAllowedModesSet(value);
    const designation = normalizeAllowedModes(value);
    const tone = !set
        ? 'bg-gray-50 text-gray-400 border-gray-200'
        : designation === 'UPS'
            ? 'bg-sky-100 text-sky-800 border-sky-300'
            : designation === 'LTL'
                ? 'bg-violet-100 text-violet-800 border-violet-300'
                : 'bg-gray-100 text-gray-600 border-gray-300';
    return (
        <span
            title={set
                ? `Ships via ${allowedModesLabel(value)}`
                : 'No shipping designation on file — treated as UPS/LTL (shows under both tabs)'}
            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border ${tone} ${className}`}
        >
            {allowedModesLabel(value)}{!set && <span className="ml-1 font-normal italic">not set</span>}
        </span>
    );
}

// The editable control on each PO-import line: compact segmented UPS / LTL / Both. Changing it
// persists immediately (optimistic) -- there is no separate save.
export function ModeSelect({ asin, value, onChange, disabled = false }) {
    const set = isAllowedModesSet(value);
    const current = normalizeAllowedModes(value);
    const options = [['UPS', 'UPS'], ['LTL', 'LTL'], ['BOTH', 'Both']];
    return (
        <span className="inline-flex items-center gap-1">
            <span className="inline-flex rounded border border-gray-300 overflow-hidden text-xs" role="group"
                  aria-label={`Shipping designation for ${asin}`}>
                {options.map(([optValue, label], idx) => {
                    const active = set && current === optValue;
                    return (
                        <button
                            key={optValue}
                            type="button"
                            disabled={disabled}
                            aria-pressed={active}
                            title={`Designate ${asin} as ${optValue === 'BOTH' ? 'UPS/LTL' : optValue}`}
                            onClick={() => onChange && onChange(asin, optValue)}
                            className={`px-1.5 py-0.5 ${idx > 0 ? 'border-l border-gray-300' : ''} ${active
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white text-gray-500 hover:bg-gray-50'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {label}
                        </button>
                    );
                })}
            </span>
            {!set && (
                <span
                    className="text-[10px] italic text-gray-400"
                    title="No shipping designation on file — treated as UPS/LTL, so this line shows under both tabs"
                >
                    not set
                </span>
            )}
        </span>
    );
}

// === Task 12: Import from PO modal ===
function ImportPoModal({
    onClose, step, poList, poListLoading, poListError, poSearch, setPoSearch,
    selectedPoNumber, onSelectPo, onBack,
    // PO lifecycle (owner 2026-09-07): hand-close/reopen + the show-closed history toggle.
    onSetPoClosed, showClosedPos, onToggleShowClosedPos,
    poItems, poItemsLoading, poItemsError, poLineChecked, poLineQty, onToggleLine, onToggleAll, onQtyChange, onNextConfirm,
    // Owner 2026-09-01: local (Orlando) split — per-line local qty, the SFP-listing gate, and
    // the create/assign controls.
    poLineLocalQty, sfpStatusByAsin, onLocalQtyChange, localControls,
    // Task 34: step 2 -- "Confirm box configurations"
    poConfirmRows, poConfirmLoading, packagingByAsin, isPackagingComplete, poConfirmSourceLabel,
    poConfirmConfirmed, onConfirmRow, onUnconfirmRow, onEditRow,
    // Task 67: bulk "unit = box" action
    unitBoxBulkCount, onApplyUnitBoxBulk,
    poConfirmSaveDefault, onToggleSaveDefault, onRemoveConfirmRow, onBackToLines,
    allPoConfirmRowsConfirmed, onAddConfirmedToPlan,
    // Task 61: UPS|LTL toggle + shipment-level LTL pallet/freight section
    shipmentMode, onShipmentModeChange, ltlForm, onLtlFieldChange, ltlReady,
    pickupDate, onPickupDateChange,
    // Task 88: per-ASIN UPS / LTL / UPS-LTL designation + the tab filter it drives
    allowedModesByAsin, onAllowedModesChange, poVisibleLines, poHiddenLineCount,
    showAllModes, onShowAllModes,
    // Milestone C phase 0: pallet-denominated entry for confirm-step rows whose packaging carries
    // a boxes-per-pallet from the packing template.
    palletsByAsin, onUpdatePoConfirmRowPallets,
    // Owner, 2026-08-25: "it seems like we should be able to select this dropdown, select a PO,
    // and the data appears on the page (not a modal)". Same body either way -- only the shell
    // changes -- so the two presentations cannot drift apart.
    inline,
}) {
    // Task 88: EVERY line-step count and control below works off the lines the current tab shows.
    // Hidden lines are hidden, not greyed -- and, structurally, cannot be counted, swept in by
    // select-all, or carried to the confirm step.
    const visibleLines = poVisibleLines || poItems;
    const checkedCount = visibleLines.filter(l => l.asin && poLineChecked[l.asin]).length;
    const confirmedCount = poConfirmRows.filter(r => poConfirmConfirmed[r.asin]).length;

    // Owner addition 2026-08-18: fully-sent lines grey out and sort to the bottom.
    const orderedPoItems = orderPoLines(visibleLines);

    // Select-all state: only lines with an ASIN are selectable, and fully-sent lines are excluded
    // from the "all checked" definition (select-all never checks them -- manual click only).
    // Task 80: a line with no New-condition SKU is not selectable at all — "select all" must not
    // sweep it in, and its own checkbox is disabled below.
    // Task 85: spoken-for lines (remaining 0) are excluded from "all checked" too.
    // Task 88: ...and a line the current tab hides isn't even in `visibleLines`.
    const selectableLines = visibleLines.filter(l =>
        l.asin && !l.blocked && !isPoLineFullySent(l) && !isPoLineSpokenFor(l));
    const allLinesChecked = selectableLines.length > 0 && selectableLines.every(l => poLineChecked[l.asin]);
    const someLinesChecked = visibleLines.some(l => l.asin && poLineChecked[l.asin]);
    const hiddenMessage = hiddenLinesMessage(poHiddenLineCount, shipmentMode);

    return (
        // Task 69 owner addition ("the modal should be large enough to fit the content, no
        // scrolling the content inside the modal should be needed"): the panel grows to content
        // height and the OVERLAY scrolls browser-natively when it exceeds the viewport
        // (items-start + overflow-y-auto on the fixed overlay, my-8 on the panel) -- no inner
        // scroll region, and the table steps are wide enough to fit without cramping.
        // Task 85 owner addition ("the modal should be large enough to accommodate the data
        // without scrolling"): the lines step carries two more columns (Assigned, Remaining) on
        // top of an already 11-column table, so it grows to 95vw -- the widest a centered panel
        // can be while still reading as a modal -- and the confirm step to 90vw. The table keeps
        // its own overflow-x-auto wrapper as a safety net for genuinely narrow screens ONLY; at
        // desktop widths nothing scrolls sideways, and the page never does.
        <Shell inline={inline} step={step}>
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {step === 'confirm'
                            ? 'Confirm box configurations'
                            : (selectedPoNumber ? `PO ${selectedPoNumber} — open lines` : 'Load from purchase order')}
                    </h3>
                    <button onClick={onClose}
                            title={inline ? 'Close this section' : 'Close'}
                            className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-5 py-4">
                    {step === 'confirm' ? (
                        <>
                            <p className="text-xs text-gray-500 mb-3">
                                Confirm the box configuration for each line before adding it to the plan — the
                                shipment planner uses exactly what's confirmed here, for this shipment.
                            </p>
                            {poConfirmLoading && <div className="text-sm text-gray-400">Loading saved packaging…</div>}
                            {/* Task 67 (owner: "most of these are oversized products and not
                                cartons - each unit is the 'box'"): one click fills + confirms
                                every unconfigured row that has an Amazon unit-box suggestion.
                                Rows stay individually editable/un-confirmable afterwards and the
                                footer gate is unchanged. */}
                            {!poConfirmLoading && unitBoxBulkCount > 0 && (
                                <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2">
                                    <p className="text-xs text-blue-900">
                                        {unitBoxBulkCount} unconfigured line{unitBoxBulkCount !== 1 ? 's' : ''} can ship
                                        each unit as its own box using Amazon package dims.
                                        {' '}
                                        {/* 2026-08-21: says what the button does now. It fills the fields and
                                            stops -- confirming each line is still a separate, deliberate act,
                                            because "Amazon's catalog says so" is not the same as "someone
                                            looked at the carton". */}
                                        <span className="text-blue-800/80">
                                            {/* Owner: "for individual units, each unit is a carton" --
                                                for these products that IS the shipping configuration,
                                                so this reads as a correct answer to confirm, not a
                                                shortcut to escape. */}
                                            If each unit ships in its own box that is the right
                                            configuration — this fills it in, you still confirm each line.
                                        </span>
                                    </p>
                                    <button
                                        onClick={onApplyUnitBoxBulk}
                                        className="flex-shrink-0 px-2.5 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                                    >
                                        Fill &apos;unit = box&apos; on all unconfigured ({unitBoxBulkCount})
                                    </button>
                                </div>
                            )}
                            {!poConfirmLoading && (
                                <div className="space-y-2">
                                    {poConfirmRows.map(rowItem => {
                                        const pkg = packagingByAsin[rowItem.asin];
                                        const complete = isPackagingComplete(pkg);
                                        const confirmed = !!poConfirmConfirmed[rowItem.asin];
                                        const source = poConfirmSourceLabel(rowItem.asin);
                                        // Task 41: the computed-math line ("5 × 5 lb + 4 lb box = 29 lb per box")
                                        // for two-tier configs; legacy configs keep the bare summary.
                                        const mathLine = packagingMathLine(pkg);
                                        // Task 67: 1/box means qty boxes at the unit weight each.
                                        const rowTotals = pkg?.computed
                                            ? computeShipmentTotals(rowItem.quantity, pkg.unitsPerBox, pkg.unitWeightLbs, pkg.tareWeightLbs)
                                            : (pkg?.style === 'UNIT' && rowItem.quantity > 0 && pkg.boxWeightLbs > 0
                                                ? { boxCount: rowItem.quantity, totalLbs: Math.round(rowItem.quantity * pkg.boxWeightLbs * 100) / 100 }
                                                : null);
                                        return (
                                            <div
                                                key={rowItem.asin}
                                                className={`rounded-lg border px-3 py-2.5 ${
                                                    confirmed ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-mono text-xs text-gray-900">{rowItem.asin}</span>
                                                            <span className="text-sm text-gray-700 truncate">{rowItem.name}</span>
                                                            <span className="text-xs text-gray-400 flex-shrink-0">qty {rowItem.quantity}</span>
                                                            {/* Milestone C phase 0: same pallet-denominated
                                                                affordance as the direct ASIN-mode row, for a
                                                                line whose packing template carries a
                                                                boxes-per-pallet. */}
                                                            {shipmentMode === 'LTL' && pkg?.boxesPerPallet > 0 && (
                                                                <span className="flex items-center gap-1 flex-shrink-0">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="1"
                                                                        placeholder="pallets"
                                                                        value={palletsByAsin?.[rowItem.asin] ?? ''}
                                                                        onChange={e => onUpdatePoConfirmRowPallets(rowItem.asin, e.target.value)}
                                                                        aria-label={`Pallets for ${rowItem.asin}`}
                                                                        className="w-16 rounded border-gray-300 text-xs text-right"
                                                                    />
                                                                    {palletDerivationLabel(palletsByAsin?.[rowItem.asin], pkg) && (
                                                                        <span
                                                                            data-testid={`pallet-qty-derivation-${rowItem.asin}`}
                                                                            className="text-[11px] text-gray-500 whitespace-nowrap"
                                                                        >
                                                                            {palletDerivationLabel(palletsByAsin?.[rowItem.asin], pkg)}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )}
                                                            {/* Task 80: the condition + FNSKU this line will ship on,
                                                                visible in the confirm step — before boxes are
                                                                configured and long before labels print. */}
                                                            <ConditionBadge
                                                                conditionLabel={rowItem.conditionLabel}
                                                                fnsku={rowItem.fnsku}
                                                                newCondition={rowItem.newCondition}
                                                                className="flex-shrink-0"
                                                            />
                                                            {/* Task 88: the designation the line
                                                                carried through the filter, restated
                                                                where the operator confirms it.
                                                                Read-only here -- it is changed on
                                                                the lines step. */}
                                                            <ModeChip
                                                                value={allowedModesByAsin?.[rowItem.asin]}
                                                                className="flex-shrink-0"
                                                            />
                                                        </div>
                                                        <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${
                                                                source.tone === 'amber'
                                                                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                                                    : source.tone === 'blue'
                                                                        ? 'bg-blue-100 text-blue-800 border border-blue-300'
                                                                        : 'bg-gray-100 text-gray-600 border border-gray-300'}`}
                                                            >
                                                                {source.text}
                                                            </span>
                                                            {complete ? (
                                                                <span className="text-gray-600">
                                                                    {/* Task 41: show the computed math, not a bare weight, when derivable. */}
                                                                    {mathLine ? (
                                                                        <>
                                                                            {mathLine}
                                                                            {' '}· {pkg.boxName ? `${pkg.boxName} ` : ''}{pkg.boxLengthIn}×{pkg.boxWidthIn}×{pkg.boxHeightIn} in
                                                                            {rowTotals?.totalLbs != null && <> · {rowTotals.boxCount} box{rowTotals.boxCount !== 1 ? 'es' : ''}, {fmtLbsShort(rowTotals.totalLbs)} lb total</>}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {packagingSummaryLine(pkg)}
                                                                            {/* Task 67: unit-as-box rows show totals too */}
                                                                            {pkg?.style === 'UNIT' && rowTotals?.totalLbs != null
                                                                                && <> · {rowTotals.boxCount} box{rowTotals.boxCount !== 1 ? 'es' : ''}, {fmtLbsShort(rowTotals.totalLbs)} lb total</>}
                                                                        </>
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <span className="text-amber-700">no packaging entered yet</span>
                                                            )}
                                                        </div>
                                                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                                                            <button
                                                                onClick={() => onEditRow(rowItem.asin, rowItem.name, rowItem.quantity)}
                                                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                                            >
                                                                {complete ? 'Edit' : 'Enter values'}
                                                            </button>
                                                            {/* Task 67: unit-as-box values are save-eligible too (UNIT-style POST). */}
                                                            <label
                                                                className={`text-xs flex items-center gap-1 ${(pkg?.computed || pkg?.style === 'UNIT') ? 'text-gray-500' : 'text-gray-300'}`}
                                                                title={(pkg?.computed || pkg?.style === 'UNIT') ? '' : 'Defaults now need a unit weight + catalog box — use Edit to enter them first'}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={!(pkg?.computed || pkg?.style === 'UNIT')}
                                                                    checked={!!poConfirmSaveDefault[rowItem.asin]}
                                                                    onChange={() => onToggleSaveDefault(rowItem.asin)}
                                                                />
                                                                also save as default
                                                            </label>
                                                            <button
                                                                onClick={() => onRemoveConfirmRow(rowItem.asin)}
                                                                className="text-xs text-red-500 hover:text-red-700 ml-auto"
                                                            >
                                                                remove
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => (confirmed ? onUnconfirmRow(rowItem.asin) : onConfirmRow(rowItem.asin))}
                                                        disabled={!complete}
                                                        title={complete ? '' : 'Enter box values before confirming'}
                                                        className={`flex-shrink-0 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                                                            confirmed
                                                                ? 'bg-green-600 border-green-600 text-white hover:bg-green-700'
                                                                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}
                                                            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white`}
                                                    >
                                                        {confirmed ? '✓ Confirmed' : 'Confirm'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {poConfirmRows.length === 0 && (
                                        <div className="text-sm text-gray-400 italic">
                                            No lines left to confirm — go back and select at least one.
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Task 65 (owner: "we dont know the height or weight until amazon
                                tells us the sku and qty it wants on each shipment"): the LTL
                                section collects ONLY what's knowable upfront -- footprint + tare
                                (warehouse facts, sane defaults), optional freight class (blank =
                                auto density), optional declared-value edit. Pallet count, height,
                                and weight are computed per shipment AFTER Amazon returns the
                                split, surfaced on each option card with an "adjust" affordance. */}
                            {!poConfirmLoading && shipmentMode === 'LTL' && poConfirmRows.length > 0 && (
                                <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-1">Pallets &amp; freight (whole shipment)</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        {/* Copy states what the code actually does. The owner asked for 72 in;
                                            that change was built and reverted unshipped because 16 of 16
                                            pallets over 50 in have never been quoted by a partnered carrier
                                            (see MAX_PALLET_HEIGHT_ABSOLUTE_IN). "Including the pallet" is
                                            explicit either way -- the old "stacked to 72 in" wording was
                                            ambiguous about the deck, which is how the number drifted. */}
                                        Pallet count, height, and weight are computed automatically once Amazon returns the
                                        shipment split (built to 45 in including the pallet — about 39 in of cartons on a
                                        6 in deck) and shown on each option card, where you can adjust and re-quote.
                                        Carton configs above are still required.
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                        <label className="block">
                                            <span className="text-gray-600">Pallet L × W (in)</span>
                                            <div className="mt-1 flex gap-1">
                                                <input type="number" step="0.1" value={ltlForm.palletLengthIn}
                                                    onChange={e => onLtlFieldChange('palletLengthIn', e.target.value)}
                                                    className="block w-full rounded border-gray-300 text-sm" />
                                                <input type="number" step="0.1" value={ltlForm.palletWidthIn}
                                                    onChange={e => onLtlFieldChange('palletWidthIn', e.target.value)}
                                                    className="block w-full rounded border-gray-300 text-sm" />
                                            </div>
                                        </label>
                                        {/* Task 70b: Amazon quotes partnered-LTL carrier
                                            appointments against this date. */}
                                        <label className="block">
                                            <span className="text-gray-600">
                                                Ready for pickup <span className="text-red-600" title="Required for LTL">*</span>
                                            </span>
                                            <input type="date" value={pickupDate || ''}
                                                min={minPickupDate()} max={maxPickupDate()}
                                                onChange={e => onPickupDateChange && onPickupDateChange(e.target.value)}
                                                aria-label="Ready for pickup date (PO import)"
                                                title="The day the pallets are staged and a freight carrier can collect them"
                                                className="mt-1 block w-full rounded border-gray-300 text-sm" />
                                        </label>
                                        <label className="block">
                                            <span className="text-gray-600">Pallet tare (lb)</span>
                                            <input type="number" step="0.1" value={ltlForm.palletTareLbs}
                                                onChange={e => onLtlFieldChange('palletTareLbs', e.target.value)}
                                                title="Empty-pallet weight added per pallet to the computed box weight"
                                                className="mt-1 block w-full rounded border-gray-300 text-sm" />
                                        </label>
                                        <label className="block">
                                            <span className="text-gray-600">Freight class</span>
                                            <select
                                                value={ltlForm.freightClass}
                                                onChange={e => onLtlFieldChange('freightClass', e.target.value)}
                                                title="Leave on auto to compute the NMFC class from each shipment's pallet density (per-ASIN overrides still win)"
                                                className="mt-1 block w-full rounded border-gray-300 text-sm"
                                            >
                                                <option value="">auto (density)</option>
                                                {FREIGHT_CLASS_OPTIONS.map(fc => (
                                                    <option key={fc} value={fc}>{fc.replace('FC_', 'Class ').replace('_5', '.5')}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="block">
                                            <span className="text-gray-600">Declared value ($)</span>
                                            <input type="number" step="0.01" value={ltlForm.declaredValue}
                                                onChange={e => onLtlFieldChange('declaredValue', e.target.value)}
                                                placeholder="auto from item costs"
                                                title="Auto-summed from units × cost per shipment; type to override the plan total"
                                                className="mt-1 block w-full rounded border-gray-300 text-sm" />
                                        </label>
                                        {/* Milestone C phase 0: the operator's up-front pallet count,
                                            declared BEFORE Amazon has split anything. Prefilled from
                                            the per-row pallet entries above when every row is
                                            pallet-denominated; blank stays blank -- a declaration
                                            never made must never read as zero. Reconciled against the
                                            built total in phase 2 (task C4). */}
                                        <label className="block">
                                            <span className="text-gray-600">Declared pallet total</span>
                                            <input type="number" min="0" step="1" value={ltlForm.declaredPalletCount ?? ''}
                                                onChange={e => onLtlFieldChange('declaredPalletCount', e.target.value)}
                                                placeholder="not declared"
                                                title="How many pallets you expect to build -- reconciled against the actual build once it's approved"
                                                className="mt-1 block w-full rounded border-gray-300 text-sm" />
                                        </label>
                                        <label className="flex items-end gap-1.5 pb-1.5 text-gray-600">
                                            <input type="checkbox" checked={ltlForm.stackable !== false}
                                                onChange={e => onLtlFieldChange('stackable', e.target.checked)} />
                                            stackable
                                        </label>
                                        <label className="flex items-end gap-1.5 pb-1.5 text-gray-600"
                                            title="Writes pallet footprint/tare/stackability/freight class to each confirmed ASIN's packaging defaults">
                                            <input type="checkbox" checked={!!ltlForm.savePalletDefault}
                                                onChange={e => onLtlFieldChange('savePalletDefault', e.target.checked)} />
                                            save pallet settings as default
                                        </label>
                                    </div>
                                    {!ltlReady && (
                                        <p className="mt-2 text-xs text-amber-700">
                                            LTL needs a positive pallet footprint and tare weight before adding to the plan.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    ) : !selectedPoNumber ? (
                        <>
                            <input
                                type="text"
                                value={poSearch}
                                onChange={e => setPoSearch(e.target.value)}
                                placeholder="Search PO number..."
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm mb-3"
                            />
                            {poListLoading && <div className="text-sm text-gray-400">Loading POs...</div>}
                            {poListError && <div className="text-sm text-red-600">{poListError}</div>}
                            {!poListLoading && !poListError && poList.length === 0 && (
                                <div className="text-sm text-gray-400 italic">No open POs.</div>
                            )}
                            {!poListLoading && poList.length > 0 && (
                                <div className="border rounded-md divide-y">
                                    {/* PO lifecycle: each row is a DIV holding two sibling buttons
                                        (nested buttons are invalid HTML) so the close/reopen
                                        control can never open the PO. */}
                                    {poList.map(po => (
                                        <div key={po.poNumber} className="flex items-center hover:bg-gray-50">
                                        <button
                                            onClick={() => onSelectPo(po.poNumber)}
                                            className="flex-1 min-w-0 text-left px-3 py-2 text-sm flex items-center justify-between"
                                        >
                                            <span className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900">PO {po.poNumber}</span>
                                                <span className="text-gray-500">{po.poDate}</span>
                                                {po.closed && (
                                                    <span className="px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 text-[10px] font-medium">
                                                        Closed
                                                    </span>
                                                )}
                                                <SfpReadyBadge sfpReady={po.sfpReady} sfpMissingAsins={po.sfpMissingAsins} />
                                                <UnrackedSfpBadge units={po.unrackedSfpUnits} />
                                                {/* Owner 2026-09-08: fully-shipped POs list too, badged
                                                    from Amazon-side sent truth. Rows arrive server-sorted
                                                    unshipped-first; render in API order. */}
                                                {po.shippedStatus === 'SHIPPED' && (
                                                    <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-medium">
                                                        Shipped
                                                    </span>
                                                )}
                                                {po.shippedStatus === 'PARTIAL' && (
                                                    <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium">
                                                        Partially shipped{po.shippedPercent != null ? ` · ${po.shippedPercent}%` : ''}
                                                    </span>
                                                )}
                                            </span>
                                            {/* "to ship" is what decides this PO is here at all --
                                                units not yet assigned to any shipment. "open" is
                                                what the SUPPLIER still owes, shown only when it is
                                                non-zero, because a fully received PO has none and
                                                still has everything left to send. */}
                                            <span className="text-gray-500 text-xs">
                                                {po.lineCount} line{po.lineCount !== 1 ? 's' : ''}
                                                {' · '}
                                                <span className="font-medium text-gray-700">
                                                    {po.unshippedUnits} to ship
                                                </span>
                                                {po.totalOpenUnits > 0 && ` · ${po.totalOpenUnits} not yet received`}
                                            </span>
                                        </button>
                                        {onSetPoClosed && (
                                            <button
                                                onClick={() => onSetPoClosed(po, !po.closed)}
                                                aria-label={`${po.closed ? 'Reopen' : 'Close'} PO ${po.poNumber}`}
                                                title={po.closed
                                                    ? 'Reopen this PO — its open units count as on-order again'
                                                    : 'Close this PO — removes its remaining units from on-order everywhere; stays visible in history'}
                                                className="px-2 py-1 mr-2 text-[11px] rounded border border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
                                            >
                                                {po.closed ? 'Reopen' : 'Close'}
                                            </button>
                                        )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!poListLoading && onToggleShowClosedPos && (
                                <button
                                    onClick={onToggleShowClosedPos}
                                    className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                                >
                                    {showClosedPos ? 'Hide closed POs' : 'Show closed POs'}
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800 mb-3">
                                &larr; Back to PO list
                            </button>
                            {/* Task 61 (owner: "in the import po modal, create a toggle for LTL or
                                UPS shipment"): prominent mode toggle. Defaults from the selected
                                lines' allowed_modes until manually switched. */}
                            <div className="flex flex-wrap items-center gap-2 mb-3 p-2.5 rounded-lg border border-indigo-200 bg-indigo-50/60">
                                <span className="text-sm font-semibold text-gray-800">Shipment type:</span>
                                <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm bg-white" role="group">
                                    {[['UPS', 'UPS small parcel'], ['LTL', 'LTL freight']].map(([value, label], idx) => (
                                        <button
                                            key={value}
                                            onClick={() => onShipmentModeChange(value)}
                                            className={`px-3 py-1.5 ${idx > 0 ? 'border-l border-gray-300' : ''} ${shipmentMode === value
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <span className="text-xs text-gray-500">
                                    {shipmentMode === 'LTL'
                                        ? 'pallet details are entered at the next step — carton configs are still required (Amazon mandates them for every mode)'
                                        : 'box-based small parcel (default)'}
                                </span>
                            </div>
                            {/* Task 88 (owner: "when the user selects the UPS tab ... the UPS and
                                UPS/LTL product will remain, when LTL tab is selected the LTL and
                                UPS/LTL products will remain"): the filtered-out lines are HIDDEN,
                                never greyed -- but never silently. The count is always stated, with
                                a one-click escape back to the full list. */}
                            {(hiddenMessage || showAllModes) && (
                                <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                                    <p className="text-xs text-amber-900">
                                        {showAllModes
                                            ? `Showing all lines regardless of designation — ${poHiddenLineCount} would be hidden in ${normalizeAllowedModes(shipmentMode)} mode.`
                                            : `${hiddenMessage}. Change a line's designation below to bring it back.`}
                                    </p>
                                    <button
                                        onClick={() => onShowAllModes(!showAllModes)}
                                        className="flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-medium border border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                                    >
                                        {showAllModes
                                            ? `Filter to ${normalizeAllowedModes(shipmentMode)} only`
                                            : 'Show all'}
                                    </button>
                                </div>
                            )}
                            {poItemsLoading && <div className="text-sm text-gray-400">Loading lines...</div>}
                            {poItemsError && <div className="text-sm text-red-600">{poItemsError}</div>}
                            {!poItemsLoading && !poItemsError && <UnrackedSfpWarning poItems={poItems} />}
                            {/* Task 107: the roll-up the operator used to have to do by hand. Summed
                                from the SAME per-line fields the rows render, so header and rows
                                cannot disagree. */}
                            {!poItemsLoading && !poItemsError && visibleLines.length > 0 && (() => {
                                const s = summarisePoLines(visibleLines);
                                return (
                                    <div className="mb-3 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-2">
                                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
                                            <span className="text-lg font-semibold text-indigo-900">
                                                {s.toShip} unit{s.toShip !== 1 ? 's' : ''} still to ship
                                            </span>
                                            <span className="text-gray-600">of {s.ordered} ordered</span>
                                            {/* Owner 2026-08-26: sent-in (confirmed) is its own
                                                figure, never lumped with paper commitments. */}
                                            {s.sent > 0 && (
                                                <span className="text-gray-600">
                                                    · <span className="font-medium text-gray-800">{s.sent} already sent in</span>
                                                </span>
                                            )}
                                            {s.inPlans > 0 && (
                                                <span className="text-gray-600">
                                                    · {s.inPlans} on open plans or drafts
                                                </span>
                                            )}
                                            {s.awaitingSupplier > 0 && (
                                                <span className="text-amber-700">
                                                    · {s.awaitingSupplier} not yet received
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 text-xs text-gray-500">
                                            {s.linesToShip} of {s.lines} line{s.lines !== 1 ? 's' : ''} still need shipping
                                            {s.linesAwaiting > 0 && ` · ${s.linesAwaiting} awaiting supplier`}
                                            {s.linesDone > 0 && ` · ${s.linesDone} fully assigned`}
                                            {s.linesBlocked > 0 && ` · ${s.linesBlocked} blocked`}
                                            {s.unknownLines > 0 && ` · ${s.unknownLines} unknown`}
                                            {/* The caveat belongs beside the number, not in a wiki. */}
                                            <span
                                                className="ml-1 cursor-help underline decoration-dotted"
                                                title={'Approximate. Assignment is counted per ASIN across every shipment run in the window, '
                                                    + 'not attributed to this specific PO -- an ASIN that also appears on another PO draws '
                                                    + 'down both, so "still to ship" can read low. Abandoned ACTIVE plans also still count '
                                                    + 'as assigned until they are cancelled.'}
                                            >
                                                why approximate?
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}
                            {!poItemsLoading && !poItemsError && (
                                /* 2026-08-23 (owner): headers stay pinned while the lines scroll.
                                   Mechanism updated 2026-09-01 (owner screenshot: columns escaped
                                   the inline card on wide screens): the overlay era's
                                   xl:overflow-x-visible existed so sticky th could attach to the
                                   OVERLAY's scroll, but the panel is in-flow now (f55e403) and
                                   visible overflow let the ever-wider table spill onto the page.
                                   This wrapper is the scroll region the sticky th attach to:
                                   bounded height, both axes contained, headers still pinned. */
                                <div className="overflow-auto max-h-[70vh]">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2">
                                                <input
                                                    type="checkbox"
                                                    title={allLinesChecked ? 'Deselect all' : 'Select all'}
                                                    disabled={selectableLines.length === 0}
                                                    checked={allLinesChecked}
                                                    ref={el => { if (el) el.indeterminate = someLinesChecked && !allLinesChecked; }}
                                                    onChange={() => onToggleAll(!allLinesChecked)}
                                                />
                                            </th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-left font-medium text-gray-500">Item code</th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-left font-medium text-gray-500">ASIN</th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-left font-medium text-gray-500">Product</th>
                                            {/* Task 80: PO lines are always New — this column proves it
                                                line by line, and names the ones that have no New SKU. */}
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-left font-medium text-gray-500">Condition / FNSKU</th>
                                            {/* Task 88: the designation lives on the ASIN, so it is
                                                set here once and remembered for every future PO. */}
                                            <th
                                                className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-left font-medium text-gray-500"
                                                title="How this product ships: UPS small parcel, LTL freight, or either. UPS/LTL products appear under both tabs; the designation is saved against the ASIN as soon as you pick it."
                                            >
                                                Ship via
                                            </th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500">Ordered</th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500">Received</th>
                                            <th
                                                className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500"
                                                title="Units of this ASIN on Amazon inbound shipments (UPS and LTL, including ones confirmed directly in Seller Central) plus confirmed planner runs Amazon hasn't synced yet, since the PO date — all shipments of this ASIN, not just ones from this PO"
                                            >
                                                Sent to FBA
                                            </th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500">Open</th>
                                            {/* Task 85: what is already committed elsewhere, and what that leaves. */}
                                            <th
                                                className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500"
                                                title="Units already committed: sent to FBA + open (unconfirmed) shipment plans + saved drafts. Repeated draws of the same plan count once."
                                            >
                                                Assigned
                                            </th>
                                            <th
                                                className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500"
                                                title="Ordered − assigned: units of this line that no shipment has claimed yet. The ship-qty box defaults to this."
                                            >
                                                Remaining
                                            </th>
                                            <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500">Ship qty</th>
                                            {/* Owner 2026-09-01: split units off to LOCAL Orlando
                                                stock, sold as NEW via SFP for 1-2 day regional
                                                delivery. Gated on the SFP listing existing. */}
                                            <th
                                                className="sticky top-0 z-10 bg-gray-50 px-2 py-2 text-right font-medium text-gray-500"
                                                title="Units to keep in Orlando as local seller-fulfilled (SFP) stock — sold as New with 1–2 day regional delivery. They go live on Amazon only when a warehouse space is assigned."
                                            >
                                                Local (Orlando)
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {orderedPoItems.map((line, idx) => {
                                            const noAsin = !line.asin;
                                            // Owner addition 2026-08-18: fully-sent lines (all
                                            // ordered units already sent to FBA) grey out + sort
                                            // to the bottom (see orderPoLines) but stay manually
                                            // selectable; checked rows highlight.
                                            const fullySent = isPoLineFullySent(line);
                                            // Task 85: nothing left over -- greyed like fully-sent
                                            // (they're spoken for), still manually selectable.
                                            const spokenFor = isPoLineSpokenFor(line);
                                            const dimmed = fullySent || spokenFor;
                                            const remaining = poLineRemaining(line);
                                            const rowChecked = !noAsin && !!poLineChecked[line.asin];
                                            const rowClass = [
                                                noAsin || dimmed ? 'text-gray-400' : '',
                                                dimmed && !rowChecked ? 'bg-gray-50' : '',
                                                rowChecked ? 'bg-indigo-50' : '',
                                            ].filter(Boolean).join(' ');
                                            return (
                                                <tr
                                                    key={line.asin || `noasin-${idx}`}
                                                    className={rowClass}
                                                    title={fullySent
                                                        ? `all ${line.ordered} ordered units already sent to FBA — unchecked by default; select manually to resend`
                                                        : (spokenFor
                                                            ? `all ${line.ordered} ordered units are already assigned to shipments — unchecked by default; select manually to ship anyway`
                                                            : (poLineShipState(line) === 'AWAITING_SUPPLIER'
                                                                ? `${poLineRemaining(line)} unit(s) still to ship, but none have arrived from the supplier yet`
                                                                : ''))}
                                                >
                                                    {/* Task 107: a line with nothing to ship because the
                                                        supplier owes us units is the OPPOSITE of one with
                                                        nothing to ship because it is all committed. They
                                                        rendered identically before this. */}
                                                    <td className={`px-2 py-2 border-l-4 ${
                                                        rowChecked ? 'border-indigo-500'
                                                        : poLineShipState(line) === 'TO_SHIP' ? 'border-emerald-400'
                                                        : poLineShipState(line) === 'AWAITING_SUPPLIER' ? 'border-amber-400'
                                                        : 'border-transparent'}`}>
                                                        <input
                                                            type="checkbox"
                                                            disabled={noAsin || line.blocked}
                                                            title={line.blocked ? line.blockedReason : undefined}
                                                            checked={rowChecked}
                                                            onChange={() => onToggleLine(line.asin)}
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">
                                                        {line.itemCode || <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">
                                                        {noAsin ? 'no ASIN mapping' : line.asin}
                                                        {/* Owner, 2026-08-25: from a PO line you should be able to
                                                            reach the plans Amazon has already drawn for that ASIN --
                                                            the combination workspace -- rather than re-analyzing. */}
                                                        {!noAsin && (
                                                            <Link
                                                                to={`/shipment-plans?asin=${line.asin}`
                                                                    + (selectedPoNumber ? `&fromPo=${encodeURIComponent(selectedPoNumber)}` : '')}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                title="View proposed shipment plans for this ASIN"
                                                                className="block font-sans text-[11px] text-indigo-600 hover:text-indigo-800"
                                                            >
                                                                view proposed plans →
                                                            </Link>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-2 max-w-xs truncate">{line.description}</td>
                                                    <td className="px-2 py-2 whitespace-nowrap">
                                                        {noAsin ? <span className="text-xs text-gray-300">—</span> : (
                                                            <ConditionBadge
                                                                conditionLabel={line.conditionLabel}
                                                                fnsku={line.fnsku}
                                                                newCondition={line.newCondition}
                                                                blockedReason={line.blocked ? line.blockedReason : null}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-2 whitespace-nowrap">
                                                        {noAsin ? <span className="text-xs text-gray-300">—</span> : (
                                                            <ModeSelect
                                                                asin={line.asin}
                                                                value={allowedModesByAsin?.[line.asin]}
                                                                onChange={onAllowedModesChange}
                                                            />
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-2 text-right">{line.ordered}</td>
                                                    <td className="px-2 py-2 text-right">{line.received}</td>
                                                    <td
                                                        className="px-2 py-2 text-right"
                                                        title="Units of this ASIN on Amazon inbound shipments (UPS + LTL + Seller Central) and confirmed planner runs since the PO date"
                                                    >
                                                        {noAsin ? '' : (line.sentToFba ?? 0)}
                                                    </td>
                                                    <td className="px-2 py-2 text-right">{line.openQty}</td>
                                                    <td
                                                        className="px-2 py-2 text-right whitespace-nowrap"
                                                        title={noAsin ? '' : poLineAssignedTooltip(line)}
                                                    >
                                                        {noAsin ? '' : poLineAssigned(line)}
                                                    </td>
                                                    <td className={`px-2 py-2 text-right whitespace-nowrap ${remaining > 0 ? 'font-medium text-gray-900' : 'text-gray-400'}`}>
                                                        {noAsin || remaining == null ? <span className="text-gray-300">—</span> : remaining}
                                                        {/* Task 92: the most recent shipment-planner run for this ASIN, stacked
                                                            under Remaining rather than a new column so the modal never grows a
                                                            horizontal scrollbar. New tab -- the operator keeps this modal and
                                                            their in-progress selections open. */}
                                                        {!noAsin && (poLineLatestRunHref(line) ? (
                                                            <a
                                                                href={poLineLatestRunHref(line)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={`Open run ${line.latestRunId} (${line.latestRunStatus || 'unknown status'}) in a new tab`}
                                                                className={`block text-[11px] font-normal normal-case whitespace-nowrap hover:underline ${poLineLatestRunColorClass(line.latestRunStatus)}`}
                                                            >
                                                                {poLineLatestRunLabel(line)}
                                                            </a>
                                                        ) : (
                                                            <span className="block text-[11px] font-normal normal-case text-gray-300">—</span>
                                                        ))}
                                                    </td>
                                                    <td className="px-2 py-2 text-right">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            disabled={noAsin}
                                                            value={noAsin ? '' : (poLineQty[line.asin] ?? poLineDefaultShipQty(line))}
                                                            onChange={e => onQtyChange(line.asin, e.target.value)}
                                                            className="w-20 rounded border-gray-300 text-sm text-right disabled:bg-gray-100"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-2 text-right whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            disabled={noAsin || line.blocked}
                                                            aria-label={`Local (Orlando) qty for ${line.asin || 'line'}`}
                                                            value={noAsin ? '' : (poLineLocalQty?.[line.asin] ?? 0)}
                                                            onChange={e => onLocalQtyChange(line.asin, e.target.value, line)}
                                                            className="w-16 rounded border-gray-300 text-sm text-right disabled:bg-gray-100"
                                                        />
                                                        {/* The SFP-listing gate chip, only once the
                                                            line is actually marked for local. */}
                                                        {!noAsin && (poLineLocalQty?.[line.asin] || 0) > 0 && (
                                                            sfpStatusByAsin?.[line.asin]?.sfpSku
                                                                ? <span className="ml-1 text-[10px] font-mono text-emerald-700"
                                                                        title={`Seller-fulfilled listing ${sfpStatusByAsin[line.asin].sfpSku} exists`}>
                                                                    SFP ✓
                                                                  </span>
                                                                : <span className="ml-1 text-[10px] text-amber-700"
                                                                        title="No SFP-prefixed seller-fulfilled listing exists for this ASIN — create it (priced at MAP) before assigning">
                                                                    no SFP listing
                                                                  </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {/* Owner 2026-09-01: the local-assignment controls. Create fills
                                    the missing SFP listings at MAP; Assign is blocked until every
                                    marked line has one (ask-don't-guess, applied to listings). */}
                                {localControls && localControls.units > 0 && (
                                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2">
                                        <span className="text-sm font-medium text-teal-900">
                                            {localControls.units} unit{localControls.units !== 1 ? 's' : ''} marked for local (Orlando)
                                        </span>
                                        {localControls.missingCount > 0 && (
                                            <button
                                                onClick={localControls.onCreateListings}
                                                disabled={localControls.busy}
                                                className="px-3 py-1.5 text-xs rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                                                title="Creates the missing SFP seller-fulfilled listings, priced at MAP — an ASIN without MAP on file is refused by name"
                                            >
                                                Create SFP listings ({localControls.missingCount})
                                            </button>
                                        )}
                                        <button
                                            onClick={localControls.onAssign}
                                            disabled={localControls.busy || localControls.missingCount > 0}
                                            className="px-3 py-1.5 text-xs rounded-md bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={localControls.missingCount > 0
                                                ? 'Every marked line needs its SFP listing first'
                                                : 'Creates the expected local units — they go live on Amazon when a warehouse space is assigned on the Manage SFP Inventory page'}
                                        >
                                            {localControls.busy ? 'Working…'
                                                : `Assign ${localControls.units} unit${localControls.units !== 1 ? 's' : ''} to local (Orlando)`}
                                        </button>
                                    </div>
                                )}
                                {/* Task 88: mode filtering can empty the table entirely -- say so
                                    rather than showing a blank grid. */}
                                {orderedPoItems.length === 0 && poItems.length > 0 && (
                                    <div className="py-6 text-center text-sm text-gray-500">
                                        No line on this PO is designated for {normalizeAllowedModes(shipmentMode)}.
                                        Switch tabs, use “Show all”, or change a line’s designation.
                                    </div>
                                )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {step === 'confirm' && (
                    <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50 rounded-b-lg">
                        <button onClick={onBackToLines} className="text-sm text-indigo-600 hover:text-indigo-800">
                            &larr; Back
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">
                                {confirmedCount}/{poConfirmRows.length} confirmed
                            </span>
                            <button onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                                Cancel
                            </button>
                            <button
                                onClick={onAddConfirmedToPlan}
                                disabled={!allPoConfirmRowsConfirmed || !ltlReady}
                                title={!allPoConfirmRowsConfirmed
                                    ? 'Every line must be confirmed first'
                                    : !ltlReady ? 'Complete the pallet section (height, tare, count) first' : ''}
                                className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add {poConfirmRows.length} confirmed to plan{shipmentMode === 'LTL' ? ' (LTL)' : ''}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'lines' && (
                    <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50 rounded-b-lg">
                        <span className="text-sm text-gray-500">{checkedCount} line{checkedCount !== 1 ? 's' : ''} selected</span>
                        <div className="flex gap-2">
                            <button onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                                Cancel
                            </button>
                            <button
                                onClick={onNextConfirm}
                                disabled={checkedCount === 0}
                                className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                                Next: confirm box sizes
                            </button>
                        </div>
                    </div>
                )}
        </Shell>
    );
}

/**
 * The PO panel's shell. Inline it is an in-flow section of the planner page; overlaid it is the
 * old modal. Only the wrapper differs — the body is identical, so the two cannot drift.
 */
function Shell({ inline, step, children }) {
    if (inline) {
        // In flow: no backdrop, no z-index, no viewport cap. The page scrolls, as the owner asked.
        // 2026-09-01 (round two): the translate/95vw breakout is gone — the PAGE container is
        // wide now (one width for every section), so the panel simply fills its parent.
        return <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">{children}</div>;
    }
    // Task 69/85: panel grows to content height and the OVERLAY scrolls browser-natively when it
    // exceeds the viewport -- no inner scroll region.
    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className={`bg-white rounded-lg shadow-xl w-full my-8 ${step === 'lines' ? 'max-w-[95vw]' : (step === 'confirm' ? 'max-w-[90vw]' : 'max-w-2xl')}`}>
                {children}
            </div>
        </div>
    );
}

// === Task 12: Paste list + total modal ===
function AllocateTotalModal({
    onClose, asinText, setAsinText, totalQtyInput, setTotalQtyInput, onRun, loading, error,
    preview, onRowQtyChange, onBack, onAddToPlan,
}) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">Paste ASIN list + total qty</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {!preview ? (
                        <>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ASINs (whitespace, comma, or newline separated)</label>
                            <textarea
                                value={asinText}
                                onChange={e => setAsinText(e.target.value)}
                                rows={6}
                                placeholder={'B00EXAMPLE1\nB00EXAMPLE2, B00EXAMPLE3'}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm font-mono mb-3"
                            />
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total quantity</label>
                            <input
                                type="number"
                                min="1"
                                value={totalQtyInput}
                                onChange={e => setTotalQtyInput(e.target.value)}
                                className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm mb-3"
                            />
                            {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
                            <button
                                onClick={onRun}
                                disabled={loading}
                                className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {loading ? 'Splitting...' : 'Preview split'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800 mb-3">
                                &larr; Back
                            </button>
                            {error && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">{error}</div>}
                            {preview.note && (
                                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">{preview.note}</div>
                            )}
                            <table className="min-w-full divide-y divide-gray-200 text-sm mb-2">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-2 py-2 text-left font-medium text-gray-500">ASIN</th>
                                        <th className="px-2 py-2 text-right font-medium text-gray-500">Mkt units/mo</th>
                                        <th className="px-2 py-2 text-right font-medium text-gray-500">Box size</th>
                                        <th className="px-2 py-2 text-right font-medium text-gray-500">Qty</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {preview.rows.map(row => (
                                        <tr key={row.asin}>
                                            <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">{row.asin}</td>
                                            <td className="px-2 py-2 text-right">{row.mktUnitsMo ?? '—'}</td>
                                            <td className="px-2 py-2 text-right">{row.unitsPerBox ?? '—'}</td>
                                            <td className="px-2 py-2 text-right">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={row.qty}
                                                    onChange={e => onRowQtyChange(row.asin, e.target.value)}
                                                    className="w-20 rounded border-gray-300 text-sm text-right"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="text-sm text-gray-600">
                                Total allocated: <strong>{preview.rows.reduce((sum, r) => sum + (r.qty || 0), 0)}</strong>
                            </div>
                        </>
                    )}
                </div>

                {preview && (
                    <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-lg">
                        <button onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button
                            onClick={onAddToPlan}
                            className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                            Add to plan
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// === FC Distribution Map Component ===
/** Exported for tests: which item list feeds the map's current-distribution markers. */
// Owner, 2026-09-01 (landing redesign): "users should have the ability to access the draft, but
// it should not be loaded by default. Users can pick from a list of drafts". The picker's rows:
// split-set members collapse to ONE resumable entry (resume opens the lowest-index member),
// newest activity first, and the draft this browser last worked on is flagged — the badge that
// replaces the old silent auto-restore.
export function groupDraftsForLanding(drafts, lastOpenedId) {
    const list = (drafts || []).filter(d => d && d.id);
    const singles = [];
    const groups = new Map();
    for (const d of list) {
        const gid = d.splitGroup && d.splitGroup.id;
        if (!gid) { singles.push(d); continue; }
        if (!groups.has(gid)) groups.set(gid, []);
        groups.get(gid).push(d);
    }
    const rows = singles.map(d => ({
        kind: 'single', id: d.id, name: d.name, units: d.units, itemCount: d.itemCount,
        mode: d.mode, source: d.source, updatedAt: d.updatedAt, savedAt: d.savedAt,
        // Owner, 2026-09-01: manufacturer SKU + product name ride the row.
        itemCode: d.itemCode || null, productName: d.productName || null,
        lastOpened: !!lastOpenedId && d.id === lastOpenedId,
    }));
    for (const members of groups.values()) {
        members.sort((a, b) => (a.splitGroup.index || 0) - (b.splitGroup.index || 0));
        const first = members[0];
        rows.push({
            kind: 'split',
            id: first.id,
            memberIds: members.map(m => m.id),
            memberCount: members.length,
            total: first.splitGroup.total || members.length,
            // "PO 5952 · B0S 1/5" -> the shared name without the member suffix.
            name: String(first.name || '').replace(/\s+\d+\/\d+$/, ''),
            units: members.reduce((s2, m) => s2 + (m.units || 0), 0),
            mode: first.mode,
            source: first.source,
            itemCode: first.itemCode || null,
            productName: first.productName || null,
            updatedAt: members.reduce((max, m) => (String(m.updatedAt || '') > String(max || '') ? m.updatedAt : max), null),
            savedAt: members.reduce((max, m) => (String(m.savedAt || '') > String(max || '') ? m.savedAt : max), null),
            lastOpened: !!lastOpenedId && members.some(m => m.id === lastOpenedId),
        });
    }
    rows.sort((a, b) => String(b.updatedAt || b.savedAt || '').localeCompare(String(a.updatedAt || a.savedAt || '')));
    return rows;
}

// Owner, 2026-09-01: "the watch in the background button should disable when the unit
// configuration is added to the queue". How the CURRENT item list compares to the ACTIVE watch
// rows: EXACT (same asin+qty set — the button disables), DIFFERENT_QTY (watched, but at another
// quantity — watching again updates it), PARTIAL (some items unwatched), NONE.
// Owner, 2026-09-01: "the score placement button - open a modal and ask if the shipment should
// be submitted as a single shipment only, as a single shipment & separate shipments broken up
// by box, or just separate broken up by box". The box choices ride the partition sweep with an
// explicit per-box variants override; this helper decides whether that override is expressible
// for the CURRENT item list and builds it. Mirrors PartitionSweepService.SWEEP_PLAN_BUDGET.
export const SWEEP_PLAN_BUDGET = 10;
export function scoreSubmissionPlan(items, packagingByAsin) {
    const list = (items || []).filter(i => i && i.asin);
    if (list.length === 0) return { eligible: false, reason: 'no items staged' };
    if (list.length > 1) {
        return { eligible: false, reason: `box breakup analyzes one ASIN at a time — this plan has `
            + `${list.length} ASINs; use “Explore box splits…” per ASIN instead` };
    }
    const { asin, quantity } = list[0];
    const perBox = packagingByAsin?.[asin]?.unitsPerBox;
    if (!perBox || perBox <= 0) {
        return { eligible: false, reason: `units/box unknown for ${asin} — confirm packaging first` };
    }
    const boxes = Math.floor(quantity / perBox);
    if (boxes < 2) {
        return { eligible: false, reason: `${quantity} unit${quantity !== 1 ? 's' : ''} at ${perBox}/box `
            + `is ${boxes} whole box${boxes !== 1 ? 'es' : ''} — nothing to break apart` };
    }
    if (boxes > SWEEP_PLAN_BUDGET) {
        return { eligible: false, reason: `${boxes} boxes exceeds the ${SWEEP_PLAN_BUDGET}-plan sweep `
            + `budget — use “Explore box splits…” for coarser chunks` };
    }
    return {
        eligible: true,
        asin,
        quantity,
        boxes,
        chunks: Array.from({ length: boxes }, () => perBox),
        remainder: quantity - boxes * perBox,
    };
}

export function watchCoverage(items, watchRows) {
    const list = (items || []).filter(i => i && i.asin);
    if (list.length === 0) return 'NONE';
    const active = new Map();
    (watchRows || []).forEach(r => {
        // Dual cadence 2026-09-04: a SPLIT row (chunkSpec) is a different shape -- only FULL
        // rows count toward full-quantity coverage. A SPREAD row (FC Spread Target, V2) is its own
        // shape even with no chunkSpec -- V1 never sees it.
        if (r && r.status === 'ACTIVE' && r.asin && !r.chunkSpec && r.purpose !== 'SPREAD') active.set(r.asin, r.qty);
    });
    let matched = 0;
    let qtyDiff = 0;
    for (const i of list) {
        if (!active.has(i.asin)) continue;
        matched += 1;
        if (Number(active.get(i.asin)) !== Number(i.quantity)) qtyDiff += 1;
    }
    if (matched === 0) return 'NONE';
    if (matched < list.length) return 'PARTIAL';
    return qtyDiff > 0 ? 'DIFFERENT_QTY' : 'EXACT';
}

export function fcMapSourceItems(analysisResult, items) {
    const fromResponse = analysisResult?.items;
    if (Array.isArray(fromResponse) && fromResponse.some(i => i?.fcInventory?.length > 0)) {
        return fromResponse;
    }
    return items || [];
}

function FcDistributionMap({ items, analysisResult, selectedPlacement }) {
    const [tooltip, setTooltip] = useState(null);
    const [mapPosition, setMapPosition] = useState({ coordinates: [-96, 38], zoom: 1 });

    // 2026-08-23 (owner, live): "current distribution should be all ASINs on the shipment."
    // The panel's row items carry fcInventory only on PO-mode's per-row enrich -- ASIN-mode rows
    // never had it, so the map's blue markers vanished. The ANALYZE RESPONSE's items are
    // server-enriched with per-FC inventory (lat/lng included) for every ASIN on the shipment --
    // prefer them whenever any carry inventory; the row items remain the pre-analyze fallback.
    const mapSourceItems = useMemo(() => fcMapSourceItems(analysisResult, items), [analysisResult, items]);

    // Aggregate current FC inventory across all items (deduplicate by fcCode). FCs without
    // resolved coordinates are NOT dropped -- they can't be plotted, so they're collected
    // separately and listed under the map ("location unknown") instead of silently vanishing.
    const { currentFcs, unknownCurrentFcs } = useMemo(() => {
        const fcMap = {};
        for (const item of mapSourceItems) {
            if (!item.fcInventory) continue;
            for (const fc of item.fcInventory) {
                if (!fcMap[fc.fcCode]) {
                    fcMap[fc.fcCode] = { ...fc, quantity: 0, asins: [] };
                }
                fcMap[fc.fcCode].quantity += fc.quantity;
                fcMap[fc.fcCode].asins.push({ asin: item.asin, name: item.name, quantity: fc.quantity });
            }
        }
        const all = Object.values(fcMap);
        return {
            currentFcs: all.filter(fc => fc.lat && fc.lng),
            unknownCurrentFcs: all.filter(fc => !fc.lat || !fc.lng)
        };
    }, [mapSourceItems]);

    // Get destination FCs from the selected placement option (same unknown-location handling)
    const { destinationFcs, unknownDestinationFcs } = useMemo(() => {
        if (!analysisResult?.placementOptions || !selectedPlacement) return { destinationFcs: [], unknownDestinationFcs: [] };
        const option = analysisResult.placementOptions.find(po => po.placementOptionId === selectedPlacement);
        if (!option?.shipments) return { destinationFcs: [], unknownDestinationFcs: [] };
        const all = option.shipments
            .filter(s => s.destinationFc)
            .map(s => ({
                fcCode: s.destinationFc,
                city: s.destinationCity,
                state: s.destinationState,
                lat: s.lat,
                lng: s.lng,
                units: s.items?.reduce((sum, i) => sum + i.quantity, 0) || 0,
                transportCost: s.transportCost
            }));
        return {
            destinationFcs: all.filter(s => s.lat && s.lng),
            unknownDestinationFcs: all.filter(s => !s.lat || !s.lng)
        };
    }, [analysisResult, selectedPlacement]);

    if (currentFcs.length === 0 && destinationFcs.length === 0
        && unknownCurrentFcs.length === 0 && unknownDestinationFcs.length === 0) return null;

    const getFcMarkerSize = (quantity) => {
        if (quantity >= 500) return 18;
        if (quantity >= 100) return 14;
        if (quantity >= 10) return 10;
        return 7;
    };

    return (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">FC Distribution Map</h3>
                <div className="flex items-center gap-5 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full bg-blue-500 opacity-70"></span>
                        Current Inventory ({currentFcs.length} FCs)
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded-full border-2 border-orange-500 bg-orange-200"></span>
                        New Shipment ({destinationFcs.length} FC{destinationFcs.length !== 1 ? 's' : ''})
                    </span>
                </div>
            </div>

            <div className="relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" style={{ height: '420px' }}>
                <ComposableMap
                    projection="geoAlbersUsa"
                    projectionConfig={{ scale: 900 }}
                    style={{ width: '100%', height: '100%' }}
                >
                    <ZoomableGroup
                        center={mapPosition.coordinates}
                        zoom={mapPosition.zoom}
                        onMoveEnd={(pos) => setMapPosition(pos)}
                    >
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rpiState || geo.properties.name}
                                        geography={geo}
                                        fill="#E5E7EB"
                                        stroke="#9CA3AF"
                                        strokeWidth={0.5}
                                        style={{
                                            default: { outline: 'none' },
                                            hover: { fill: '#D1D5DB', outline: 'none' },
                                            pressed: { outline: 'none' },
                                        }}
                                    />
                                ))
                            }
                        </Geographies>

                        {/* Current inventory FC markers (blue) */}
                        {currentFcs.map((fc) => (
                            <Marker
                                key={`cur-${fc.fcCode}`}
                                coordinates={[fc.lng, fc.lat]}
                                onMouseEnter={() => setTooltip({
                                    type: 'current',
                                    fcCode: fc.fcCode,
                                    city: fc.city,
                                    state: fc.state,
                                    quantity: fc.quantity,
                                    asins: fc.asins
                                })}
                                onMouseLeave={() => setTooltip(null)}
                            >
                                <circle
                                    r={getFcMarkerSize(fc.quantity)}
                                    fill="rgba(59, 130, 246, 0.6)"
                                    stroke="#1D4ED8"
                                    strokeWidth={1}
                                />
                            </Marker>
                        ))}

                        {/* New destination FC markers (orange ring) */}
                        {destinationFcs.map((fc) => (
                            <Marker
                                key={`dest-${fc.fcCode}`}
                                coordinates={[fc.lng, fc.lat]}
                                onMouseEnter={() => setTooltip({
                                    type: 'destination',
                                    fcCode: fc.fcCode,
                                    city: fc.city,
                                    state: fc.state,
                                    units: fc.units,
                                    transportCost: fc.transportCost
                                })}
                                onMouseLeave={() => setTooltip(null)}
                            >
                                <circle
                                    r={12}
                                    fill="rgba(251, 146, 60, 0.3)"
                                    stroke="#EA580C"
                                    strokeWidth={2.5}
                                />
                                <circle
                                    r={5}
                                    fill="#EA580C"
                                />
                            </Marker>
                        ))}
                    </ZoomableGroup>
                </ComposableMap>

                {/* Tooltip */}
                {tooltip && (
                    <div className="absolute top-3 right-3 bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-xs z-10">
                        {tooltip.type === 'current' ? (
                            <div>
                                <div className="font-semibold text-blue-700">{tooltip.fcCode}</div>
                                <div className="text-xs text-gray-500">{tooltip.city}, {tooltip.state}</div>
                                <div className="text-sm font-medium mt-1">{tooltip.quantity.toLocaleString()} units</div>
                                {tooltip.asins && tooltip.asins.length > 0 && (
                                    <div className="mt-1.5 pt-1.5 border-t border-gray-100 space-y-0.5">
                                        {tooltip.asins.map(a => (
                                            <div key={a.asin} className="text-xs text-gray-500">
                                                <span className="font-mono">{a.asin}</span> — {a.quantity}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <div className="font-semibold text-orange-700">{tooltip.fcCode}</div>
                                <div className="text-xs text-gray-500">{tooltip.city}, {tooltip.state}</div>
                                <div className="text-sm font-medium mt-1">{tooltip.units} units incoming</div>
                                {tooltip.transportCost != null && (
                                    <div className="text-xs text-gray-500">Transport: ${tooltip.transportCost.toFixed(2)}</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* FCs that can't be plotted (no coordinates on file) -- listed instead of hidden */}
            {(unknownCurrentFcs.length > 0 || unknownDestinationFcs.length > 0) && (
                <div className="mt-2 text-xs text-gray-500">
                    <span className="font-medium text-gray-600">Location unknown (not on map):</span>{' '}
                    {unknownCurrentFcs.map(fc => (
                        <span key={`ucur-${fc.fcCode}`} className="inline-flex items-center gap-1 mr-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-500 opacity-70"></span>
                            <span className="font-mono">{fc.fcCode}</span> ({fc.quantity.toLocaleString()} units)
                        </span>
                    ))}
                    {unknownDestinationFcs.map(fc => (
                        <span key={`udest-${fc.fcCode}`} className="inline-flex items-center gap-1 mr-2">
                            <span className="inline-block w-2 h-2 rounded-full border border-orange-500 bg-orange-200"></span>
                            <span className="font-mono">{fc.fcCode}</span> ({fc.units} units incoming)
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
